"use client";

import { INITIAL_SERVICE_PROFESSIONALS_PARAM, parseInitialServiceProfessionals } from "@/app/booking/core/appointmentInitialServiceProfessionals";

import { buildGroupServiceCatalogOtherLines } from '@/app/booking/rewards/serviceCatalogGroupContext';
import type { ServiceCatalogBenefitsContextV1 } from '@/app/booking/rewards/serviceCatalogBenefits';
import useServiceBenefitsGroupBookingPreview from '@/app/booking/rewards/useServiceBenefitsGroupBookingPreview';
import { frontendSessionCustomerProfileProvider } from '@/app/profile/frontendSessionCustomerProfileProvider';
import { readServiceBenefitsGroupCatalogChoice } from '@/app/booking/rewardsIntegration/serviceBenefitsGroupChoice';
import { readServiceBenefitsAppointmentSelection } from '@/app/booking/rewardsIntegration/serviceBenefitsAppointmentSelection';
import { resolveFrontendMembershipSessionCartPreviewV2 } from '@/app/booking/memberships/frontendMembershipSessionRedemptionPreview';
import { specialistPriceAmountToMinorUnits } from '@/app/specialists/priceSemantics';

import { stagePackagePurchaseNavigationCompanion } from "@/app/commerce/packages/packagePurchaseNavigationCompanion";
import { projectPackageGiftCatalogHref, readPackageGiftCatalogIntent, retirePackageGiftCompanion, stagePackageGiftCompanion } from "@/app/commerce/packages/packageGiftCompanion";
import { createFrontendPackagePurchaseDefinitionV2, getFrontendPackagePurchaseV2PublicationForEntry } from "@/app/commerce/packages/frontendPackagePurchaseCatalogV2";
import { captureCustomerRewardAwareWalletReturn as captureCustomerWalletBookingReturn, captureCustomerRewardCatalogExitWalletReturn, bindCustomerRewardBookingReturn, prepareCustomerRewardServicesBinding } from "@/app/commerce/rewards/customerRewardBookingReturn";
import { cancelCustomerRewardBookingEntry, prepareCustomerRewardBookingEntryBinding, readCustomerRewardBookingEntryForRoute, readCustomerRewardBookingEntryForCatalogExit, resolveCustomerRewardBookingEntryForRoute, retireSettledCustomerRewardBookingEntry } from "@/app/commerce/rewards/customerRewardBookingEntry";
import { isCustomerRewardServicesProjectionCurrent, projectCustomerRewardServicesHref } from "@/app/commerce/rewards/customerRewardServicesHref";
import { projectCustomerRewardProductStoreHref } from "@/app/commerce/rewards/customerRewardProductEntry";
import { createCustomerRewardServicesDisplay } from "@/app/commerce/rewards/customerRewardServicesDisplay";
import { isRewardAppointmentSelectionCurrentV1, prepareRewardAppointmentSourceChangeV1, releaseRewardAppointmentSelectionForNavigationV1, rewardAppointmentProfileOwnerIsCurrent } from "@/app/booking/rewardsIntegration/rewardAppointmentSelection";
import { readRewardAppointmentSelectionV1 } from "@/app/booking/rewardsIntegration/rewardAppointmentSelectionStore";

import BookingCompletionLoading from "@/app/book/BookingCompletionLoading";
import { useGroupBookingDevelopmentPreview } from "@/app/booking/group/development/useGroupBookingDevelopmentPreview";
import { isGroupBookingDevelopmentPreviewLocation } from "@/app/booking/group/development/groupBookingDevelopmentPreviewGate";

import { captureCustomerOwnedSessionStorageMutationProof } from "@/app/booking/core/customerOwnedSessionStorageMutationCoordinator";
import { validateLocationAppointmentCatalogEmbeddedEligibility } from "@/app/booking/adapters/locationAppointmentCatalog";
import { useGroupBookingResourceCatalogPermissionV1 } from "@/app/booking/group/useGroupBookingResourceCatalogPermissionV1";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useLocale, useTranslations } from "next-intl";
import { toast } from "@/app/components/toast";

import { getCustomerAccessForFrontendUi } from "@/app/auth/frontendUiAuthSession";
import {
  readPackageSessionBookingUiCustomerOwner,
  readPackageSessionBookingUiCustomerOwnerForIntent,
} from "@/app/booking/packages/packageSessionBookingUiCustomerOwner";
import { readGroupBookingLocationServicesStoredCustomerOwner } from "@/app/booking/group/development/groupBookingDevelopmentPreviewAccess";
import { isSpecialistBookingFrontendPreviewEnabled } from "@/app/specialists/locationGateway/frontendPreviewActivation";
import { resolveSpecialistMarketplaceDiscovery } from "@/app/specialists/marketplaceDiscovery";
import {
  CUSTOMER_AUTH_SESSION_EVENT,
  CUSTOMER_AUTH_SESSION_STORAGE_KEY,
  readStoredCustomerAuthSessionStrict,
} from "@/app/services/authApi";
import { createLocationServicesAppointmentDraft } from "@/app/booking/adapters/locationServicesAppointmentDraft";
import {
  isLocationAppointmentProfessionalEligibleForTasksFromCatalog,
  isLocationGroupBookingEntryFeasibleFromCatalog,
  type LocationAppointmentCatalog,
  type LocationAppointmentProfessional,
} from "@/app/booking/adapters/locationAppointmentCatalog";
import { createSpecialistServicesAppointmentDraft } from "@/app/booking/adapters/specialistServicesAppointmentDraft";
import { preparePackageSessionServicesReturn } from "@/app/booking/packages/packageSessionServicesReturn";
import { removeAppointmentCompletionDrafts } from "@/app/booking/completion/appointmentCompletionDraftCleanup";
import { ensureAppointmentSchedulingDescendantsRemovedBeforeEdit } from "@/app/booking/completion/appointmentSchedulingDescendantCleanup";
import AppointmentGroupRosterStep from "@/app/booking/components/AppointmentGroupRosterStep";
import BookingFlowPageShell from "@/app/booking/components/BookingFlowPageShell";
import VoleraButton from "@/app/components/VoleraButton";
import bookingCompletionStyles from "@/app/book/BookingCompletionPage.module.css";
import type { AppointmentGroupRosterParticipant } from "@/app/booking/components/AppointmentGroupRoster";
import BookingFlowSelectionFooter from "@/app/booking/components/BookingFlowSelectionFooter";
import {
  APPOINTMENT_DRAFT_SCHEMA_VERSION,
  type AppointmentBookingDraftV1,
  type AppointmentDraftMode,
} from "@/app/booking/core/appointmentBookingDraft";
import {
  createAppointmentBookingDraftStrictPersisted,
  removeAppointmentBookingDraft,
  removeAppointmentBookingDraftStrictPersisted,
  storeAppointmentBookingDraft,
} from "@/app/booking/core/appointmentDraftStore";
import { quarantineCustomerOwnedSessionStorage } from "@/app/booking/core/customerOwnedSessionStorageBarrier";
import {
  appendAppointmentResultsEntryHandoffToHref,
  bindResultsServicesEntryToAppointmentDraftStrict,
  readResultsServicesEntryStrict,
  removeAppointmentResultsEntryPreferenceStrict,
  revalidateResultsServicesEntryStrict,
  type AppointmentResultsEntryPreferenceV1,
  type ResultsServicesEntryV1,
} from "@/app/booking/results";
import {
  appendPackageSessionBookingHandoffToHref,
  PACKAGE_SESSION_BOOKING_ID_QUERY_KEY,
  PACKAGE_SESSION_BOOKING_VERSION_QUERY_KEY,
  parsePackageSessionBookingHandoff,
} from "@/app/booking/packages/packageSessionBookingHandoff";
import { readPackageSessionBookingIntentStrict } from "@/app/booking/packages/packageSessionBookingStore";
import type { PackageSessionBookingIntentV1 } from "@/app/booking/packages/packageSessionBookingIntent";
import { isPackageSessionBookingIntentV2Fresh, type PackageSessionBookingIntentV2 } from "@/app/booking/packages/packageSessionBookingIntentV2";
import { createAppointmentPackageSessionPreferenceV2, type AppointmentPackageSessionPreferenceV2 } from "@/app/booking/packages/appointmentPackageSessionPreferenceV2";
import { readPackageSessionBookingIntentV2, createAppointmentPackageSessionPreferenceV2StrictPersisted,
  inspectAppointmentPackageSessionPreferenceForIntentV2, removeAppointmentPackageSessionPreferenceV2StrictPersisted,
  removePackageSessionBookingIntentV2StrictPersisted } from "@/app/booking/packages/packageSessionEntryStoreV2";
import { readFrontendCombinedPackageOwnedInventoryV2 } from "@/app/commerce/packages/frontendPackageOwnedLegacyV2";
import { createPackageSessionServicesDisplayV2 } from "@/app/booking/packages/packageSessionServicesDisplayV2";
import { samePackageContractData } from "@/app/commerce/packages/packagePurchaseContractData";
import {
  hasExactUnconfiguredPackageSessionServiceSelection,
  isUnconfiguredPackageSessionServiceSelection,
} from "@/app/booking/packages/packageSessionBookingSelection";
import {
  bindPackageSessionBookingIntentToAppointmentStrict,
  cleanupPackageSessionBookingJourneyStrict,
  inspectAppointmentPackageSessionPreferenceForIntentStrict,
  inspectAppointmentPackageSessionPreferencePresenceStrict,
  readAppointmentPackageSessionForRouteVersionedStrict as readAppointmentPackageSessionForRouteStrict,
  removeAppointmentPackageSessionPreferenceStrict,
} from "@/app/booking/packages/appointmentPackageSessionPreferenceStore";
import {
  beginSpecialistLocationGatewayCustomerOwnedWrite,
  completeSpecialistLocationGatewayCustomerOwnedWrite,
  specialistLocationGatewayCustomerOwnedWriteLeaseIsCurrent,
} from "@/app/specialists/locationGateway/terminalWriteCoordinator";
import type { AppointmentPackageSessionPreferenceV1 } from "@/app/booking/packages/appointmentPackageSessionPreference";
import { appendMembershipSessionBookingHandoff, parseMembershipSessionBookingHandoff } from "@/app/booking/memberships/membershipSessionBookingHandoff";
import { continueMembershipSessionServices } from "@/app/booking/memberships/membershipSessionServicesContinuation";
import { closeMembershipSessionServicesIntentStrict } from "@/app/booking/memberships/membershipSessionServicesIntentCleanup";
import { inspectAppointmentMembershipSessionPreferenceForIntentStrict } from "@/app/booking/memberships/appointmentMembershipSessionPreferenceStore";
import { canReplaceMembershipServicesProjectionInPlace, readMembershipSessionServicesEntry } from "@/app/booking/memberships/membershipSessionServicesPreview";
import { createMembershipSessionServicesDisplay } from "@/app/booking/memberships/membershipSessionServicesDisplay";
import {
  RESULTS_APPOINTMENT_INTENT_ID_QUERY_KEY,
  RESULTS_APPOINTMENT_INTENT_SCHEMA_VERSION,
  RESULTS_APPOINTMENT_INTENT_VERSION_QUERY_KEY,
  appendResultsAppointmentIntentHandoffToHref,
  isResultsAppointmentIntentOpaquePointer,
} from "@/app/results/booking";
import { createBookingDraftId } from "@/app/booking/core/draftId";
import {
  createGroupBookingCoordinatorDraft,
  createGroupBookingCoordinatorDraftId,
  isCurrentGroupBookingCoordinatorCapability,
  isGroupBookingCoordinatorReadyForAcceptance,
  removeGroupBookingCoordinatorGuest,
  validateGroupBookingCoordinatorDraft,
  type GroupBookingCoordinatorDraftV2,
} from "@/app/booking/group/groupBookingCoordinatorDraft";
import {
  GROUP_BOOKING_STORAGE_MAX_AGE_MS,
  bindGroupBookingCoordinatorAfterAuth,
  createGroupBookingCoordinatorAuthResumeTarget,
  createSpecialistLocationGatewayGroupBookingCoordinatorDestinationLifecycle,
  createSpecialistLocationGatewayGroupBookingCoordinatorDestinationTransaction,
  ensureGroupBookingCoordinatorDraftRemoved,
  getOrCreateGroupBookingAnonymousSessionOwnerId,
  inspectGroupBookingCoordinatorDraftPersisted,
  readGroupBookingAnonymousSessionOwnerId,
  reconcileCurrentAcceptedGroupAppointmentHandoff,
  reconcileCurrentAcceptedSpecialistGroupAppointmentHandoff,
  replaceGroupBookingCoordinatorDraftOwnerAwarePersisted,
  removeLocationGroupBookingBuildingDraftStrictPersisted,
  storeGroupBookingCoordinatorDraftPersisted,
} from "@/app/booking/group/groupBookingDraftStore";
import {
  appendGroupBookingCoordinatorHandoff,
  GROUP_BOOKING_COORDINATOR_DRAFT_PARAM,
  GROUP_BOOKING_COORDINATOR_VERSION_PARAM,
  parseGroupBookingCoordinatorHandoff,
} from "@/app/booking/group/groupBookingCoordinatorHandoff";
import { createGroupBookingCoordinatorFingerprint } from "@/app/booking/group/groupBookingCoordinatorFingerprint";
import {
  isCurrentGroupBookingFrontendServicesHost,
  type GroupBookingFrontendServicesHostV1,
} from "@/app/booking/group/groupBookingFrontendServicesHost";
import { createCanonicalGroupBookingTupleKey } from "@/app/booking/group/groupBookingCanonicalFingerprint";
import {
  readDefaultSpecialistLocationGatewayGroupDownstreamRegistry,
  resolveSpecialistLocationGatewayGroupDownstreamAuthority,
  resolveSpecialistLocationGatewayGroupCurrentAuthoritySnapshot,
  type SpecialistLocationGatewayGroupDownstreamRegistryV1,
} from "@/app/booking/group/specialistLocationGatewayGroupDownstreamAuthority";
import type { GroupAppointmentBookingDraftV2 } from "@/app/booking/group/groupAppointmentBookingDraft";
import {
  createSpecialistLocationGatewayGroupAppointmentDestinationLifecycle,
  discardCurrentGroupAppointmentFlow,
} from "@/app/booking/group/groupAppointmentLifecycleTransaction";
import {
  acceptCurrentGroupBookingCoordinator,
  refreshGroupBookingExactGraphAuthority,
} from "@/app/booking/group/groupBookingCoordinatorAuthority";
import {
  buildGroupAppointmentBookingDraftHandoffHref,
  canConsumeGroupAppointmentV2AtSelectSlot,
  GROUP_APPOINTMENT_V2_SELECT_SLOT_CONSUMER_STATUS,
} from "@/app/booking/group/groupAppointmentBookingDraftHandoff";
import { canActivatePublicGroupLocationBooking } from "@/app/booking/group/groupLocationBookingActivation";
import {
  canSpecialistGroupStepOneRuntimeConsumeDownstream,
  createSpecialistCurrentAssignmentGroupCapabilityProviderNarrowingAdapter,
  createSpecialistCurrentAssignmentGroupCatalogFingerprint,
  resolveSpecialistCurrentAssignmentGroupStepOneRuntime,
  specialistGroupStepOneRuntimeOwnsAcceptedHandoff,
  specialistGroupStepOneRuntimeOwnsCapability,
  specialistGroupStepOneRuntimeOwnsCoordinator,
  validateSpecialistCurrentAssignmentGroupStepOneRuntime,
  type SpecialistCurrentAssignmentGroupStepOneRuntimeV1,
} from "@/app/booking/group/specialistCurrentAssignmentGroupStepOneRuntime";
import {
  bindSpecialistLocationGatewayGroupBookingCoordinatorAfterAuth,
  canSpecialistLocationGatewayGroupStepOneRuntimeConsumeDownstream,
  createSpecialistLocationGatewayGroupStepOneRuntime,
  createSpecialistLocationGatewayGroupCapabilityProviderNarrowingAdapter,
  reconcileCurrentAcceptedSpecialistLocationGatewayGroupAppointmentHandoff,
  resolveSpecialistLocationGatewayGroupStepOneRuntimeContext,
  specialistLocationGatewayGroupStepOneRuntimeOwnsAcceptedHandoff,
  specialistLocationGatewayGroupStepOneRuntimeOwnsCapability,
  specialistLocationGatewayGroupStepOneRuntimeOwnsCoordinator,
  SPECIALIST_LOCATION_GATEWAY_GROUP_STEP_ONE_DOWNSTREAM_CONSUMER_ID,
  SPECIALIST_LOCATION_GATEWAY_GROUP_STEP_ONE_DOWNSTREAM_CONSUMER_REVISION,
  SPECIALIST_LOCATION_GATEWAY_GROUP_STEP_ONE_DOWNSTREAM_CONSUMER_SCHEMA_VERSION,
  validateSpecialistLocationGatewayGroupStepOneRuntime,
  type SpecialistLocationGatewayGroupStepOneRuntimeV1,
} from "@/app/booking/group/specialistLocationGatewayGroupStepOneRuntime";
import { frontendFixtureGroupBookingCapabilityProvider } from "@/app/booking/group/frontendFixtureGroupBookingCapabilityProvider";
import { refreshLocationGroupBookingRosterCatalog } from "@/app/booking/group/refreshLocationGroupBookingRosterCatalog";
import {
  GROUP_BOOKING_PROVIDER_REQUEST_SCHEMA_VERSION,
  resolveGroupBookingPreliminaryCapability,
  type GroupBookingCapabilityProvider,
} from "@/app/booking/group/groupBookingProviders";
import {
  applyGroupBookingParticipantCatalogCommitIntent,
  createGroupBookingParticipantCatalogCommitIntent,
  createGroupBookingParticipantCatalogTransaction,
  reduceGroupBookingParticipantCatalogTransaction,
  type GroupBookingParticipantCatalogTransactionV1,
} from "@/app/booking/group/groupBookingParticipantCatalogTransaction";
import {
  frontendFixtureLocationGroupBookingCatalogLineProvider,
  LOCATION_GROUP_BOOKING_CATALOG_LINE_REQUEST_SCHEMA_VERSION,
  resolveEditableLocationGroupBookingParticipantCatalog,
  resolveLocationGroupBookingParticipantCatalog,
  type LocationGroupBookingCatalogLineProvider,
} from "@/app/booking/group/locationGroupBookingCatalogLineProvider";
import {
  projectLocationGroupBookingCatalogSelections,
  projectOwnedLocationGroupBookingCatalogSelections,
  type LocationGroupBookingCatalogLinePublicationV1,
} from "@/app/booking/group/locationGroupBookingCatalogAdapter";
import {
  createSpecialistOrganizerGroupBookingCatalogFingerprint,
  resolveEditableSpecialistOrganizerGroupBookingParticipantCatalog,
  resolveSpecialistOrganizerGroupBookingParticipantCatalog,
  SPECIALIST_ORGANIZER_GROUP_BOOKING_CATALOG_REQUEST_SCHEMA_VERSION,
  type SpecialistOrganizerGroupBookingCatalogLineProvider,
  type SpecialistOrganizerGroupBookingCatalogSourceV1,
} from "@/app/booking/group/specialistOrganizerGroupBookingCatalogLineProvider";
import {
  refreshSpecialistOrganizerGroupBookingCatalogPersisted,
  validateCurrentSpecialistOrganizerGroupBookingCoordinatorCatalog,
} from "@/app/booking/group/specialistOrganizerGroupBookingCatalogTransaction";
import {
  resolveCurrentSpecialistLocationGatewayPreDraftGroupCatalogDescriptor,
  resolveEditableSpecialistLocationGatewayOrganizerGroupBookingParticipantCatalog,
  resolvePreDraftSpecialistLocationGatewayOrganizerGroupBookingParticipantCatalog,
  validateCurrentPreDraftSpecialistLocationGatewayOrganizerGroupBookingParticipantCatalog,
  type SpecialistLocationGatewayPreDraftGroupCatalogDescriptorV1,
  type SpecialistLocationGatewayOrganizerGroupBookingCatalogLineProvider,
} from "@/app/booking/group/specialistLocationGatewayOrganizerGroupBookingCatalogLineProvider";
import { frontendPreviewSpecialistLocationGatewayOrganizerGroupCatalogLineProvider } from "@/app/booking/group/frontendPreviewSpecialistLocationGatewayOrganizerGroupCatalogLineProvider";
import {
  refreshSpecialistLocationGatewayOrganizerGroupBookingCatalogPersisted,
  validateCurrentSpecialistLocationGatewayOrganizerGroupBookingCoordinatorCatalog,
} from "@/app/booking/group/specialistLocationGatewayOrganizerGroupBookingCatalogTransaction";
import { resolveGroupBookingSelectionSummary } from "@/app/booking/presentation/groupBookingSelectionSummary";
import { getAppointmentLineConfigurationSummary } from "@/app/booking/presentation/appointmentLineConfigurationSummary";
import { appointmentSchedulingRuntime } from "@/app/booking/scheduling/appointmentSchedulingRuntime";
import { isAppointmentTimeProfessionalMutationEvidenceCurrent } from "@/app/booking/scheduling/appointmentTimeProfessionalMutation";
import {
  appendAppointmentTimeServicesResumeHandoff,
  APPOINTMENT_TIME_SERVICES_RESUME_DRAFT_PARAM,
  APPOINTMENT_TIME_SERVICES_RESUME_SCHEMA_PARAM,
} from "@/app/booking/scheduling/appointmentTimeServicesResumeHandoff";
import { isSpecialistStepOneFeasible } from "@/app/booking/core/specialistStepOneFeasibility";
import {
  getBusinessGiftCardCatalog,
} from "@/app/commerce/giftCards/businessGiftCardCatalog";
import {
  buildLocationMembershipsPath,
  getMembershipsForLocation,
} from "@/app/commerce/memberships/membershipCatalog";
import { createPackagePurchaseDraft } from "@/app/commerce/packages/packagePurchaseDraft";
import { isPackageCatalogDefinitionV1, isPackageCatalogDefinitionV2, type PackageCatalogDefinition } from "@/app/commerce/packages/packageCatalogDefinition";
import { packagePurchaseLocationEntryV2IsCurrent, preparePackagePurchaseLocationEntryV2, type PackagePurchaseLocationEntryV2 } from "@/app/commerce/packages/packagePurchaseLocationEntryV2";
import { buildPackagePurchaseDraftHandoffHref } from "@/app/commerce/packages/packagePurchaseDraftHandoff";
import {
  ensurePackagePurchaseDraftRemoved,
  storePackagePurchaseDraft,
} from "@/app/commerce/packages/packagePurchaseDraftStore";
import {
  appendBookingFlowServiceParams,
  BOOKING_FLOW_ORIGIN_PARAM,
  BOOKING_FLOW_ORIGIN_LOCATION,
  BOOKING_FLOW_ORIGIN_SPECIALIST,
  getBookingFlowSelectedServiceIds,
  getBookingFlowSelectedServiceVariantSelections,
  serializeBookingFlowServiceVariantSelections,
} from "@/app/components/booking-flow/bookingFlowCatalog";
import ConfirmationModal from "@/app/components/ConfirmationModal";
import CheckoutConfirmationModal from "@/app/components/CheckoutConfirmationModal";
import { prepareSpecialistBookingHistoryNavigation } from "@/app/components/booking-flow/specialistBookingSafety";
import { buildConnectHref } from "@/app/connect/connectReturnPath";
import {
  buildLocationStorePath,
  getOnlinePurchasableLocationStore,
} from "@/app/locations/locationStore";
import {
  buildLocationGiftsPath,
  buildLocationProfilePath,
  buildLocationServicesHref,
  buildLocationServicesPath,
  LOCATION_CONFIGURE_SERVICE_PARAM,
  LOCATION_CONFIGURE_VARIANT_PARAM,
  LOCATION_INITIAL_PROFESSIONAL_PARAM,
} from "@/app/locations/routes";
import { getLocationServicesBackDistance } from "@/app/locations/locationBrowserNavigation";
import { useKeyedHandoffHydrationState } from "@/app/hooks/useKeyedHandoffHydrationState";
import type { LocationServicePortfolioImage } from "@/app/locations/locationServicePortfolio";
import {
  EMPTY_LOCATION_SERVICE_REVIEW_SUMMARIES,
  type PublishedLocationServiceReviewSummary,
} from "@/app/locations/locationServiceReviewSummary";
import {
  claimSpecialistServicesPageAnalyticsSource,
  emitSpecialistAnalyticsEvent,
} from "@/app/specialists/analytics";
import type { SpecialistWorkplacePresentation } from "@/app/specialists/components/SpecialistWorkplaceCard";
import SpecialistBookingSummary, {
  type SpecialistBookingSummaryItem,
} from "@/app/specialists/components/SpecialistBookingSummary";
import {
  resolveSpecialistCatalogCategories,
  type SpecialistCatalogCategory,
} from "@/app/specialists/catalogCategories";
import type {
  SpecialistAssignment,
  SpecialistPerson,
  SpecialistService,
  SpecialistServiceOffering,
  SpecialistServiceSelection,
  SpecialistWorkplace,
} from "@/app/specialists/model";
import {
  getSpecialistGroupBookingPolicy,
  isSpecialistOfferingPublishedAtWorkplace,
} from "@/app/specialists/onlineBooking";
import {
  SPECIALIST_SERVICE_ADD_ONS_PARAM,
  getSpecialistServiceAddOnQuerySelections,
  normalizeSpecialistServiceAddOnQuerySelections,
  resolveSpecialistServiceAddOnQuerySelections,
  resolveSpecialistServiceConfiguration,
  serializeSpecialistServiceAddOnQuerySelections,
  type SpecialistServiceAddOnQuerySelection,
} from "@/app/specialists/serviceAddOns";
import {
  SPECIALIST_SERVICE_BUNDLE_ADD_ONS_PARAM,
  createSpecialistServiceBundleAddOnPlan,
  getSpecialistServiceBundleAddOnQuerySelections,
  normalizeSpecialistServiceBundleAddOnQuerySelections,
  resolveSpecialistServiceBundleConfiguration,
  serializeSpecialistServiceBundleAddOnQuerySelections,
  type SpecialistServiceBundleAddOnQuerySelection,
} from "@/app/specialists/serviceBundleAddOns";
import { isSpecialistServiceConfigurationAvailableAtWorkplace } from "@/app/specialists/serviceConfigurationAvailability";
import {
  getSelectableSpecialistServiceVariant,
  specialistOfferingRequiresVariantSelection,
} from "@/app/specialists/serviceVariants";
import { formatSpecialistCurrencyAmount } from "@/app/specialists/priceFormatting";
import { getServiceSessionDemoBadgeLabel } from "@/app/specialists/serviceSessionDemoFixtures";
import { specialistPriceMinorUnitsToAmount } from "@/app/specialists/priceSemantics";
import {
  buildSpecialistProfilePath,
  buildSpecialistServicesHref,
  buildSpecialistServicesPath,
  SPECIALIST_CONFIGURE_SERVICE_PARAM,
  SPECIALIST_CONFIGURE_VARIANT_PARAM,
  type SpecialistCatalogFocusTarget,
  type SpecialistCatalogView,
  type SpecialistServiceConfigurationTarget,
} from "@/app/specialists/routes";
import {
  appendSpecialistLocationGatewayHandoff,
  beginSpecialistLocationGatewayEntry,
  buildSpecialistLocationGatewayResolvedAppointmentServicesHref,
  buildSpecialistLocationGatewayResolvedPackageServicesHref,
  buildSpecialistLocationGatewayResolvedServicesBackHref,
  canActivatePublicSpecialistLocationGateway,
  createSpecialistLocationGatewayAppointmentDestinationTransaction,
  createSpecialistLocationGatewayPackageDestinationTransaction,
  closeSpecialistLocationGatewayCommittedDestinationStrict,
  commitSpecialistLocationGatewayNavigationLineage,
  discardSpecialistLocationGatewayPreDraftForOwnerStrict,
  encodeSpecialistLocationGatewayTuple,
  FRONTEND_FIXTURE_SPECIALIST_LOCATION_GATEWAY_PROVIDER_ID,
  FRONTEND_FIXTURE_SPECIALIST_LOCATION_GATEWAY_PROVIDER_REVISION,
  getFrontendFixtureSpecialistLocationGatewayPackageIntent,
  getFrontendFixtureSpecialistLocationGatewayServiceIntent,
  getFrontendFixtureSpecialistLocationGatewaySource,
  isResultsSpecialistLocationGatewaySourceAction,
  parseSpecialistLocationGatewayServicesHandoff,
  removeSpecialistLocationGatewayIntentStrict,
  readSpecialistLocationGatewayCommittedLifecycleAuthContextStrict,
  resolveSpecialistLocationGatewaySelectedDestinationStrict,
  specialistLocationGatewayServicesProjectionOwnerIsExact,
  returnSpecialistLocationGatewayDestinationToPreDraftStrict,
  transitionSpecialistLocationGatewayCatalog,
  type SpecialistLocationGatewayCommittedNavigationLineageV1,
  type SpecialistLocationGatewayIntentRecordV1,
  type SpecialistLocationGatewayProviderV1,
  type SpecialistLocationGatewayServicesSessionV1,
} from "@/app/specialists/locationGateway";
import {
  createFrontendFixtureSpecialistLocationGatewayServicesCartProvider,
  projectSpecialistLocationGatewayServicesCartSelections,
  resolveSpecialistLocationGatewayServicesCartProvider,
  type SpecialistLocationGatewayServicesCartOwnerAuthorityV1,
  type SpecialistLocationGatewayServicesCartProviderV1,
} from "@/app/specialists/locationGateway/cart/configuredServicesCartAdapterProvider";
import {
  specialistLocationGatewayServicesCartIsContinuable,
  type SpecialistLocationGatewayConfiguredServicesCartV1,
} from "@/app/specialists/locationGateway/cart/configuredServicesCartIntent";
import {
  publishSpecialistLocationGatewayConfiguredServicesCartStrict,
  readSpecialistLocationGatewayConfiguredServicesCartStrict,
  removeSpecialistLocationGatewayConfiguredServicesCartStrict,
  replaceSpecialistLocationGatewayConfiguredServicesCartStrict,
  retireSpecialistLocationGatewayConfiguredServicesCartsForIntentStrict,
} from "@/app/specialists/locationGateway/cart/configuredServicesCartIntentStore";

import SpecialistServicesModal, {
  type SpecialistInitialConfigurationOutcome,
  type SpecialistServicesEntryPointKind,
  type SpecialistServicesPageExitAction,
} from "../SpecialistServicesModal";
import {
  resolveAppointmentTimeServicesResumeRoute,
  type AppointmentTimeServicesResumeRouteResolution,
} from "./appointmentTimeServicesResumeRoute";

export type ServicesPageRouteDescriptor =
  | { kind: "specialist-services" }
  | { kind: "location-services"; locationId: string };
const DEFAULT_SERVICES_PAGE_ROUTE = Object.freeze({
  kind: "specialist-services" as const,
});
const EMPTY_LOCATION_PROFESSIONALS: readonly LocationAppointmentProfessional[] =
  [];
const GROUP_ORGANIZER_PARTICIPANT_ID = "organizer";
const GROUP_FIRST_GUEST_NUMBER = 2;
const FRONTEND_FIXTURE_SPECIALIST_LOCATION_GATEWAY_SERVICES_CART_PROVIDER =
  createFrontendFixtureSpecialistLocationGatewayServicesCartProvider();

export function readLocationResultsAppointmentIntentPointer(
  searchParams: Pick<URLSearchParams, "getAll">,
) {
  const ids = searchParams.getAll(RESULTS_APPOINTMENT_INTENT_ID_QUERY_KEY);
  const versions = searchParams.getAll(
    RESULTS_APPOINTMENT_INTENT_VERSION_QUERY_KEY,
  );
  return ids.length === 1 &&
    versions.length === 1 &&
    isResultsAppointmentIntentOpaquePointer(ids[0]) &&
    versions[0] === String(RESULTS_APPOINTMENT_INTENT_SCHEMA_VERSION)
    ? ids[0]!
    : null;
}

export function hasLocationResultsAppointmentIntentHandoff(
  searchParams: Pick<URLSearchParams, "getAll">,
) {
  return (
    searchParams.getAll(RESULTS_APPOINTMENT_INTENT_ID_QUERY_KEY).length > 0 ||
    searchParams.getAll(RESULTS_APPOINTMENT_INTENT_VERSION_QUERY_KEY).length > 0
  );
}

export const LOCATION_PACKAGE_SESSION_SERVICES_ROUTE_QUERY_KEYS = [
  "appointment_mode",
  "catalog",
  "configure_service_id",
  "configure_variant_id",
  "focus_package_id",
  "focus_service_id",
  "packages",
  "professional",
  "service_addons",
  "service_bundle_addons",
  "service_variants",
  "services",
  APPOINTMENT_TIME_SERVICES_RESUME_DRAFT_PARAM,
  APPOINTMENT_TIME_SERVICES_RESUME_SCHEMA_PARAM,
] as const;

type LocationPackageSessionServicesEntryResult =
  | { readonly status: "none" }
  | {
      readonly status: "ready";
      readonly intent: PackageSessionBookingIntentV1 | PackageSessionBookingIntentV2;
      readonly nowEpochMs: number;
    }
  | { readonly status: "unavailable" };

function canonicalServiceIdentifiers(value: string | null) {
  if (value === null || value === "") return [];
  const identifiers = value.split(",");
  return identifiers.length > 0 &&
    identifiers.every(
      (identifier) =>
        identifier.length > 0 &&
        identifier.length <= 160 &&
        identifier === identifier.trim() &&
        !/[\u0000-\u001f\u007f]/u.test(identifier),
    ) &&
    new Set(identifiers).size === identifiers.length
    ? identifiers
    : null;
}

/**
 * Rejoins the Activity-owned package pointer to one exact current Location
 * catalog. The covered service may be temporarily absent from the cart, but
 * no URL value can replace the stored customer/entitlement/service authority.
 */
export function readLocationPackageSessionServicesEntry(input: {
  readonly locationId: string;
  readonly pathname: string;
  readonly searchParams: URLSearchParams;
  readonly catalog: LocationAppointmentCatalog | null;
  readonly expectedCustomerAccountId: string | null | undefined;
  readonly nowEpochMs: number | null;
}): LocationPackageSessionServicesEntryResult {
  const handoff = parsePackageSessionBookingHandoff(
    input.searchParams,
    LOCATION_PACKAGE_SESSION_SERVICES_ROUTE_QUERY_KEYS,
  );
  if (handoff.status === "absent") return { status: "none" };
  if (
    handoff.status !== "ready" ||
    !input.catalog ||
    typeof input.expectedCustomerAccountId !== "string" ||
    input.nowEpochMs === null ||
    input.pathname !== buildLocationServicesPath(input.locationId) ||
    input.searchParams.getAll("appointment_mode").length > 0 ||
    input.searchParams.getAll("packages").length > 0 ||
    input.searchParams.getAll("focus_package_id").length > 0
  ) {
    return { status: "unavailable" };
  }

  const catalogValues = input.searchParams.getAll("catalog");
  const servicesValues = input.searchParams.getAll("services");
  const configureValues = input.searchParams.getAll(
    LOCATION_CONFIGURE_SERVICE_PARAM,
  );
  if (
    catalogValues.length > 1 ||
    (catalogValues.length === 1 && catalogValues[0] !== "services") ||
    servicesValues.length > 1 ||
    configureValues.length > 1
  ) {
    return { status: "unavailable" };
  }
  const selectedServiceIds = canonicalServiceIdentifiers(
    servicesValues[0] ?? null,
  );
  const configureServiceIds = canonicalServiceIdentifiers(
    configureValues[0] ?? null,
  );
  if (
    !selectedServiceIds ||
    !configureServiceIds ||
    configureServiceIds.length > 1
  ) {
    return { status: "unavailable" };
  }

  const readV2 = handoff.schemaVersion === 2 ? readPackageSessionBookingIntentV2(handoff.handoffId, input.expectedCustomerAccountId, input.nowEpochMs) : null;
  const read = handoff.schemaVersion === 2 ? (readV2 && isPackageSessionBookingIntentV2Fresh(readV2, input.nowEpochMs) ? { status: "ready" as const, intent: readV2 } : { status: "unavailable" as const }) : readPackageSessionBookingIntentStrict({
    handoffId: handoff.handoffId,
    expectedCustomerAccountId: input.expectedCustomerAccountId,
    nowEpochMs: input.nowEpochMs,
  });
  if (read.status !== "ready") return { status: "unavailable" };
  const intent = read.intent;
  const requiredVariantId = intent.schemaVersion === 2 && intent.variant.kind === "variant" ? intent.variant.variantId : null;
  if (intent.schemaVersion === 2) {
    const inventory = readFrontendCombinedPackageOwnedInventoryV2(intent.customerAccountId);
    if (inventory.status !== "ready" || inventory.entitlements.filter(owned => samePackageContractData(owned, intent.ownedSnapshot)).length !== 1) return { status: "unavailable" };
  }
  const currentServiceIds = new Set(
    input.catalog.services.map(({ serviceId }) => serviceId),
  );
  const exactServices = input.catalog.services.filter(
    (service) =>
      service.kind === "service" &&
      service.serviceId === intent.serviceId &&
      service.serviceVersion === intent.serviceVersion,
  );
  const exactOfferings = input.catalog.offerings.filter(
    (offering) =>
      offering.serviceId === intent.serviceId &&
      offering.offeringId === intent.offeringId &&
      offering.catalogVersion === intent.catalogVersion &&
      offering.userLocationId ===
        input.catalog!.catalogAssignment.userLocationId &&
      offering.isOnlineBookable,
  );
  if (
    intent.locationId !== input.locationId ||
    intent.catalogVersion !== input.catalog.workplace.catalogVersion ||
    input.catalog.workplace.locationId !== input.locationId ||
    input.catalog.catalogAssignment.locationId !== input.locationId ||
    exactServices.length !== 1 ||
    exactOfferings.length !== 1 ||
    (intent.schemaVersion === 2 && (intent.variant.kind === "variant"
      ? exactOfferings[0]?.variantLoadState !== "ready" || exactOfferings[0].variants?.filter(row => row.variantId === requiredVariantId && row.serviceId === intent.serviceId).length !== 1
      : specialistOfferingRequiresVariantSelection(exactOfferings[0]))) ||
    [...selectedServiceIds, ...configureServiceIds].some(
      (serviceId) => !currentServiceIds.has(serviceId),
    )
  ) {
    return { status: "unavailable" };
  }

  return { status: "ready", intent, nowEpochMs: input.nowEpochMs };
}

/**
 * Removes an Activity-owned Package intent when the customer closes the
 * Location Services entry before an Appointment draft exists. Once an
 * Appointment preference exists, the caller must instead use the coordinated
 * complete-draft cleanup so the preference and ordinary descendants cannot be
 * orphaned.
 */
export function closeLocationPackageSessionServicesIntentStrict(input: {
  readonly expectedIntent: PackageSessionBookingIntentV1 | PackageSessionBookingIntentV2;
  readonly nowEpochMs: number;
  readonly ownerIsCurrent: () => boolean;
}) {
  const mutableOwnerIsCurrent = () => {
    try {
      return input.ownerIsCurrent() === true;
    } catch {
      return false;
    }
  };
  if (
    !Number.isSafeInteger(input.nowEpochMs) ||
    input.nowEpochMs < 0 ||
    !mutableOwnerIsCurrent()
  ) {
    return { status: "blocked" } as const;
  }

  if (input.expectedIntent.schemaVersion === 2) {
    const companion = inspectAppointmentPackageSessionPreferenceForIntentV2(input.expectedIntent);
    if (companion.status !== "absent" || !mutableOwnerIsCurrent()) return { status: "blocked" } as const;
    const removed = removePackageSessionBookingIntentV2StrictPersisted(input.expectedIntent, { evaluatedAtEpochMs: input.nowEpochMs, mutationIsCurrent: mutableOwnerIsCurrent });
    return removed.status === "removed" && mutableOwnerIsCurrent() ? { status: "closed" as const, returnHref: input.expectedIntent.canonicalActivityDetailReturnHref } : { status: "blocked" as const };
  }

  const writeLease = beginSpecialistLocationGatewayCustomerOwnedWrite(
    [
      "package-session-services-close",
      input.expectedIntent.customerAccountId,
      input.expectedIntent.draftId,
    ].join(":"),
    mutableOwnerIsCurrent,
  );
  if (!writeLease) return { status: "blocked" } as const;

  const writeLeaseIsCurrent = () =>
    specialistLocationGatewayCustomerOwnedWriteLeaseIsCurrent(writeLease);
  const exactOwnerIsCurrentOutsideMutation = () =>
    writeLeaseIsCurrent() && mutableOwnerIsCurrent() && writeLeaseIsCurrent();

  try {
    if (!exactOwnerIsCurrentOutsideMutation()) {
      return { status: "blocked" } as const;
    }
    const companion = inspectAppointmentPackageSessionPreferenceForIntentStrict(
      {
        expectedIntent: input.expectedIntent,
        nowEpochMs: input.nowEpochMs,
      },
    );
    if (
      companion.status !== "absent" ||
      !exactOwnerIsCurrentOutsideMutation()
    ) {
      return { status: "blocked" } as const;
    }
    const cleaned = cleanupPackageSessionBookingJourneyStrict({
      expectedIntent: input.expectedIntent,
      nowEpochMs: input.nowEpochMs,
      // Storage mutation receives only the pure teardown/write generation.
      mutationIsCurrent: writeLeaseIsCurrent,
    });
    if (cleaned.status !== "cleaned" || !exactOwnerIsCurrentOutsideMutation()) {
      return { status: "blocked" } as const;
    }
    return {
      status: "closed",
      returnHref: input.expectedIntent.canonicalActivityDetailReturnHref,
    } as const;
  } catch {
    return { status: "blocked" } as const;
  } finally {
    completeSpecialistLocationGatewayCustomerOwnedWrite(writeLease);
  }
}

/**
 * Resolves Services Close from persisted Package state, never from the
 * mutable Time-resume query. The returned exact preference is subsequently
 * consumed by the coordinated Appointment + Package teardown.
 */
export function resolveLocationPackageSessionServicesCloseStateStrict(input: {
  readonly expectedIntent: PackageSessionBookingIntentV1 | PackageSessionBookingIntentV2;
  readonly nowEpochMs: number;
}) {
  const companion =
    input.expectedIntent.schemaVersion === 2 ? inspectAppointmentPackageSessionPreferenceForIntentV2(input.expectedIntent) : inspectAppointmentPackageSessionPreferenceForIntentStrict({ ...input, expectedIntent: input.expectedIntent });
  if (companion.status === "unreadable") {
    return { status: "blocked" } as const;
  }
  return companion.status === "present"
    ? ({
        status: "appointment-companion",
        preference: companion.preference,
      } as const)
    : ({ status: "pre-appointment" } as const);
}

export function readSpecialistServicesSafeEpochMs(
  now: () => number = Date.now,
) {
  try {
    const candidate = now();
    return Number.isSafeInteger(candidate) && candidate >= 0 ? candidate : null;
  } catch {
    return null;
  }
}

export function shouldBlockResultsServicesGroupContinuation(status: string) {
  return status !== "none";
}

export function shouldCompensateResultsPreferenceAfterGatewayCommit(
  status: string,
) {
  return status === "failed";
}

export function quarantineAfterAmbiguousSpecialistGatewayCommitThrow(
  quarantine: () => unknown = quarantineCustomerOwnedSessionStorage,
) {
  try {
    quarantine();
  } catch {
    // The storage barrier owns its own best-effort persistence. An injected
    // host failure must still leave this frontier non-actionable.
  }
}

export async function runSpecialistGatewayCommitWithAmbiguousFailureQuarantine<
  T,
>(
  commit: () => Promise<T>,
  quarantine: () => unknown = quarantineCustomerOwnedSessionStorage,
) {
  try {
    return await commit();
  } catch (error) {
    quarantineAfterAmbiguousSpecialistGatewayCommitThrow(quarantine);
    throw error;
  }
}

export function createSpecialistServicesAppointmentDraftId(
  isResultsEntry: boolean,
  isPackageSessionEntry = false,
) {
  return createBookingDraftId(
    isResultsEntry
      ? "appointment-results"
      : isPackageSessionEntry ? "appointment-package-session" : "appointment",
  );
}

function appointmentDraftStrictMutationIsAmbiguous(status: string) {
  return (
    status === "unreadable" ||
    status === "persistence-failed" ||
    status === "readback-mismatch"
  );
}

function removeAppointmentDraftExactAfterFailedServicesTransaction(
  draft: AppointmentBookingDraftV1,
) {
  try {
    const removed = removeAppointmentBookingDraftStrictPersisted(draft);
    if (removed.status === "removed" || removed.status === "absent") {
      return true;
    }
    quarantineCustomerOwnedSessionStorage();
    return false;
  } catch {
    quarantineCustomerOwnedSessionStorage();
    return false;
  }
}

function getExactPackageSessionAppointmentLine(input: {
  draft: AppointmentBookingDraftV1;
  intent: PackageSessionBookingIntentV1 | PackageSessionBookingIntentV2;
}) {
  const organizer = input.draft.participants.find(
    (participant) =>
      participant.participantId === input.draft.organizerParticipantId &&
      participant.role === "organizer" &&
      participant.identity?.kind === "current-customer",
  );
  if (!organizer) return null;

  const matches = organizer.serviceLines.filter(
    (line) =>
      line.selection.kind === "service" &&
      line.selection.serviceId === input.intent.serviceId &&
      line.selection.offeringId === input.intent.offeringId &&
      line.onlineBooking.catalogVersion === input.intent.catalogVersion,
  );

  const line = matches.length === 1 ? matches[0]! : null;
  if (input.intent.schemaVersion === 2) return line?.selection.kind === "service" && line.selection.variantId === (input.intent.variant.kind === "variant" ? input.intent.variant.variantId : null) ? line : null;
  return line && line.selection.kind === "service" &&
    isUnconfiguredPackageSessionServiceSelection(line.selection) &&
    line.snapshot.variantName === null && line.snapshot.addOns.length === 0
      ? line
      : null;
}

function resolveSpecialistServicesEpochMs(value?: number) {
  return value === undefined
    ? readSpecialistServicesSafeEpochMs()
    : Number.isSafeInteger(value) && value >= 0
      ? value
      : null;
}

type LocationGroupCoordinatorRouteState =
  | { status: "absent" }
  | { status: "invalid"; draftId: string | null }
  | {
      status: "auth-binding-required";
      coordinator: GroupBookingCoordinatorDraftV2;
    }
  | {
      status: "accepted-handoff";
      coordinator: GroupBookingCoordinatorDraftV2;
      appointment: GroupAppointmentBookingDraftV2;
      appointmentDraftId: string;
    }
  | { status: "ready"; coordinator: GroupBookingCoordinatorDraftV2 };
type LocationGroupCoordinatorHydrationState =
  { status: "hydrating" } | LocationGroupCoordinatorRouteState;

type GroupCoordinatorRouteOriginScope =
  | { kind: "location"; locationId: string }
  | {
      kind: "specialist-current-assignment";
      runtime: SpecialistCurrentAssignmentGroupStepOneRuntimeV1;
    }
  | {
      kind: "specialist-location-gateway";
      runtime: SpecialistLocationGatewayGroupStepOneRuntimeV1;
      groupAggregateActivationRevision: string;
    };

function hasGroupBookingCoordinatorPointer(searchParams: URLSearchParams) {
  return (
    searchParams.has(GROUP_BOOKING_COORDINATOR_DRAFT_PARAM) ||
    searchParams.has(GROUP_BOOKING_COORDINATOR_VERSION_PARAM)
  );
}

function readGroupBookingCoordinatorAuthorityPersisted(draftId: string) {
  const inspection = inspectGroupBookingCoordinatorDraftPersisted(draftId);
  if (inspection.status === "unreadable") {
    quarantineCustomerOwnedSessionStorage();
  }
  return inspection.status === "present" ? inspection.draft : null;
}

function resolveCurrentGroupCoordinatorRouteState(input: {
  searchParams: URLSearchParams;
  locationId: string;
  catalogVersion: string;
  catalogOwnerUserLocationId: string;
  returnHref: string;
  originScope: GroupCoordinatorRouteOriginScope;
  customerAccountId: string | null;
  anonymousSessionOwnerId: string | null;
  evaluatedAtEpochMs?: number;
}): LocationGroupCoordinatorRouteState {
  if (!hasGroupBookingCoordinatorPointer(input.searchParams)) {
    return { status: "absent" };
  }

  const handoff = parseGroupBookingCoordinatorHandoff(input.searchParams);
  if (!handoff.ok) return { status: "invalid", draftId: null };
  const stored = readGroupBookingCoordinatorAuthorityPersisted(handoff.draftId);
  const validation = validateGroupBookingCoordinatorDraft(stored);
  const evaluatedAtEpochMs = resolveSpecialistServicesEpochMs(
    input.evaluatedAtEpochMs,
  );
  if (evaluatedAtEpochMs === null) {
    return { status: "invalid", draftId: handoff.draftId };
  }
  const originMatches = Boolean(
    validation.valid &&
    (input.originScope.kind === "location"
      ? validation.draft.origin.kind === "location" &&
        validation.draft.origin.locationId === input.originScope.locationId
      : input.originScope.kind === "specialist-current-assignment"
        ? validation.draft.authorityState === "accepted"
          ? specialistGroupStepOneRuntimeOwnsAcceptedHandoff({
              runtime: input.originScope.runtime,
              coordinator: validation.draft,
              evaluatedAtEpochMs,
            })
          : specialistGroupStepOneRuntimeOwnsCoordinator({
              runtime: input.originScope.runtime,
              coordinator: validation.draft,
              requireBuildingAuthority: true,
              requireCurrentRuntime: true,
              evaluatedAtEpochMs,
            })
        : specialistLocationGatewayGroupStepOneRuntimeOwnsCoordinator({
            runtime: input.originScope.runtime,
            coordinator: validation.draft,
            requireBuildingAuthority:
              validation.draft.authorityState !== "accepted",
            // Committed Group may outlive the pre-draft TTL. The route
            // dispatcher and every mutation re-resolve A/B/C/provider
            // currentness; this structural check must not strand it.
            requireCurrentRuntime: false,
            evaluatedAtEpochMs: input.originScope.runtime.generatedAtEpochMs,
          })),
  );

  if (
    !validation.valid ||
    !originMatches ||
    validation.draft.locationId !== input.locationId ||
    validation.draft.catalogVersion !== input.catalogVersion ||
    validation.draft.catalogOwnerUserLocationId !==
      input.catalogOwnerUserLocationId ||
    validation.draft.returnHref !== input.returnHref ||
    (validation.draft.customerAccountId !== null
      ? validation.draft.customerAccountId !== input.customerAccountId
      : validation.draft.anonymousSessionOwnerId !==
        input.anonymousSessionOwnerId)
  ) {
    return { status: "invalid", draftId: handoff.draftId };
  }

  if (validation.draft.authorityState === "accepted") {
    if (!input.customerAccountId) {
      return { status: "invalid", draftId: handoff.draftId };
    }
    const commonReconciliationInput = {
      coordinatorDraftId: validation.draft.draftId,
      customerAccountId: input.customerAccountId,
      locationId: input.locationId,
      catalogVersion: input.catalogVersion,
      catalogOwnerUserLocationId: input.catalogOwnerUserLocationId,
      returnHref: input.returnHref,
      evaluatedAtEpochMs,
    };
    const reconciled =
      input.originScope.kind === "location"
        ? reconcileCurrentAcceptedGroupAppointmentHandoff(
            commonReconciliationInput,
          )
        : input.originScope.kind === "specialist-current-assignment"
          ? reconcileCurrentAcceptedSpecialistGroupAppointmentHandoff({
              ...commonReconciliationInput,
              personId: input.originScope.runtime.sourcePersonId,
              sourceLocationId: input.originScope.runtime.sourceLocationId,
              sourceUserLocationId:
                input.originScope.runtime.sourceUserLocationId,
              selectedLocationId: input.originScope.runtime.selectedLocationId,
              selectedUserLocationId:
                input.originScope.runtime.selectedUserLocationId,
            })
          : reconcileCurrentAcceptedSpecialistLocationGatewayGroupAppointmentHandoff(
              {
                runtime: input.originScope.runtime,
                customerAccountId: input.customerAccountId,
                groupAggregateActivationRevision:
                  input.originScope.groupAggregateActivationRevision,
                evaluatedAtEpochMs,
              },
            );

    return reconciled.status === "ready"
      ? {
          status: "accepted-handoff",
          coordinator: reconciled.coordinator,
          appointment: reconciled.appointment,
          appointmentDraftId: reconciled.appointment.draftId,
        }
      : { status: "invalid", draftId: handoff.draftId };
  }

  if (validation.draft.authorityState !== "building") {
    return { status: "invalid", draftId: handoff.draftId };
  }

  if (
    validation.draft.expiresAtEpochMs <= evaluatedAtEpochMs ||
    !isCurrentGroupBookingCoordinatorCapability(
      validation.draft.capability,
      evaluatedAtEpochMs,
    ) ||
    validation.draft.participants.some(
      (participant) =>
        (input.originScope.kind === "location" ||
          participant.role === "guest") &&
        participant.serviceLines.some(
          (line) =>
            evaluatedAtEpochMs < line.quoteAuthority.generatedAtEpochMs ||
            evaluatedAtEpochMs >= line.quoteAuthority.expiresAtEpochMs,
        ),
    )
  ) {
    return { status: "invalid", draftId: handoff.draftId };
  }

  if (
    validation.draft.customerAccountId === null &&
    input.customerAccountId !== null
  ) {
    return {
      status: "auth-binding-required",
      coordinator: validation.draft,
    };
  }

  return { status: "ready", coordinator: validation.draft };
}

export function resolveLocationGroupCoordinatorRouteState(
  input: Omit<
    Parameters<typeof resolveCurrentGroupCoordinatorRouteState>[0],
    "originScope"
  >,
): LocationGroupCoordinatorRouteState {
  return resolveCurrentGroupCoordinatorRouteState({
    ...input,
    originScope: { kind: "location", locationId: input.locationId },
  });
}

export function resolveSpecialistCurrentAssignmentGroupCoordinatorRouteState(
  input: Omit<
    Parameters<typeof resolveCurrentGroupCoordinatorRouteState>[0],
    "originScope"
  > & { runtime: SpecialistCurrentAssignmentGroupStepOneRuntimeV1 },
): LocationGroupCoordinatorRouteState {
  return resolveCurrentGroupCoordinatorRouteState({
    ...input,
    originScope: {
      kind: "specialist-current-assignment",
      runtime: input.runtime,
    },
  });
}

export function resolveSpecialistLocationGatewayGroupCoordinatorRouteState(
  input: Omit<
    Parameters<typeof resolveCurrentGroupCoordinatorRouteState>[0],
    "originScope"
  > & {
    runtime: SpecialistLocationGatewayGroupStepOneRuntimeV1;
    groupAggregateActivationRevision: string;
  },
): LocationGroupCoordinatorRouteState {
  return resolveCurrentGroupCoordinatorRouteState({
    ...input,
    originScope: {
      kind: "specialist-location-gateway",
      runtime: input.runtime,
      groupAggregateActivationRevision: input.groupAggregateActivationRevision,
    },
  });
}

function reconcileAcceptedGroupAppointmentForOriginScope(input: {
  originScope: GroupCoordinatorRouteOriginScope;
  coordinatorDraftId: string;
  customerAccountId: string;
  locationId: string;
  catalogVersion: string;
  catalogOwnerUserLocationId: string;
  returnHref: string;
  evaluatedAtEpochMs: number;
}) {
  const { originScope, ...common } = input;
  if (originScope.kind === "location") {
    return reconcileCurrentAcceptedGroupAppointmentHandoff(common);
  }
  if (originScope.kind === "specialist-current-assignment") {
    return reconcileCurrentAcceptedSpecialistGroupAppointmentHandoff({
      ...common,
      personId: originScope.runtime.sourcePersonId,
      sourceLocationId: originScope.runtime.sourceLocationId,
      sourceUserLocationId: originScope.runtime.sourceUserLocationId,
      selectedLocationId: originScope.runtime.selectedLocationId,
      selectedUserLocationId: originScope.runtime.selectedUserLocationId,
    });
  }
  return reconcileCurrentAcceptedSpecialistLocationGatewayGroupAppointmentHandoff(
    {
      runtime: originScope.runtime,
      customerAccountId: common.customerAccountId,
      groupAggregateActivationRevision:
        originScope.groupAggregateActivationRevision,
      evaluatedAtEpochMs: common.evaluatedAtEpochMs,
    },
  );
}

function specialistGroupOriginScopeCanConsumeDownstream(
  originScope: Exclude<GroupCoordinatorRouteOriginScope, { kind: "location" }>,
  evaluatedAtEpochMs?: number,
) {
  const safeEpochMs = resolveSpecialistServicesEpochMs(evaluatedAtEpochMs);
  if (safeEpochMs === null) return false;
  return originScope.kind === "specialist-current-assignment"
    ? canSpecialistGroupStepOneRuntimeConsumeDownstream(
        originScope.runtime,
        safeEpochMs,
      )
    : canSpecialistLocationGatewayGroupStepOneRuntimeConsumeDownstream(
        originScope.runtime,
        {
          evaluatedAtEpochMs: safeEpochMs,
          groupAggregateActivationRevision:
            originScope.groupAggregateActivationRevision,
        },
      );
}

function specialistGroupOriginScopeOwnsAcceptedHandoff(input: {
  originScope: Exclude<GroupCoordinatorRouteOriginScope, { kind: "location" }>;
  coordinator: unknown;
  appointment: unknown;
  evaluatedAtEpochMs?: number;
}) {
  return input.originScope.kind === "specialist-current-assignment"
    ? specialistGroupStepOneRuntimeOwnsAcceptedHandoff({
        runtime: input.originScope.runtime,
        coordinator: input.coordinator,
        evaluatedAtEpochMs: input.evaluatedAtEpochMs,
      })
    : specialistLocationGatewayGroupStepOneRuntimeOwnsAcceptedHandoff({
        runtime: input.originScope.runtime,
        coordinator: input.coordinator,
        appointment: input.appointment,
        groupAggregateActivationRevision:
          input.originScope.groupAggregateActivationRevision,
        evaluatedAtEpochMs: input.evaluatedAtEpochMs,
      });
}

/** Current construction authority permits repair without asserting graph feasibility. */
export function isLocationGroupRosterBuildingAuthorityCurrent(input: {
  coordinator: unknown;
  evaluatedAtEpochMs?: number;
}) {
  const validation = validateGroupBookingCoordinatorDraft(input.coordinator);

  if (!validation.valid) return false;
  const coordinator = validation.draft;
  const evaluatedAtEpochMs = resolveSpecialistServicesEpochMs(
    input.evaluatedAtEpochMs,
  );
  if (evaluatedAtEpochMs === null) return false;
  return Boolean(
    coordinator.authorityState === "building" &&
    evaluatedAtEpochMs < coordinator.expiresAtEpochMs &&
    isCurrentGroupBookingCoordinatorCapability(
      coordinator.capability,
      evaluatedAtEpochMs,
    ) &&
    coordinator.participants.every((participant) =>
      participant.serviceLines.every(
        (line) =>
          evaluatedAtEpochMs >= line.quoteAuthority.generatedAtEpochMs &&
          evaluatedAtEpochMs < line.quoteAuthority.expiresAtEpochMs,
      ),
    ),
  );
}


/** Acceptance still requires the provider's complete fresh exact graph. */
export function isLocationGroupRosterAuthorityCurrent(input: {
  coordinator: unknown;
  evaluatedAtEpochMs?: number;
}) {
  const validation = validateGroupBookingCoordinatorDraft(input.coordinator);
  const evaluatedAtEpochMs = resolveSpecialistServicesEpochMs(input.evaluatedAtEpochMs);
  if (!validation.valid || evaluatedAtEpochMs === null) return false;
  const exact = validation.draft.exactGraphPublication;
  return Boolean(
    isLocationGroupRosterBuildingAuthorityCurrent({ coordinator: validation.draft, evaluatedAtEpochMs }) &&
    exact && evaluatedAtEpochMs >= exact.generatedAtEpochMs && evaluatedAtEpochMs < exact.expiresAtEpochMs,
  );
}

/** Repair/exit never authorizes acceptance; storage receives only the captured pure lease. */
export function closeLocationGroupRosterStrict(input: {
  coordinator: GroupBookingCoordinatorDraftV2;
  evaluatedAtEpochMs: number;
  ownerIsCurrent: () => boolean;
  operationIsCurrent: () => boolean;
}) {
  const current = () => {
    try {
      return input.operationIsCurrent() && input.ownerIsCurrent() && input.operationIsCurrent();
    } catch { return false; }
  };
  if (input.coordinator.origin.kind !== "location" ||
      !isLocationGroupRosterBuildingAuthorityCurrent(input) || !current() ||
      !isExactLiveGroupCoordinatorOwner(input.coordinator)) return false;
  const lease = beginSpecialistLocationGatewayCustomerOwnedWrite(
    `group-location-roster-close:${input.coordinator.draftId}`, current,
  );
  if (!lease) return false;
  const pureLeaseIsCurrent = () =>
    specialistLocationGatewayCustomerOwnedWriteLeaseIsCurrent(lease) && input.operationIsCurrent();
  try {
    if (!pureLeaseIsCurrent() || !current() || !isExactLiveGroupCoordinatorOwner(input.coordinator)) return false;
    const removed = removeLocationGroupBookingBuildingDraftStrictPersisted(input.coordinator, {
      evaluatedAtEpochMs: input.evaluatedAtEpochMs,
      mutationProof: captureCustomerOwnedSessionStorageMutationProof(),
      mutationIsCurrent: pureLeaseIsCurrent,
    });
    return removed.status === "removed" && pureLeaseIsCurrent() && current();
  } finally {
    completeSpecialistLocationGatewayCustomerOwnedWrite(lease);
  }
}

function areGroupCatalogServiceLinesCurrent(
  serviceLines: GroupBookingCoordinatorDraftV2["participants"][number]["serviceLines"],
  evaluatedAtEpochMs?: number,
) {
  const safeEpochMs = resolveSpecialistServicesEpochMs(evaluatedAtEpochMs);
  return (
    safeEpochMs !== null &&
    serviceLines.length > 0 &&
    serviceLines.every(
      ({ quoteAuthority }) =>
        safeEpochMs >= quoteAuthority.generatedAtEpochMs &&
        safeEpochMs < quoteAuthority.expiresAtEpochMs,
    )
  );
}

function isExactLiveGroupCoordinatorOwner(
  coordinator: GroupBookingCoordinatorDraftV2,
) {
  const live = readGroupBookingCoordinatorAuthorityPersisted(
    coordinator.draftId,
  );
  if (!live || !haveExactSerializableStructure(live, coordinator)) {
    return false;
  }

  return isLiveGroupCoordinatorSessionOwner(coordinator);
}

function isLiveGroupCoordinatorSessionOwner(
  coordinator: GroupBookingCoordinatorDraftV2,
) {
  const customerAccountId = getStoredCustomerAccountId();
  return coordinator.customerAccountId === null
    ? customerAccountId === null &&
        readGroupBookingAnonymousSessionOwnerId() ===
          coordinator.anonymousSessionOwnerId
    : customerAccountId === coordinator.customerAccountId;
}

export function isLocationGroupRosterContinueReady(input: {
  coordinator: unknown;
  customerAccountId: string | null;
  anonymousSessionOwnerId: string | null;
  evaluatedAtEpochMs?: number;
  /** Continue itself resolves the demo graph; an in-flight preview is not an error. */
  allowProviderRefresh?: boolean;
}) {
  const validation = validateGroupBookingCoordinatorDraft(input.coordinator);

  if (!validation.valid) return false;
  const coordinator = validation.draft;
  const evaluatedAtEpochMs = resolveSpecialistServicesEpochMs(
    input.evaluatedAtEpochMs,
  );
  if (evaluatedAtEpochMs === null) return false;
  const guestCount = coordinator.participants.length - 1;
  const exact = coordinator.exactGraphPublication;

  if (process.env.NODE_ENV === "development" && input.allowProviderRefresh &&
      isLocationGroupRosterBuildingAuthorityCurrent({ coordinator, evaluatedAtEpochMs }) &&
      guestCount >= coordinator.capability.minimumGuests && guestCount <= coordinator.capability.effectiveMaximumGuests) {
    return coordinator.customerAccountId !== null
      ? coordinator.customerAccountId === input.customerAccountId
      : input.customerAccountId === null && coordinator.anonymousSessionOwnerId === input.anonymousSessionOwnerId;
  }

  if (
    !isLocationGroupRosterAuthorityCurrent({
      coordinator,
      evaluatedAtEpochMs,
    }) ||
    !exact ||
    guestCount < coordinator.capability.minimumGuests ||
    guestCount > coordinator.capability.effectiveMaximumGuests
  ) {
    return false;
  }

  if (input.customerAccountId !== null) {
    return (
      coordinator.customerAccountId === input.customerAccountId &&
      isGroupBookingCoordinatorReadyForAcceptance(
        coordinator,
        evaluatedAtEpochMs,
      )
    );
  }

  return (
    coordinator.customerAccountId === null &&
    coordinator.anonymousSessionOwnerId === input.anonymousSessionOwnerId &&
    exact.customerEligibility.kind === "authentication-required"
  );
}

function isExpectedGroupAuthResumeTarget(input: {
  target: ReturnType<typeof createGroupBookingCoordinatorAuthResumeTarget>;
  coordinator: GroupBookingCoordinatorDraftV2;
  originScope: GroupCoordinatorRouteOriginScope;
}) {
  return Boolean(
    input.target &&
    input.target.kind === "group-booking-coordinator" &&
    input.target.schemaVersion === 1 &&
    input.target.draftVersion === 2 &&
    input.target.stage === "roster" &&
    input.target.draftId === input.coordinator.draftId &&
    (input.originScope.kind === "location"
      ? input.target.origin.kind === "location" &&
        input.target.origin.locationId === input.originScope.locationId
      : input.originScope.kind === "specialist-current-assignment" &&
        input.target.origin.kind === "specialist" &&
        input.target.origin.locationId ===
          input.originScope.runtime.selectedLocationId &&
        input.target.origin.userLocationId ===
          input.originScope.runtime.selectedUserLocationId &&
        specialistGroupStepOneRuntimeOwnsCoordinator({
          runtime: input.originScope.runtime,
          coordinator: input.coordinator,
          requireBuildingAuthority: true,
        })),
  );
}

function getNextGroupGuestParticipantId(
  participants: GroupBookingCoordinatorDraftV2["participants"],
) {
  const highestNumber = participants.reduce((highest, participant) => {
    if (participant.role !== "guest") return highest;
    const match = /^guest-(\d+)$/u.exec(participant.participantId);
    const value = match ? Number(match[1]) : Number.NaN;

    return Number.isSafeInteger(value) ? Math.max(highest, value) : highest;
  }, GROUP_FIRST_GUEST_NUMBER - 1);

  return `guest-${highestNumber + 1}`;
}

function getStableGroupGuestNumber(participantId: string, index: number) {
  const match = /^guest-(\d+)$/u.exec(participantId);
  const value = match ? Number(match[1]) : Number.NaN;

  return Number.isSafeInteger(value) && value >= GROUP_FIRST_GUEST_NUMBER
    ? value
    : index + GROUP_FIRST_GUEST_NUMBER;
}

function getCustomerDisplayName() {
  const access = getCustomerAccessForFrontendUi();
  const name = [access?.account.first_name, access?.account.last_name]
    .filter((part): part is string => Boolean(part?.trim()))
    .join(" ")
    .trim();

  return name || null;
}

function canActivateSpecialistBookingFrontendRuntime() {
  return (
    canActivatePublicSpecialistLocationGateway() ||
    isSpecialistBookingFrontendPreviewEnabled()
  );
}

function getStoredCustomerAccountId() {
  const owner = readGroupBookingLocationServicesStoredCustomerOwner();
  return owner.status === "ready" ? owner.customerAccountId : undefined;
}

function isSpecialistLocationGatewaySessionCurrent(
  session: SpecialistLocationGatewayServicesSessionV1,
) {
  try {
    const pureSignalIsCurrent = () => !session.signal.aborted;
    if (!pureSignalIsCurrent() || session.isCurrentOwner() !== true) {
      return false;
    }
    if (!pureSignalIsCurrent()) return false;
    const currentCustomerAccountId = session.readCustomerAccountId();
    return (
      pureSignalIsCurrent() &&
      session.isCurrentOwner() === true &&
      pureSignalIsCurrent() &&
      currentCustomerAccountId === session.resolution.customerAccountId
    );
  } catch {
    return false;
  }
}

function readSpecialistLocationGatewaySessionEpoch(
  session: SpecialistLocationGatewayServicesSessionV1,
) {
  try {
    const value = session.now();
    return Number.isSafeInteger(value) && value >= 0 ? value : null;
  } catch {
    return null;
  }
}

async function refreshSpecialistLocationGatewayServicesSession(
  session: SpecialistLocationGatewayServicesSessionV1,
) {
  if (!isSpecialistLocationGatewaySessionCurrent(session)) return null;
  const result =
    await resolveSpecialistLocationGatewaySelectedDestinationStrict({
      resolutionId: session.resolution.draftId,
      expectedCustomerAccountId: session.resolution.customerAccountId,
      expectedLocale: session.resolution.locale,
      expectedSelectedLocationId:
        session.resolution.selection.selectedLocationId,
      expectedSelectedUserLocationId:
        session.resolution.selection.selectedUserLocationId,
      provider: session.provider,
      signal: session.signal,
      now: session.now,
      isCurrentOwner: () => isSpecialistLocationGatewaySessionCurrent(session),
    });
  return result.status === "ready" &&
    haveExactSerializableStructure(result.intent, session.intent) &&
    haveExactSerializableStructure(result.resolution, session.resolution) &&
    isSpecialistLocationGatewaySessionCurrent(session)
    ? result
    : null;
}

export function resolveServicesPageRouteContract(
  route: ServicesPageRouteDescriptor,
  identity: { locationId: string; userLocationId: string },
) {
  if (route.kind === "location-services") {
    if (route.locationId !== identity.locationId) {
      throw new Error(
        "Location Services route does not match the published catalog identity.",
      );
    }

    return {
      canonicalProfilePath: buildLocationProfilePath(route.locationId),
      canonicalServicesPath: buildLocationServicesPath(route.locationId),
      entryPointKind: route.kind,
    } as const;
  }

  return {
    canonicalProfilePath: buildSpecialistProfilePath(identity),
    canonicalServicesPath: buildSpecialistServicesPath(identity),
    entryPointKind: route.kind,
  } as const;
}

export function shouldUseNativeServicesPageBack(input: {
  entryPointKind: SpecialistServicesEntryPointKind;
  historyLength: number;
  canLocationNavigateBackWithinOrigin: boolean;
}) {
  return (
    input.historyLength > 1 &&
    (input.entryPointKind !== "location-services" ||
      input.canLocationNavigateBackWithinOrigin)
  );
}

export interface SpecialistLocationGatewayCommittedGroupSessionV1 {
  ownerKey: string;
  intent: SpecialistLocationGatewayIntentRecordV1;
  navigationLineage: SpecialistLocationGatewayCommittedNavigationLineageV1;
  provider: SpecialistLocationGatewayProviderV1;
  signal: AbortSignal;
  now: () => number;
  readCustomerAccountId: () => string | null;
  isCurrentOwner: () => boolean;
}

function isSpecialistLocationGatewayCommittedGroupSessionCurrent(
  session: SpecialistLocationGatewayCommittedGroupSessionV1,
) {
  try {
    if (session.signal.aborted || session.isCurrentOwner() !== true) {
      return false;
    }
    if (session.signal.aborted) return false;
    const currentCustomerAccountId = session.readCustomerAccountId();
    return (
      !session.signal.aborted &&
      session.isCurrentOwner() === true &&
      !session.signal.aborted &&
      currentCustomerAccountId === session.navigationLineage.customerAccountId
    );
  } catch {
    return false;
  }
}

interface SpecialistLocationGatewayPostCommitNavigationRecoveryV1 {
  lineage: SpecialistLocationGatewayCommittedNavigationLineageV1;
  href: string;
  navigation: "push" | "replace";
  groupDestinationAnalytics?: Readonly<{
    coordinatorDraftId: string;
    organizerParticipantId: string;
    organizerParticipantRole: "organizer";
    selection: Readonly<{
      selectedLocationId: string;
      selectedUserLocationId: string;
    }>;
  }>;
}

export function dispatchSpecialistLocationGatewayFinalGroupDestinationReport(input: {
  consumePendingRecovery: () => void;
  reportGroupDestinationCommitted: () => boolean;
  restorePendingRecovery: () => void;
}) {
  try {
    input.consumePendingRecovery();
  } catch {
    return false;
  }
  try {
    if (input.reportGroupDestinationCommitted() === true) return true;
  } catch {
    // A failed host dispatch has no success event, so recovery is restored.
  }
  try {
    input.restorePendingRecovery();
  } catch {
    // The caller still receives failure and keeps the route fail closed.
  }
  return false;
}

export interface SpecialistServicesPageClientProps {
  route?: ServicesPageRouteDescriptor;
  person: SpecialistPerson | null;
  assignment: SpecialistAssignment;
  workplace: SpecialistWorkplace;
  workplacePresentation: SpecialistWorkplacePresentation | null;
  services: readonly SpecialistService[];
  offerings: readonly SpecialistServiceOffering[];
  serviceReviewSummaries?: readonly PublishedLocationServiceReviewSummary[];
  servicePortfolioImages?: readonly LocationServicePortfolioImage[];
  packages: readonly PackageCatalogDefinition[];
  locationAppointmentCatalog?: LocationAppointmentCatalog | null;
  timeMutationLocationAppointmentCatalog?: LocationAppointmentCatalog | null;
  serviceCategoryPublication: readonly SpecialistCatalogCategory[];
  packageCategoryPublication: readonly SpecialistCatalogCategory[];
  /** Exact source-A/selected-B SLG authority owned by the route dispatcher. */
  specialistLocationGatewaySession?: SpecialistLocationGatewayServicesSessionV1 | null;
  /**
   * Exact configured-cart mapping provider. It is consumed only while the
   * selected-B pre-draft session is live; URL selection bytes never replace
   * its customer-owned cart authority.
   */
  specialistLocationGatewayServicesCartProvider?: SpecialistLocationGatewayServicesCartProviderV1;
  /** Committed Group owner recovered from the opaque Coordinator pointer. */
  specialistLocationGatewayCommittedGroupSession?: SpecialistLocationGatewayCommittedGroupSessionV1 | null;
  /**
   * Invalidates the route-owned committed snapshot after the paired auth saga
   * rotates its customer owner. The callback carries no authority bytes: the
   * dispatcher must reread the exact Coordinator/intent/lineage/runtime pair.
   */
  onSpecialistLocationGatewayCommittedGroupRebind?: () => void;
  /** Injectable aggregate gate; production defaults to the closed SLG ledger. */
  specialistLocationGatewayActivation?: () => boolean;
  /**
   * Identity-only, versioned current-assignment Specialist Group projection.
   * Absent by default, so public Specialist Group remains unavailable until an
   * explicit route runtime and downstream consumer are injected.
   */
  specialistGroupStepOneRuntime?: SpecialistCurrentAssignmentGroupStepOneRuntimeV1 | null;
  /**
   * Separately versioned A -> B Group authority. Merely crafting both URL
   * pointers never creates this runtime/lineage pair; production leaves it
   * absent until the aggregate registry composes the exact downstream proof.
   */
  specialistLocationGatewayGroupStepOneRuntime?: SpecialistLocationGatewayGroupStepOneRuntimeV1 | null;
  specialistLocationGatewayGroupNavigationLineage?: SpecialistLocationGatewayCommittedNavigationLineageV1 | null;
  /** Fresh live registry revision; the frozen runtime alone is insufficient. */
  specialistLocationGatewayGroupAggregateActivationRevision?: string | null;
  /** Atomic live A/B/C registry shared with SelectSlot and mutable downstream. */
  specialistLocationGatewayGroupDownstreamRegistry?: SpecialistLocationGatewayGroupDownstreamRegistryV1 | null;
  /**
   * Presentation metadata for the exact rejoined Location catalog C. Rows and
   * configuration authority still come only from `locationAppointmentCatalog`.
   */
  specialistGroupGuestServiceCategoryPublication?: readonly SpecialistCatalogCategory[];
  /** Route-owned Group capability provider; defaults to the frontend fixture. */
  groupCapabilityProvider?: GroupBookingCapabilityProvider;
  /** Route-owned rich catalog-line provider; defaults to the frontend fixture. */
  groupCatalogLineProvider?: LocationGroupBookingCatalogLineProvider;
  /**
   * Exact Specialist organizer B-quote/C-eligibility provider. It is absent by
   * default, so a public Specialist route cannot activate Group Step-1 merely
   * by receiving catalog rows or a mutable URL pointer.
   */
  specialistOrganizerGroupCatalogLineProvider?: SpecialistOrganizerGroupBookingCatalogLineProvider | null;
  /** Dual B-quote/C-authorization provider used only by the SLG discriminator. */
  specialistLocationGatewayOrganizerGroupCatalogLineProvider?: SpecialistLocationGatewayOrganizerGroupBookingCatalogLineProvider | null;
  /** Async provider/test seam; publication remains owned by this page. */
  cleanupSchedulingDescendantsBeforeEdit?: typeof ensureAppointmentSchedulingDescendantsRemovedBeforeEdit;
  /** Validated page clock; hostile values withdraw mutable Services actions. */
  servicesClock?: () => number;
}

type AppointmentTimeServicesResumeHydrationState =
  { status: "hydrating" } | AppointmentTimeServicesResumeRouteResolution;

type AppointmentTimeResumeCanonicalizationRetryState = {
  handoffKey: string;
  failedAttempts: number;
  status: "active" | "blocked";
};

type AppointmentTimeResumeCanonicalizationAttempt =
  | { status: "succeeded" }
  | {
      status: "retry";
      retryDelayMs: number;
      state: AppointmentTimeResumeCanonicalizationRetryState;
    }
  | {
      status: "blocked";
      state: AppointmentTimeResumeCanonicalizationRetryState;
    };

const INITIAL_TIME_RESUME_CANONICALIZATION_RETRY_STATE = Object.freeze({
  handoffKey: "",
  failedAttempts: 0,
  status: "active" as const,
});
const TIME_RESUME_CANONICALIZATION_MAX_ATTEMPTS = 5;
const TIME_RESUME_CANONICALIZATION_BASE_RETRY_DELAY_MS = 50;

const TIME_PROFESSIONAL_PROVIDER_IDENTITY = Object.freeze({
  providerId: appointmentSchedulingRuntime.timeProfessionalMutation.providerId,
  evidenceVersion:
    appointmentSchedulingRuntime.timeProfessionalMutation.evidenceVersion,
});

function getRequestedPackageIds(searchParams: URLSearchParams) {
  const values = searchParams.getAll("packages");

  if (values.length !== 1) {
    return [];
  }

  const packageIds = values[0]?.split(",") ?? [];

  if (packageIds.length !== 1) {
    return [];
  }

  const packageId = packageIds[0]?.trim() ?? "";
  return packageId && packageId.length <= 160 ? [packageId] : [];
}

export function getRequestedAppointmentMode(
  searchParams: URLSearchParams,
  groupBookingAvailable: boolean,
  hasCoordinatorPointer = false,
): AppointmentDraftMode {
  if (hasCoordinatorPointer && groupBookingAvailable) return "group";
  const values = searchParams.getAll("appointment_mode");

  return groupBookingAvailable && values.length === 1 && values[0] === "group"
    ? "group"
    : "individual";
}

export function canReplaceSpecialistLocationGatewayServicesProjectionInPlace(input: {
  canonicalServicesPath: string;
  currentHref: string;
  nextHref: string;
}) {
  try {
    const origin = "https://widget.volera.invalid";
    const current = new URL(input.currentHref, origin);
    const next = new URL(input.nextHref, origin);

    if (
      current.origin !== origin ||
      next.origin !== origin ||
      current.hash !== "" ||
      next.hash !== "" ||
      current.pathname !== input.canonicalServicesPath ||
      next.pathname !== input.canonicalServicesPath
    ) {
      return false;
    }

    return specialistLocationGatewayServicesProjectionOwnerIsExact(
      current.search.slice(1),
      next.search.slice(1),
    );
  } catch {
    return false;
  }
}

function getSingleFocusIdentifier(searchParams: URLSearchParams, key: string) {
  const values = searchParams.getAll(key);

  if (values.length !== 1) {
    return null;
  }

  const normalized = values[0]?.trim() ?? "";
  return normalized && normalized.length <= 160 ? normalized : null;
}

function ignoreModalCloseInPagePresentation() {
  // Page exits are reported through `onPageExit` after the selector's shared
  // selected-service dismissal guard has completed.
}

function cloneServiceSelections(
  selections: readonly SpecialistServiceSelection[],
) {
  return selections.map<SpecialistServiceSelection>((selection) => ({
    serviceId: selection.serviceId,
    variantId: selection.variantId,
    ...(selection.addOns
      ? { addOns: selection.addOns.map((addOn) => ({ ...addOn })) }
      : {}),
    ...(selection.bundleItems
      ? {
          bundleItems: selection.bundleItems.map((item) => ({
            ...item,
            addOns: item.addOns.map((addOn) => ({ ...addOn })),
          })),
        }
      : {}),
  }));
}

function haveExactSerializableStructure(left: unknown, right: unknown) {
  try {
    return JSON.stringify(left) === JSON.stringify(right);
  } catch {
    return false;
  }
}

export function resolveGroupRosterServiceSummary(
  serviceLines: GroupBookingCoordinatorDraftV2["participants"][number]["serviceLines"],
  formatCount: (count: number) => string,
) {
  const count = serviceLines.reduce((total, line) => total + line.quantity, 0);
  return count === 1
    ? serviceLines[0].snapshot.serviceName
    : formatCount(count);
}

export function shouldCanonicalizeSpecialistServicesHref({
  canonicalServicesHref,
  canonicalServicesPath,
  currentHref,
  hasCanonicalSelectionAuthority,
  hasInitialConfigurationTarget,
  hasInitialFocusTarget,
  hasTimeResumeRequest,
  isGroupResumeHydrating,
  pathname,
}: {
  canonicalServicesHref: string;
  canonicalServicesPath: string;
  currentHref: string;
  hasCanonicalSelectionAuthority: boolean;
  hasInitialConfigurationTarget: boolean;
  hasInitialFocusTarget: boolean;
  hasTimeResumeRequest: boolean;
  isGroupResumeHydrating: boolean;
  pathname: string;
}) {
  return (
    hasCanonicalSelectionAuthority &&
    !isGroupResumeHydrating &&
    !hasTimeResumeRequest &&
    pathname === canonicalServicesPath &&
    !hasInitialFocusTarget &&
    !hasInitialConfigurationTarget &&
    currentHref !== canonicalServicesHref
  );
}

export function attemptTimeResumeCanonicalization(input: {
  handoffKey: string;
  retryState: AppointmentTimeResumeCanonicalizationRetryState;
  replace: () => void;
}): AppointmentTimeResumeCanonicalizationAttempt {
  const retryStateOwnsHandoff =
    input.retryState.handoffKey === input.handoffKey;

  if (retryStateOwnsHandoff && input.retryState.status === "blocked") {
    return { status: "blocked", state: input.retryState };
  }

  const previousFailedAttempts = retryStateOwnsHandoff
    ? input.retryState.failedAttempts
    : 0;

  try {
    input.replace();
    return { status: "succeeded" };
  } catch {
    const failedAttempts = previousFailedAttempts + 1;
    const isBlocked =
      failedAttempts >= TIME_RESUME_CANONICALIZATION_MAX_ATTEMPTS;
    const state: AppointmentTimeResumeCanonicalizationRetryState = {
      handoffKey: input.handoffKey,
      failedAttempts,
      status: isBlocked ? "blocked" : "active",
    };

    if (isBlocked) {
      return { status: "blocked", state };
    }

    return {
      status: "retry",
      retryDelayMs: Math.min(
        TIME_RESUME_CANONICALIZATION_BASE_RETRY_DELAY_MS *
          2 ** (failedAttempts - 1),
        1_000,
      ),
      state,
    };
  }
}

export function shouldCanonicalizeAppointmentTimeResumeStatus(
  status: AppointmentTimeServicesResumeHydrationState["status"],
) {
  return status === "invalid" || status === "invalid-cleaned";
}

export function resolveTimeResumeLocationProfessionalName(input: {
  assignment:
    "any-professional" | "multiple-professionals" | "specific-professional";
  userLocationId?: string;
  professionals: readonly Pick<
    LocationAppointmentProfessional,
    "name" | "userLocationId"
  >[];
  multipleProfessionalsLabel: string;
}) {
  if (input.assignment === "any-professional") {
    return null;
  }

  if (input.assignment === "multiple-professionals") {
    const label = input.multipleProfessionalsLabel.trim();
    return label.length > 0 ? label : null;
  }

  return (
    input.professionals.find(
      ({ userLocationId }) => userLocationId === input.userLocationId,
    )?.name ?? null
  );
}

function getSelectedNestedQueryRowCounts(
  searchParams: URLSearchParams,
  paramName:
    | typeof SPECIALIST_SERVICE_ADD_ONS_PARAM
    | typeof SPECIALIST_SERVICE_BUNDLE_ADD_ONS_PARAM,
  selectedServiceIds: readonly string[],
) {
  const selectedServiceIdSet = new Set(selectedServiceIds);
  const rowCountByParentId = new Map<string, number>();

  searchParams.getAll(paramName).forEach((rawValue) => {
    rawValue.split(",").forEach((rawRow) => {
      const rawParentId = rawRow.split(":", 1)[0] ?? "";
      let parentId: string;

      try {
        parentId = decodeURIComponent(rawParentId).trim();
      } catch {
        return;
      }

      if (!selectedServiceIdSet.has(parentId)) {
        return;
      }

      rowCountByParentId.set(
        parentId,
        (rowCountByParentId.get(parentId) ?? 0) + 1,
      );
    });
  });

  return rowCountByParentId;
}

function didServiceAddOnQueryResolveAtomically(
  querySelections: readonly SpecialistServiceAddOnQuerySelection[],
  resolvedSelections: readonly {
    groupId: string;
    addOnId: string;
    quantity: number;
  }[],
) {
  if (querySelections.length !== resolvedSelections.length) {
    return false;
  }

  const unmatchedSelections = [...resolvedSelections];

  return querySelections.every((querySelection) => {
    const matchingIndex = unmatchedSelections.findIndex(
      (resolvedSelection) =>
        (querySelection.groupId === null ||
          querySelection.groupId === resolvedSelection.groupId) &&
        querySelection.addOnId === resolvedSelection.addOnId &&
        querySelection.quantity === resolvedSelection.quantity,
    );

    if (matchingIndex < 0) {
      return false;
    }

    unmatchedSelections.splice(matchingIndex, 1);
    return true;
  });
}

/**
 * Serializes nested configuration as one transaction. A non-empty source
 * must survive normalization and the bounded serializer in full; otherwise
 * callers abort the URL write instead of publishing a parent-only downgrade.
 */
export function serializeSpecialistServiceSelectionQueryState(
  selections: readonly SpecialistServiceSelection[],
) {
  const serviceIds = selections.map(({ serviceId }) => serviceId);
  const addOnRows: SpecialistServiceAddOnQuerySelection[] = selections.flatMap(
    ({ serviceId, addOns }) =>
      (addOns ?? []).map(({ groupId, addOnId, quantity }) => ({
        serviceId,
        groupId,
        addOnId,
        quantity,
      })),
  );
  const normalizedAddOnRows = normalizeSpecialistServiceAddOnQuerySelections(
    addOnRows,
    serviceIds,
  );

  if (
    normalizedAddOnRows.length !== addOnRows.length ||
    normalizedAddOnRows.some(({ groupId }) => groupId === null)
  ) {
    return null;
  }

  const serviceAddOns = serializeSpecialistServiceAddOnQuerySelections(
    normalizedAddOnRows,
    serviceIds,
  );

  if (normalizedAddOnRows.length > 0 && !serviceAddOns) {
    return null;
  }

  const bundleAddOnRows: SpecialistServiceBundleAddOnQuerySelection[] =
    selections.flatMap(({ serviceId, bundleItems }) =>
      (bundleItems ?? []).flatMap(
        ({ bundleItemId, serviceId: childServiceId, addOns }) =>
          addOns.map(({ groupId, addOnId, quantity }) => ({
            parentServiceId: serviceId,
            bundleItemId,
            serviceId: childServiceId,
            groupId,
            addOnId,
            quantity,
          })),
      ),
    );
  const normalizedBundleAddOnRows =
    normalizeSpecialistServiceBundleAddOnQuerySelections(
      bundleAddOnRows,
      serviceIds,
    );

  if (normalizedBundleAddOnRows.length !== bundleAddOnRows.length) {
    return null;
  }

  const serviceBundleAddOns =
    serializeSpecialistServiceBundleAddOnQuerySelections(
      normalizedBundleAddOnRows,
      serviceIds,
    );

  if (normalizedBundleAddOnRows.length > 0 && !serviceBundleAddOns) {
    return null;
  }

  return { serviceAddOns, serviceBundleAddOns };
}

type SpecialistLocationGatewayServicesCartHydrationState =
  | { status: "inactive" }
  | { status: "hydrating" | "unavailable"; handoffKey: string }
  | {
      status: "empty";
      handoffKey: string;
      authority: SpecialistLocationGatewayServicesCartOwnerAuthorityV1;
    }
  | {
      status: "ready";
      handoffKey: string;
      authority: SpecialistLocationGatewayServicesCartOwnerAuthorityV1;
      cart: SpecialistLocationGatewayConfiguredServicesCartV1;
      selections: readonly SpecialistServiceSelection[];
    };

type ResultsServicesEntryHydrationState =
  | { status: "none" }
  | { status: "hydrating" | "unavailable"; handoffKey: string }
  | {
      status: "ready";
      handoffKey: string;
      entry: ResultsServicesEntryV1;
      nowEpochMs: number;
    };

type PackageSessionServicesEntryHydrationState =
  | { status: "none" }
  | { status: "hydrating" | "unavailable"; handoffKey: string }
  | {
      status: "ready";
      handoffKey: string;
      intent: PackageSessionBookingIntentV1 | PackageSessionBookingIntentV2;
      nowEpochMs: number;
    };

interface SpecialistLocationGatewayServicesCartCommitLease {
  cart: SpecialistLocationGatewayConfiguredServicesCartV1;
  handoffKey: string;
  isCurrentOwner: () => boolean;
}

function specialistLocationGatewaySessionUsesServicesCart(
  session: SpecialistLocationGatewayServicesSessionV1 | null,
) {
  if (
    !session ||
    session.resolution.selection.authority.kind !== "appointment"
  ) {
    return false;
  }

  const intent = session.intent.intent;
  return (
    intent.kind === "appointment-unscoped" ||
    intent.kind === "service" ||
    intent.kind === "service-bundle" ||
    (intent.kind === "catalog" && intent.initialCatalog === "services")
  );
}

type SpecialistLocationGatewayPreDraftGroupServicesDescriptorV1 = Readonly<
  SpecialistLocationGatewayPreDraftGroupCatalogDescriptorV1 & {
    evaluatedAtEpochMs: number;
    returnHref: string;
    servicesCartHandoffKey: string;
    servicesOwnerKey: string;
  }
>;

function resolveCurrentSpecialistLocationGatewayPreDraftGroupServicesDescriptor(input: {
  session: SpecialistLocationGatewayServicesSessionV1 | null;
  servicesCartHandoffKey: string;
  expectedGroupAggregateActivationRevision: string | null;
  provider: SpecialistLocationGatewayOrganizerGroupBookingCatalogLineProvider | null;
  person: SpecialistPerson;
  assignment: SpecialistAssignment;
  workplace: SpecialistWorkplace;
  organizerCatalog: SpecialistOrganizerGroupBookingCatalogSourceV1;
  locationCatalog: LocationAppointmentCatalog | null;
}): SpecialistLocationGatewayPreDraftGroupServicesDescriptorV1 | null {
  const session = input.session;
  const locationCatalog = input.locationCatalog;
  if (
    !session ||
    !locationCatalog ||
    !input.provider ||
    !input.expectedGroupAggregateActivationRevision ||
    !input.servicesCartHandoffKey ||
    !specialistLocationGatewaySessionUsesServicesCart(session) ||
    !isSpecialistLocationGatewaySessionCurrent(session)
  ) {
    return null;
  }
  const evaluatedAtEpochMs = readSpecialistLocationGatewaySessionEpoch(session);
  if (evaluatedAtEpochMs === null) return null;
  const catalogDescriptor =
    resolveCurrentSpecialistLocationGatewayPreDraftGroupCatalogDescriptor({
      intentRecord: session.intent,
      resolution: session.resolution,
      expectedGroupAggregateActivationRevision:
        input.expectedGroupAggregateActivationRevision,
      provider: input.provider,
      organizerCatalog: input.organizerCatalog,
      locationCatalog,
      evaluatedAtEpochMs,
    });
  if (
    !catalogDescriptor ||
    catalogDescriptor.sourcePersonId !== input.person.personId ||
    session.intent.source.personId !== input.person.personId ||
    input.assignment.personId !== input.person.personId ||
    input.assignment.locationId !== input.workplace.locationId ||
    catalogDescriptor.selectedLocationId !== input.assignment.locationId ||
    catalogDescriptor.selectedUserLocationId !==
      input.assignment.userLocationId ||
    session.resolution.selection.selectedLocationId !==
      input.assignment.locationId ||
    session.resolution.selection.selectedUserLocationId !==
      input.assignment.userLocationId ||
    catalogDescriptor.organizerCatalogVersion !==
      input.workplace.catalogVersion ||
    catalogDescriptor.catalogVersion !==
      locationCatalog.workplace.catalogVersion ||
    catalogDescriptor.catalogOwnerUserLocationId !==
      locationCatalog.catalogAssignment.userLocationId ||
    locationCatalog.workplace.locationId !== input.assignment.locationId
  ) {
    return null;
  }
  const servicesOwnerKey = createCanonicalGroupBookingTupleKey([
    "specialist-location-gateway-pre-draft-group-services-descriptor-v1",
    catalogDescriptor.descriptorKey,
    session.ownerKey,
    input.servicesCartHandoffKey,
    session.intent.source.canonicalReturnHref,
  ]);
  return Object.freeze({
    ...catalogDescriptor,
    evaluatedAtEpochMs,
    returnHref: session.intent.source.canonicalReturnHref,
    servicesCartHandoffKey: input.servicesCartHandoffKey,
    servicesOwnerKey,
  });
}

/** Public routes retain closed gates; local Glow Room composes the shared preview. */
export default function SpecialistServicesPageClient(props: SpecialistServicesPageClientProps) {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const search = searchParams.toString();
  const href = `${pathname}${search ? `?${search}` : ""}`;
  const modes = searchParams.getAll("appointment_mode");
  const coordinatorReturn = modes.length === 0 && parseGroupBookingCoordinatorHandoff(searchParams).ok &&
    [...searchParams.keys()].every(key => key === "group_booking_draft_id" || key === "group_booking_draft_version");
  const developmentGroup = props.route?.kind === "location-services" &&
    isGroupBookingDevelopmentPreviewLocation(props.route.locationId) &&
    (modes.length === 1 && modes[0] === "group" || coordinatorReturn);
  const { preview, hydrated } = useGroupBookingDevelopmentPreview(developmentGroup);
  const [authenticationRead, setAuthenticationRead] = useState<{ href: string; status: "resolved" | "connecting" } | null>(null);
  useEffect(() => {
    if (!developmentGroup || !hydrated || preview) return;
    let disposed = false;
    queueMicrotask(() => {
      if (disposed) return;
      // The ordinary local preview uses the existing customer. When UI bypass is
      // disabled, Connect obtains that customer before the Group controller mounts.
      const resolved = () => setAuthenticationRead({ href, status: "resolved" });
      try {
        const before = readStoredCustomerAuthSessionStrict();
        if (before.status !== "ready" || before.session) { resolved(); return; }
        const access = getCustomerAccessForFrontendUi();
        const after = readStoredCustomerAuthSessionStrict();
        if (after.status !== "ready" || after.session || access || window.location.pathname + window.location.search !== href) { resolved(); return; }
        const connectHref = buildConnectHref(href);
        if (connectHref === "/connect") { resolved(); return; }
        setAuthenticationRead({ href, status: "connecting" });
        router.replace(connectHref);
      } catch { resolved(); }
    });
    return () => { disposed = true; };
  }, [developmentGroup, hydrated, preview, href, router]);
  if (!hydrated || developmentGroup && !preview && (authenticationRead?.href !== href || authenticationRead.status === "connecting")) return <BookingCompletionLoading />;
  if (developmentGroup && preview) {
    return <SpecialistServicesPageExperience {...props}
      frontendGroupServicesHost={preview.servicesHost}
      groupCapabilityProvider={preview.journey.capabilityProvider}
      groupCatalogLineProvider={preview.journey.catalogLineProvider}
      servicesClock={preview.now}
    />;
  }
  return <SpecialistServicesPageExperience {...props} frontendGroupServicesHost={null} />;
}

/** Shared Services controller/presentation for production and an explicit,
 * branded development host. All catalog/customer/Coordinator guards are shared. */
export function SpecialistServicesPageExperience({
  route = DEFAULT_SERVICES_PAGE_ROUTE,
  person,
  assignment,
  workplace,
  workplacePresentation,
  services,
  offerings,
  serviceReviewSummaries = EMPTY_LOCATION_SERVICE_REVIEW_SUMMARIES,
  servicePortfolioImages = [],
  packages,
  locationAppointmentCatalog = null,
  timeMutationLocationAppointmentCatalog = null,
  serviceCategoryPublication,
  packageCategoryPublication,
  specialistLocationGatewaySession = null,
  specialistLocationGatewayServicesCartProvider = FRONTEND_FIXTURE_SPECIALIST_LOCATION_GATEWAY_SERVICES_CART_PROVIDER,
  specialistLocationGatewayCommittedGroupSession = null,
  onSpecialistLocationGatewayCommittedGroupRebind,
  specialistLocationGatewayActivation = canActivateSpecialistBookingFrontendRuntime,
  specialistGroupStepOneRuntime = null,
  specialistLocationGatewayGroupStepOneRuntime = null,
  specialistLocationGatewayGroupNavigationLineage = null,
  specialistLocationGatewayGroupAggregateActivationRevision = null,
  specialistLocationGatewayGroupDownstreamRegistry,
  specialistGroupGuestServiceCategoryPublication = [],
  groupCapabilityProvider = frontendFixtureGroupBookingCapabilityProvider,
  groupCatalogLineProvider = frontendFixtureLocationGroupBookingCatalogLineProvider,
  specialistOrganizerGroupCatalogLineProvider = null,
  specialistLocationGatewayOrganizerGroupCatalogLineProvider = isSpecialistBookingFrontendPreviewEnabled()
    ? frontendPreviewSpecialistLocationGatewayOrganizerGroupCatalogLineProvider
    : null,
  cleanupSchedulingDescendantsBeforeEdit = ensureAppointmentSchedulingDescendantsRemovedBeforeEdit,
  servicesClock = Date.now,
  frontendGroupServicesHost = null,
}: SpecialistServicesPageClientProps & {
  frontendGroupServicesHost?: GroupBookingFrontendServicesHostV1 | null;
}) {
  const t = useTranslations("specialist");
  const tAll = useTranslations();
  const locale = useLocale();
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const servicesClockRef = useRef(servicesClock);
  servicesClockRef.current = servicesClock;
  const searchString = searchParams.toString();
  const currentServicesHref = `${pathname}${
    searchString ? `?${searchString}` : ""
  }`;
  const giftPackageCatalogIntent = readPackageGiftCatalogIntent(currentServicesHref);
  const isGiftPackageCatalog = giftPackageCatalogIntent !== null;
  const packageEntryRouteRef = useRef({ href: currentServicesHref, packages, routeKind: route.kind });
  packageEntryRouteRef.current = { href: currentServicesHref, packages, routeKind: route.kind };
  const packageEntryMountedRef = useRef(true);
  useEffect(() => {
    packageEntryMountedRef.current = true;
    return () => { packageEntryMountedRef.current = false; };
  }, []);
  const packageEntryV2Ref = useRef<{
    entry: PackagePurchaseLocationEntryV2;
    sourceHref: string;
    customerAccountId: string | null;
  } | null>(null);
  const specialistLocationGatewayServicesCartIsApplicable =
    specialistLocationGatewaySessionUsesServicesCart(
      specialistLocationGatewaySession,
    );
  const specialistLocationGatewayServicesCartHandoffKey =
    specialistLocationGatewayServicesCartIsApplicable &&
    specialistLocationGatewaySession
      ? encodeSpecialistLocationGatewayTuple([
          specialistLocationGatewaySession.ownerKey,
          specialistLocationGatewaySession.intent.draftId,
          specialistLocationGatewaySession.resolution.draftId,
          specialistLocationGatewaySession.resolution.customerAccountId,
        ])
      : "";
  const returnFocusRef = useRef<HTMLElement | null>(null);
  const viewedServicesPathRef = useRef<string | null>(null);
  const continueGuardRef = useRef(false);
  const packageSelectionPublicationRef = useRef<{ href: string; handoffId: string } | null>(null);
  const [packageSelectionPublication, setPackageSelectionPublication] = useState<{ href: string; handoffId: string } | null>(null);
  useEffect(() => {
    const pending = packageSelectionPublicationRef.current;
    if (!pending) return;
    const handoff = parsePackageSessionBookingHandoff(new URLSearchParams(searchString));
    const expected = new URL(pending.href, window.location.origin);
    if ((pathname === expected.pathname && searchString === expected.searchParams.toString()) ||
      handoff.status !== "ready" || handoff.handoffId !== pending.handoffId) {
      packageSelectionPublicationRef.current = null;
      setPackageSelectionPublication(null);
    }
  }, [pathname, searchString]);
  const servicesCartOperationGenerationRef = useRef(0);
  const servicesCartOperationAbortRef = useRef<AbortController | null>(null);
  const servicesCartContinueGuardRef = useRef(false);
  const directGatewayGenerationRef = useRef(0);
  const gatewayCatalogTransitionRef = useRef(false);
  const timeResumeDiscardingRef = useRef(false);
  const servicesEditCleanupPendingRef = useRef(false);
  const servicesEditCleanupPromiseRef = useRef<Promise<boolean> | null>(null);
  const [timeResumeCanonicalizationRetry, setTimeResumeCanonicalizationRetry] =
    useState<AppointmentTimeResumeCanonicalizationRetryState>(
      INITIAL_TIME_RESUME_CANONICALIZATION_RETRY_STATE,
    );
  const latestPackageDraftRef = useRef<{
    draftId: string;
    selectionKey: string;
  } | null>(null);
  const [isContinuing, setIsContinuing] = useState(false);
  const [
    specialistLocationGatewayServicesCartState,
    setSpecialistLocationGatewayServicesCartState,
  ] = useState<SpecialistLocationGatewayServicesCartHydrationState>({
    status: "inactive",
  });
  const currentSpecialistLocationGatewayServicesCartState = useMemo(
    () =>
      specialistLocationGatewayServicesCartIsApplicable
        ? specialistLocationGatewayServicesCartState.status !== "inactive" &&
          specialistLocationGatewayServicesCartState.handoffKey ===
            specialistLocationGatewayServicesCartHandoffKey
          ? specialistLocationGatewayServicesCartState
          : ({
              status: "hydrating",
              handoffKey: specialistLocationGatewayServicesCartHandoffKey,
            } as const)
        : ({ status: "inactive" } as const),
    [
      specialistLocationGatewayServicesCartHandoffKey,
      specialistLocationGatewayServicesCartIsApplicable,
      specialistLocationGatewayServicesCartState,
    ],
  );
  const retireSpecialistLocationGatewayServicesCartAfterCanonicalNavigation =
    useCallback(
      (input: {
        lineage: SpecialistLocationGatewayCommittedNavigationLineageV1;
        isNavigationOwnerCurrent: () => boolean;
      }) => {
        const session = specialistLocationGatewayServicesCartIsApplicable
          ? specialistLocationGatewaySession
          : null;
        const state = currentSpecialistLocationGatewayServicesCartState;
        if (!session) return true;
        if (
          state.status !== "ready" ||
          state.handoffKey !==
            specialistLocationGatewayServicesCartHandoffKey ||
          state.cart.intentDraftId !== input.lineage.intentDraftId ||
          state.cart.resolutionId !== input.lineage.resolutionId ||
          state.cart.customerAccountId !== input.lineage.customerAccountId ||
          input.lineage.selection.selectedLocationId !==
            state.cart.selectedAssignmentB.locationId ||
          input.lineage.selection.selectedUserLocationId !==
            state.cart.selectedAssignmentB.userLocationId
        ) {
          return false;
        }
        const evaluatedAtEpochMs =
          readSpecialistLocationGatewaySessionEpoch(session);
        if (evaluatedAtEpochMs === null) return false;
        const terminalOwnerIsCurrent = () => {
          try {
            if (
              session.signal.aborted ||
              input.isNavigationOwnerCurrent() !== true ||
              getStoredCustomerAccountId() !== input.lineage.customerAccountId
            ) {
              return false;
            }
            const current =
              readSpecialistLocationGatewayCommittedLifecycleAuthContextStrict({
                destinationDraftId: input.lineage.destinationDraftId,
                expectedDestinationFingerprint:
                  input.lineage.destinationDraftFingerprint,
                expectedCustomerAccountId: input.lineage.customerAccountId,
                expectedDestinationKind: input.lineage.destinationKind,
                expectedSelectedLocationId:
                  input.lineage.selection.selectedLocationId,
                expectedSelectedUserLocationId:
                  input.lineage.selection.selectedUserLocationId,
              });
            return (
              current.status === "ready" &&
              haveExactSerializableStructure(current.lineage, input.lineage)
            );
          } catch {
            return false;
          }
        };
        if (!terminalOwnerIsCurrent()) return false;
        const retired =
          removeSpecialistLocationGatewayConfiguredServicesCartStrict({
            expected: state.cart,
            nowEpochMs: evaluatedAtEpochMs,
            isCurrentOwner: terminalOwnerIsCurrent,
          });
        return retired.status === "removed" || retired.status === "absent";
      },
      [
        currentSpecialistLocationGatewayServicesCartState,
        specialistLocationGatewayServicesCartHandoffKey,
        specialistLocationGatewayServicesCartIsApplicable,
        specialistLocationGatewaySession,
      ],
    );
  const reportSpecialistLocationGatewayGroupDestinationCommitted = useCallback(
    (input: {
      pending: SpecialistLocationGatewayPostCommitNavigationRecoveryV1;
      isNavigationOwnerCurrent: () => boolean;
      consumePendingRecovery: () => void;
      restorePendingRecovery: () => void;
    }) => {
      const analytics = input.pending.groupDestinationAnalytics;
      const session = specialistLocationGatewaySession;
      if (!analytics || !session) return false;
      const lineage = input.pending.lineage;
      if (
        lineage.destinationKind !== "group" ||
        analytics.coordinatorDraftId !== lineage.destinationDraftId ||
        analytics.organizerParticipantRole !== "organizer" ||
        session.intent.draftId !== lineage.intentDraftId ||
        session.resolution.draftId !== lineage.resolutionId ||
        session.resolution.selection.selectedLocationId !==
          analytics.selection.selectedLocationId ||
        session.resolution.selection.selectedUserLocationId !==
          analytics.selection.selectedUserLocationId ||
        lineage.selection.selectedLocationId !==
          analytics.selection.selectedLocationId ||
        lineage.selection.selectedUserLocationId !==
          analytics.selection.selectedUserLocationId
      ) {
        return false;
      }
      const current =
        readSpecialistLocationGatewayCommittedLifecycleAuthContextStrict({
          destinationDraftId: lineage.destinationDraftId,
          expectedDestinationFingerprint: lineage.destinationDraftFingerprint,
          expectedCustomerAccountId: lineage.customerAccountId,
          expectedDestinationKind: "group",
          expectedSelectedLocationId: lineage.selection.selectedLocationId,
          expectedSelectedUserLocationId:
            lineage.selection.selectedUserLocationId,
        });
      if (
        current.status !== "ready" ||
        !haveExactSerializableStructure(current.lineage, lineage) ||
        !isSpecialistLocationGatewaySessionCurrent(session)
      ) {
        return false;
      }
      try {
        if (input.isNavigationOwnerCurrent() !== true) return false;
      } catch {
        return false;
      }

      // This success-like publication is deliberately the final callback after
      // router settlement, exact cart retirement, and the last owner proof.
      return dispatchSpecialistLocationGatewayFinalGroupDestinationReport({
        consumePendingRecovery: input.consumePendingRecovery,
        reportGroupDestinationCommitted: () =>
          session.analyticsReporter.groupDestinationCommitted(analytics),
        restorePendingRecovery: input.restorePendingRecovery,
      });
    },
    [specialistLocationGatewaySession],
  );
  const [postCommitNavigationRecovery, setPostCommitNavigationRecovery] =
    useState<SpecialistLocationGatewayPostCommitNavigationRecoveryV1 | null>(
      null,
    );
  const [groupCoordinatorRefreshVersion, setGroupCoordinatorRefreshVersion] =
    useState(0);
  const [groupCatalogTransaction, setGroupCatalogTransaction] =
    useState<GroupBookingParticipantCatalogTransactionV1 | null>(null);
  const [groupCatalogSelections, setGroupCatalogSelections] = useState<
    SpecialistServiceSelection[]
  >([]);
  const [groupCatalogQuantities, setGroupCatalogQuantities] = useState<
    number[]
  >([]);
  const [pendingGroupRemoval, setPendingGroupRemoval] = useState<{
    coordinatorDraftId: string;
    coordinatorRevision: number;
    participantId: string;
  } | null>(null);
  const [pendingGroupExit, setPendingGroupExit] = useState<{
    action: SpecialistServicesPageExitAction;
    coordinatorDraftId: string;
    coordinatorRevision: number;
  } | null>(null);
  const [
    groupLifecycleNavigationRecovery,
    setGroupLifecycleNavigationRecovery,
  ] = useState<{
    action: SpecialistServicesPageExitAction;
    href: string;
    navigation: "push" | "replace";
  } | null>(null);
  const [isGroupMutationPending, setIsGroupMutationPending] = useState(false);
  const [groupCustomerOwnerState, setGroupCustomerOwnerState] = useState<{
    hydrated: boolean;
    customerAccountId: string | null;
    anonymousSessionOwnerId: string | null;
  }>({
    hydrated: false,
    customerAccountId: null,
    anonymousSessionOwnerId: null,
  });
  const [groupExactGraphClockVersion, setGroupExactGraphClockVersion] =
    useState(0);
  const groupMutationInFlightRef = useRef(false);
  const groupProviderGenerationRef = useRef(0);
  const groupProviderAbortRef = useRef<AbortController | null>(null);
  const groupHydrationGenerationRef = useRef(0);
  const groupExactGraphAttemptedOwnerRef = useRef<string | null>(null);
  const groupExactGraphGenerationRef = useRef(0);
  const groupExactGraphAbortRef = useRef<AbortController | null>(null);
  const groupExactGraphRetryTimerRef = useRef<number | null>(null);
  const groupExactGraphRetryRef = useRef<{
    ownerKey: string | null;
    failedAttempts: number;
  }>({ ownerKey: null, failedAttempts: 0 });
  const groupRouteOperationKeyRef = useRef("");
  const groupCatalogTransactionOwnerRef = useRef<string | null>(null);
  const groupRosterReturnFocusOwnerRef = useRef<{
    coordinatorDraftId: string;
    participantId: string;
  } | null>(null);

  useEffect(() => {
    const publishStoredCustomerOwner = () => {
      const owner = readGroupBookingLocationServicesStoredCustomerOwner();
      if (owner.status === "unreadable") {
        setGroupCustomerOwnerState({
          hydrated: false,
          customerAccountId: null,
          anonymousSessionOwnerId: null,
        });
        return;
      }
      setGroupCustomerOwnerState({
        hydrated: true,
        customerAccountId: owner.customerAccountId,
        anonymousSessionOwnerId: readGroupBookingAnonymousSessionOwnerId(),
      });
    };
    const publishCrossTabCustomerOwner = (event: StorageEvent) => {
      if (event.key !== CUSTOMER_AUTH_SESSION_STORAGE_KEY) return;

      try {
        if (event.storageArea !== window.localStorage) return;
      } catch {
        return;
      }

      publishStoredCustomerOwner();
    };

    publishStoredCustomerOwner();
    window.addEventListener(
      CUSTOMER_AUTH_SESSION_EVENT,
      publishStoredCustomerOwner,
    );
    window.addEventListener("pageshow", publishStoredCustomerOwner);
    window.addEventListener("storage", publishCrossTabCustomerOwner);

    return () => {
      window.removeEventListener(
        CUSTOMER_AUTH_SESSION_EVENT,
        publishStoredCustomerOwner,
      );
      window.removeEventListener("pageshow", publishStoredCustomerOwner);
      window.removeEventListener("storage", publishCrossTabCustomerOwner);
    };
  }, []);

  const retryPostCommitNavigation = useCallback(async () => {
    const pending = postCommitNavigationRecovery;
    if (!pending) return false;
    const current =
      readSpecialistLocationGatewayCommittedLifecycleAuthContextStrict({
        destinationDraftId: pending.lineage.destinationDraftId,
        expectedDestinationFingerprint:
          pending.lineage.destinationDraftFingerprint,
        expectedCustomerAccountId: pending.lineage.customerAccountId,
        expectedDestinationKind: pending.lineage.destinationKind,
        expectedSelectedLocationId:
          pending.lineage.selection.selectedLocationId,
        expectedSelectedUserLocationId:
          pending.lineage.selection.selectedUserLocationId,
      });
    if (
      current.status !== "ready" ||
      !haveExactSerializableStructure(current.lineage, pending.lineage) ||
      getStoredCustomerAccountId() !== pending.lineage.customerAccountId
    ) {
      setPostCommitNavigationRecovery(null);
      continueGuardRef.current = false;
      setIsContinuing(false);
      toast.error(t("serviceAddOnsModal.submissionError"));
      return true;
    }
    const navigationOwnerIsCurrent = () => {
      if (
        postCommitNavigationRecovery !== pending ||
        getStoredCustomerAccountId() !== pending.lineage.customerAccountId
      ) {
        return false;
      }
      if (!pending.groupDestinationAnalytics) return true;
      const session = specialistLocationGatewaySession;
      return Boolean(
        session &&
        session.intent.draftId === pending.lineage.intentDraftId &&
        session.resolution.draftId === pending.lineage.resolutionId &&
        isSpecialistLocationGatewaySessionCurrent(session),
      );
    };
    continueGuardRef.current = true;
    setIsContinuing(true);
    try {
      const navigation =
        pending.navigation === "replace"
          ? router.replace(pending.href, { scroll: false })
          : router.push(pending.href);
      await Promise.resolve(navigation);
      const retired =
        retireSpecialistLocationGatewayServicesCartAfterCanonicalNavigation({
          lineage: pending.lineage,
          isNavigationOwnerCurrent: navigationOwnerIsCurrent,
        });
      continueGuardRef.current = false;
      setIsContinuing(false);
      if (!retired) return true;
      if (!pending.groupDestinationAnalytics) {
        setPostCommitNavigationRecovery(null);
        return true;
      }
      const reported = reportSpecialistLocationGatewayGroupDestinationCommitted(
        {
          pending,
          isNavigationOwnerCurrent: navigationOwnerIsCurrent,
          consumePendingRecovery: () => setPostCommitNavigationRecovery(null),
          restorePendingRecovery: () =>
            setPostCommitNavigationRecovery(pending),
        },
      );
      if (!reported) {
        toast.error(t("serviceAddOnsModal.submissionError"));
      }
      return true;
    } catch {
      continueGuardRef.current = false;
      setIsContinuing(false);
      toast.error(t("serviceAddOnsModal.submissionError"));
    }
    return true;
  }, [
    postCommitNavigationRecovery,
    reportSpecialistLocationGatewayGroupDestinationCommitted,
    retireSpecialistLocationGatewayServicesCartAfterCanonicalNavigation,
    router,
    specialistLocationGatewaySession,
    t,
  ]);
  const entryPointKind: SpecialistServicesEntryPointKind = route.kind;
  const isLocationEntryPoint = route.kind === "location-services";
  const resultsAppointmentHandoffIsPresent =
    hasLocationResultsAppointmentIntentHandoff(searchParams);
  const locationResultsAppointmentHandoffIsPresent = isLocationEntryPoint
    ? resultsAppointmentHandoffIsPresent
    : false;
  const locationResultsAppointmentIntentPointer = isLocationEntryPoint
    ? readLocationResultsAppointmentIntentPointer(searchParams)
    : null;
  const specialistResultsAppointmentIntentPointer = (() => {
    if (isLocationEntryPoint || !specialistLocationGatewaySession) return null;
    const parsed = parseSpecialistLocationGatewayServicesHandoff(
      new URLSearchParams(searchString),
    );
    return parsed.ok &&
      parsed.handoffId === specialistLocationGatewaySession.intent.draftId &&
      parsed.selectedAssignmentHint ===
        specialistLocationGatewaySession.resolution.selection
          .selectedUserLocationId
      ? parsed.resultsAppointmentIntentHandoffId
      : null;
  })();
  const routeLocationId = isLocationEntryPoint
    ? route.locationId
    : assignment.locationId;
  const locationProfessionals: readonly LocationAppointmentProfessional[] =
    locationAppointmentCatalog?.professionals ?? EMPTY_LOCATION_PROFESSIONALS;
  const readCurrentResultsServicesEntry = useCallback(():
    | { status: "none" }
    | { status: "ready"; entry: ResultsServicesEntryV1; nowEpochMs: number }
    | { status: "unavailable" } => {
    const expectedCustomerAccountId = getStoredCustomerAccountId();
    const nowEpochMs = readSpecialistServicesSafeEpochMs(
      servicesClockRef.current,
    );
    if (expectedCustomerAccountId === undefined || nowEpochMs === null) {
      return { status: "unavailable" };
    }

    if (isLocationEntryPoint) {
      if (!locationResultsAppointmentHandoffIsPresent) {
        return { status: "none" };
      }
      if (!locationAppointmentCatalog) {
        return { status: "unavailable" };
      }
      const result = readResultsServicesEntryStrict({
        source: {
          kind: "location",
          locationId: routeLocationId,
          pathname,
          searchParams: new URLSearchParams(searchString),
          catalog: locationAppointmentCatalog,
          additionalAllowedRouteQueryKeys: [
            APPOINTMENT_TIME_SERVICES_RESUME_DRAFT_PARAM,
            APPOINTMENT_TIME_SERVICES_RESUME_SCHEMA_PARAM,
          ],
        },
        expectedCustomerAccountId,
        expectedLocale: locale,
        nowEpochMs,
      });
      return result.status === "ready"
        ? { status: "ready", entry: result.entry, nowEpochMs }
        : { status: "unavailable" };
    }

    if (!specialistLocationGatewaySession) {
      return resultsAppointmentHandoffIsPresent
        ? { status: "unavailable" }
        : { status: "none" };
    }
    const result = readResultsServicesEntryStrict({
      source: {
        kind: "specialist",
        session: specialistLocationGatewaySession,
        searchParams: new URLSearchParams(searchString),
        resolveCurrentMarketplaceDiscovery:
          resolveSpecialistMarketplaceDiscovery,
      },
      expectedCustomerAccountId,
      expectedLocale: locale,
      nowEpochMs,
    });
    if (result.status === "ready") {
      return { status: "ready", entry: result.entry, nowEpochMs };
    }
    return result.reason === "intent-absent"
      ? { status: "none" }
      : { status: "unavailable" };
  }, [
    isLocationEntryPoint,
    locale,
    locationAppointmentCatalog,
    locationResultsAppointmentHandoffIsPresent,
    pathname,
    resultsAppointmentHandoffIsPresent,
    routeLocationId,
    searchString,
    specialistLocationGatewaySession,
  ]);
  const revalidateCurrentResultsServicesEntry = useCallback(
    (expectedEntry: ResultsServicesEntryV1) => {
      const expectedCustomerAccountId = getStoredCustomerAccountId();
      const nowEpochMs = readSpecialistServicesSafeEpochMs(
        servicesClockRef.current,
      );
      if (expectedCustomerAccountId === undefined || nowEpochMs === null) {
        return { status: "unavailable" as const };
      }

      const common = {
        expectedCustomerAccountId,
        expectedLocale: locale,
        nowEpochMs,
        expectedEntry,
      };
      if (isLocationEntryPoint) {
        if (
          !locationResultsAppointmentHandoffIsPresent ||
          !locationAppointmentCatalog
        ) {
          return { status: "unavailable" as const };
        }
        const result = revalidateResultsServicesEntryStrict({
          ...common,
          source: {
            kind: "location",
            locationId: routeLocationId,
            pathname,
            searchParams: new URLSearchParams(searchString),
            catalog: locationAppointmentCatalog,
            additionalAllowedRouteQueryKeys: [
              APPOINTMENT_TIME_SERVICES_RESUME_DRAFT_PARAM,
              APPOINTMENT_TIME_SERVICES_RESUME_SCHEMA_PARAM,
            ],
          },
        });
        return result.status === "ready"
          ? { status: "ready" as const, entry: result.entry, nowEpochMs }
          : { status: "unavailable" as const };
      }
      if (!specialistLocationGatewaySession) {
        return { status: "unavailable" as const };
      }
      const result = revalidateResultsServicesEntryStrict({
        ...common,
        source: {
          kind: "specialist",
          session: specialistLocationGatewaySession,
          searchParams: new URLSearchParams(searchString),
          resolveCurrentMarketplaceDiscovery:
            resolveSpecialistMarketplaceDiscovery,
        },
      });
      return result.status === "ready"
        ? { status: "ready" as const, entry: result.entry, nowEpochMs }
        : { status: "unavailable" as const };
    },
    [
      isLocationEntryPoint,
      locale,
      locationAppointmentCatalog,
      locationResultsAppointmentHandoffIsPresent,
      pathname,
      routeLocationId,
      searchString,
      specialistLocationGatewaySession,
    ],
  );
  const specialistResultsServicesEntryIsRequired = Boolean(
    !isLocationEntryPoint &&
    specialistLocationGatewaySession &&
    isResultsSpecialistLocationGatewaySourceAction(
      specialistLocationGatewaySession.intent.sourceAction,
    ),
  );
  const resultsServicesEntryIsRequired = isLocationEntryPoint
    ? resultsAppointmentHandoffIsPresent
    : resultsAppointmentHandoffIsPresent ||
      specialistResultsServicesEntryIsRequired;
  const [
    resultsServicesEntryRefreshVersion,
    setResultsServicesEntryRefreshVersion,
  ] = useState(0);
  const resultsServicesEntryHandoffKey = useMemo(
    () =>
      JSON.stringify([
        currentServicesHref,
        locale,
        routeLocationId,
        specialistLocationGatewaySession?.intent.draftId ?? null,
        specialistLocationGatewaySession?.intent.ownerKey ?? null,
        specialistLocationGatewaySession?.resolution.draftId ?? null,
        groupCustomerOwnerState.hydrated
          ? groupCustomerOwnerState.customerAccountId
          : "customer-owner-hydrating",
        locationAppointmentCatalog?.workplace.catalogVersion ?? null,
        resultsServicesEntryRefreshVersion,
      ]),
    [
      currentServicesHref,
      groupCustomerOwnerState.customerAccountId,
      groupCustomerOwnerState.hydrated,
      locale,
      locationAppointmentCatalog?.workplace.catalogVersion,
      resultsServicesEntryRefreshVersion,
      routeLocationId,
      specialistLocationGatewaySession?.intent.draftId,
      specialistLocationGatewaySession?.intent.ownerKey,
      specialistLocationGatewaySession?.resolution.draftId,
    ],
  );
  const resultsServicesEntryFailClosedState =
    useMemo<ResultsServicesEntryHydrationState>(
      () =>
        resultsServicesEntryIsRequired
          ? {
              status: "hydrating",
              handoffKey: resultsServicesEntryHandoffKey,
            }
          : { status: "none" },
      [resultsServicesEntryHandoffKey, resultsServicesEntryIsRequired],
    );
  const {
    isCurrentHandoff: isCurrentResultsServicesEntryHandoff,
    publishForHandoff: publishResultsServicesEntryForHandoff,
    state: resultsServicesEntryHydration,
  } = useKeyedHandoffHydrationState<ResultsServicesEntryHydrationState>(
    resultsServicesEntryHandoffKey,
    resultsServicesEntryFailClosedState,
  );

  useEffect(() => {
    if (!resultsServicesEntryIsRequired || !groupCustomerOwnerState.hydrated) {
      return;
    }

    const ownerHandoffKey = resultsServicesEntryHandoffKey;
    const result = readCurrentResultsServicesEntry();
    const storedCustomerAccountId = getStoredCustomerAccountId();
    if (!isCurrentResultsServicesEntryHandoff(ownerHandoffKey)) return;

    if (
      storedCustomerAccountId === undefined ||
      storedCustomerAccountId !== groupCustomerOwnerState.customerAccountId ||
      result.status !== "ready"
    ) {
      publishResultsServicesEntryForHandoff(ownerHandoffKey, {
        status: "unavailable",
        handoffKey: ownerHandoffKey,
      });
      return;
    }

    publishResultsServicesEntryForHandoff(ownerHandoffKey, {
      status: "ready",
      handoffKey: ownerHandoffKey,
      entry: result.entry,
      nowEpochMs: result.nowEpochMs,
    });
  }, [
    groupCustomerOwnerState.customerAccountId,
    groupCustomerOwnerState.hydrated,
    isCurrentResultsServicesEntryHandoff,
    publishResultsServicesEntryForHandoff,
    readCurrentResultsServicesEntry,
    resultsServicesEntryHandoffKey,
    resultsServicesEntryIsRequired,
  ]);
  const packageSessionBookingHandoffIsPresent = useMemo(() => {
    const params = new URLSearchParams(searchString);
    return (
      params.getAll(PACKAGE_SESSION_BOOKING_ID_QUERY_KEY).length > 0 ||
      params.getAll(PACKAGE_SESSION_BOOKING_VERSION_QUERY_KEY).length > 0
    );
  }, [searchString]);
  const packageSessionServicesEntryIsRequired =
    packageSessionBookingHandoffIsPresent;
  const packageSessionServicesEntryHandoffKey = useMemo(
    () =>
      JSON.stringify([
        currentServicesHref,
        locale,
        routeLocationId,
        isLocationEntryPoint,
        groupCustomerOwnerState.hydrated
          ? groupCustomerOwnerState.customerAccountId
          : "customer-owner-hydrating",
        locationAppointmentCatalog?.workplace.catalogVersion ?? null,
      ]),
    [
      currentServicesHref,
      groupCustomerOwnerState.customerAccountId,
      groupCustomerOwnerState.hydrated,
      isLocationEntryPoint,
      locale,
      locationAppointmentCatalog?.workplace.catalogVersion,
      routeLocationId,
    ],
  );
  const packageSessionServicesEntryFailClosedState =
    useMemo<PackageSessionServicesEntryHydrationState>(
      () =>
        packageSessionServicesEntryIsRequired
          ? {
              status: "hydrating",
              handoffKey: packageSessionServicesEntryHandoffKey,
            }
          : { status: "none" },
      [
        packageSessionServicesEntryHandoffKey,
        packageSessionServicesEntryIsRequired,
      ],
    );
  const {
    isCurrentHandoff: isCurrentPackageSessionServicesEntryHandoff,
    publishForHandoff: publishPackageSessionServicesEntryForHandoff,
    state: packageSessionServicesEntryHydration,
  } = useKeyedHandoffHydrationState<PackageSessionServicesEntryHydrationState>(
    packageSessionServicesEntryHandoffKey,
    packageSessionServicesEntryFailClosedState,
  );

  const readCurrentPackageSessionServicesEntry = useCallback(
    (): LocationPackageSessionServicesEntryResult => {
      if (!isLocationEntryPoint) return packageSessionServicesEntryIsRequired
        ? { status: "unavailable" } : { status: "none" };
      const currentSearchParams = new URLSearchParams(searchString);
      const handoff = parsePackageSessionBookingHandoff(currentSearchParams, LOCATION_PACKAGE_SESSION_SERVICES_ROUTE_QUERY_KEYS);
      const nowEpochMs = readSpecialistServicesSafeEpochMs(servicesClockRef.current);
      const owner = handoff.status === "ready" && nowEpochMs !== null
        ? readPackageSessionBookingUiCustomerOwnerForIntent({ handoffId: handoff.handoffId, nowEpochMs })
        : null;
      return readLocationPackageSessionServicesEntry({
            locationId: routeLocationId,
            pathname,
            searchParams: currentSearchParams,
            catalog: locationAppointmentCatalog,
            expectedCustomerAccountId: owner?.status === "ready" ? owner.customerAccountId : null,
            nowEpochMs,
          });
    },
    [
      isLocationEntryPoint,
      locationAppointmentCatalog,
      packageSessionServicesEntryIsRequired,
      pathname,
      routeLocationId,
      searchString,
    ],
  );

  useEffect(() => {
    if (
      !packageSessionServicesEntryIsRequired ||
      !groupCustomerOwnerState.hydrated
    ) {
      return;
    }
    const ownerHandoffKey = packageSessionServicesEntryHandoffKey;
    const result = readCurrentPackageSessionServicesEntry();
    if (!isCurrentPackageSessionServicesEntryHandoff(ownerHandoffKey)) {
      return;
    }
    publishPackageSessionServicesEntryForHandoff(
      ownerHandoffKey,
      result.status === "ready"
        ? {
            status: "ready",
            handoffKey: ownerHandoffKey,
            intent: result.intent,
            nowEpochMs: result.nowEpochMs,
          }
        : { status: "unavailable", handoffKey: ownerHandoffKey },
    );
  }, [
    groupCustomerOwnerState.hydrated,
    isCurrentPackageSessionServicesEntryHandoff,
    packageSessionServicesEntryHandoffKey,
    packageSessionServicesEntryIsRequired,
    publishPackageSessionServicesEntryForHandoff,
    readCurrentPackageSessionServicesEntry,
  ]);
  const membershipSessionServicesEntryIsRequired =
    parseMembershipSessionBookingHandoff(new URLSearchParams(searchString)).status !== "absent";
  const membershipServicesRouteKey = `${pathname}?${searchString}`;
  const membershipServicesRouteKeyRef = useRef(membershipServicesRouteKey);
  membershipServicesRouteKeyRef.current = membershipServicesRouteKey;
  const membershipServicesMountedRef = useRef(true);
  useEffect(() => {
    membershipServicesMountedRef.current = true;
    return () => { membershipServicesMountedRef.current = false; };
  }, []);
  const [, refreshMembershipSessionSource] = useState(0);
  const readCurrentMembershipSessionServicesEntry = useCallback(() =>
    !membershipServicesMountedRef.current || membershipServicesRouteKeyRef.current !== membershipServicesRouteKey
      ? { status: "unavailable" as const } : readMembershipSessionServicesEntry({
    pathname, searchParams: new URLSearchParams(searchString), locationCatalog: locationAppointmentCatalog,
    nowEpochMs: readSpecialistServicesSafeEpochMs(servicesClockRef.current) ?? Number.NaN,
  }), [locationAppointmentCatalog, membershipServicesRouteKey, pathname, searchString]);
  // The mutable source is re-read during render, not reset after an owner change.
  const membershipSessionServicesEntryState = groupCustomerOwnerState.hydrated
    ? readCurrentMembershipSessionServicesEntry()
    : { status: "unavailable" as const };
  const readyMembershipSessionServicesEntry = membershipSessionServicesEntryState.status === "ready"
    ? membershipSessionServicesEntryState.entry : null;
  const membershipSessionExpiry = readyMembershipSessionServicesEntry?.publication.expiresAtEpochMs ?? null;
  useEffect(() => {
    if (!membershipSessionServicesEntryIsRequired) return;
    const refresh = () => refreshMembershipSessionSource((value) => value + 1);
    const timer = membershipSessionExpiry === null ? undefined : window.setTimeout(refresh, Math.max(0, membershipSessionExpiry - Date.now()));
    window.addEventListener("pageshow", refresh);
    window.addEventListener("storage", refresh);
    window.addEventListener(CUSTOMER_AUTH_SESSION_EVENT, refresh);
    document.addEventListener("visibilitychange", refresh);
    return () => {
      if (timer !== undefined) window.clearTimeout(timer);
      window.removeEventListener("pageshow", refresh);
      window.removeEventListener("storage", refresh);
      window.removeEventListener(CUSTOMER_AUTH_SESSION_EVENT, refresh);
      document.removeEventListener("visibilitychange", refresh);
    };
  }, [membershipSessionExpiry, membershipSessionServicesEntryIsRequired]);
  const identity = useMemo(
    () => ({
      locationId: assignment.locationId,
      userLocationId: assignment.userLocationId,
    }),
    [assignment.locationId, assignment.userLocationId],
  );
  const routeContract = useMemo(
    () => resolveServicesPageRouteContract(route, identity),
    [identity, route],
  );
  const { canonicalProfilePath, canonicalServicesPath } = routeContract;
  const membershipsHref = useMemo(() => {
    const hasOnlineMemberships = getMembershipsForLocation(
      workplace.locationId,
    ).some(
      (membership) =>
        membership.isPublished &&
        membership.onlineSalesEnabled &&
        membership.purchaseProviderState === "ready",
    );

    return hasOnlineMemberships
      ? (buildLocationMembershipsPath(workplace.locationId) ?? undefined)
      : undefined;
  }, [workplace.locationId]);
  const giftCardsHref = useMemo(
    () =>
      getBusinessGiftCardCatalog(workplace.locationId)
        ? buildLocationGiftsPath(workplace.locationId)
        : undefined,
    [workplace.locationId],
  );
  const productsHref = useMemo(
    () =>
      workplace.offersOnlineProducts &&
      getOnlinePurchasableLocationStore(workplace.locationId)
        ? projectCustomerRewardProductStoreHref(buildLocationStorePath(workplace.locationId), currentServicesHref)
        : undefined,
    [currentServicesHref, workplace.locationId, workplace.offersOnlineProducts],
  );
  const serviceIdSet = useMemo(
    () => new Set(services.map(({ serviceId }) => serviceId)),
    [services],
  );
  const serviceByServiceId = useMemo(
    () => new Map(services.map((service) => [service.serviceId, service])),
    [services],
  );
  const offeringByServiceId = useMemo(
    () => new Map(offerings.map((offering) => [offering.serviceId, offering])),
    [offerings],
  );
  const specialistLocationGatewayServicesCartCatalog = useMemo(
    () => person ? ({
      personId: person.personId,
      assignment,
      workplace,
      services,
      offerings,
    }) : null,
    [assignment, offerings, person, services, workplace],
  );
  const specialistLocationGatewayServicesSourceCartCatalog =
    specialistLocationGatewaySession?.servicesCartSourceCatalog ?? null;
  const locationResourceOnlyExecutionsV1 = isLocationEntryPoint && locationAppointmentCatalog && validateLocationAppointmentCatalogEmbeddedEligibility(locationAppointmentCatalog) ? locationAppointmentCatalog.eligibilityPublication.resourceOnlyExecutionsV1 : undefined;
  const hasPublishedServices = useMemo(
    () =>
      services.some((service) => {
        const offering = offeringByServiceId.get(service.serviceId);

        return offering
          ? isSpecialistOfferingPublishedAtWorkplace(
              assignment,
              workplace,
              offering,
              locationResourceOnlyExecutionsV1,
            )
          : false;
      }),
    [assignment, offeringByServiceId, services, workplace, locationResourceOnlyExecutionsV1],
  );
  const bundlePlanByServiceId = useMemo(
    () =>
      new Map(
        services.flatMap((service) => {
          if (service.kind !== "bundle") {
            return [];
          }

          const offering = offeringByServiceId.get(service.serviceId);

          if (!offering) {
            return [];
          }

          const plan = createSpecialistServiceBundleAddOnPlan({
            parentService: service,
            parentOffering: offering,
            resolveChild: (serviceId) => {
              const childService = serviceByServiceId.get(serviceId);
              const childOffering = offeringByServiceId.get(serviceId);

              return childService && childOffering
                ? {
                    service: childService,
                    offering: childOffering,
                  }
                : null;
            },
          });

          return plan ? [[service.serviceId, plan] as const] : [];
        }),
      ),
    [offeringByServiceId, serviceByServiceId, services],
  );
  const packageIdSet = useMemo(
    () => new Set(packages.filter(item => isPackageCatalogDefinitionV1(item) ||
      (isLocationEntryPoint && !specialistLocationGatewaySession && isPackageCatalogDefinitionV2(item)))
      .map(({ packageId }) => packageId)),
    [packages, isLocationEntryPoint, specialistLocationGatewaySession],
  );
  const groupBookingPolicy = useMemo(
    () => getSpecialistGroupBookingPolicy(workplace),
    [workplace],
  );
  const groupLocationCatalogVersion =
    locationAppointmentCatalog?.workplace.catalogVersion ?? null;
  const specialistStepOneCapabilityOwner = useMemo(
    () => ({
      locationId: assignment.locationId,
      userLocationId: assignment.userLocationId,
      catalogVersion: workplace.catalogVersion,
    }),
    [
      assignment.locationId,
      assignment.userLocationId,
      workplace.catalogVersion,
    ],
  );
  const hasStepOneBookableService = useMemo(
    () =>
      services.some((service) => {
        const offering = offeringByServiceId.get(service.serviceId);
        if (
          !offering ||
          !isSpecialistOfferingPublishedAtWorkplace(
            assignment,
            workplace,
            offering,
              locationResourceOnlyExecutionsV1,
          )
        ) {
          return false;
        }

        return (
          (isLocationEntryPoint ||
            isSpecialistStepOneFeasible({
              branch: "service",
              owner: specialistStepOneCapabilityOwner,
              service,
            })) &&
          isSpecialistServiceConfigurationAvailableAtWorkplace({
            locationResourceOnlyExecutionsV1,
            assignment,
            workplace,
            service,
            offering,
            bundlePlan: bundlePlanByServiceId.get(service.serviceId),
          })
        );
      }),
    [
      assignment,
      bundlePlanByServiceId,
      isLocationEntryPoint,
      offeringByServiceId,
      services,
      specialistStepOneCapabilityOwner,
      workplace,
    locationResourceOnlyExecutionsV1,
  ],
  );
  const locationGroupPublicGateIsOpen = canActivatePublicGroupLocationBooking();
  const isLocationGroupServicesEntryAvailable = useCallback((evaluatedAtEpochMs?: number) =>
    canActivatePublicGroupLocationBooking() || isCurrentGroupBookingFrontendServicesHost({
      host: frontendGroupServicesHost,
      catalog: locationAppointmentCatalog,
      locationId: routeLocationId,
      catalogOwnerUserLocationId: assignment.userLocationId,
      capabilityProvider: groupCapabilityProvider,
      catalogLineProvider: groupCatalogLineProvider,
      evaluatedAtEpochMs: evaluatedAtEpochMs ?? readSpecialistServicesSafeEpochMs(servicesClockRef.current),
    }), [frontendGroupServicesHost, locationAppointmentCatalog, routeLocationId,
      assignment.userLocationId, groupCapabilityProvider, groupCatalogLineProvider]);
  const isLocationGroupSelectSlotCompositionAvailable = useCallback(() =>
    canConsumeGroupAppointmentV2AtSelectSlot() ||
    Boolean(frontendGroupServicesHost && isLocationGroupServicesEntryAvailable()),
  [frontendGroupServicesHost, isLocationGroupServicesEntryAvailable]);
  const locationGroupServicesEntryAvailable = isLocationGroupServicesEntryAvailable();
  const locationGroupBookingAvailable = useMemo(() => {
    if (
      !locationGroupServicesEntryAvailable ||
      groupBookingPolicy === null ||
      !hasStepOneBookableService ||
      !isLocationEntryPoint
    ) {
      return false;
    }

    return Boolean(
      locationAppointmentCatalog &&
      locationAppointmentCatalog.workplace.locationId === routeLocationId &&
      locationAppointmentCatalog.catalogAssignment.userLocationId ===
        assignment.userLocationId &&
      isLocationGroupBookingEntryFeasibleFromCatalog(
        locationAppointmentCatalog,
      ),
    );
  }, [
    assignment.userLocationId,
    groupBookingPolicy,
    hasStepOneBookableService,
    isLocationEntryPoint,
    locationGroupServicesEntryAvailable,
    locationAppointmentCatalog,
    routeLocationId,
  ]);
  const specialistGroupStepOneRuntimeResolution = useMemo(() => {
    void groupCoordinatorRefreshVersion;
    const evaluatedAtEpochMs = readSpecialistServicesSafeEpochMs(
      servicesClockRef.current,
    );
    return isLocationEntryPoint || !person || evaluatedAtEpochMs === null
      ? ({ status: "unavailable" } as const)
      : resolveSpecialistCurrentAssignmentGroupStepOneRuntime({
          runtime: specialistGroupStepOneRuntime,
          sourcePersonId: person?.personId,
          sourceAssignmentPersonId: assignment.personId,
          sourceLocationId: assignment.locationId,
          sourceUserLocationId: assignment.userLocationId,
          returnHref: canonicalProfilePath,
          catalog: locationAppointmentCatalog,
          evaluatedAtEpochMs,
        });
  }, [
    assignment.locationId,
    assignment.personId,
    assignment.userLocationId,
    canonicalProfilePath,
    groupCoordinatorRefreshVersion,
    isLocationEntryPoint,
    locationAppointmentCatalog,
    specialistGroupStepOneRuntime,
    person,
  ]);
  const specialistGroupStepOneRuntimeIdentity = useMemo(
    () =>
      validateSpecialistCurrentAssignmentGroupStepOneRuntime(
        specialistGroupStepOneRuntime,
      ),
    [specialistGroupStepOneRuntime],
  );
  const specialistLocationGatewayGroupRuntimeIdentity = useMemo(
    () =>
      validateSpecialistLocationGatewayGroupStepOneRuntime(
        specialistLocationGatewayGroupStepOneRuntime,
      ),
    [specialistLocationGatewayGroupStepOneRuntime],
  );
  const specialistGroupCurrentCatalogFingerprint = useMemo(
    () =>
      createSpecialistCurrentAssignmentGroupCatalogFingerprint(
        locationAppointmentCatalog,
      ),
    [locationAppointmentCatalog],
  );
  const specialistOrganizerGroupCatalog =
    useMemo<SpecialistOrganizerGroupBookingCatalogSourceV1>(
      () => ({ assignment, workplace, services, offerings }),
      [assignment, offerings, services, workplace],
    );
  const specialistOrganizerGroupCatalogFingerprint = useMemo(
    () =>
      createSpecialistOrganizerGroupBookingCatalogFingerprint(
        specialistOrganizerGroupCatalog,
      ),
    [specialistOrganizerGroupCatalog],
  );
  const specialistLocationGatewayGroupIntent =
    specialistLocationGatewayCommittedGroupSession?.intent ??
    specialistLocationGatewaySession?.intent ??
    null;
  const specialistLocationGatewayGroupSessionOwner =
    specialistLocationGatewayCommittedGroupSession ??
    specialistLocationGatewaySession;
  const resolvedSpecialistLocationGatewayGroupNavigationLineage =
    useMemo(() => {
      const committedLineage =
        specialistLocationGatewayCommittedGroupSession?.navigationLineage;
      if (!committedLineage) {
        return specialistLocationGatewayGroupNavigationLineage;
      }
      return !specialistLocationGatewayGroupNavigationLineage ||
        haveExactSerializableStructure(
          committedLineage,
          specialistLocationGatewayGroupNavigationLineage,
        )
        ? committedLineage
        : null;
    }, [
      specialistLocationGatewayCommittedGroupSession,
      specialistLocationGatewayGroupNavigationLineage,
    ]);
  const specialistLocationGatewayGroupRuntimeContext = useMemo(() => {
    const evaluatedAtEpochMs = readSpecialistServicesSafeEpochMs(
      servicesClockRef.current,
    );
    if (
      evaluatedAtEpochMs === null ||
      isLocationEntryPoint ||
      !specialistLocationGatewayGroupIntent ||
      !resolvedSpecialistLocationGatewayGroupNavigationLineage ||
      !specialistLocationGatewayGroupRuntimeIdentity ||
      !locationAppointmentCatalog ||
      !specialistLocationGatewayGroupAggregateActivationRevision ||
      !canSpecialistLocationGatewayGroupStepOneRuntimeConsumeDownstream(
        specialistLocationGatewayGroupRuntimeIdentity,
        {
          evaluatedAtEpochMs,
          groupAggregateActivationRevision:
            specialistLocationGatewayGroupAggregateActivationRevision,
        },
      )
    ) {
      return { status: "unavailable" as const };
    }

    return resolveSpecialistLocationGatewayGroupStepOneRuntimeContext({
      runtime: specialistLocationGatewayGroupRuntimeIdentity,
      intentRecord: specialistLocationGatewayGroupIntent,
      navigationLineage:
        resolvedSpecialistLocationGatewayGroupNavigationLineage,
      sourceAssignmentPersonId:
        specialistLocationGatewayGroupRuntimeIdentity.sourcePersonId,
      sourceAssignmentLocationId:
        specialistLocationGatewayGroupRuntimeIdentity.sourceLocationId,
      sourceAssignmentUserLocationId:
        specialistLocationGatewayGroupRuntimeIdentity.sourceUserLocationId,
      sourceReturnHref:
        specialistLocationGatewayGroupRuntimeIdentity.returnHref,
      sourceProfileRevision:
        specialistLocationGatewayGroupRuntimeIdentity.sourceProfileRevision,
      sourceCatalogVersion:
        specialistLocationGatewayGroupRuntimeIdentity.sourceCatalogVersion,
      organizerCatalog: specialistOrganizerGroupCatalog,
      catalog: locationAppointmentCatalog,
      evaluatedAtEpochMs,
    });
  }, [
    isLocationEntryPoint,
    locationAppointmentCatalog,
    specialistLocationGatewayGroupAggregateActivationRevision,
    resolvedSpecialistLocationGatewayGroupNavigationLineage,
    specialistLocationGatewayGroupIntent,
    specialistLocationGatewayGroupRuntimeIdentity,
    specialistOrganizerGroupCatalog,
  ]);
  const readCurrentSpecialistLocationGatewayPreDraftGroupDescriptor =
    useCallback(() => {
      if (
        isLocationEntryPoint ||
        !person ||
        specialistLocationGatewayCommittedGroupSession ||
        specialistLocationGatewayGroupRuntimeIdentity ||
        resolvedSpecialistLocationGatewayGroupNavigationLineage
      ) {
        return null;
      }
      return resolveCurrentSpecialistLocationGatewayPreDraftGroupServicesDescriptor(
        {
          session: specialistLocationGatewaySession,
          servicesCartHandoffKey:
            specialistLocationGatewayServicesCartHandoffKey,
          expectedGroupAggregateActivationRevision:
            specialistLocationGatewayGroupAggregateActivationRevision,
          provider: specialistLocationGatewayOrganizerGroupCatalogLineProvider,
          person,
          assignment,
          workplace,
          organizerCatalog: specialistOrganizerGroupCatalog,
          locationCatalog: locationAppointmentCatalog,
        },
      );
    }, [
      assignment,
      isLocationEntryPoint,
      locationAppointmentCatalog,
      person,
      resolvedSpecialistLocationGatewayGroupNavigationLineage,
      specialistLocationGatewayCommittedGroupSession,
      specialistLocationGatewayGroupAggregateActivationRevision,
      specialistLocationGatewayGroupRuntimeIdentity,
      specialistLocationGatewayOrganizerGroupCatalogLineProvider,
      specialistLocationGatewayServicesCartHandoffKey,
      specialistLocationGatewaySession,
      specialistOrganizerGroupCatalog,
      workplace,
    ]);
  const specialistLocationGatewayPreDraftGroupDescriptorCandidate =
    readCurrentSpecialistLocationGatewayPreDraftGroupDescriptor();
  const specialistLocationGatewayPreDraftGroupDescriptorRef =
    useRef<SpecialistLocationGatewayPreDraftGroupServicesDescriptorV1 | null>(
      null,
    );
  if (
    specialistLocationGatewayPreDraftGroupDescriptorRef.current
      ?.servicesOwnerKey !==
    specialistLocationGatewayPreDraftGroupDescriptorCandidate?.servicesOwnerKey
  ) {
    specialistLocationGatewayPreDraftGroupDescriptorRef.current =
      specialistLocationGatewayPreDraftGroupDescriptorCandidate;
  }
  const specialistLocationGatewayPreDraftGroupDescriptor =
    specialistLocationGatewayPreDraftGroupDescriptorRef.current;
  const readExactCurrentSpecialistLocationGatewayPreDraftGroupDescriptor =
    useCallback(() => {
      const current =
        readCurrentSpecialistLocationGatewayPreDraftGroupDescriptor();
      return current &&
        specialistLocationGatewayPreDraftGroupDescriptor &&
        current.servicesOwnerKey ===
          specialistLocationGatewayPreDraftGroupDescriptor.servicesOwnerKey
        ? current
        : null;
    }, [
      readCurrentSpecialistLocationGatewayPreDraftGroupDescriptor,
      specialistLocationGatewayPreDraftGroupDescriptor,
    ]);
  const specialistOrganizerGroupCatalogProviderId = useMemo(() => {
    try {
      const providerId =
        specialistOrganizerGroupCatalogLineProvider?.providerId;
      return typeof providerId === "string" &&
        providerId === providerId.trim() &&
        providerId.length > 0 &&
        providerId.length <= 240
        ? providerId
        : null;
    } catch {
      return null;
    }
  }, [specialistOrganizerGroupCatalogLineProvider]);
  const specialistGroupGuestServiceCategories = useMemo(() => {
    if (specialistGroupStepOneRuntimeResolution.status !== "ready") {
      return null;
    }

    return resolveSpecialistCatalogCategories(
      specialistGroupStepOneRuntimeResolution.catalog.services.map(
        ({ categoryId }) => categoryId,
      ),
      specialistGroupGuestServiceCategoryPublication,
    );
  }, [
    specialistGroupGuestServiceCategoryPublication,
    specialistGroupStepOneRuntimeResolution,
  ]);
  const specialistGroupBookingPolicy = useMemo(
    () =>
      specialistGroupStepOneRuntimeResolution.status === "ready"
        ? getSpecialistGroupBookingPolicy(
            specialistGroupStepOneRuntimeResolution.catalog.workplace,
          )
        : null,
    [specialistGroupStepOneRuntimeResolution],
  );
  const specialistLocationGatewayGroupGuestServiceCategories = useMemo(() => {
    if (
      (specialistLocationGatewayGroupRuntimeContext.status !== "ready" &&
        !specialistLocationGatewayPreDraftGroupDescriptor) ||
      !locationAppointmentCatalog
    ) {
      return null;
    }

    return resolveSpecialistCatalogCategories(
      locationAppointmentCatalog.services.map(({ categoryId }) => categoryId),
      specialistGroupGuestServiceCategoryPublication,
    );
  }, [
    locationAppointmentCatalog,
    specialistGroupGuestServiceCategoryPublication,
    specialistLocationGatewayPreDraftGroupDescriptor,
    specialistLocationGatewayGroupRuntimeContext.status,
  ]);
  const specialistLocationGatewayGroupBookingPolicy = useMemo(
    () =>
      (specialistLocationGatewayGroupRuntimeContext.status === "ready" ||
        specialistLocationGatewayPreDraftGroupDescriptor) &&
      locationAppointmentCatalog
        ? getSpecialistGroupBookingPolicy(locationAppointmentCatalog.workplace)
        : null,
    [
      locationAppointmentCatalog,
      specialistLocationGatewayPreDraftGroupDescriptor,
      specialistLocationGatewayGroupRuntimeContext.status,
    ],
  );
  const specialistCurrentAssignmentGroupBookingAvailable = Boolean(
    !isLocationEntryPoint &&
    specialistGroupStepOneRuntimeResolution.status === "ready" &&
    specialistGroupGuestServiceCategories &&
    specialistGroupBookingPolicy &&
    specialistOrganizerGroupCatalogFingerprint &&
    specialistOrganizerGroupCatalogProviderId &&
    hasStepOneBookableService &&
    isLocationGroupBookingEntryFeasibleFromCatalog(
      specialistGroupStepOneRuntimeResolution.catalog,
    ),
  );
  const specialistLocationGatewayGroupBookingAvailable = Boolean(
    !isLocationEntryPoint &&
    (specialistLocationGatewayGroupRuntimeContext.status === "ready" ||
      specialistLocationGatewayPreDraftGroupDescriptor) &&
    specialistLocationGatewayOrganizerGroupCatalogLineProvider &&
    specialistLocationGatewayGroupGuestServiceCategories &&
    specialistLocationGatewayGroupBookingPolicy &&
    specialistOrganizerGroupCatalogFingerprint &&
    hasStepOneBookableService &&
    locationAppointmentCatalog &&
    isLocationGroupBookingEntryFeasibleFromCatalog(locationAppointmentCatalog),
  );
  const specialistGroupBookingAvailable =
    specialistCurrentAssignmentGroupBookingAvailable ||
    specialistLocationGatewayGroupBookingAvailable;
  const activeSpecialistGroupGuestServiceCategories =
    specialistLocationGatewayGroupRuntimeContext.status === "ready" ||
    specialistLocationGatewayPreDraftGroupDescriptor
      ? specialistLocationGatewayGroupGuestServiceCategories
      : specialistGroupGuestServiceCategories;
  const groupBookingAvailable = isLocationEntryPoint
    ? locationGroupBookingAvailable
    : specialistGroupBookingAvailable;
  const activeGroupBookingPolicy = isLocationEntryPoint
    ? groupBookingPolicy
    : specialistLocationGatewayGroupRuntimeContext.status === "ready" ||
        specialistLocationGatewayPreDraftGroupDescriptor
      ? specialistLocationGatewayGroupBookingPolicy
      : specialistGroupBookingPolicy;
  const groupCoordinatorOriginScope =
    useMemo<GroupCoordinatorRouteOriginScope | null>(() => {
      if (isLocationEntryPoint) {
        return locationGroupServicesEntryAvailable
          ? { kind: "location", locationId: routeLocationId }
          : null;
      }
      const gatewayRuntime = specialistLocationGatewayGroupRuntimeIdentity;
      if (
        specialistLocationGatewayGroupRuntimeContext.status === "ready" &&
        gatewayRuntime &&
        !specialistGroupStepOneRuntimeIdentity &&
        specialistLocationGatewayGroupAggregateActivationRevision &&
        gatewayRuntime.selectedLocationId === assignment.locationId &&
        gatewayRuntime.selectedUserLocationId === assignment.userLocationId &&
        gatewayRuntime.catalogVersion === groupLocationCatalogVersion &&
        gatewayRuntime.catalogOwnerUserLocationId ===
          locationAppointmentCatalog?.catalogAssignment.userLocationId
      ) {
        return {
          kind: "specialist-location-gateway",
          runtime: gatewayRuntime,
          groupAggregateActivationRevision:
            specialistLocationGatewayGroupAggregateActivationRevision,
        };
      }

      const runtime = specialistGroupStepOneRuntimeIdentity;
      if (
        !runtime ||
        specialistLocationGatewayGroupRuntimeIdentity ||
        !specialistGroupCurrentCatalogFingerprint ||
        runtime.catalogFingerprint !==
          specialistGroupCurrentCatalogFingerprint ||
        runtime.sourcePersonId !== person?.personId ||
        assignment.personId !== person?.personId ||
        runtime.sourceLocationId !== assignment.locationId ||
        runtime.sourceUserLocationId !== assignment.userLocationId ||
        runtime.selectedLocationId !== assignment.locationId ||
        runtime.selectedUserLocationId !== assignment.userLocationId ||
        runtime.returnHref !== canonicalProfilePath
      ) {
        return null;
      }

      return { kind: "specialist-current-assignment", runtime };
    }, [
      assignment.locationId,
      assignment.personId,
      assignment.userLocationId,
      canonicalProfilePath,
      isLocationEntryPoint,
      locationGroupServicesEntryAvailable,
      person?.personId,
      routeLocationId,
      specialistGroupCurrentCatalogFingerprint,
      specialistGroupStepOneRuntimeIdentity,
      specialistLocationGatewayGroupAggregateActivationRevision,
      specialistLocationGatewayGroupRuntimeContext.status,
      specialistLocationGatewayGroupRuntimeIdentity,
      locationAppointmentCatalog,
      groupLocationCatalogVersion,
    ]);
  useEffect(() => {
    if (
      isLocationEntryPoint ||
      specialistGroupStepOneRuntimeResolution.status !== "ready"
    ) {
      return undefined;
    }

    const evaluatedAtEpochMs = readSpecialistServicesSafeEpochMs(
      servicesClockRef.current,
    );
    const delayMs =
      evaluatedAtEpochMs === null
        ? 0
        : specialistGroupStepOneRuntimeResolution.runtime.expiresAtEpochMs -
          evaluatedAtEpochMs;
    if (delayMs <= 0) {
      const frame = window.requestAnimationFrame(() => {
        setGroupCoordinatorRefreshVersion((current) => current + 1);
      });
      return () => window.cancelAnimationFrame(frame);
    }

    const timer = window.setTimeout(
      () => setGroupCoordinatorRefreshVersion((current) => current + 1),
      Math.min(delayMs + 1, 2_147_483_647),
    );
    return () => window.clearTimeout(timer);
  }, [isLocationEntryPoint, specialistGroupStepOneRuntimeResolution]);
  const isCurrentGroupCoordinatorRouteAuthority = useCallback(
    (
      coordinator: GroupBookingCoordinatorDraftV2,
      evaluatedAtEpochMs?: number,
    ) => {
      const safeEpochMs =
        evaluatedAtEpochMs === undefined
          ? readSpecialistServicesSafeEpochMs(servicesClockRef.current)
          : resolveSpecialistServicesEpochMs(evaluatedAtEpochMs);
      if (safeEpochMs === null) return false;
      if (isLocationEntryPoint) return Boolean(
        isLocationGroupServicesEntryAvailable(safeEpochMs) &&
        locationAppointmentCatalog &&
        coordinator.origin.kind === "location" &&
        coordinator.locationId === locationAppointmentCatalog.workplace.locationId &&
        coordinator.catalogVersion === locationAppointmentCatalog.workplace.catalogVersion &&
        coordinator.catalogOwnerUserLocationId === locationAppointmentCatalog.catalogAssignment.userLocationId &&
        (coordinator.authorityState !== "building" ||
          isLocationGroupRosterBuildingAuthorityCurrent({ coordinator, evaluatedAtEpochMs: safeEpochMs })),
      );
      if (groupCoordinatorOriginScope?.kind === "specialist-location-gateway") {
        return Boolean(
          specialistLocationGatewayGroupRuntimeContext.status === "ready" &&
          specialistLocationGatewayOrganizerGroupCatalogLineProvider &&
          specialistLocationGatewayGroupStepOneRuntimeOwnsCoordinator({
            runtime: groupCoordinatorOriginScope.runtime,
            coordinator,
            requireBuildingAuthority: true,
            requireCurrentRuntime: true,
            evaluatedAtEpochMs: safeEpochMs,
          }) &&
          locationAppointmentCatalog &&
          validateCurrentSpecialistLocationGatewayOrganizerGroupBookingCoordinatorCatalog(
            {
              runtime: groupCoordinatorOriginScope.runtime,
              coordinator,
              provider:
                specialistLocationGatewayOrganizerGroupCatalogLineProvider,
              organizerCatalog: specialistOrganizerGroupCatalog,
              locationCatalog: locationAppointmentCatalog,
              evaluatedAtEpochMs: safeEpochMs,
            },
          ).status === "current",
        );
      }
      if (!person || !specialistGroupGuestServiceCategories) return false;

      const resolution = resolveSpecialistCurrentAssignmentGroupStepOneRuntime({
        runtime: specialistGroupStepOneRuntime,
        sourcePersonId: person?.personId,
        sourceAssignmentPersonId: assignment.personId,
        sourceLocationId: assignment.locationId,
        sourceUserLocationId: assignment.userLocationId,
        returnHref: canonicalProfilePath,
        catalog: locationAppointmentCatalog,
        evaluatedAtEpochMs: safeEpochMs,
      });

      const runtimeOwnsCoordinator = Boolean(
        resolution.status === "ready" &&
        specialistGroupStepOneRuntimeOwnsCoordinator({
          runtime: resolution.runtime,
          coordinator,
          requireBuildingAuthority: true,
          requireCurrentRuntime: true,
          evaluatedAtEpochMs: safeEpochMs,
        }),
      );
      if (
        !runtimeOwnsCoordinator ||
        !locationAppointmentCatalog ||
        !specialistOrganizerGroupCatalogLineProvider
      ) {
        return false;
      }

      return (
        validateCurrentSpecialistOrganizerGroupBookingCoordinatorCatalog({
          coordinator,
          provider: specialistOrganizerGroupCatalogLineProvider,
          organizerCatalog: specialistOrganizerGroupCatalog,
          locationCatalog: locationAppointmentCatalog,
          evaluatedAtEpochMs: safeEpochMs,
        }).status === "current"
      );
    },
    [
      assignment.locationId,
      assignment.personId,
      assignment.userLocationId,
      canonicalProfilePath,
      isLocationEntryPoint,
      isLocationGroupServicesEntryAvailable,
      locationAppointmentCatalog,
      groupCoordinatorOriginScope,
      specialistLocationGatewayGroupRuntimeContext.status,
      specialistLocationGatewayOrganizerGroupCatalogLineProvider,
      specialistOrganizerGroupCatalog,
      specialistOrganizerGroupCatalogLineProvider,
      specialistGroupGuestServiceCategories,
      specialistGroupStepOneRuntime,
      person,
    ],
  );
  const isRecoverableSpecialistOrganizerCoordinator = useCallback(
    (
      coordinator: GroupBookingCoordinatorDraftV2,
      evaluatedAtEpochMs?: number,
    ) => {
      const safeEpochMs =
        evaluatedAtEpochMs === undefined
          ? readSpecialistServicesSafeEpochMs(servicesClockRef.current)
          : resolveSpecialistServicesEpochMs(evaluatedAtEpochMs);
      if (safeEpochMs === null) return false;
      if (groupCoordinatorOriginScope?.kind === "specialist-location-gateway") {
        const registry =
          specialistLocationGatewayGroupDownstreamRegistry === undefined
            ? readDefaultSpecialistLocationGatewayGroupDownstreamRegistry()
            : specialistLocationGatewayGroupDownstreamRegistry;
        return Boolean(
          specialistLocationGatewayGroupRuntimeContext.status === "ready" &&
          specialistLocationGatewayCommittedGroupSession &&
          registry &&
          specialistLocationGatewayGroupStepOneRuntimeOwnsCoordinator({
            runtime: groupCoordinatorOriginScope.runtime,
            coordinator,
            requireBuildingAuthority: true,
            requireCurrentRuntime: false,
            evaluatedAtEpochMs:
              groupCoordinatorOriginScope.runtime.generatedAtEpochMs,
          }) &&
          locationAppointmentCatalog &&
          resolveSpecialistLocationGatewayGroupCurrentAuthoritySnapshot({
            registry,
            intentRecord: specialistLocationGatewayCommittedGroupSession.intent,
            navigationLineage:
              specialistLocationGatewayCommittedGroupSession.navigationLineage,
            coordinator,
            organizerCatalog: specialistOrganizerGroupCatalog,
            locationCatalog: locationAppointmentCatalog,
            evaluatedAtEpochMs: safeEpochMs,
          }) !== null &&
          isSpecialistLocationGatewayCommittedGroupSessionCurrent(
            specialistLocationGatewayCommittedGroupSession,
          ),
        );
      }
      if (
        isLocationEntryPoint ||
        specialistGroupStepOneRuntimeResolution.status !== "ready" ||
        !locationAppointmentCatalog ||
        !specialistOrganizerGroupCatalogLineProvider ||
        !specialistGroupStepOneRuntimeOwnsCoordinator({
          runtime: specialistGroupStepOneRuntimeResolution.runtime,
          coordinator,
          requireBuildingAuthority: true,
          requireCurrentRuntime: true,
          evaluatedAtEpochMs: safeEpochMs,
        })
      ) {
        return false;
      }

      return (
        validateCurrentSpecialistOrganizerGroupBookingCoordinatorCatalog({
          coordinator,
          provider: specialistOrganizerGroupCatalogLineProvider,
          organizerCatalog: specialistOrganizerGroupCatalog,
          locationCatalog: locationAppointmentCatalog,
          evaluatedAtEpochMs: safeEpochMs,
        }).status !== "invalid"
      );
    },
    [
      isLocationEntryPoint,
      groupCoordinatorOriginScope,
      locationAppointmentCatalog,
      specialistLocationGatewayCommittedGroupSession,
      specialistLocationGatewayGroupDownstreamRegistry,
      specialistLocationGatewayGroupRuntimeContext.status,
      specialistGroupStepOneRuntimeResolution,
      specialistOrganizerGroupCatalog,
      specialistOrganizerGroupCatalogLineProvider,
    ],
  );
  const routeGroupCapabilityProvider = useMemo(() => {
    if (groupCoordinatorOriginScope?.kind === "specialist-current-assignment") {
      return createSpecialistCurrentAssignmentGroupCapabilityProviderNarrowingAdapter(
        {
          runtime: groupCoordinatorOriginScope.runtime,
          provider: groupCapabilityProvider,
        },
      );
    }
    if (
      groupCoordinatorOriginScope?.kind === "specialist-location-gateway" &&
      specialistLocationGatewayGroupIntent &&
      resolvedSpecialistLocationGatewayGroupNavigationLineage
    ) {
      return createSpecialistLocationGatewayGroupCapabilityProviderNarrowingAdapter(
        {
          runtime: groupCoordinatorOriginScope.runtime,
          intentRecord: specialistLocationGatewayGroupIntent,
          navigationLineage:
            resolvedSpecialistLocationGatewayGroupNavigationLineage,
          sourceAssignmentPersonId:
            groupCoordinatorOriginScope.runtime.sourcePersonId,
          sourceAssignmentLocationId:
            groupCoordinatorOriginScope.runtime.sourceLocationId,
          sourceAssignmentUserLocationId:
            groupCoordinatorOriginScope.runtime.sourceUserLocationId,
          sourceReturnHref: groupCoordinatorOriginScope.runtime.returnHref,
          sourceProfileRevision:
            groupCoordinatorOriginScope.runtime.sourceProfileRevision,
          sourceCatalogVersion:
            groupCoordinatorOriginScope.runtime.sourceCatalogVersion,
          organizerCatalog: specialistOrganizerGroupCatalog,
          provider: groupCapabilityProvider,
        },
      );
    }
    return groupCapabilityProvider;
  }, [
    groupCapabilityProvider,
    groupCoordinatorOriginScope,
    resolvedSpecialistLocationGatewayGroupNavigationLineage,
    specialistLocationGatewayGroupIntent,
    specialistOrganizerGroupCatalog,
  ]);
  const routeGroupCatalogOwnerUserLocationId =
    locationAppointmentCatalog?.catalogAssignment.userLocationId ?? null;
  const groupCoordinatorPointerIsPresent = useMemo(
    () => hasGroupBookingCoordinatorPointer(new URLSearchParams(searchString)),
    [searchString],
  );
  const appointmentMode = useMemo(
    () =>
      membershipSessionServicesEntryIsRequired ? "individual" as const : getRequestedAppointmentMode(
        new URLSearchParams(searchString),
        groupBookingAvailable,
        groupCoordinatorOriginScope !== null &&
          groupCoordinatorPointerIsPresent,
      ),
    [
      groupBookingAvailable,
      groupCoordinatorPointerIsPresent,
      groupCoordinatorOriginScope,
      searchString,
      membershipSessionServicesEntryIsRequired,
    ],
  );

  useEffect(() => {
    if (
      (!groupCoordinatorOriginScope &&
        !specialistLocationGatewayPreDraftGroupDescriptor) ||
      appointmentMode !== "group" ||
      groupCoordinatorPointerIsPresent ||
      !groupCustomerOwnerState.hydrated ||
      groupCustomerOwnerState.anonymousSessionOwnerId !== null
    ) {
      return;
    }

    const anonymousSessionOwnerId =
      getOrCreateGroupBookingAnonymousSessionOwnerId();

    if (!anonymousSessionOwnerId) return;
    setGroupCustomerOwnerState((current) =>
      current.hydrated && current.anonymousSessionOwnerId === null
        ? { ...current, anonymousSessionOwnerId }
        : current,
    );
  }, [
    appointmentMode,
    groupCoordinatorPointerIsPresent,
    groupCoordinatorOriginScope,
    specialistLocationGatewayPreDraftGroupDescriptor,
    groupLocationCatalogVersion,
    groupCustomerOwnerState,
  ]);
  const requestedInitialProfessionalId = useMemo(() => {
    if (!isLocationEntryPoint) {
      return null;
    }

    const professionalId = getSingleFocusIdentifier(
      new URLSearchParams(searchString),
      LOCATION_INITIAL_PROFESSIONAL_PARAM,
    );

    return professionalId &&
      locationProfessionals.some(
        ({ userLocationId }) => userLocationId === professionalId,
      )
      ? professionalId
      : null;
  }, [isLocationEntryPoint, locationProfessionals, searchString]);
  const requestedInitialServiceProfessionals = useMemo(
    () => isLocationEntryPoint && appointmentMode === "individual" && !requestedInitialProfessionalId
      ? parseInitialServiceProfessionals(new URLSearchParams(searchString).getAll(INITIAL_SERVICE_PROFESSIONALS_PARAM))
      : [],
    [appointmentMode, isLocationEntryPoint, requestedInitialProfessionalId, searchString],
  );
  const initialProfessionalName = useMemo(
    () =>
      locationProfessionals.find(
        ({ userLocationId }) =>
          userLocationId === requestedInitialProfessionalId,
      )?.name ?? null,
    [locationProfessionals, requestedInitialProfessionalId],
  );
  const requestedPackageIds = useMemo(
    () =>
      getRequestedPackageIds(new URLSearchParams(searchString)).filter(
        (packageId) => packageIdSet.has(packageId),
      ),
    [packageIdSet, searchString],
  );
  const catalogView = useMemo<SpecialistCatalogView>(() => {
    if (isGiftPackageCatalog) return "packages";
    if (appointmentMode === "group") {
      return "services";
    }

    if (packages.length > 0 && !hasPublishedServices) {
      return "packages";
    }

    return searchParams.get("catalog") === "packages" && packages.length > 0
      ? "packages"
      : "services";
  }, [appointmentMode, hasPublishedServices, isGiftPackageCatalog, packages.length, searchParams]);
  useEffect(() => {
    const session = specialistLocationGatewaySession;
    if (!session || !isSpecialistLocationGatewaySessionCurrent(session)) return;
    const selection = {
      selectedLocationId: session.resolution.selection.selectedLocationId,
      selectedUserLocationId:
        session.resolution.selection.selectedUserLocationId,
    } as const;
    if (catalogView === "packages") {
      session.analyticsReporter.viewed("package-catalog", selection);
      return;
    }
    const state = currentSpecialistLocationGatewayServicesCartState;
    if (
      !specialistLocationGatewayServicesCartIsApplicable ||
      state.status !== "ready" ||
      state.handoffKey !== specialistLocationGatewayServicesCartHandoffKey ||
      state.cart.intentDraftId !== session.intent.draftId ||
      state.cart.resolutionId !== session.resolution.draftId ||
      state.cart.customerAccountId !== session.resolution.customerAccountId ||
      state.cart.sourceAssignmentA.locationId !==
        session.intent.source.sourceLocationId ||
      state.cart.sourceAssignmentA.userLocationId !==
        session.intent.source.sourceUserLocationId ||
      state.cart.selectedAssignmentB.locationId !==
        selection.selectedLocationId ||
      state.cart.selectedAssignmentB.userLocationId !==
        selection.selectedUserLocationId
    ) {
      return;
    }
    const evaluatedAtEpochMs =
      readSpecialistLocationGatewaySessionEpoch(session);
    if (evaluatedAtEpochMs === null) return;
    const readBack = readSpecialistLocationGatewayConfiguredServicesCartStrict({
      draftId: state.cart.draftId,
      expectedOwnerKey: state.cart.ownerKey,
      expectedCustomerAccountId: state.cart.customerAccountId,
      expectedIntentDraftId: state.cart.intentDraftId,
      expectedResolutionId: state.cart.resolutionId,
      nowEpochMs: evaluatedAtEpochMs,
    });
    if (
      readBack.status !== "ready" ||
      !haveExactSerializableStructure(readBack.cart, state.cart) ||
      !isSpecialistLocationGatewaySessionCurrent(session)
    ) {
      return;
    }
    session.analyticsReporter.viewed("services", selection);
  }, [
    catalogView,
    currentSpecialistLocationGatewayServicesCartState,
    specialistLocationGatewayServicesCartHandoffKey,
    specialistLocationGatewayServicesCartIsApplicable,
    specialistLocationGatewaySession,
  ]);
  const requestedRouteServiceIds = useMemo(
    () => getBookingFlowSelectedServiceIds(searchParams),
    [searchParams],
  );
  const requestedServiceIds = useMemo(
    () =>
      requestedRouteServiceIds.filter(
        (serviceId) =>
          serviceIdSet.has(serviceId) && offeringByServiceId.has(serviceId),
      ),
    [offeringByServiceId, requestedRouteServiceIds, serviceIdSet],
  );
  const requestedVariantSelections = useMemo(
    () => getBookingFlowSelectedServiceVariantSelections(searchParams),
    [searchParams],
  );
  const requestedAddOnSelections = useMemo(
    () =>
      getSpecialistServiceAddOnQuerySelections(
        searchParams,
        requestedServiceIds,
      ),
    [requestedServiceIds, searchParams],
  );
  const requestedBundleAddOnSelections = useMemo(
    () =>
      getSpecialistServiceBundleAddOnQuerySelections(
        searchParams,
        requestedServiceIds,
        ({ parentServiceId, bundleItemId, serviceId, addOnId }) => {
          const plan = bundlePlanByServiceId.get(parentServiceId);
          const step = plan?.steps.find(
            (candidate) =>
              candidate.bundleItem.itemId === bundleItemId &&
              candidate.service.serviceId === serviceId,
          );
          const matchingGroups =
            step?.addOnGroups.filter((group) =>
              group.options.some((option) => option.addOnId === addOnId),
            ) ?? [];

          return matchingGroups.length === 1
            ? matchingGroups[0]!.groupId
            : null;
        },
      ),
    [bundlePlanByServiceId, requestedServiceIds, searchParams],
  );
  const nestedQueryRowCounts = useMemo(() => {
    const params = new URLSearchParams(searchString);

    return {
      serviceAddOns: getSelectedNestedQueryRowCounts(
        params,
        SPECIALIST_SERVICE_ADD_ONS_PARAM,
        requestedServiceIds,
      ),
      serviceBundleAddOns: getSelectedNestedQueryRowCounts(
        params,
        SPECIALIST_SERVICE_BUNDLE_ADD_ONS_PARAM,
        requestedServiceIds,
      ),
    };
  }, [requestedServiceIds, searchString]);
  const requestedServicesProjection = useMemo(() => {
    const variantIdByServiceId = new Map(
      requestedVariantSelections.map(({ serviceId, variantId }) => [
        serviceId,
        variantId,
      ]),
    );
    const incompleteServiceIds: string[] = [];

    const selections = requestedServiceIds.flatMap<SpecialistServiceSelection>(
      (serviceId) => {
        const service = serviceByServiceId.get(serviceId);
        const offering = offeringByServiceId.get(serviceId);

        if (!service || !offering) {
          return [];
        }

        if (
          !isLocationEntryPoint &&
          !isSpecialistStepOneFeasible({
            branch: "service",
            owner: specialistStepOneCapabilityOwner,
            service,
          })
        ) {
          return [];
        }

        if (service.kind === "service") {
          const queryAddOns = requestedAddOnSelections.filter(
            (selection) => selection.serviceId === serviceId,
          );

          if (
            (nestedQueryRowCounts.serviceAddOns.get(serviceId) ?? 0) !==
              queryAddOns.length ||
            (nestedQueryRowCounts.serviceBundleAddOns.get(serviceId) ?? 0) > 0
          ) {
            return [];
          }

          const variantId = specialistOfferingRequiresVariantSelection(offering)
            ? (variantIdByServiceId.get(serviceId) ?? null)
            : null;
          const addOns = resolveSpecialistServiceAddOnQuerySelections(
            offering,
            queryAddOns,
          );

          if (!didServiceAddOnQueryResolveAtomically(queryAddOns, addOns)) {
            return [];
          }

          const resolved = resolveSpecialistServiceConfiguration(offering, {
            serviceId,
            variantId,
            ...(addOns.length > 0 ? { addOns } : {}),
          });

          if (
            !resolved &&
            (!variantId ||
              getSelectableSpecialistServiceVariant(offering, variantId)) &&
            isSpecialistServiceConfigurationAvailableAtWorkplace({
            locationResourceOnlyExecutionsV1,
              assignment,
              workplace,
              service,
              offering,
            })
          ) {
            incompleteServiceIds.push(serviceId);
          }

          return resolved ? [resolved.selection] : [];
        }

        const plan = bundlePlanByServiceId.get(serviceId);

        if (!plan) {
          return [];
        }

        const queryBundleAddOns = requestedBundleAddOnSelections.filter(
          (selection) => selection.parentServiceId === serviceId,
        );

        if (
          (nestedQueryRowCounts.serviceBundleAddOns.get(serviceId) ?? 0) !==
            queryBundleAddOns.length ||
          (nestedQueryRowCounts.serviceAddOns.get(serviceId) ?? 0) > 0
        ) {
          return [];
        }

        let mappedBundleAddOnCount = 0;
        const bundleItems = plan.steps.map((step) => {
          const addOns = queryBundleAddOns
            .filter(
              (selection) =>
                selection.bundleItemId === step.bundleItem.itemId &&
                selection.serviceId === step.service.serviceId,
            )
            .map(({ groupId, addOnId, quantity }) => ({
              groupId,
              addOnId,
              quantity,
            }));

          mappedBundleAddOnCount += addOns.length;

          return {
            bundleItemId: step.bundleItem.itemId,
            serviceId: step.service.serviceId,
            variantId: step.variantId,
            addOns,
          };
        });

        if (mappedBundleAddOnCount !== queryBundleAddOns.length) {
          return [];
        }

        const resolved = resolveSpecialistServiceBundleConfiguration(plan, {
          serviceId,
          variantId: null,
          bundleItems,
        });

        if (
          !resolved?.canConfirm &&
          isSpecialistServiceConfigurationAvailableAtWorkplace({
            locationResourceOnlyExecutionsV1,
            assignment,
            workplace,
            service,
            offering,
            bundlePlan: plan,
          })
        ) {
          incompleteServiceIds.push(serviceId);
        }

        return resolved?.canConfirm ? [resolved.selection] : [];
      },
    );

    return { incompleteServiceIds, selections };
  }, [
    assignment,
    bundlePlanByServiceId,
    nestedQueryRowCounts,
    offeringByServiceId,
    requestedAddOnSelections,
    requestedBundleAddOnSelections,
    requestedServiceIds,
    requestedVariantSelections,
    isLocationEntryPoint,
    serviceByServiceId,
    specialistStepOneCapabilityOwner,
    workplace,
    locationResourceOnlyExecutionsV1,
  ]);
  const requestedServices = requestedServicesProjection.selections;
  const initialServiceProfessionalNames = useMemo(() => {
    const names: Record<string, string> = {};
    if (!locationAppointmentCatalog) return names;
    for (const preference of requestedInitialServiceProfessionals) {
      if (!requestedServices.some(row => row.serviceId === preference.serviceId) ||
        serviceByServiceId.get(preference.serviceId)?.kind !== "service") continue;
      const offering = offeringByServiceId.get(preference.serviceId);
      const professional = locationProfessionals.find(row => row.userLocationId === preference.userLocationId);
      if (offering && professional && isLocationAppointmentProfessionalEligibleForTasksFromCatalog(
        locationAppointmentCatalog,
        [{ serviceId: preference.serviceId, offeringId: offering.offeringId }],
        preference.userLocationId,
      )) names[preference.serviceId] = professional.name;
    }
    return names;
  }, [locationAppointmentCatalog, locationProfessionals, offeringByServiceId,
    requestedInitialServiceProfessionals, requestedServices, serviceByServiceId]);

  const hasIncompleteDirectSpecialistServiceRequest = useMemo(
    () =>
      !isLocationEntryPoint &&
      !specialistLocationGatewaySession &&
      requestedServicesProjection.incompleteServiceIds.length > 0,
    [
      isLocationEntryPoint,
      requestedServicesProjection.incompleteServiceIds.length,
      specialistLocationGatewaySession,
    ],
  );
  const eligibleInitialProfessionalName = useMemo(() => {
    if (
      !isLocationEntryPoint ||
      appointmentMode !== "individual" ||
      !requestedInitialProfessionalId ||
      !initialProfessionalName
    ) {
      return null;
    }
    if (requestedServices.length === 0) return initialProfessionalName;

    if (!locationAppointmentCatalog) return null;

    const taskGroups = requestedServices.map((selection) => {
      const service = serviceByServiceId.get(selection.serviceId);
      const offering = offeringByServiceId.get(selection.serviceId);

      if (!service || !offering) return [];
      if (service.kind !== "bundle") {
        return [
          { serviceId: service.serviceId, offeringId: offering.offeringId },
        ];
      }

      if (service.scheduleType === "parallel") {
        return [];
      }

      return (bundlePlanByServiceId.get(service.serviceId)?.steps ?? []).map(
        (step) => ({
          serviceId: step.service.serviceId,
          offeringId: step.offering.offeringId,
        }),
      );
    });

    if (taskGroups.some((tasks) => tasks.length === 0)) return null;
    const tasks = taskGroups.flat();

    return isLocationAppointmentProfessionalEligibleForTasksFromCatalog(
      locationAppointmentCatalog,
      tasks,
      requestedInitialProfessionalId,
    )
      ? initialProfessionalName
      : null;
  }, [
    appointmentMode,
    bundlePlanByServiceId,
    initialProfessionalName,
    isLocationEntryPoint,
    locationAppointmentCatalog,
    offeringByServiceId,
    requestedInitialProfessionalId,
    requestedServices,
    serviceByServiceId,
  ]);
  const timeResumeRequestKey = useMemo(() => {
    const params = new URLSearchParams(searchString);

    return params.has(APPOINTMENT_TIME_SERVICES_RESUME_DRAFT_PARAM) ||
      params.has(APPOINTMENT_TIME_SERVICES_RESUME_SCHEMA_PARAM)
      ? currentServicesHref
      : null;
  }, [currentServicesHref, searchString]);
  const [timeResumeRefreshVersion, setTimeResumeRefreshVersion] = useState(0);
  const timeResumeHandoffKey = `${timeResumeRequestKey ?? "absent"}:${timeResumeRefreshVersion}`;
  const timeResumeFailClosedState =
    useMemo<AppointmentTimeServicesResumeHydrationState>(
      () =>
        timeResumeRequestKey ? { status: "hydrating" } : { status: "absent" },
      [timeResumeRequestKey],
    );
  const {
    isCurrentHandoff: isCurrentTimeResumeHandoff,
    publishForHandoff: publishTimeResumeForHandoff,
    state: timeResumeHydration,
  } = useKeyedHandoffHydrationState<AppointmentTimeServicesResumeHydrationState>(
    timeResumeHandoffKey,
    timeResumeFailClosedState,
  );
  const timeResumeRouteTarget = useMemo(
    () =>
      isLocationEntryPoint
        ? {
            kind: "location-services" as const,
            locationId: routeLocationId,
          }
        : {
            kind: "specialist-services" as const,
            locationId: assignment.locationId,
            userLocationId: assignment.userLocationId,
          },
    [
      assignment.locationId,
      assignment.userLocationId,
      isLocationEntryPoint,
      routeLocationId,
    ],
  );
  const resolveCurrentTimeResume = useCallback(
    (evaluatedAtEpochMs?: number) => {
      const safeEpochMs =
        evaluatedAtEpochMs === undefined
          ? readSpecialistServicesSafeEpochMs(servicesClockRef.current)
          : resolveSpecialistServicesEpochMs(evaluatedAtEpochMs);
      return safeEpochMs === null
        ? ({ status: "invalid" } as const)
        : resolveAppointmentTimeServicesResumeRoute({
            searchParams: new URLSearchParams(searchString),
            currentHref: currentServicesHref,
            route: timeResumeRouteTarget,
            locationAppointmentCatalog: timeMutationLocationAppointmentCatalog,
            evaluatedAtEpochMs: safeEpochMs,
          });
    },
    [
      currentServicesHref,
      searchString,
      timeMutationLocationAppointmentCatalog,
      timeResumeRouteTarget,
    ],
  );

  useEffect(() => {
    if (!timeResumeRequestKey) {
      timeResumeDiscardingRef.current = false;
      return;
    }

    const ownerHandoffKey = timeResumeHandoffKey;
    const resolution = resolveCurrentTimeResume();

    if (!isCurrentTimeResumeHandoff(ownerHandoffKey)) {
      return;
    }

    publishTimeResumeForHandoff(ownerHandoffKey, resolution);
  }, [
    isCurrentTimeResumeHandoff,
    publishTimeResumeForHandoff,
    resolveCurrentTimeResume,
    timeResumeHandoffKey,
    timeResumeRequestKey,
  ]);

  useEffect(() => {
    if (timeResumeHydration.status !== "restored") {
      return undefined;
    }

    const evaluatedAtEpochMs = readSpecialistServicesSafeEpochMs(
      servicesClockRef.current,
    );
    const expiresInMs =
      evaluatedAtEpochMs === null
        ? 0
        : timeResumeHydration.timeProfessionalMutationEvidence
            .expiresAtEpochMs - evaluatedAtEpochMs;

    if (expiresInMs <= 0) {
      const frame = window.requestAnimationFrame(() => {
        setTimeResumeRefreshVersion((current) => current + 1);
      });
      return () => window.cancelAnimationFrame(frame);
    }

    const timer = window.setTimeout(() => {
      setTimeResumeRefreshVersion((current) => current + 1);
    }, expiresInMs);

    return () => window.clearTimeout(timer);
  }, [timeResumeHydration]);

  useEffect(() => {
    if (!timeResumeRequestKey) {
      return undefined;
    }

    const refreshOnPageShow = () => {
      setTimeResumeRefreshVersion((current) => current + 1);
    };

    window.addEventListener("pageshow", refreshOnPageShow);
    return () => window.removeEventListener("pageshow", refreshOnPageShow);
  }, [timeResumeRequestKey]);

  const timeResumeIsCurrent =
    timeResumeHydration.status === "restored" &&
    !timeResumeDiscardingRef.current &&
    isAppointmentTimeProfessionalMutationEvidenceCurrent({
      evidence: timeResumeHydration.timeProfessionalMutationEvidence,
      expectedProviderIdentity: TIME_PROFESSIONAL_PROVIDER_IDENTITY,
    });
  const isTimeResumeBlocking =
    timeResumeRequestKey !== null && !timeResumeIsCurrent;
  const isTimeResumeCanonicalizationBlocked =
    timeResumeCanonicalizationRetry.handoffKey === timeResumeHandoffKey &&
    timeResumeCanonicalizationRetry.status === "blocked";
  const currentServicesProfessionalName = useMemo(() => {
    if (
      !isLocationEntryPoint ||
      !timeResumeIsCurrent ||
      timeResumeHydration.status !== "restored" ||
      timeResumeHydration.projection.kind !== "location-services"
    ) {
      return eligibleInitialProfessionalName;
    }

    return resolveTimeResumeLocationProfessionalName({
      assignment: timeResumeHydration.projection.assignment,
      userLocationId: timeResumeHydration.projection.userLocationId,
      professionals:
        timeMutationLocationAppointmentCatalog?.professionals ?? [],
      multipleProfessionalsLabel: tAll(
        "booking.selectSlot.timeMultipleProfessionals",
      ),
    });
  }, [
    eligibleInitialProfessionalName,
    isLocationEntryPoint,
    tAll,
    timeMutationLocationAppointmentCatalog,
    timeResumeHydration,
    timeResumeIsCurrent,
  ]);
  const groupCoordinatorReturnHref =
    groupCoordinatorOriginScope?.kind === "specialist-location-gateway"
      ? groupCoordinatorOriginScope.runtime.returnHref
      : specialistLocationGatewayPreDraftGroupDescriptor
        ? specialistLocationGatewayPreDraftGroupDescriptor.returnHref
        : canonicalProfilePath;
  const groupCoordinatorHandoffKey = useMemo(() => {
    const locationKeyParts = [
      groupCoordinatorPointerIsPresent ? searchString : "absent",
      route.kind,
      routeLocationId,
      routeGroupCatalogOwnerUserLocationId ?? "missing-catalog-owner",
      workplace.catalogVersion,
      groupCoordinatorReturnHref,
      groupCustomerOwnerState.hydrated ? "auth-ready" : "auth-hydrating",
      groupCustomerOwnerState.customerAccountId ?? "anonymous",
      groupCustomerOwnerState.anonymousSessionOwnerId ??
        "missing-anonymous-owner",
      locationGroupPublicGateIsOpen
        ? "location-group-public-open"
        : locationGroupServicesEntryAvailable
          ? "location-group-frontend-host-ready"
        : "location-group-public-closed",
    ];

    if (isLocationEntryPoint) {
      // Keep the established Location hydration owner byte-semantic.
      return [...locationKeyParts, groupCoordinatorRefreshVersion].join(":");
    }

    if (specialistLocationGatewayPreDraftGroupDescriptor) {
      return createCanonicalGroupBookingTupleKey([
        "specialist-location-gateway-pre-draft-group-services-owner-v1",
        ...locationKeyParts,
        specialistLocationGatewayPreDraftGroupDescriptor.servicesOwnerKey,
        activeSpecialistGroupGuestServiceCategories
          ? JSON.stringify(activeSpecialistGroupGuestServiceCategories)
          : "invalid-guest-category-publication",
      ]);
    }

    if (groupCoordinatorOriginScope?.kind === "specialist-location-gateway") {
      const runtime = groupCoordinatorOriginScope.runtime;
      return createCanonicalGroupBookingTupleKey([
        "specialist-location-gateway-group-services-owner-v1",
        ...locationKeyParts,
        runtime.gatewayIntentDraftId,
        runtime.gatewayIntentFingerprint,
        runtime.gatewayResolutionId,
        runtime.gatewayResolutionFingerprint,
        runtime.coordinatorDraftId,
        runtime.sourceLocationId,
        runtime.sourceUserLocationId,
        runtime.selectedLocationId,
        runtime.selectedUserLocationId,
        runtime.organizerCatalogFingerprint,
        runtime.catalogFingerprint,
        runtime.groupAggregateActivationRevision,
        groupCoordinatorOriginScope.groupAggregateActivationRevision,
        runtime.returnHref,
        runtime.generatedAtEpochMs,
        runtime.expiresAtEpochMs,
      ]);
    }

    const runtime = specialistGroupStepOneRuntimeIdentity;
    // A clock/storage refresh republishes the same opaque Specialist owner;
    // it must not rotate the route-operation key and abort a safe stale-B
    // republication between its strict CAS and acceptance. Every mutable
    // identity byte remains below, while the hydration effect separately
    // observes groupCoordinatorRefreshVersion.
    return [
      ...locationKeyParts,
      "specialist-current-assignment-group",
      specialistGroupStepOneRuntimeResolution.status,
      runtime?.providerId ?? "invalid-runtime-provider",
      runtime?.providerRevision ?? "invalid-runtime-revision",
      runtime?.sourcePersonId ?? "invalid-source-person",
      runtime?.sourceLocationId ?? "invalid-source-location",
      runtime?.sourceUserLocationId ?? "invalid-source-assignment",
      runtime?.selectedLocationId ?? "invalid-selected-location",
      runtime?.selectedUserLocationId ?? "invalid-selected-assignment",
      runtime?.catalogVersion ?? "invalid-runtime-catalog",
      runtime?.catalogOwnerUserLocationId ?? "invalid-runtime-catalog-owner",
      runtime?.catalogFingerprint ?? "invalid-runtime-catalog-fingerprint",
      specialistGroupCurrentCatalogFingerprint ??
        "invalid-current-catalog-fingerprint",
      specialistOrganizerGroupCatalogFingerprint ??
        "invalid-organizer-catalog-fingerprint",
      specialistOrganizerGroupCatalogProviderId ??
        "invalid-organizer-catalog-provider",
      runtime?.returnHref ?? "invalid-runtime-return",
      runtime?.generatedAtEpochMs ?? "invalid-runtime-generated",
      runtime?.expiresAtEpochMs ?? "invalid-runtime-expires",
      runtime?.downstreamConsumer.consumerId ?? "invalid-downstream-consumer",
      runtime?.downstreamConsumer.consumerRevision ??
        "invalid-downstream-revision",
      runtime?.downstreamConsumer.status ?? "invalid-downstream-status",
      activeSpecialistGroupGuestServiceCategories
        ? JSON.stringify(activeSpecialistGroupGuestServiceCategories)
        : "invalid-guest-category-publication",
    ].join(":");
  }, [
    groupCoordinatorReturnHref,
    groupCoordinatorRefreshVersion,
    groupCoordinatorPointerIsPresent,
    groupCoordinatorOriginScope,
    groupCustomerOwnerState,
    isLocationEntryPoint,
    locationGroupPublicGateIsOpen,
    locationGroupServicesEntryAvailable,
    route.kind,
    routeGroupCatalogOwnerUserLocationId,
    routeLocationId,
    searchString,
    specialistGroupCurrentCatalogFingerprint,
    activeSpecialistGroupGuestServiceCategories,
    specialistOrganizerGroupCatalogFingerprint,
    specialistOrganizerGroupCatalogProviderId,
    specialistGroupStepOneRuntimeIdentity,
    specialistGroupStepOneRuntimeResolution.status,
    specialistLocationGatewayPreDraftGroupDescriptor,
    workplace.catalogVersion,
  ]);
  groupRouteOperationKeyRef.current = groupCoordinatorHandoffKey;

  useEffect(() => {
    groupProviderAbortRef.current?.abort();
    groupProviderAbortRef.current = null;
    groupProviderGenerationRef.current += 1;
    groupExactGraphAbortRef.current?.abort();
    groupExactGraphAbortRef.current = null;
    if (groupExactGraphRetryTimerRef.current !== null) {
      window.clearTimeout(groupExactGraphRetryTimerRef.current);
      groupExactGraphRetryTimerRef.current = null;
    }
    groupExactGraphGenerationRef.current += 1;
    groupExactGraphAttemptedOwnerRef.current = null;
    groupExactGraphRetryRef.current = {
      ownerKey: null,
      failedAttempts: 0,
    };
    groupMutationInFlightRef.current = false;
    setIsGroupMutationPending(false);

    return () => {
      groupProviderAbortRef.current?.abort();
      groupExactGraphAbortRef.current?.abort();
      if (groupExactGraphRetryTimerRef.current !== null) {
        window.clearTimeout(groupExactGraphRetryTimerRef.current);
        groupExactGraphRetryTimerRef.current = null;
      }
    };
  }, [groupCoordinatorHandoffKey]);
  const groupCoordinatorFailClosedState =
    useMemo<LocationGroupCoordinatorHydrationState>(
      () =>
        groupCoordinatorPointerIsPresent && groupCustomerOwnerState.hydrated
          ? { status: "hydrating" }
          : groupCoordinatorPointerIsPresent
            ? { status: "hydrating" }
            : { status: "absent" },
      [groupCoordinatorPointerIsPresent, groupCustomerOwnerState.hydrated],
    );
  const {
    isCurrentHandoff: isCurrentGroupCoordinatorHandoff,
    publishForHandoff: publishGroupCoordinatorForHandoff,
    state: groupCoordinatorRouteState,
  } = useKeyedHandoffHydrationState<LocationGroupCoordinatorHydrationState>(
    groupCoordinatorHandoffKey,
    groupCoordinatorFailClosedState,
  );

  useEffect(() => {
    if (
      !groupCoordinatorPointerIsPresent ||
      !groupCustomerOwnerState.hydrated
    ) {
      return;
    }
    const ownerKey = groupCoordinatorHandoffKey;

    if (
      !groupCoordinatorOriginScope ||
      !locationAppointmentCatalog ||
      !groupLocationCatalogVersion ||
      routeGroupCatalogOwnerUserLocationId === null
    ) {
      if (isCurrentGroupCoordinatorHandoff(ownerKey)) {
        publishGroupCoordinatorForHandoff(ownerKey, {
          status: "invalid",
          draftId: null,
        });
      }
      return;
    }

    const evaluatedAtEpochMs = readSpecialistServicesSafeEpochMs(
      servicesClockRef.current,
    );
    if (evaluatedAtEpochMs === null) {
      if (isCurrentGroupCoordinatorHandoff(ownerKey)) {
        publishGroupCoordinatorForHandoff(ownerKey, {
          status: "invalid",
          draftId: null,
        });
      }
      return;
    }

    const resolutionInput = {
      searchParams: new URLSearchParams(searchString),
      locationId: routeLocationId,
      catalogVersion: groupLocationCatalogVersion,
      catalogOwnerUserLocationId: routeGroupCatalogOwnerUserLocationId,
      returnHref: groupCoordinatorReturnHref,
      customerAccountId: groupCustomerOwnerState.customerAccountId,
      anonymousSessionOwnerId: groupCustomerOwnerState.anonymousSessionOwnerId,
      evaluatedAtEpochMs,
    };
    const resolution =
      groupCoordinatorOriginScope.kind === "location"
        ? resolveLocationGroupCoordinatorRouteState(resolutionInput)
        : groupCoordinatorOriginScope.kind === "specialist-current-assignment"
          ? resolveSpecialistCurrentAssignmentGroupCoordinatorRouteState({
              ...resolutionInput,
              runtime: groupCoordinatorOriginScope.runtime,
            })
          : resolveSpecialistLocationGatewayGroupCoordinatorRouteState({
              ...resolutionInput,
              runtime: groupCoordinatorOriginScope.runtime,
              groupAggregateActivationRevision:
                groupCoordinatorOriginScope.groupAggregateActivationRevision,
            });

    // Development UI pauses must not discard the roster just because its
    // short-lived provider publications expired. Re-resolve the exact saved
    // selections before publishing the normal route state; never revive an
    // accepted lineage or a different owner's source.
    if (
      process.env.NODE_ENV === "development" &&
      resolution.status === "invalid" && resolution.draftId &&
      groupCoordinatorOriginScope.kind === "location" && activeGroupBookingPolicy &&
      isCurrentGroupBookingFrontendServicesHost({
        host: frontendGroupServicesHost, catalog: locationAppointmentCatalog,
        locationId: routeLocationId, catalogOwnerUserLocationId: routeGroupCatalogOwnerUserLocationId,
        capabilityProvider: routeGroupCapabilityProvider, catalogLineProvider: groupCatalogLineProvider,
        evaluatedAtEpochMs,
      })
    ) {
      const stored = readGroupBookingCoordinatorAuthorityPersisted(resolution.draftId);
      const candidate = validateGroupBookingCoordinatorDraft(stored);
      const draft = candidate.valid ? candidate.draft : null;
      const ownerMatches = draft && (draft.customerAccountId === null
        ? draft.anonymousSessionOwnerId === groupCustomerOwnerState.anonymousSessionOwnerId
        : draft.customerAccountId === groupCustomerOwnerState.customerAccountId);
      if (draft && ownerMatches && draft.authorityState === "building" && draft.appointmentLineage === null &&
          draft.origin.kind === "location" && draft.origin.locationId === routeLocationId &&
          draft.locationId === routeLocationId && draft.catalogVersion === groupLocationCatalogVersion &&
          draft.catalogOwnerUserLocationId === routeGroupCatalogOwnerUserLocationId &&
          draft.returnHref === groupCoordinatorReturnHref && draft.expiresAtEpochMs > evaluatedAtEpochMs &&
          (draft.capability.expiresAtEpochMs <= evaluatedAtEpochMs || draft.participants.some(participant =>
            participant.serviceLines.some(line => line.quoteAuthority.expiresAtEpochMs <= evaluatedAtEpochMs)))) {
        const controller = new AbortController();
        const requestGeneration = ++groupHydrationGenerationRef.current;
        const operationIsCurrent = () => !controller.signal.aborted &&
          groupHydrationGenerationRef.current === requestGeneration && isCurrentGroupCoordinatorHandoff(ownerKey);
        publishGroupCoordinatorForHandoff(ownerKey, { status: "hydrating" });
        void (async () => {
          let expected = draft;
          // One retry may complete the roster CAS after our capability-only
          // commit. An unrelated concurrent edit is never adopted.
          for (let attempt = 0; attempt < 2 && operationIsCurrent(); attempt += 1) {
            const result = await refreshLocationGroupBookingRosterCatalog({
              expected, catalog: locationAppointmentCatalog,
              capabilityProvider: routeGroupCapabilityProvider, catalogLineProvider: groupCatalogLineProvider,
              downstreamMaximumParticipants: activeGroupBookingPolicy.maximumParticipants,
              signal: controller.signal, isCurrent: operationIsCurrent,
              isOwnerCurrent: () => getStoredCustomerAccountId() === groupCustomerOwnerState.customerAccountId &&
                readGroupBookingAnonymousSessionOwnerId() === groupCustomerOwnerState.anonymousSessionOwnerId &&
                isCurrentGroupBookingFrontendServicesHost({
                  host: frontendGroupServicesHost, catalog: locationAppointmentCatalog,
                  locationId: routeLocationId, catalogOwnerUserLocationId: routeGroupCatalogOwnerUserLocationId,
                  capabilityProvider: routeGroupCapabilityProvider, catalogLineProvider: groupCatalogLineProvider,
                  evaluatedAtEpochMs: readSpecialistServicesSafeEpochMs(servicesClockRef.current),
                }),
              now: () => readSpecialistServicesSafeEpochMs(servicesClockRef.current) ?? Number.NaN,
            });
            if (!operationIsCurrent()) return;
            if (result?.status === "refreshed") {
              const renewed = resolveLocationGroupCoordinatorRouteState({
                ...resolutionInput,
                evaluatedAtEpochMs: readSpecialistServicesSafeEpochMs(servicesClockRef.current) ?? Number.NaN,
              });
              if (renewed.status === "ready") {
                // Retain an in-progress guest/service edit across this exact
                // evidence refresh. Its selections still pass the ordinary
                // provider validation when the user explicitly continues.
                setGroupCatalogTransaction(transaction => {
                  if (!transaction || transaction.status !== "editing" ||
                      transaction.source.coordinatorDraftId !== draft.draftId ||
                      transaction.source.expectedCoordinatorRevision !== draft.revision ||
                      transaction.source.customerAccountId !== draft.customerAccountId ||
                      transaction.source.anonymousSessionOwnerId !== draft.anonymousSessionOwnerId) return transaction;
                  return transaction.operation === "add-guest"
                    ? createGroupBookingParticipantCatalogTransaction({ operation: "add-guest",
                        draft: renewed.coordinator, guestParticipantId: transaction.source.participantId })
                    : transaction.operation === "edit-participant"
                      ? createGroupBookingParticipantCatalogTransaction({ operation: "edit-participant",
                          draft: renewed.coordinator, participantId: transaction.source.participantId })
                      : transaction;
                });
                publishGroupCoordinatorForHandoff(ownerKey, renewed);
              } else {
                // The existing auth-binding lane owns any pending sign-in.
                setGroupCoordinatorRefreshVersion(current => current + 1);
              }
              return;
            }
            if (result?.status !== "retry") break;
            expected = result.draft;
          }
          if (operationIsCurrent()) publishGroupCoordinatorForHandoff(ownerKey, resolution);
        })().catch(() => {
          if (operationIsCurrent()) publishGroupCoordinatorForHandoff(ownerKey, resolution);
        });
        return () => controller.abort();
      }
    }

    if (resolution.status !== "auth-binding-required") {
      const currentAssignmentPublicationIsInvalid =
        groupCoordinatorOriginScope.kind === "specialist-current-assignment" &&
        resolution.status === "ready" &&
        (!specialistOrganizerGroupCatalogLineProvider ||
          validateCurrentSpecialistOrganizerGroupBookingCoordinatorCatalog({
            coordinator: resolution.coordinator,
            provider: specialistOrganizerGroupCatalogLineProvider,
            organizerCatalog: specialistOrganizerGroupCatalog,
            locationCatalog: locationAppointmentCatalog,
            evaluatedAtEpochMs,
          }).status === "invalid");
      const gatewayPublicationIsInvalid =
        groupCoordinatorOriginScope.kind === "specialist-location-gateway" &&
        resolution.status === "ready" &&
        (!specialistLocationGatewayOrganizerGroupCatalogLineProvider ||
          validateCurrentSpecialistLocationGatewayOrganizerGroupBookingCoordinatorCatalog(
            {
              runtime: groupCoordinatorOriginScope.runtime,
              coordinator: resolution.coordinator,
              provider:
                specialistLocationGatewayOrganizerGroupCatalogLineProvider,
              organizerCatalog: specialistOrganizerGroupCatalog,
              locationCatalog: locationAppointmentCatalog,
              evaluatedAtEpochMs,
            },
          ).status === "invalid");
      const publishedResolution =
        currentAssignmentPublicationIsInvalid || gatewayPublicationIsInvalid
          ? ({
              status: "invalid",
              draftId: resolution.coordinator.draftId,
            } as const)
          : resolution;
      if (isCurrentGroupCoordinatorHandoff(ownerKey)) {
        publishGroupCoordinatorForHandoff(ownerKey, publishedResolution);
      }
      return;
    }

    const anonymousSessionOwnerId =
      groupCustomerOwnerState.anonymousSessionOwnerId;
    const customerAccountId = groupCustomerOwnerState.customerAccountId;

    if (
      !anonymousSessionOwnerId ||
      !customerAccountId ||
      !activeGroupBookingPolicy
    ) {
      if (isCurrentGroupCoordinatorHandoff(ownerKey)) {
        publishGroupCoordinatorForHandoff(ownerKey, {
          status: "invalid",
          draftId: resolution.coordinator.draftId,
        });
      }
      return;
    }

    const controller = new AbortController();
    const requestGeneration = groupHydrationGenerationRef.current + 1;
    groupHydrationGenerationRef.current = requestGeneration;

    void (async () => {
      let bindingCoordinator = resolution.coordinator;
      if (
        groupCoordinatorOriginScope.kind === "specialist-current-assignment"
      ) {
        const fingerprint =
          createGroupBookingCoordinatorFingerprint(bindingCoordinator);
        if (!fingerprint || !specialistOrganizerGroupCatalogLineProvider) {
          if (isCurrentGroupCoordinatorHandoff(ownerKey)) {
            publishGroupCoordinatorForHandoff(ownerKey, {
              status: "invalid",
              draftId: bindingCoordinator.draftId,
            });
          }
          return;
        }
        const refresh =
          await refreshSpecialistOrganizerGroupBookingCatalogPersisted({
            owner: {
              draftId: bindingCoordinator.draftId,
              revision: bindingCoordinator.revision,
              fingerprint,
            },
            provider: specialistOrganizerGroupCatalogLineProvider,
            organizerCatalog: specialistOrganizerGroupCatalog,
            locationCatalog: locationAppointmentCatalog,
            requestId: createGroupBookingCoordinatorDraftId(),
            requestGeneration,
            signal: controller.signal,
            evaluatedAtEpochMs,
            now: () => evaluatedAtEpochMs,
            isOperationOwnerCurrent: () =>
              !controller.signal.aborted &&
              groupHydrationGenerationRef.current === requestGeneration &&
              isCurrentGroupCoordinatorHandoff(ownerKey) &&
              getStoredCustomerAccountId() === customerAccountId &&
              readGroupBookingAnonymousSessionOwnerId() ===
                anonymousSessionOwnerId,
          });
        if (refresh.status !== "refreshed") {
          if (
            !controller.signal.aborted &&
            groupHydrationGenerationRef.current === requestGeneration &&
            isCurrentGroupCoordinatorHandoff(ownerKey)
          ) {
            publishGroupCoordinatorForHandoff(ownerKey, {
              status: "invalid",
              draftId: bindingCoordinator.draftId,
            });
          }
          return;
        }
        bindingCoordinator = refresh.coordinator;
      } else if (
        groupCoordinatorOriginScope.kind === "specialist-location-gateway"
      ) {
        const fingerprint =
          createGroupBookingCoordinatorFingerprint(bindingCoordinator);
        if (
          !fingerprint ||
          !specialistLocationGatewayOrganizerGroupCatalogLineProvider
        ) {
          if (isCurrentGroupCoordinatorHandoff(ownerKey)) {
            publishGroupCoordinatorForHandoff(ownerKey, {
              status: "invalid",
              draftId: bindingCoordinator.draftId,
            });
          }
          return;
        }
        const refresh =
          await refreshSpecialistLocationGatewayOrganizerGroupBookingCatalogPersisted(
            {
              runtime: groupCoordinatorOriginScope.runtime,
              owner: {
                draftId: bindingCoordinator.draftId,
                revision: bindingCoordinator.revision,
                fingerprint,
              },
              provider:
                specialistLocationGatewayOrganizerGroupCatalogLineProvider,
              organizerCatalog: specialistOrganizerGroupCatalog,
              locationCatalog: locationAppointmentCatalog,
              requestId: createGroupBookingCoordinatorDraftId(),
              requestGeneration,
              signal: controller.signal,
              evaluatedAtEpochMs,
              now: () => evaluatedAtEpochMs,
              isOperationOwnerCurrent: () =>
                !controller.signal.aborted &&
                groupHydrationGenerationRef.current === requestGeneration &&
                isCurrentGroupCoordinatorHandoff(ownerKey) &&
                getStoredCustomerAccountId() === customerAccountId &&
                readGroupBookingAnonymousSessionOwnerId() ===
                  anonymousSessionOwnerId,
            },
          );
        if (refresh.status !== "refreshed") {
          if (
            !controller.signal.aborted &&
            groupHydrationGenerationRef.current === requestGeneration &&
            isCurrentGroupCoordinatorHandoff(ownerKey)
          ) {
            publishGroupCoordinatorForHandoff(ownerKey, {
              status: "invalid",
              draftId: bindingCoordinator.draftId,
            });
          }
          return;
        }
        bindingCoordinator = refresh.coordinator;
      }

      const currentCapability = await resolveGroupBookingPreliminaryCapability({
        provider: routeGroupCapabilityProvider,
        request: {
          schemaVersion: GROUP_BOOKING_PROVIDER_REQUEST_SCHEMA_VERSION,
          kind: "group-booking-preliminary-capability-request",
          requestId: createGroupBookingCoordinatorDraftId(),
          locationId: routeLocationId,
          catalogVersion: groupLocationCatalogVersion,
          catalogOwnerUserLocationId: routeGroupCatalogOwnerUserLocationId,
          downstreamMaximumGuests:
            activeGroupBookingPolicy.maximumParticipants - 1,
          downstreamMaximumParticipants:
            activeGroupBookingPolicy.maximumParticipants,
          requestGeneration,
          evaluatedAtEpochMs,
        },
        catalog: locationAppointmentCatalog,
        signal: controller.signal,
      });
      const liveCoordinator = readGroupBookingCoordinatorAuthorityPersisted(
        bindingCoordinator.draftId,
      );
      const ownerIsCurrent =
        !controller.signal.aborted &&
        groupHydrationGenerationRef.current === requestGeneration &&
        isCurrentGroupCoordinatorHandoff(ownerKey) &&
        getStoredCustomerAccountId() === customerAccountId &&
        readGroupBookingAnonymousSessionOwnerId() === anonymousSessionOwnerId &&
        Boolean(
          liveCoordinator &&
          haveExactSerializableStructure(liveCoordinator, bindingCoordinator),
        );
      const bindingEvaluatedAtEpochMs = evaluatedAtEpochMs;
      const coordinatorAuthorityIsCurrent =
        groupCoordinatorOriginScope.kind === "location"
          ? isLocationGroupRosterAuthorityCurrent({
              coordinator: bindingCoordinator,
              evaluatedAtEpochMs: bindingEvaluatedAtEpochMs,
            })
          : bindingCoordinator.authorityState === "building" &&
            bindingCoordinator.expiresAtEpochMs > bindingEvaluatedAtEpochMs &&
            isCurrentGroupBookingCoordinatorCapability(
              bindingCoordinator.capability,
              bindingEvaluatedAtEpochMs,
            ) &&
            bindingCoordinator.participants.every((participant) =>
              areGroupCatalogServiceLinesCurrent(
                participant.serviceLines,
                bindingEvaluatedAtEpochMs,
              ),
            ) &&
            isCurrentGroupCoordinatorRouteAuthority(
              bindingCoordinator,
              bindingEvaluatedAtEpochMs,
            );
      const runtimeOwnsCurrentCapability =
        currentCapability &&
        (groupCoordinatorOriginScope.kind === "location"
          ? true
          : groupCoordinatorOriginScope.kind === "specialist-current-assignment"
            ? specialistGroupStepOneRuntimeOwnsCapability({
                runtime: groupCoordinatorOriginScope.runtime,
                capability: currentCapability,
                requireCurrentCapability: true,
                requireCurrentRuntime: true,
                evaluatedAtEpochMs: bindingEvaluatedAtEpochMs,
              })
            : specialistLocationGatewayGroupStepOneRuntimeOwnsCapability({
                runtime: groupCoordinatorOriginScope.runtime,
                capability: currentCapability,
                requireCurrentCapability: true,
                requireCurrentRuntime: true,
                evaluatedAtEpochMs: bindingEvaluatedAtEpochMs,
              }));
      const commonBindingIsReady = Boolean(
        ownerIsCurrent &&
        coordinatorAuthorityIsCurrent &&
        currentCapability &&
        runtimeOwnsCurrentCapability,
      );
      const binding =
        commonBindingIsReady &&
        currentCapability &&
        groupCoordinatorOriginScope.kind === "specialist-location-gateway" &&
        specialistLocationGatewayGroupSessionOwner &&
        resolvedSpecialistLocationGatewayGroupNavigationLineage
          ? await bindSpecialistLocationGatewayGroupBookingCoordinatorAfterAuth(
              {
                runtime: groupCoordinatorOriginScope.runtime,
                anonymousIntent:
                  specialistLocationGatewayGroupSessionOwner.intent,
                anonymousLineage:
                  resolvedSpecialistLocationGatewayGroupNavigationLineage,
                expectedCoordinator: bindingCoordinator,
                anonymousSessionOwnerId,
                authenticatedCustomerAccountId: customerAccountId,
                authAttemptId: `slg-group-services-auth-${requestGeneration}`,
                currentCapability,
                organizerCatalog: specialistOrganizerGroupCatalog,
                locationCatalog: locationAppointmentCatalog,
                provider: specialistLocationGatewayGroupSessionOwner.provider,
                signal: controller.signal,
                now: specialistLocationGatewayGroupSessionOwner.now,
                isCurrentOwner: () =>
                  !controller.signal.aborted &&
                  specialistLocationGatewayGroupSessionOwner.isCurrentOwner() &&
                  groupHydrationGenerationRef.current === requestGeneration &&
                  isCurrentGroupCoordinatorHandoff(ownerKey) &&
                  getStoredCustomerAccountId() === customerAccountId &&
                  readGroupBookingAnonymousSessionOwnerId() ===
                    anonymousSessionOwnerId,
              },
            )
          : commonBindingIsReady &&
              currentCapability &&
              groupCoordinatorOriginScope.kind !== "specialist-location-gateway"
            ? bindGroupBookingCoordinatorAfterAuth({
                expectedCoordinator: bindingCoordinator,
                anonymousSessionOwnerId,
                customerAccountId,
                currentCapability,
                evaluatedAtEpochMs: bindingEvaluatedAtEpochMs,
              })
            : null;

      if (!ownerIsCurrent) return;
      const bindingReadBack =
        binding?.status === "bound"
          ? readGroupBookingCoordinatorAuthorityPersisted(
              binding.coordinator.draftId,
            )
          : null;
      const publicationOwnerIsCurrent = Boolean(
        !controller.signal.aborted &&
        groupHydrationGenerationRef.current === requestGeneration &&
        isCurrentGroupCoordinatorHandoff(ownerKey) &&
        getStoredCustomerAccountId() === customerAccountId &&
        readGroupBookingAnonymousSessionOwnerId() === anonymousSessionOwnerId &&
        binding?.status === "bound" &&
        bindingReadBack &&
        haveExactSerializableStructure(bindingReadBack, binding.coordinator),
      );
      if (!publicationOwnerIsCurrent) return;
      const bindingIsCurrent = (() => {
        if (binding?.status !== "bound") return false;
        if (
          binding.resumeTarget.kind ===
          "specialist-location-gateway-group-booking-coordinator"
        ) {
          return (
            "intentRecord" in binding &&
            groupCoordinatorOriginScope.kind ===
              "specialist-location-gateway" &&
            binding.resumeTarget.stage === "roster-reconfirmation" &&
            binding.resumeTarget.requiresProviderReresolution === true &&
            binding.resumeTarget.requiresExplicitContinue === true &&
            binding.resumeTarget.draftId === binding.coordinator.draftId &&
            binding.resumeTarget.gatewayHandoff.handoffId ===
              binding.intentRecord.draftId &&
            binding.resumeTarget.gatewayHandoff.generation ===
              binding.intentRecord.generation
          );
        }
        return isExpectedGroupAuthResumeTarget({
          target: binding.resumeTarget,
          coordinator: binding.coordinator,
          originScope: groupCoordinatorOriginScope,
        });
      })();
      if (
        binding?.status === "bound" &&
        bindingIsCurrent &&
        binding.resumeTarget.kind ===
          "specialist-location-gateway-group-booking-coordinator" &&
        onSpecialistLocationGatewayCommittedGroupRebind
      ) {
        try {
          onSpecialistLocationGatewayCommittedGroupRebind();
        } catch {
          publishGroupCoordinatorForHandoff(ownerKey, {
            status: "invalid",
            draftId: bindingCoordinator.draftId,
          });
        }
        return;
      }
      publishGroupCoordinatorForHandoff(
        ownerKey,
        binding?.status === "bound" && bindingIsCurrent
          ? { status: "ready", coordinator: binding.coordinator }
          : { status: "invalid", draftId: bindingCoordinator.draftId },
      );
    })();

    return () => controller.abort();
  }, [
    canonicalProfilePath,
    activeGroupBookingPolicy,
    frontendGroupServicesHost,
    groupCatalogLineProvider,
    routeGroupCapabilityProvider,
    groupCoordinatorHandoffKey,
    groupCoordinatorPointerIsPresent,
    groupCoordinatorReturnHref,
    groupCoordinatorOriginScope,
    groupCoordinatorRefreshVersion,
    groupCustomerOwnerState,
    groupLocationCatalogVersion,
    isCurrentGroupCoordinatorHandoff,
    isCurrentGroupCoordinatorRouteAuthority,
    locationAppointmentCatalog,
    onSpecialistLocationGatewayCommittedGroupRebind,
    publishGroupCoordinatorForHandoff,
    routeLocationId,
    routeGroupCatalogOwnerUserLocationId,
    searchString,
    specialistOrganizerGroupCatalog,
    specialistOrganizerGroupCatalogLineProvider,
    specialistLocationGatewayOrganizerGroupCatalogLineProvider,
    resolvedSpecialistLocationGatewayGroupNavigationLineage,
    specialistLocationGatewayGroupSessionOwner,
    workplace.catalogVersion,
  ]);
  const currentGroupCoordinator =
    groupCoordinatorRouteState.status === "ready" ||
    groupCoordinatorRouteState.status === "accepted-handoff"
      ? groupCoordinatorRouteState.coordinator
      : null;
  const acceptedGroupAppointmentDraftId =
    groupCoordinatorRouteState.status === "accepted-handoff"
      ? groupCoordinatorRouteState.appointmentDraftId
      : null;
  const acceptedGroupSelectSlotHref = acceptedGroupAppointmentDraftId
    ? buildGroupAppointmentBookingDraftHandoffHref(
        "/book/select-slot",
        acceptedGroupAppointmentDraftId,
      )
    : null;

  useEffect(() => {
    servicesCartOperationAbortRef.current?.abort();
    const generation = servicesCartOperationGenerationRef.current + 1;
    servicesCartOperationGenerationRef.current = generation;
    const session = specialistLocationGatewayServicesCartIsApplicable
      ? specialistLocationGatewaySession
      : null;
    const handoffKey = specialistLocationGatewayServicesCartHandoffKey;

    if (!session) {
      setSpecialistLocationGatewayServicesCartState({ status: "inactive" });
      return undefined;
    }

    const controller = new AbortController();
    servicesCartOperationAbortRef.current = controller;
    setSpecialistLocationGatewayServicesCartState({
      status: "hydrating",
      handoffKey,
    });
    const operationIsCurrent = () =>
      !controller.signal.aborted &&
      servicesCartOperationGenerationRef.current === generation &&
      isSpecialistLocationGatewaySessionCurrent(session);

    void (async () => {
      const evaluatedAtEpochMs =
        readSpecialistLocationGatewaySessionEpoch(session);
      if (
        evaluatedAtEpochMs === null ||
        !specialistLocationGatewayServicesSourceCartCatalog || !specialistLocationGatewayServicesCartCatalog
      ) {
        if (operationIsCurrent()) {
          setSpecialistLocationGatewayServicesCartState({
            status: "unavailable",
            handoffKey,
          });
        }
        return;
      }
      const resolved =
        await resolveSpecialistLocationGatewayServicesCartProvider({
          provider: specialistLocationGatewayServicesCartProvider,
          request: {
            intent: session.intent,
            resolution: session.resolution,
            publication: session.publication,
            sourceCatalog: specialistLocationGatewayServicesSourceCartCatalog,
            destinationCatalog: specialistLocationGatewayServicesCartCatalog,
            selections: [],
            previousCart: null,
            purpose: "hydrate",
            evaluatedAtEpochMs,
          },
          signal: controller.signal,
          isCurrentOwner: operationIsCurrent,
        });
      if (resolved.status !== "ready" || !operationIsCurrent()) {
        if (operationIsCurrent()) {
          setSpecialistLocationGatewayServicesCartState({
            status: "unavailable",
            handoffKey,
          });
        }
        return;
      }
      const superseded =
        retireSpecialistLocationGatewayConfiguredServicesCartsForIntentStrict({
          intentDraftId: session.intent.draftId,
          preserveDraftId: resolved.authority.draftId,
          nowEpochMs: evaluatedAtEpochMs,
          isCurrentOwner: operationIsCurrent,
        });
      if (
        (superseded.status !== "retired" && superseded.status !== "absent") ||
        !operationIsCurrent()
      ) {
        if (operationIsCurrent()) {
          setSpecialistLocationGatewayServicesCartState({
            status: "unavailable",
            handoffKey,
          });
        }
        return;
      }
      const stored = readSpecialistLocationGatewayConfiguredServicesCartStrict({
        draftId: resolved.authority.draftId,
        expectedOwnerKey: resolved.authority.ownerKey,
        expectedCustomerAccountId: resolved.authority.customerAccountId,
        expectedIntentDraftId: resolved.authority.intentDraftId,
        expectedResolutionId: resolved.authority.resolutionId,
        nowEpochMs: evaluatedAtEpochMs,
      });
      if (!operationIsCurrent()) return;
      if (stored.status === "ready") {
        const selections =
          projectSpecialistLocationGatewayServicesCartSelections(stored.cart);
        if (specialistLocationGatewayServicesCartIsContinuable(stored.cart)) {
          const current =
            await resolveSpecialistLocationGatewayServicesCartProvider({
              provider: specialistLocationGatewayServicesCartProvider,
              request: {
                intent: session.intent,
                resolution: session.resolution,
                publication: session.publication,
                sourceCatalog:
                  specialistLocationGatewayServicesSourceCartCatalog,
                destinationCatalog:
                  specialistLocationGatewayServicesCartCatalog,
                selections,
                previousCart: stored.cart,
                purpose: "continue",
                evaluatedAtEpochMs,
              },
              signal: controller.signal,
              isCurrentOwner: operationIsCurrent,
            });
          if (
            current.status !== "ready" ||
            !current.cart ||
            !haveExactSerializableStructure(current.cart, stored.cart) ||
            !operationIsCurrent()
          ) {
            if (operationIsCurrent()) {
              setSpecialistLocationGatewayServicesCartState({
                status: "unavailable",
                handoffKey,
              });
            }
            return;
          }
        } else if (
          !resolved.cart ||
          !haveExactSerializableStructure(resolved.cart, stored.cart)
        ) {
          setSpecialistLocationGatewayServicesCartState({
            status: "unavailable",
            handoffKey,
          });
          return;
        }
        setSpecialistLocationGatewayServicesCartState({
          status: "ready",
          handoffKey,
          authority: resolved.authority,
          cart: stored.cart,
          selections,
        });
        return;
      }
      if (stored.reason !== "missing") {
        setSpecialistLocationGatewayServicesCartState({
          status: "unavailable",
          handoffKey,
        });
        return;
      }
      if (!resolved.cart) {
        setSpecialistLocationGatewayServicesCartState({
          status: "empty",
          handoffKey,
          authority: resolved.authority,
        });
        return;
      }
      const published =
        publishSpecialistLocationGatewayConfiguredServicesCartStrict({
          cart: resolved.cart,
          nowEpochMs: evaluatedAtEpochMs,
          isCurrentOwner: operationIsCurrent,
        });
      if (published.status !== "published" || !operationIsCurrent()) {
        if (operationIsCurrent()) {
          setSpecialistLocationGatewayServicesCartState({
            status: "unavailable",
            handoffKey,
          });
        }
        return;
      }
      setSpecialistLocationGatewayServicesCartState({
        status: "ready",
        handoffKey,
        authority: resolved.authority,
        cart: published.cart,
        selections: projectSpecialistLocationGatewayServicesCartSelections(
          published.cart,
        ),
      });
    })().catch(() => {
      if (operationIsCurrent()) {
        setSpecialistLocationGatewayServicesCartState({
          status: "unavailable",
          handoffKey,
        });
      }
    });

    return () => {
      controller.abort();
      if (servicesCartOperationAbortRef.current === controller) {
        servicesCartOperationAbortRef.current = null;
      }
    };
  }, [
    specialistLocationGatewayServicesCartCatalog,
    specialistLocationGatewayServicesSourceCartCatalog,
    specialistLocationGatewayServicesCartHandoffKey,
    specialistLocationGatewayServicesCartIsApplicable,
    specialistLocationGatewayServicesCartProvider,
    specialistLocationGatewaySession,
  ]);

  useEffect(() => {
    if (currentGroupCoordinator) {
      setIsContinuing(false);
    }
  }, [currentGroupCoordinator]);

  const activeGroupCatalogTransaction = useMemo(() => {
    if (
      !groupCatalogTransaction ||
      groupCatalogTransaction.status !== "editing" ||
      !currentGroupCoordinator
    ) {
      return null;
    }
    const source = groupCatalogTransaction.source;

    return source.coordinatorDraftId === currentGroupCoordinator.draftId &&
      source.expectedCoordinatorRevision === currentGroupCoordinator.revision &&
      source.anonymousSessionOwnerId ===
        currentGroupCoordinator.anonymousSessionOwnerId &&
      source.customerAccountId === currentGroupCoordinator.customerAccountId &&
      source.locationId === currentGroupCoordinator.locationId &&
      source.catalogVersion === currentGroupCoordinator.catalogVersion &&
      source.catalogOwnerUserLocationId ===
        currentGroupCoordinator.catalogOwnerUserLocationId &&
      currentGroupCoordinator.participants.some(
        ({ participantId }) => participantId === source.participantId,
      ) ===
        (groupCatalogTransaction.operation === "edit-participant")
      ? groupCatalogTransaction
      : null;
  }, [currentGroupCoordinator, groupCatalogTransaction]);
  const routeSelectedServices = useMemo(
    () =>
      specialistLocationGatewayServicesCartIsApplicable
        ? currentSpecialistLocationGatewayServicesCartState.status === "ready"
          ? currentSpecialistLocationGatewayServicesCartState.selections
          : []
        : requestedServices,
    [
      currentSpecialistLocationGatewayServicesCartState,
      requestedServices,
      specialistLocationGatewayServicesCartIsApplicable,
    ],
  );
  // The gateway cart, not its temporary hydration placeholder, owns the URL
  // selection. Waiting here prevents ready -> hydrating remounts from erasing
  // and then restoring the same `services` query in a navigation loop.
  const hasCanonicalSelectionAuthority =
    !specialistLocationGatewayServicesCartIsApplicable ||
    currentSpecialistLocationGatewayServicesCartState.status === "empty" ||
    currentSpecialistLocationGatewayServicesCartState.status === "ready";
  const activeSelectedServices = activeGroupCatalogTransaction
    ? groupCatalogSelections
    : routeSelectedServices;
  groupCatalogTransactionOwnerRef.current =
    activeGroupCatalogTransaction?.ownerKey ?? null;

  useEffect(() => {
    if (!groupCatalogTransaction || activeGroupCatalogTransaction ||
        (process.env.NODE_ENV === "development" && frontendGroupServicesHost &&
          groupCoordinatorRouteState.status === "hydrating")) {
      return;
    }

    const frame = window.requestAnimationFrame(() => {
      setGroupCatalogTransaction(null);
      setGroupCatalogSelections([]);
      setGroupCatalogQuantities([]);
    });

    return () => window.cancelAnimationFrame(frame);
  }, [activeGroupCatalogTransaction, groupCatalogTransaction, frontendGroupServicesHost, groupCoordinatorRouteState.status]);
  const effectiveSelectedServiceIds = useMemo(
    () =>
      activeSelectedServices.flatMap((selection) => {
        const { serviceId } = selection;
        const service = serviceByServiceId.get(serviceId);
        const offering = offeringByServiceId.get(serviceId);

        if (!service || !offering) {
          return [];
        }

        if (service.kind === "bundle") {
          const plan = bundlePlanByServiceId.get(serviceId);
          const resolved = plan
            ? resolveSpecialistServiceBundleConfiguration(plan, selection)
            : null;

          return resolved?.canConfirm ? [serviceId] : [];
        }

        return resolveSpecialistServiceConfiguration(offering, selection)
          ? [serviceId]
          : [];
      }),
    [
      bundlePlanByServiceId,
      offeringByServiceId,
      activeSelectedServices,
      serviceByServiceId,
    ],
  );
  const effectiveSelectedServiceCount = effectiveSelectedServiceIds.length;
  const initialConfigurationTarget =
    useMemo<SpecialistServiceConfigurationTarget | null>(() => {
      const params = new URLSearchParams(searchString);

      if (
        catalogView !== "services" ||
        params.has("focus_service_id") ||
        params.has("focus_package_id")
      ) {
        return null;
      }

      const serviceId = getSingleFocusIdentifier(
        params,
        isLocationEntryPoint
          ? LOCATION_CONFIGURE_SERVICE_PARAM
          : SPECIALIST_CONFIGURE_SERVICE_PARAM,
      );
      const configureVariantParam = isLocationEntryPoint
        ? LOCATION_CONFIGURE_VARIANT_PARAM
        : SPECIALIST_CONFIGURE_VARIANT_PARAM;
      const variantValues = params.getAll(configureVariantParam);

      if (
        !serviceId ||
        variantValues.length > 1 ||
        requestedServiceIds.includes(serviceId)
      ) {
        return null;
      }

      const service = serviceByServiceId.get(serviceId);
      const offering = offeringByServiceId.get(serviceId);

      if (
        !service ||
        !offering ||
        (!isLocationEntryPoint &&
          !isSpecialistStepOneFeasible({
            branch: "service",
            owner: specialistStepOneCapabilityOwner,
            service,
          })) ||
        !isSpecialistServiceConfigurationAvailableAtWorkplace({
            locationResourceOnlyExecutionsV1,
          assignment,
          workplace,
          service,
          offering,
          bundlePlan: bundlePlanByServiceId.get(serviceId),
        })
      ) {
        return null;
      }

      if (variantValues.length === 0) {
        return { serviceId, variantId: null };
      }

      const variantId = getSingleFocusIdentifier(params, configureVariantParam);
      const variant =
        service.kind === "service"
          ? getSelectableSpecialistServiceVariant(offering, variantId)
          : null;

      return variant ? { serviceId, variantId: variant.variantId } : null;
    }, [
      assignment,
      bundlePlanByServiceId,
      catalogView,
      isLocationEntryPoint,
      offeringByServiceId,
      requestedServiceIds,
      searchString,
      serviceByServiceId,
      specialistStepOneCapabilityOwner,
      workplace,
    locationResourceOnlyExecutionsV1,
  ]);
  const initialFocusTarget =
    useMemo<SpecialistCatalogFocusTarget | null>(() => {
      const params = new URLSearchParams(searchString);
      const hasServiceFocus = params.has("focus_service_id");
      const hasPackageFocus = params.has("focus_package_id");
      const configureServiceParam = isLocationEntryPoint
        ? LOCATION_CONFIGURE_SERVICE_PARAM
        : SPECIALIST_CONFIGURE_SERVICE_PARAM;
      const configureVariantParam = isLocationEntryPoint
        ? LOCATION_CONFIGURE_VARIANT_PARAM
        : SPECIALIST_CONFIGURE_VARIANT_PARAM;

      // Two simultaneous intents are ambiguous and therefore normalize away.
      if (
        hasServiceFocus === hasPackageFocus ||
        params.has(configureServiceParam) ||
        params.has(configureVariantParam)
      ) {
        return null;
      }

      if (catalogView === "packages" && hasPackageFocus) {
        const packageId = getSingleFocusIdentifier(params, "focus_package_id");

        return packageId && requestedPackageIds.includes(packageId)
          ? { catalog: "packages", itemId: packageId }
          : null;
      }

      if (catalogView === "services" && hasServiceFocus) {
        const serviceId = getSingleFocusIdentifier(params, "focus_service_id");

        return serviceId && effectiveSelectedServiceIds.includes(serviceId)
          ? { catalog: "services", itemId: serviceId }
          : null;
      }

      return null;
    }, [
      catalogView,
      effectiveSelectedServiceIds,
      isLocationEntryPoint,
      requestedPackageIds,
      searchString,
    ]);
  const readyPackageSessionServicesEntry =
    packageSessionServicesEntryHydration.status === "ready"
      ? packageSessionServicesEntryHydration
      : null;
  const buildSelectionHref = useCallback(
    ({
      nextAppointmentMode = appointmentMode,
      nextCatalogView = catalogView,
      packageIds = requestedPackageIds,
      selections = routeSelectedServices,
      timeResumeDraftId = timeResumeIsCurrent &&
      timeResumeHydration.status === "restored"
        ? timeResumeHydration.sourceDraft.draftId
        : null,
    }: {
      nextAppointmentMode?: AppointmentDraftMode;
      nextCatalogView?: SpecialistCatalogView;
      packageIds?: readonly string[];
      selections?: readonly SpecialistServiceSelection[];
      timeResumeDraftId?: string | null;
    } = {}) => {
      if (
        packageSessionServicesEntryIsRequired &&
        !readyPackageSessionServicesEntry
      ) {
        return null;
      }
      if (membershipSessionServicesEntryIsRequired && (!readyMembershipSessionServicesEntry ||
        nextAppointmentMode !== "individual" || nextCatalogView !== "services" || packageIds.length > 0 || timeResumeDraftId)) return null;
      const serviceIds = selections.map(({ serviceId }) => serviceId);
      const nestedQueryState =
        serializeSpecialistServiceSelectionQueryState(selections);

      if (!nestedQueryState) {
        return null;
      }

      const serviceVariants = serializeBookingFlowServiceVariantSelections(
        selections.flatMap(({ serviceId, variantId }) =>
          variantId ? [{ serviceId, variantId }] : [],
        ),
        serviceIds,
      );

      const hrefOptions = {
        appointmentMode: nextAppointmentMode,
        catalog: nextCatalogView,
        initialServiceProfessionals: requestedInitialServiceProfessionals,
        initialProfessionalId:
          nextCatalogView === "services"
            ? requestedInitialProfessionalId
            : null,
        packageIds,
        serviceAddOns: nestedQueryState.serviceAddOns,
        serviceBundleAddOns: nestedQueryState.serviceBundleAddOns,
        serviceIds,
        serviceVariants,
      } as const;
      const href = isLocationEntryPoint
        ? buildLocationServicesHref(routeLocationId, hrefOptions)
        : buildSpecialistServicesHref(identity, hrefOptions);

      if (!href) {
        return null;
      }

      const ownedHref = specialistLocationGatewaySession
        ? appendSpecialistLocationGatewayHandoff({
            href,
            handoffId: specialistLocationGatewaySession.resolution.draftId,
            selectedAssignmentHint:
              specialistLocationGatewaySession.resolution.selection
                .selectedUserLocationId,
            resultsAppointmentIntentHandoffId:
              specialistResultsAppointmentIntentPointer,
          })
        : href;

      if (!ownedHref) return null;

      const canonicalOwnedHref = (() => {
        if (
          !specialistLocationGatewaySession ||
          nextCatalogView !== "services"
        ) {
          return ownedHref;
        }
        try {
          const origin = "https://widget.volera.invalid";
          const url = new URL(ownedHref, origin);
          if (url.origin !== origin || url.hash) return null;
          url.searchParams.set("appointment_mode", nextAppointmentMode);
          url.searchParams.set("catalog", "services");
          return `${url.pathname}${url.search}`;
        } catch {
          return null;
        }
      })();

      if (!canonicalOwnedHref) return null;

      const resultsOwnedHref = locationResultsAppointmentIntentPointer
        ? appendResultsAppointmentIntentHandoffToHref({
            href: canonicalOwnedHref,
            handoffId: locationResultsAppointmentIntentPointer,
          })
        : canonicalOwnedHref;

      if (!resultsOwnedHref) return null;

      const packageSessionOwnedHref = readyPackageSessionServicesEntry
        ? appendPackageSessionBookingHandoffToHref({
            href: resultsOwnedHref,
            handoffId: readyPackageSessionServicesEntry.intent.draftId,
          })
        : resultsOwnedHref;

      if (!packageSessionOwnedHref) return null;

      const membershipOwnedHref = readyMembershipSessionServicesEntry
        ? appendMembershipSessionBookingHandoff(packageSessionOwnedHref, readyMembershipSessionServicesEntry.intent.draftId)
        : packageSessionOwnedHref;
      if (!membershipOwnedHref) return null;

      const resumedHref = timeResumeDraftId
        ? appendAppointmentTimeServicesResumeHandoff(
            membershipOwnedHref,
            timeResumeDraftId,
          )
        : membershipOwnedHref;
      if (!resumedHref) return null;
      const rewardOwnedHref = projectCustomerRewardServicesHref(resumedHref, currentServicesHref);
      return rewardOwnedHref ? projectPackageGiftCatalogHref(
        rewardOwnedHref,
        currentServicesHref,
      ) : null;
    },
    [
      appointmentMode,
      catalogView,
      currentServicesHref,
      identity,
      isLocationEntryPoint,
      locationResultsAppointmentIntentPointer,
      packageSessionServicesEntryIsRequired,
      requestedPackageIds,
      requestedInitialProfessionalId,
      requestedInitialServiceProfessionals,
      readyPackageSessionServicesEntry,
      readyMembershipSessionServicesEntry,
      membershipSessionServicesEntryIsRequired,
      routeSelectedServices,
      routeLocationId,
      specialistLocationGatewaySession,
      specialistResultsAppointmentIntentPointer,
      timeResumeHydration,
      timeResumeIsCurrent,
    ],
  );
  const canonicalGroupCoordinatorHref = useMemo(() => {
    if (!currentGroupCoordinator) return null;

    return appendGroupBookingCoordinatorHandoff(
      canonicalServicesPath,
      currentGroupCoordinator.draftId,
    );
  }, [canonicalServicesPath, currentGroupCoordinator]);
  const canonicalServicesHref = useMemo(() => {
    if (groupCoordinatorPointerIsPresent) {
      return canonicalGroupCoordinatorHref ?? currentServicesHref;
    }

    // A route selection is one atomic request. Keep unresolved IDs in the URL
    // so canonicalization cannot silently narrow a multi-Service request to
    // the subset that happened not to require configuration. The customer can
    // still configure or remove the unresolved row through the catalog.
    if (hasIncompleteDirectSpecialistServiceRequest) {
      return currentServicesHref;
    }

    return buildSelectionHref() ?? currentServicesHref;
  }, [
    buildSelectionHref,
    canonicalGroupCoordinatorHref,
    currentServicesHref,
    groupCoordinatorPointerIsPresent,
    hasIncompleteDirectSpecialistServiceRequest,
  ]);
  const replaceServicesHref = useCallback(
    (nextHref: string) => {
      try {
        if (!isCustomerRewardServicesProjectionCurrent(currentServicesHref,
          `${window.location.pathname}${window.location.search}${window.location.hash}`)) return false;
        if (membershipSessionServicesEntryIsRequired) {
          const nowEpochMs = readSpecialistServicesSafeEpochMs(servicesClockRef.current);
          if (!hasCanonicalSelectionAuthority || !readyMembershipSessionServicesEntry || nowEpochMs === null ||
            !canReplaceMembershipServicesProjectionInPlace({
              currentHref: window.location.href,
              nextHref,
              origin: window.location.origin,
              expectedEntry: readyMembershipSessionServicesEntry,
              locationCatalog: locationAppointmentCatalog,
              nowEpochMs,
            })) return false;
          // Public History API keeps the local selection synchronous without an
          // RSC navigation. Do not pass Next's internal __NA state: its wrapper
          // then skips publishing the new URL to useSearchParams consumers.
          window.history.replaceState(null, "", nextHref);
          return true;
        }
        if (
          specialistLocationGatewayServicesCartIsApplicable &&
          hasCanonicalSelectionAuthority &&
          specialistLocationGatewaySession &&
          isSpecialistLocationGatewaySessionCurrent(
            specialistLocationGatewaySession,
          ) &&
          canReplaceSpecialistLocationGatewayServicesProjectionInPlace({
            canonicalServicesPath,
            currentHref: currentServicesHref,
            nextHref,
          })
        ) {
          window.history.replaceState(window.history.state, "", nextHref);
          return true;
        }

        router.replace(nextHref, { scroll: false });
        return true;
      } catch {
        return false;
      }
    },
    [
      canonicalServicesPath,
      currentServicesHref,
      hasCanonicalSelectionAuthority,
      locationAppointmentCatalog,
      membershipSessionServicesEntryIsRequired,
      readyMembershipSessionServicesEntry,
      router,
      specialistLocationGatewayServicesCartIsApplicable,
      specialistLocationGatewaySession,
    ],
  );

  useEffect(() => {
    if (
      !timeResumeRequestKey ||
      timeResumeDiscardingRef.current ||
      !hasCanonicalSelectionAuthority ||
      !shouldCanonicalizeAppointmentTimeResumeStatus(
        timeResumeHydration.status,
      ) ||
      isTimeResumeCanonicalizationBlocked ||
      canonicalServicesHref === currentServicesHref
    ) {
      return;
    }

    const attempt = attemptTimeResumeCanonicalization({
      handoffKey: timeResumeHandoffKey,
      retryState: timeResumeCanonicalizationRetry,
      replace: () => {
        if (!replaceServicesHref(canonicalServicesHref)) {
          throw new Error("Unable to replace Services URL.");
        }
      },
    });

    if (attempt.status === "succeeded") {
      return;
    }

    // A transient synchronous history failure receives a bounded exponential
    // retry. The terminal failure is also published through a one-shot timer
    // so the effect never enters a synchronous render loop.
    const retryTimer = window.setTimeout(
      () => setTimeResumeCanonicalizationRetry(attempt.state),
      attempt.status === "retry" ? attempt.retryDelayMs : 0,
    );

    return () => window.clearTimeout(retryTimer);
  }, [
    canonicalServicesHref,
    currentServicesHref,
    hasCanonicalSelectionAuthority,
    isTimeResumeCanonicalizationBlocked,
    replaceServicesHref,
    timeResumeCanonicalizationRetry,
    timeResumeHandoffKey,
    timeResumeHydration.status,
    timeResumeRequestKey,
  ]);
  const analyticsContext = useMemo(
    () => person ? ({
      locationId: assignment.locationId,
      specialistId: person.personId,
      userLocationId: assignment.userLocationId,
    }) : null,
    [assignment.locationId, assignment.userLocationId, person],
  );

  useEffect(() => {
    if (
      isLocationEntryPoint ||
      !analyticsContext ||
      pathname !== canonicalServicesPath ||
      viewedServicesPathRef.current === canonicalServicesPath
    ) {
      return;
    }

    viewedServicesPathRef.current = canonicalServicesPath;
    const currentHref = `${pathname}${searchString ? `?${searchString}` : ""}`;
    emitSpecialistAnalyticsEvent({
      ...analyticsContext,
      name: "specialist_services_page_viewed",
      source: claimSpecialistServicesPageAnalyticsSource(currentHref),
    });
  }, [
    analyticsContext,
    canonicalServicesPath,
    isLocationEntryPoint,
    pathname,
    searchString,
  ]);

  useEffect(() => {
    const currentHref = `${pathname}${searchString ? `?${searchString}` : ""}`;

    if (
      shouldCanonicalizeSpecialistServicesHref({
        canonicalServicesHref,
        canonicalServicesPath,
        currentHref,
        hasCanonicalSelectionAuthority,
        hasInitialConfigurationTarget: Boolean(initialConfigurationTarget),
        hasInitialFocusTarget: Boolean(initialFocusTarget),
        hasTimeResumeRequest: timeResumeRequestKey !== null,
        isGroupResumeHydrating: groupCoordinatorPointerIsPresent,
        pathname,
      })
    ) {
      replaceServicesHref(canonicalServicesHref);
    }
  }, [
    canonicalServicesHref,
    canonicalServicesPath,
    hasCanonicalSelectionAuthority,
    initialConfigurationTarget,
    initialFocusTarget,
    groupCoordinatorPointerIsPresent,
    pathname,
    replaceServicesHref,
    searchString,
    timeResumeRequestKey,
  ]);

  const handleInitialFocusHandled = useCallback(() => {
    replaceServicesHref(canonicalServicesHref);
  }, [canonicalServicesHref, replaceServicesHref]);

  const handleInitialConfigurationHandled = useCallback(
    (outcome: SpecialistInitialConfigurationOutcome) => {
      // A direct commit synchronously publishes the complete canonical
      // selection. A second empty-cart replace here would race and erase it.
      if (outcome !== "committed") {
        replaceServicesHref(canonicalServicesHref);
      }
    },
    [canonicalServicesHref, replaceServicesHref],
  );

  useEffect(() => {
    const resetPendingBookingNavigation = () => {
      continueGuardRef.current = false;
      latestPackageDraftRef.current = null;
      setIsContinuing(false);
    };

    window.addEventListener("popstate", resetPendingBookingNavigation);
    window.addEventListener("pageshow", resetPendingBookingNavigation);

    return () => {
      window.removeEventListener("popstate", resetPendingBookingNavigation);
      window.removeEventListener("pageshow", resetPendingBookingNavigation);
    };
  }, []);

  const discardSchedulingResumeHandoffsBeforeEdit = useCallback(async () => {
    if (!timeResumeRequestKey) {
      return true;
    }

    if (servicesEditCleanupPromiseRef.current) {
      return false;
    }

    if (!timeResumeIsCurrent || timeResumeHydration.status !== "restored") {
      toast.error(t("serviceAddOnsModal.submissionError"));
      return false;
    }

    const ownerHandoffKey = timeResumeHandoffKey;
    const timeSourceDraftId =
      timeResumeHydration.status === "restored"
        ? timeResumeHydration.sourceDraft.draftId
        : null;
    const cleanupPromise = (async () => {
      if (timeSourceDraftId) {
        const timeCleaned = await cleanupSchedulingDescendantsBeforeEdit({
          appointmentDraftId: timeSourceDraftId,
          preserveStaffChoiceEvidence: false,
          preserveTimeProfessionalWorkingDraft: false,
        });

        if (!timeCleaned) {
          return false;
        }
      }

      return true;
    })();
    servicesEditCleanupPendingRef.current = true;
    servicesEditCleanupPromiseRef.current = cleanupPromise;
    let cleaned = false;

    try {
      cleaned = (await cleanupPromise) === true;
    } catch {
      cleaned = false;
    } finally {
      servicesEditCleanupPromiseRef.current = null;
      servicesEditCleanupPendingRef.current = false;
    }

    if (
      !cleaned ||
      !isCurrentTimeResumeHandoff(ownerHandoffKey) ||
      timeResumeHydration.status !== "restored" ||
      timeResumeHydration.sourceDraft.draftId !== timeSourceDraftId
    ) {
      toast.error(t("serviceAddOnsModal.submissionError"));
      return false;
    }

    return true;
  }, [
    cleanupSchedulingDescendantsBeforeEdit,
    isCurrentTimeResumeHandoff,
    t,
    timeResumeHandoffKey,
    timeResumeHydration,
    timeResumeIsCurrent,
    timeResumeRequestKey,
  ]);

  const replaceAfterServicesEdit = useCallback(
    (nextHref: string) => {
      const previousDiscarding = timeResumeDiscardingRef.current;

      if (timeResumeRequestKey) {
        // Block stale-pointer canonicalization while Next publishes the exact
        // pointer-free edited route. A thrown history write restores the
        // retryable pre-publication state; descendant cleanup is idempotent.
        timeResumeDiscardingRef.current = true;
      }
      try {
        if (replaceServicesHref(nextHref)) return true;
        throw new Error("Unable to replace Services URL.");
      } catch {
        timeResumeDiscardingRef.current = previousDiscarding;
        toast.error(t("serviceAddOnsModal.submissionError"));
        return false;
      }
    },
    [replaceServicesHref, t, timeResumeRequestKey],
  );

  const replaceSpecialistLocationGatewayServicesCartSelections = useCallback(
    async (selections: readonly SpecialistServiceSelection[]) => {
      const session = specialistLocationGatewayServicesCartIsApplicable
        ? specialistLocationGatewaySession
        : null;
      const state = currentSpecialistLocationGatewayServicesCartState;
      if (!session || !specialistLocationGatewayServicesCartCatalog) return null;
      if (state.status !== "empty" && state.status !== "ready") return false;

      servicesCartOperationAbortRef.current?.abort();
      const generation = servicesCartOperationGenerationRef.current + 1;
      servicesCartOperationGenerationRef.current = generation;
      const controller = new AbortController();
      servicesCartOperationAbortRef.current = controller;
      const operationIsCurrent = () =>
        !controller.signal.aborted &&
        servicesCartOperationGenerationRef.current === generation &&
        isSpecialistLocationGatewaySessionCurrent(session);
      const evaluatedAtEpochMs =
        readSpecialistLocationGatewaySessionEpoch(session);
      if (
        evaluatedAtEpochMs === null ||
        !specialistLocationGatewayServicesSourceCartCatalog || !specialistLocationGatewayServicesCartCatalog ||
        !operationIsCurrent()
      ) {
        return null;
      }

      if (selections.length === 0) {
        if (state.status === "empty") return true;
        if (state.cart.scope.kind !== "unscoped") return false;
        const removed =
          removeSpecialistLocationGatewayConfiguredServicesCartStrict({
            expected: state.cart,
            nowEpochMs: evaluatedAtEpochMs,
            isCurrentOwner: operationIsCurrent,
          });
        if (
          (removed.status !== "removed" && removed.status !== "absent") ||
          !operationIsCurrent()
        ) {
          if (operationIsCurrent()) {
            setSpecialistLocationGatewayServicesCartState({
              status: "unavailable",
              handoffKey: specialistLocationGatewayServicesCartHandoffKey,
            });
          }
          return false;
        }
        setSpecialistLocationGatewayServicesCartState({
          status: "empty",
          handoffKey: specialistLocationGatewayServicesCartHandoffKey,
          authority: state.authority,
        });
        return true;
      }

      const previousCart = state.status === "ready" ? state.cart : null;
      const resolved =
        await resolveSpecialistLocationGatewayServicesCartProvider({
          provider: specialistLocationGatewayServicesCartProvider,
          request: {
            intent: session.intent,
            resolution: session.resolution,
            publication: session.publication,
            sourceCatalog: specialistLocationGatewayServicesSourceCartCatalog,
            destinationCatalog: specialistLocationGatewayServicesCartCatalog,
            selections,
            previousCart,
            purpose: "replace",
            evaluatedAtEpochMs,
          },
          signal: controller.signal,
          isCurrentOwner: operationIsCurrent,
        });
      if (
        resolved.status !== "ready" ||
        !resolved.cart ||
        !operationIsCurrent()
      ) {
        if (operationIsCurrent() && resolved.status !== "ready") {
          setSpecialistLocationGatewayServicesCartState({
            status: "unavailable",
            handoffKey: specialistLocationGatewayServicesCartHandoffKey,
          });
        }
        return null;
      }
      if (
        resolved.authority.ownerKey !== state.authority.ownerKey ||
        resolved.cart.ownerKey !== state.authority.ownerKey
      ) {
        setSpecialistLocationGatewayServicesCartState({
          status: "unavailable",
          handoffKey: specialistLocationGatewayServicesCartHandoffKey,
        });
        return null;
      }
      const persisted = previousCart
        ? replaceSpecialistLocationGatewayConfiguredServicesCartStrict({
            expected: previousCart,
            next: resolved.cart,
            nowEpochMs: evaluatedAtEpochMs,
            isCurrentOwner: operationIsCurrent,
          })
        : publishSpecialistLocationGatewayConfiguredServicesCartStrict({
            cart: resolved.cart,
            nowEpochMs: evaluatedAtEpochMs,
            isCurrentOwner: operationIsCurrent,
          });
      if (
        (persisted.status !== "published" && persisted.status !== "replaced") ||
        !operationIsCurrent()
      ) {
        if (operationIsCurrent()) {
          setSpecialistLocationGatewayServicesCartState({
            status: "unavailable",
            handoffKey: specialistLocationGatewayServicesCartHandoffKey,
          });
        }
        return false;
      }
      const readBack =
        readSpecialistLocationGatewayConfiguredServicesCartStrict({
          draftId: persisted.cart.draftId,
          expectedOwnerKey: persisted.cart.ownerKey,
          expectedCustomerAccountId: persisted.cart.customerAccountId,
          expectedIntentDraftId: persisted.cart.intentDraftId,
          expectedResolutionId: persisted.cart.resolutionId,
          nowEpochMs: evaluatedAtEpochMs,
        });
      if (
        readBack.status !== "ready" ||
        !haveExactSerializableStructure(readBack.cart, persisted.cart) ||
        !operationIsCurrent()
      ) {
        if (operationIsCurrent()) {
          setSpecialistLocationGatewayServicesCartState({
            status: "unavailable",
            handoffKey: specialistLocationGatewayServicesCartHandoffKey,
          });
        }
        return false;
      }
      setSpecialistLocationGatewayServicesCartState({
        status: "ready",
        handoffKey: specialistLocationGatewayServicesCartHandoffKey,
        authority: resolved.authority,
        cart: readBack.cart,
        selections: projectSpecialistLocationGatewayServicesCartSelections(
          readBack.cart,
        ),
      });
      return true;
    },
    [
      currentSpecialistLocationGatewayServicesCartState,
      specialistLocationGatewayServicesCartCatalog,
      specialistLocationGatewayServicesCartHandoffKey,
      specialistLocationGatewayServicesCartIsApplicable,
      specialistLocationGatewayServicesCartProvider,
      specialistLocationGatewayServicesSourceCartCatalog,
      specialistLocationGatewaySession,
    ],
  );

  const revalidateSpecialistLocationGatewayServicesCartForContinue =
    useCallback(
      async (selections: readonly SpecialistServiceSelection[]) => {
        const session = specialistLocationGatewayServicesCartIsApplicable
          ? specialistLocationGatewaySession
          : null;
        const state = currentSpecialistLocationGatewayServicesCartState;
        if (!session || !specialistLocationGatewayServicesCartCatalog) return null;
        if (
          state.status !== "ready" ||
          !specialistLocationGatewayServicesSourceCartCatalog || !specialistLocationGatewayServicesCartCatalog ||
          !specialistLocationGatewayServicesCartIsContinuable(state.cart) ||
          !haveExactSerializableStructure(state.selections, selections)
        ) {
          return null;
        }
        servicesCartOperationAbortRef.current?.abort();
        const generation = servicesCartOperationGenerationRef.current + 1;
        servicesCartOperationGenerationRef.current = generation;
        const controller = new AbortController();
        servicesCartOperationAbortRef.current = controller;
        const operationIsCurrent = () =>
          !controller.signal.aborted &&
          servicesCartOperationGenerationRef.current === generation &&
          isSpecialistLocationGatewaySessionCurrent(session);
        const refreshed =
          await refreshSpecialistLocationGatewayServicesSession(session);
        const evaluatedAtEpochMs =
          readSpecialistLocationGatewaySessionEpoch(session);
        if (
          !refreshed ||
          evaluatedAtEpochMs === null ||
          !operationIsCurrent()
        ) {
          return null;
        }
        const resolved =
          await resolveSpecialistLocationGatewayServicesCartProvider({
            provider: specialistLocationGatewayServicesCartProvider,
            request: {
              intent: refreshed.intent,
              resolution: refreshed.resolution,
              publication: refreshed.publication,
              sourceCatalog: specialistLocationGatewayServicesSourceCartCatalog,
              destinationCatalog: specialistLocationGatewayServicesCartCatalog,
              selections,
              previousCart: state.cart,
              purpose: "continue",
              evaluatedAtEpochMs,
            },
            signal: controller.signal,
            isCurrentOwner: operationIsCurrent,
          });
        if (
          resolved.status !== "ready" ||
          !resolved.cart ||
          !haveExactSerializableStructure(resolved.cart, state.cart) ||
          !operationIsCurrent()
        ) {
          return null;
        }
        const readBack =
          readSpecialistLocationGatewayConfiguredServicesCartStrict({
            draftId: state.cart.draftId,
            expectedOwnerKey: state.cart.ownerKey,
            expectedCustomerAccountId: state.cart.customerAccountId,
            expectedIntentDraftId: state.cart.intentDraftId,
            expectedResolutionId: state.cart.resolutionId,
            nowEpochMs: evaluatedAtEpochMs,
          });
        if (
          readBack.status !== "ready" ||
          !haveExactSerializableStructure(readBack.cart, state.cart) ||
          !operationIsCurrent()
        ) {
          return null;
        }
        const leaseIsCurrent = () => {
          if (!operationIsCurrent()) return false;
          const currentEpochMs =
            readSpecialistLocationGatewaySessionEpoch(session);
          if (currentEpochMs === null) return false;
          const current =
            readSpecialistLocationGatewayConfiguredServicesCartStrict({
              draftId: state.cart.draftId,
              expectedOwnerKey: state.cart.ownerKey,
              expectedCustomerAccountId: state.cart.customerAccountId,
              expectedIntentDraftId: state.cart.intentDraftId,
              expectedResolutionId: state.cart.resolutionId,
              nowEpochMs: currentEpochMs,
            });
          return Boolean(
            current.status === "ready" &&
            haveExactSerializableStructure(current.cart, state.cart) &&
            operationIsCurrent(),
          );
        };
        return leaseIsCurrent()
          ? ({
              cart: state.cart,
              handoffKey: specialistLocationGatewayServicesCartHandoffKey,
              isCurrentOwner: leaseIsCurrent,
            } satisfies SpecialistLocationGatewayServicesCartCommitLease)
          : null;
      },
      [
        currentSpecialistLocationGatewayServicesCartState,
        specialistLocationGatewayServicesCartCatalog,
        specialistLocationGatewayServicesCartHandoffKey,
        specialistLocationGatewayServicesCartIsApplicable,
        specialistLocationGatewayServicesCartProvider,
        specialistLocationGatewayServicesSourceCartCatalog,
        specialistLocationGatewaySession,
      ],
    );

  const handleSelectedServicesChange = useCallback(
    async (selections: SpecialistServiceSelection[]) => {
      if (packageSessionServicesEntryIsRequired && packageSelectionPublicationRef.current) return false;
      // Membership history may already contain a newer selection while React
      // is publishing it. Even a captured apparent no-op must validate and
      // commit against that actual URL (including removal back to empty).
      if (!membershipSessionServicesEntryIsRequired && haveExactSerializableStructure(selections, activeSelectedServices)) {
        return true;
      }

      if (activeGroupCatalogTransaction) {
        const quantityByServiceId = new Map(
          activeSelectedServices.map((selection, index) => [
            selection.serviceId,
            groupCatalogQuantities[index] ?? 1,
          ]),
        );
        setGroupCatalogSelections(cloneServiceSelections(selections));
        setGroupCatalogQuantities(
          selections.map(
            ({ serviceId }) => quantityByServiceId.get(serviceId) ?? 1,
          ),
        );
        return true;
      }

      const nextHref = buildSelectionHref({
        selections,
        timeResumeDraftId: null,
      });

      if (!nextHref) {
        toast.error(t("serviceAddOnsModal.submissionError"));
        return false;
      }

      const packagePending = packageSessionServicesEntryIsRequired && readyPackageSessionServicesEntry
        ? { href: nextHref, handoffId: readyPackageSessionServicesEntry.intent.draftId } : null;
      if (packageSessionServicesEntryIsRequired && !packagePending) return false;
      if (packagePending) {
        // Router replacement acknowledges dispatch, not publication. Keep
        // Continue locked until the exact new selection reaches this owner.
        packageSelectionPublicationRef.current = packagePending;
        setPackageSelectionPublication(packagePending);
      }
      let published = false;
      try {
        if (!(await discardSchedulingResumeHandoffsBeforeEdit())) return false;
        if (specialistLocationGatewayServicesCartIsApplicable &&
          !(await replaceSpecialistLocationGatewayServicesCartSelections(selections))) {
          toast.error(t("serviceAddOnsModal.submissionError"));
          return false;
        }
        published = replaceAfterServicesEdit(nextHref);
        return published;
      } finally {
        if (!published && packagePending && packageSelectionPublicationRef.current === packagePending) {
          packageSelectionPublicationRef.current = null;
          setPackageSelectionPublication(null);
        }
      }
    },
    [
      activeGroupCatalogTransaction,
      activeSelectedServices,
      buildSelectionHref,
      discardSchedulingResumeHandoffsBeforeEdit,
      replaceAfterServicesEdit,
      groupCatalogQuantities,
      membershipSessionServicesEntryIsRequired,
      packageSessionServicesEntryIsRequired,
      readyPackageSessionServicesEntry,
      replaceSpecialistLocationGatewayServicesCartSelections,
      specialistLocationGatewayServicesCartIsApplicable,
      t,
    ],
  );

  const beginGroupProviderOperation = useCallback(() => {
    if (groupMutationInFlightRef.current) return null;
    groupExactGraphAbortRef.current?.abort();
    groupExactGraphAbortRef.current = null;
    groupExactGraphGenerationRef.current += 1;
    groupExactGraphAttemptedOwnerRef.current = null;
    if (groupExactGraphRetryTimerRef.current !== null) {
      window.clearTimeout(groupExactGraphRetryTimerRef.current);
      groupExactGraphRetryTimerRef.current = null;
    }
    groupProviderAbortRef.current?.abort();
    const controller = new AbortController();
    const generation = groupProviderGenerationRef.current + 1;
    groupProviderGenerationRef.current = generation;
    groupProviderAbortRef.current = controller;
    groupMutationInFlightRef.current = true;
    setIsGroupMutationPending(true);

    return {
      controller,
      generation,
      routeOperationKey: groupRouteOperationKeyRef.current,
    };
  }, []);

  const releaseGroupProviderOperation = useCallback(
    (operation: { controller: AbortController; generation: number }) => {
      if (
        groupProviderAbortRef.current !== operation.controller ||
        groupProviderGenerationRef.current !== operation.generation
      ) {
        return;
      }

      groupProviderAbortRef.current = null;
      groupMutationInFlightRef.current = false;
      setIsGroupMutationPending(false);
    },
    [],
  );

  const continueInitialGroupOrganizerCatalog = useCallback(
    async (
      selections: readonly SpecialistServiceSelection[],
      servicesCartCommitLease: SpecialistLocationGatewayServicesCartCommitLease | null,
    ) => {
      if (postCommitNavigationRecovery) {
        await retryPostCommitNavigation();
        return;
      }
      const evaluatedAtEpochMs = readSpecialistServicesSafeEpochMs(
        servicesClockRef.current,
      );
      if (evaluatedAtEpochMs === null) {
        toast.error(t("serviceAddOnsModal.submissionError"));
        return;
      }
      const anonymousSessionOwnerId = readGroupBookingAnonymousSessionOwnerId();
      const currentCustomerAccountId = getStoredCustomerAccountId();
      const currentSpecialistRuntimeResolution = isLocationEntryPoint || !person
        ? null
        : resolveSpecialistCurrentAssignmentGroupStepOneRuntime({
            runtime: specialistGroupStepOneRuntime,
            sourcePersonId: person?.personId,
            sourceAssignmentPersonId: assignment.personId,
            sourceLocationId: assignment.locationId,
            sourceUserLocationId: assignment.userLocationId,
            returnHref: canonicalProfilePath,
            catalog: locationAppointmentCatalog,
            evaluatedAtEpochMs,
          });
      const currentPreDraftGroupDescriptor = isLocationEntryPoint
        ? null
        : readExactCurrentSpecialistLocationGatewayPreDraftGroupDescriptor();
      const currentOriginScope = isLocationEntryPoint
        ? groupCoordinatorOriginScope?.kind === "location"
          ? groupCoordinatorOriginScope
          : null
        : currentPreDraftGroupDescriptor && specialistLocationGatewaySession
          ? ({
              kind: "specialist-location-gateway-pre-draft",
              descriptor: currentPreDraftGroupDescriptor,
              session: specialistLocationGatewaySession,
              groupAggregateActivationRevision:
                currentPreDraftGroupDescriptor.groupAggregateActivationRevision,
            } as const)
          : currentSpecialistRuntimeResolution?.status === "ready"
            ? ({
                kind: "specialist-current-assignment",
                runtime: currentSpecialistRuntimeResolution.runtime,
              } as const)
            : null;
      const servicesCartCommitLeaseIsCurrent = () => {
        if (
          currentOriginScope?.kind !== "specialist-location-gateway-pre-draft"
        ) {
          return true;
        }
        if (
          !servicesCartCommitLease ||
          servicesCartCommitLease.handoffKey !==
            currentOriginScope.descriptor.servicesCartHandoffKey
        ) {
          return false;
        }
        try {
          const descriptor =
            readExactCurrentSpecialistLocationGatewayPreDraftGroupDescriptor();
          return Boolean(
            descriptor &&
            descriptor.servicesOwnerKey ===
              currentOriginScope.descriptor.servicesOwnerKey &&
            servicesCartCommitLease.isCurrentOwner() === true,
          );
        } catch {
          return false;
        }
      };

      if (
        !currentOriginScope ||
        !groupBookingAvailable ||
        !activeGroupBookingPolicy ||
        !locationAppointmentCatalog ||
        !groupLocationCatalogVersion ||
        groupCoordinatorPointerIsPresent ||
        currentGroupCoordinator ||
        selections.length === 0 ||
        anonymousSessionOwnerId === null ||
        anonymousSessionOwnerId !==
          groupCustomerOwnerState.anonymousSessionOwnerId ||
        currentCustomerAccountId !==
          groupCustomerOwnerState.customerAccountId ||
        locationAppointmentCatalog.workplace.locationId !== routeLocationId ||
        locationAppointmentCatalog.workplace.catalogVersion !==
          groupLocationCatalogVersion ||
        routeGroupCatalogOwnerUserLocationId === null ||
        locationAppointmentCatalog.catalogAssignment.userLocationId !==
          routeGroupCatalogOwnerUserLocationId ||
        !servicesCartCommitLeaseIsCurrent()
      ) {
        toast.error(t("serviceAddOnsModal.submissionError"));
        return;
      }

      if (
        currentOriginScope.kind === "specialist-current-assignment" &&
        (!specialistOrganizerGroupCatalogLineProvider ||
          !specialistOrganizerGroupCatalogFingerprint ||
          !specialistGroupCurrentCatalogFingerprint)
      ) {
        toast.error(t("serviceAddOnsModal.submissionError"));
        return;
      }
      if (
        currentOriginScope.kind === "specialist-location-gateway-pre-draft" &&
        (!specialistLocationGatewayOrganizerGroupCatalogLineProvider ||
          !specialistOrganizerGroupCatalogFingerprint ||
          !specialistGroupCurrentCatalogFingerprint)
      ) {
        toast.error(t("serviceAddOnsModal.submissionError"));
        return;
      }

      const operation = beginGroupProviderOperation();
      if (!operation) return;
      const coordinatorDraftId = createGroupBookingCoordinatorDraftId();
      const transaction = createGroupBookingParticipantCatalogTransaction({
        operation: "initial-organizer",
        coordinatorDraftId,
        anonymousSessionOwnerId,
        customerAccountId: currentCustomerAccountId,
        locationId: routeLocationId,
        catalogVersion: groupLocationCatalogVersion,
        catalogOwnerUserLocationId: routeGroupCatalogOwnerUserLocationId,
        organizerParticipantId: GROUP_ORGANIZER_PARTICIPANT_ID,
      });

      if (!transaction) {
        releaseGroupProviderOperation(operation);
        toast.error(t("serviceAddOnsModal.submissionError"));
        return;
      }

      const catalogRequestId = createGroupBookingCoordinatorDraftId();
      const specialistCatalogRequest =
        currentOriginScope.kind === "location"
          ? null
          : {
              schemaVersion:
                SPECIALIST_ORGANIZER_GROUP_BOOKING_CATALOG_REQUEST_SCHEMA_VERSION,
              kind: "specialist-organizer-group-booking-catalog-request" as const,
              requestId: catalogRequestId,
              requestGeneration: operation.generation,
              participantId: GROUP_ORGANIZER_PARTICIPANT_ID,
              participantRole: "organizer" as const,
              sourcePersonId:
                currentOriginScope.kind === "specialist-current-assignment"
                  ? currentOriginScope.runtime.sourcePersonId
                  : currentOriginScope.session.intent.source.personId,
              sourceLocationId:
                currentOriginScope.kind === "specialist-current-assignment"
                  ? currentOriginScope.runtime.sourceLocationId
                  : currentOriginScope.session.intent.source.sourceLocationId,
              sourceUserLocationId:
                currentOriginScope.kind === "specialist-current-assignment"
                  ? currentOriginScope.runtime.sourceUserLocationId
                  : currentOriginScope.session.intent.source
                      .sourceUserLocationId,
              selectedLocationId:
                currentOriginScope.kind === "specialist-current-assignment"
                  ? currentOriginScope.runtime.selectedLocationId
                  : currentOriginScope.session.resolution.selection
                      .selectedLocationId,
              selectedUserLocationId:
                currentOriginScope.kind === "specialist-current-assignment"
                  ? currentOriginScope.runtime.selectedUserLocationId
                  : currentOriginScope.session.resolution.selection
                      .selectedUserLocationId,
              organizerCatalogVersion: workplace.catalogVersion,
              organizerCatalogFingerprint:
                specialistOrganizerGroupCatalogFingerprint!,
              locationId: routeLocationId,
              catalogVersion: groupLocationCatalogVersion,
              catalogOwnerUserLocationId: routeGroupCatalogOwnerUserLocationId,
              locationCatalogFingerprint:
                specialistGroupCurrentCatalogFingerprint!,
              selections: cloneServiceSelections(selections),
              quantities: selections.map(() => 1),
              evaluatedAtEpochMs,
            };
      const catalogResolution =
        currentOriginScope.kind === "location"
          ? resolveLocationGroupBookingParticipantCatalog({
              provider: groupCatalogLineProvider,
              request: {
                schemaVersion:
                  LOCATION_GROUP_BOOKING_CATALOG_LINE_REQUEST_SCHEMA_VERSION,
                kind: "location-group-booking-catalog-line-request",
                requestId: catalogRequestId,
                requestGeneration: operation.generation,
                participantId: GROUP_ORGANIZER_PARTICIPANT_ID,
                locationId: routeLocationId,
                catalogVersion: groupLocationCatalogVersion,
                catalogOwnerUserLocationId:
                  routeGroupCatalogOwnerUserLocationId,
                selections: cloneServiceSelections(selections),
                quantities: selections.map(() => 1),
                evaluatedAtEpochMs,
              },
              catalog: locationAppointmentCatalog,
              signal: operation.controller.signal,
            })
          : currentOriginScope.kind === "specialist-current-assignment"
            ? resolveSpecialistOrganizerGroupBookingParticipantCatalog({
                provider: specialistOrganizerGroupCatalogLineProvider!,
                request: specialistCatalogRequest!,
                organizerCatalog: specialistOrganizerGroupCatalog,
                locationCatalog: locationAppointmentCatalog,
                signal: operation.controller.signal,
              })
            : resolvePreDraftSpecialistLocationGatewayOrganizerGroupBookingParticipantCatalog(
                {
                  intentRecord: currentOriginScope.session.intent,
                  resolution: currentOriginScope.session.resolution,
                  expectedGroupAggregateActivationRevision:
                    currentOriginScope.groupAggregateActivationRevision,
                  provider:
                    specialistLocationGatewayOrganizerGroupCatalogLineProvider!,
                  request: specialistCatalogRequest!,
                  organizerCatalog: specialistOrganizerGroupCatalog,
                  locationCatalog: locationAppointmentCatalog,
                  signal: operation.controller.signal,
                  now: currentOriginScope.session.now,
                },
              );
      const [capability, catalogResult] = await Promise.all([
        resolveGroupBookingPreliminaryCapability({
          provider: routeGroupCapabilityProvider,
          request: {
            schemaVersion: GROUP_BOOKING_PROVIDER_REQUEST_SCHEMA_VERSION,
            kind: "group-booking-preliminary-capability-request",
            requestId: createGroupBookingCoordinatorDraftId(),
            locationId: routeLocationId,
            catalogVersion: groupLocationCatalogVersion,
            catalogOwnerUserLocationId: routeGroupCatalogOwnerUserLocationId,
            downstreamMaximumGuests:
              activeGroupBookingPolicy.maximumParticipants - 1,
            downstreamMaximumParticipants:
              activeGroupBookingPolicy.maximumParticipants,
            requestGeneration: operation.generation,
            evaluatedAtEpochMs,
          },
          catalog: locationAppointmentCatalog,
          signal: operation.controller.signal,
        }),
        catalogResolution,
      ]);
      // The preliminary provider work may outlive the mutable catalog window.
      // Re-read the clock only before any durable publication, validate it
      // through the no-throw boundary, and never consult it again after commit.
      const freshPersistEpochMs = readSpecialistServicesSafeEpochMs(
        servicesClockRef.current,
      );
      const freshPersistEpochIsSafe =
        freshPersistEpochMs !== null &&
        freshPersistEpochMs >= evaluatedAtEpochMs &&
        freshPersistEpochMs - evaluatedAtEpochMs <=
          GROUP_BOOKING_STORAGE_MAX_AGE_MS;
      const pureRouteOwnerIsCurrent = () =>
        !operation.controller.signal.aborted &&
        groupRouteOperationKeyRef.current === operation.routeOperationKey &&
        groupProviderGenerationRef.current === operation.generation;
      const basicRouteOwnerIsCurrent = () => {
        if (!pureRouteOwnerIsCurrent()) return false;
        const currentAnonymousSessionOwnerId =
          readGroupBookingAnonymousSessionOwnerId();
        if (
          !pureRouteOwnerIsCurrent() ||
          currentAnonymousSessionOwnerId !== anonymousSessionOwnerId
        ) {
          return false;
        }
        const storedCustomerAccountId = getStoredCustomerAccountId();
        return (
          pureRouteOwnerIsCurrent() &&
          storedCustomerAccountId === currentCustomerAccountId
        );
      };
      const routeNavigationOwnerIsCurrent = () => {
        if (!basicRouteOwnerIsCurrent()) return false;
        if (
          currentOriginScope.kind === "specialist-location-gateway-pre-draft"
        ) {
          try {
            const descriptor =
              readExactCurrentSpecialistLocationGatewayPreDraftGroupDescriptor();
            if (
              !descriptor ||
              descriptor.servicesOwnerKey !==
                currentOriginScope.descriptor.servicesOwnerKey ||
              currentOriginScope.session.isCurrentOwner() !== true
            ) {
              return false;
            }
          } catch {
            return false;
          }
        }
        return basicRouteOwnerIsCurrent();
      };
      const routeOperationOwnerIsCurrent = () => {
        if (!routeNavigationOwnerIsCurrent()) return false;
        if (
          currentOriginScope.kind === "specialist-location-gateway-pre-draft"
        ) {
          try {
            if (!servicesCartCommitLeaseIsCurrent()) return false;
          } catch {
            return false;
          }
        }
        return routeNavigationOwnerIsCurrent();
      };
      const ownerIsCurrent = routeOperationOwnerIsCurrent();
      let storedCoordinator: GroupBookingCoordinatorDraftV2 | null = null;
      let persistedCoordinatorDraftId: string | null = null;
      let gatewayLineageCommitted = false;
      let committedGatewayLineage: SpecialistLocationGatewayCommittedNavigationLineageV1 | null =
        null;

      if (
        ownerIsCurrent &&
        freshPersistEpochMs !== null &&
        freshPersistEpochIsSafe &&
        capability &&
        isCurrentGroupBookingCoordinatorCapability(
          capability,
          freshPersistEpochMs,
        ) &&
        (currentOriginScope.kind === "location" ||
          currentOriginScope.kind === "specialist-location-gateway-pre-draft" ||
          specialistGroupStepOneRuntimeOwnsCapability({
            runtime: currentOriginScope.runtime,
            capability,
            requireCurrentCapability: true,
            evaluatedAtEpochMs: freshPersistEpochMs,
          })) &&
        catalogResult.status === "ready" &&
        areGroupCatalogServiceLinesCurrent(
          catalogResult.serviceLines,
          freshPersistEpochMs,
        )
      ) {
        const staged = reduceGroupBookingParticipantCatalogTransaction(
          transaction,
          {
            type: "stage-service-lines",
            serviceLines: catalogResult.serviceLines,
          },
        );
        const requested = reduceGroupBookingParticipantCatalogTransaction(
          staged,
          { type: "request-commit" },
        );
        const intent =
          createGroupBookingParticipantCatalogCommitIntent(requested);
        const coordinator =
          intent?.kind === "create-organizer-coordinator"
            ? createGroupBookingCoordinatorDraft({
                draftId: coordinatorDraftId,
                anonymousSessionOwnerId,
                customerAccountId: currentCustomerAccountId,
                origin:
                  currentOriginScope.kind === "location"
                    ? { kind: "location", locationId: routeLocationId }
                    : {
                        kind: "specialist",
                        personId:
                          currentOriginScope.kind ===
                          "specialist-current-assignment"
                            ? currentOriginScope.runtime.sourcePersonId
                            : currentOriginScope.session.intent.source.personId,
                        sourceLocationId:
                          currentOriginScope.kind ===
                          "specialist-current-assignment"
                            ? currentOriginScope.runtime.sourceLocationId
                            : currentOriginScope.session.intent.source
                                .sourceLocationId,
                        sourceUserLocationId:
                          currentOriginScope.kind ===
                          "specialist-current-assignment"
                            ? currentOriginScope.runtime.sourceUserLocationId
                            : currentOriginScope.session.intent.source
                                .sourceUserLocationId,
                        selectedLocationId:
                          currentOriginScope.kind ===
                          "specialist-current-assignment"
                            ? currentOriginScope.runtime.selectedLocationId
                            : currentOriginScope.session.resolution.selection
                                .selectedLocationId,
                        selectedUserLocationId:
                          currentOriginScope.kind ===
                          "specialist-current-assignment"
                            ? currentOriginScope.runtime.selectedUserLocationId
                            : currentOriginScope.session.resolution.selection
                                .selectedUserLocationId,
                      },
                returnHref:
                  currentOriginScope.kind ===
                  "specialist-location-gateway-pre-draft"
                    ? currentOriginScope.session.intent.source
                        .canonicalReturnHref
                    : canonicalProfilePath,
                locationId: routeLocationId,
                timeZone: locationAppointmentCatalog.workplace.timeZone,
                catalogVersion: groupLocationCatalogVersion,
                catalogOwnerUserLocationId:
                  routeGroupCatalogOwnerUserLocationId,
                capability,
                organizerParticipantId: intent.participantId,
                organizerServiceLines: intent.serviceLines,
                createdAtEpochMs: freshPersistEpochMs,
                expiresAtEpochMs:
                  freshPersistEpochMs + GROUP_BOOKING_STORAGE_MAX_AGE_MS,
              })
            : null;
        const coordinatorCatalogIsCurrent = Boolean(
          coordinator &&
          (currentOriginScope.kind === "location" ||
            (currentOriginScope.kind ===
              "specialist-location-gateway-pre-draft" &&
              specialistLocationGatewayOrganizerGroupCatalogLineProvider &&
              specialistCatalogRequest &&
              validateCurrentPreDraftSpecialistLocationGatewayOrganizerGroupBookingParticipantCatalog(
                {
                  intentRecord: currentOriginScope.session.intent,
                  resolution: currentOriginScope.session.resolution,
                  expectedGroupAggregateActivationRevision:
                    currentOriginScope.groupAggregateActivationRevision,
                  provider:
                    specialistLocationGatewayOrganizerGroupCatalogLineProvider,
                  request: specialistCatalogRequest,
                  serviceLines:
                    coordinator.participants.find(
                      ({ participantId }) =>
                        participantId === coordinator.organizerParticipantId,
                    )?.serviceLines ?? [],
                  organizerCatalog: specialistOrganizerGroupCatalog,
                  locationCatalog: locationAppointmentCatalog,
                  evaluatedAtEpochMs: freshPersistEpochMs,
                },
              ).status === "current") ||
            (specialistOrganizerGroupCatalogLineProvider &&
              validateCurrentSpecialistOrganizerGroupBookingCoordinatorCatalog({
                coordinator,
                provider: specialistOrganizerGroupCatalogLineProvider,
                organizerCatalog: specialistOrganizerGroupCatalog,
                locationCatalog: locationAppointmentCatalog,
                evaluatedAtEpochMs: freshPersistEpochMs,
              }).status === "current")),
        );
        if (
          coordinator &&
          coordinatorCatalogIsCurrent &&
          currentOriginScope.kind === "specialist-location-gateway-pre-draft"
        ) {
          const destinationTransaction =
            createSpecialistLocationGatewayGroupBookingCoordinatorDestinationTransaction(
              coordinator,
            );
          const revalidated =
            await refreshSpecialistLocationGatewayServicesSession(
              currentOriginScope.session,
            );
          const claimedAtEpochMs = readSpecialistLocationGatewaySessionEpoch(
            currentOriginScope.session,
          );
          const gatewayCommitOwnerIsCurrent = () => {
            if (!routeOperationOwnerIsCurrent()) return false;
            const currentEpochMs = readSpecialistLocationGatewaySessionEpoch(
              currentOriginScope.session,
            );
            if (
              currentEpochMs === null ||
              !routeOperationOwnerIsCurrent() ||
              !specialistLocationGatewayOrganizerGroupCatalogLineProvider ||
              !specialistCatalogRequest
            ) {
              return false;
            }
            const current =
              validateCurrentPreDraftSpecialistLocationGatewayOrganizerGroupBookingParticipantCatalog(
                {
                  intentRecord: currentOriginScope.session.intent,
                  resolution: currentOriginScope.session.resolution,
                  expectedGroupAggregateActivationRevision:
                    currentOriginScope.groupAggregateActivationRevision,
                  provider:
                    specialistLocationGatewayOrganizerGroupCatalogLineProvider,
                  request: specialistCatalogRequest,
                  serviceLines:
                    coordinator.participants.find(
                      ({ participantId }) =>
                        participantId === coordinator.organizerParticipantId,
                    )?.serviceLines ?? [],
                  organizerCatalog: specialistOrganizerGroupCatalog,
                  locationCatalog: locationAppointmentCatalog,
                  evaluatedAtEpochMs: currentEpochMs,
                },
              );
            return (
              current.status === "current" && routeOperationOwnerIsCurrent()
            );
          };
          const committed =
            destinationTransaction &&
            revalidated &&
            claimedAtEpochMs !== null &&
            gatewayCommitOwnerIsCurrent()
              ? await commitSpecialistLocationGatewayNavigationLineage({
                  resolution: currentOriginScope.session.resolution,
                  revalidatedResolution: revalidated.resolution,
                  attemptId: createGroupBookingCoordinatorDraftId(),
                  destinationKind: "group",
                  destinationDraftId: destinationTransaction.destinationDraftId,
                  expectedDestinationFingerprint:
                    destinationTransaction.expectedDestinationFingerprint,
                  claimedAtEpochMs,
                  now: currentOriginScope.session.now,
                  signal: operation.controller.signal,
                  isCurrentOwner: gatewayCommitOwnerIsCurrent,
                  destinationDraft: destinationTransaction.transaction,
                })
              : null;
          gatewayLineageCommitted = committed?.status === "committed";
          committedGatewayLineage =
            committed?.status === "committed" ? committed.lineage : null;
          persistedCoordinatorDraftId = gatewayLineageCommitted
            ? coordinator.draftId
            : null;
          const runtime =
            committed?.status === "committed"
              ? createSpecialistLocationGatewayGroupStepOneRuntime({
                  intentRecord: currentOriginScope.session.intent,
                  navigationLineage: committed.lineage,
                  sourceAssignmentPersonId:
                    currentOriginScope.session.intent.source.personId,
                  sourceAssignmentLocationId:
                    currentOriginScope.session.intent.source.sourceLocationId,
                  sourceAssignmentUserLocationId:
                    currentOriginScope.session.intent.source
                      .sourceUserLocationId,
                  sourceReturnHref:
                    currentOriginScope.session.intent.source
                      .canonicalReturnHref,
                  sourceProfileRevision:
                    currentOriginScope.session.intent.source
                      .sourceProfileRevision,
                  sourceCatalogVersion:
                    currentOriginScope.session.intent.source
                      .sourceCatalogVersion,
                  organizerCatalog: specialistOrganizerGroupCatalog,
                  catalog: locationAppointmentCatalog,
                  capability,
                  downstreamConsumer: {
                    schemaVersion:
                      SPECIALIST_LOCATION_GATEWAY_GROUP_STEP_ONE_DOWNSTREAM_CONSUMER_SCHEMA_VERSION,
                    kind: "specialist-location-gateway-group-step-one-downstream-consumer",
                    consumerId:
                      SPECIALIST_LOCATION_GATEWAY_GROUP_STEP_ONE_DOWNSTREAM_CONSUMER_ID,
                    consumerRevision:
                      SPECIALIST_LOCATION_GATEWAY_GROUP_STEP_ONE_DOWNSTREAM_CONSUMER_REVISION,
                    groupAggregateActivationRevision:
                      currentOriginScope.groupAggregateActivationRevision,
                    status: "ready",
                  },
                })
              : null;
          const readBack = gatewayLineageCommitted
            ? readGroupBookingCoordinatorAuthorityPersisted(coordinator.draftId)
            : null;
          const postCommitEpochMs = gatewayLineageCommitted
            ? readSpecialistLocationGatewaySessionEpoch(
                currentOriginScope.session,
              )
            : null;
          const postCommitOwnerIsCurrent = Boolean(
            postCommitEpochMs !== null &&
            postCommitEpochMs >= freshPersistEpochMs &&
            routeOperationOwnerIsCurrent(),
          );
          const postCommitCatalogIsCurrent = Boolean(
            runtime &&
            readBack &&
            postCommitEpochMs !== null &&
            specialistLocationGatewayOrganizerGroupCatalogLineProvider &&
            validateCurrentSpecialistLocationGatewayOrganizerGroupBookingCoordinatorCatalog(
              {
                runtime,
                coordinator: readBack,
                provider:
                  specialistLocationGatewayOrganizerGroupCatalogLineProvider,
                organizerCatalog: specialistOrganizerGroupCatalog,
                locationCatalog: locationAppointmentCatalog,
                evaluatedAtEpochMs: postCommitEpochMs,
              },
            ).status === "current" &&
            routeOperationOwnerIsCurrent(),
          );
          storedCoordinator =
            runtime &&
            readBack &&
            postCommitEpochMs !== null &&
            postCommitOwnerIsCurrent &&
            haveExactSerializableStructure(readBack, coordinator) &&
            canSpecialistLocationGatewayGroupStepOneRuntimeConsumeDownstream(
              runtime,
              {
                groupAggregateActivationRevision:
                  currentOriginScope.groupAggregateActivationRevision,
              },
            ) &&
            specialistLocationGatewayGroupStepOneRuntimeOwnsCoordinator({
              runtime,
              coordinator: readBack,
              requireBuildingAuthority: true,
              requireCurrentRuntime: true,
              evaluatedAtEpochMs: postCommitEpochMs,
            }) &&
            postCommitCatalogIsCurrent
              ? readBack
              : null;
        } else {
          const stored =
            coordinator && coordinatorCatalogIsCurrent
              ? storeGroupBookingCoordinatorDraftPersisted(coordinator)
              : null;
          persistedCoordinatorDraftId = stored?.draftId ?? null;
          const readBack = stored
            ? readGroupBookingCoordinatorAuthorityPersisted(stored.draftId)
            : null;

          storedCoordinator =
            stored &&
            readBack &&
            haveExactSerializableStructure(stored, readBack)
              ? readBack
              : null;
        }
      }

      releaseGroupProviderOperation(operation);
      const finalOwnerIsCurrent = routeOperationOwnerIsCurrent();

      if (!finalOwnerIsCurrent || !storedCoordinator) {
        if (persistedCoordinatorDraftId && !gatewayLineageCommitted) {
          ensureGroupBookingCoordinatorDraftRemoved(
            persistedCoordinatorDraftId,
          );
        }
        if (finalOwnerIsCurrent) {
          toast.error(t("serviceAddOnsModal.submissionError"));
        }
        return;
      }

      const rosterHref = appendGroupBookingCoordinatorHandoff(
        currentOriginScope.kind === "specialist-location-gateway-pre-draft"
          ? currentServicesHref
          : canonicalServicesPath,
        storedCoordinator.draftId,
      );

      if (!rosterHref) {
        if (!gatewayLineageCommitted) {
          ensureGroupBookingCoordinatorDraftRemoved(storedCoordinator.draftId);
        }
        toast.error(t("serviceAddOnsModal.submissionError"));
        return;
      }

      const navigationTarget = committedGatewayLineage
        ? {
            lineage: committedGatewayLineage,
            href: rosterHref,
            navigation: "replace" as const,
            groupDestinationAnalytics: {
              coordinatorDraftId: storedCoordinator.draftId,
              organizerParticipantId: storedCoordinator.organizerParticipantId,
              organizerParticipantRole: "organizer" as const,
              selection: {
                selectedLocationId:
                  currentOriginScope.kind ===
                  "specialist-location-gateway-pre-draft"
                    ? currentOriginScope.session.resolution.selection
                        .selectedLocationId
                    : committedGatewayLineage.selection.selectedLocationId,
                selectedUserLocationId:
                  currentOriginScope.kind ===
                  "specialist-location-gateway-pre-draft"
                    ? currentOriginScope.session.resolution.selection
                        .selectedUserLocationId
                    : committedGatewayLineage.selection.selectedUserLocationId,
              },
            },
          }
        : null;
      if (navigationTarget) {
        setPostCommitNavigationRecovery(navigationTarget);
      }

      setIsContinuing(true);
      try {
        await Promise.resolve(router.replace(rosterHref, { scroll: false }));
        if (navigationTarget) {
          const retired =
            routeOperationOwnerIsCurrent() &&
            retireSpecialistLocationGatewayServicesCartAfterCanonicalNavigation(
              {
                lineage: navigationTarget.lineage,
                isNavigationOwnerCurrent: routeNavigationOwnerIsCurrent,
              },
            );
          continueGuardRef.current = false;
          setIsContinuing(false);
          if (!retired) return;
          const reported =
            reportSpecialistLocationGatewayGroupDestinationCommitted({
              pending: navigationTarget,
              isNavigationOwnerCurrent: routeNavigationOwnerIsCurrent,
              consumePendingRecovery: () =>
                setPostCommitNavigationRecovery(null),
              restorePendingRecovery: () =>
                setPostCommitNavigationRecovery(navigationTarget),
            });
          if (!reported) {
            toast.error(t("serviceAddOnsModal.submissionError"));
            return;
          }
          return;
        }
      } catch {
        if (!gatewayLineageCommitted) {
          ensureGroupBookingCoordinatorDraftRemoved(storedCoordinator.draftId);
        }
        continueGuardRef.current = false;
        setIsContinuing(false);
        toast.error(t("serviceAddOnsModal.submissionError"));
      }
    },
    [
      beginGroupProviderOperation,
      activeGroupBookingPolicy,
      assignment.locationId,
      assignment.personId,
      assignment.userLocationId,
      canonicalProfilePath,
      canonicalServicesPath,
      currentServicesHref,
      currentGroupCoordinator,
      groupCoordinatorOriginScope,
      groupBookingAvailable,
      routeGroupCapabilityProvider,
      groupCatalogLineProvider,
      groupCoordinatorPointerIsPresent,
      groupCustomerOwnerState,
      groupLocationCatalogVersion,
      isLocationEntryPoint,
      locationAppointmentCatalog,
      postCommitNavigationRecovery,
      reportSpecialistLocationGatewayGroupDestinationCommitted,
      releaseGroupProviderOperation,
      retireSpecialistLocationGatewayServicesCartAfterCanonicalNavigation,
      routeGroupCatalogOwnerUserLocationId,
      routeLocationId,
      router,
      retryPostCommitNavigation,
      readExactCurrentSpecialistLocationGatewayPreDraftGroupDescriptor,
      specialistGroupStepOneRuntime,
      specialistGroupCurrentCatalogFingerprint,
      specialistLocationGatewayOrganizerGroupCatalogLineProvider,
      specialistLocationGatewaySession,
      specialistOrganizerGroupCatalog,
      specialistOrganizerGroupCatalogFingerprint,
      specialistOrganizerGroupCatalogLineProvider,
      t,
      workplace,
      person,
    ],
  );

  useEffect(() => {
    // Accepted lineage is immutable terminal evidence. Mutable Coordinator,
    // capability, quote, and Step-1 TTLs may withdraw editing but must never
    // spin the accepted retry/Close document through mutable refresh cycles.
    if (
      !currentGroupCoordinator ||
      groupCoordinatorRouteState.status === "accepted-handoff"
    ) {
      return undefined;
    }
    const currentEpochMs = readSpecialistServicesSafeEpochMs(
      servicesClockRef.current,
    );
    if (currentEpochMs === null) {
      const frame = window.requestAnimationFrame(() => {
        setGroupCoordinatorRefreshVersion((current) => current + 1);
      });
      return () => window.cancelAnimationFrame(frame);
    }
    const authorityExpiryEpochMs = Math.min(
      currentGroupCoordinator.expiresAtEpochMs,
      currentGroupCoordinator.capability.expiresAtEpochMs,
      ...currentGroupCoordinator.participants.flatMap((participant) =>
        participant.serviceLines.map(
          (line) => line.quoteAuthority.expiresAtEpochMs,
        ),
      ),
    );
    const delayMs = authorityExpiryEpochMs - currentEpochMs;

    if (delayMs <= 0) {
      const frame = window.requestAnimationFrame(() => {
        setGroupCoordinatorRefreshVersion((current) => current + 1);
      });

      return () => window.cancelAnimationFrame(frame);
    }

    const timer = window.setTimeout(
      () => setGroupCoordinatorRefreshVersion((current) => current + 1),
      Math.min(delayMs + 1, 2_147_483_647),
    );

    return () => window.clearTimeout(timer);
  }, [currentGroupCoordinator, groupCoordinatorRouteState.status]);

  useEffect(() => {
    if (
      !currentGroupCoordinator ||
      !locationAppointmentCatalog ||
      activeGroupCatalogTransaction ||
      isGroupMutationPending ||
      !isCurrentGroupCoordinatorRouteAuthority(currentGroupCoordinator)
    ) {
      return;
    }
    const evaluatedAtEpochMs = readSpecialistServicesSafeEpochMs(
      servicesClockRef.current,
    );
    if (evaluatedAtEpochMs === null) return;
    const exact = currentGroupCoordinator.exactGraphPublication;
    const exactIsCurrent = Boolean(
      exact &&
      evaluatedAtEpochMs >= exact.generatedAtEpochMs &&
      evaluatedAtEpochMs < exact.expiresAtEpochMs,
    );

    if (exactIsCurrent && exact) {
      const timer = window.setTimeout(
        () => setGroupExactGraphClockVersion((current) => current + 1),
        Math.min(
          exact.expiresAtEpochMs - evaluatedAtEpochMs + 1,
          2_147_483_647,
        ),
      );

      return () => window.clearTimeout(timer);
    }

    const fingerprint = createGroupBookingCoordinatorFingerprint(
      currentGroupCoordinator,
    );
    const attemptedOwnerKey = fingerprint
      ? `${currentGroupCoordinator.draftId}:${currentGroupCoordinator.revision}:${fingerprint}`
      : null;

    if (
      !fingerprint ||
      !attemptedOwnerKey ||
      groupExactGraphAttemptedOwnerRef.current === attemptedOwnerKey
    ) {
      return;
    }
    groupExactGraphAttemptedOwnerRef.current = attemptedOwnerKey;
    const controller = new AbortController();
    const generation = groupExactGraphGenerationRef.current + 1;
    const routeOperationKey = groupRouteOperationKeyRef.current;
    groupExactGraphGenerationRef.current = generation;
    groupExactGraphAbortRef.current?.abort();
    groupExactGraphAbortRef.current = controller;

    void (async () => {
      const liveOwnerProvider: GroupBookingCapabilityProvider = {
        getPreliminaryCapability: (request, context) =>
          routeGroupCapabilityProvider.getPreliminaryCapability(
            request,
            context,
          ),
        async getExactGraphPublication(request, context) {
          if (
            !isExactLiveGroupCoordinatorOwner(currentGroupCoordinator) ||
            !isCurrentGroupCoordinatorRouteAuthority(
              currentGroupCoordinator,
              evaluatedAtEpochMs,
            )
          ) {
            return null;
          }
          const publication =
            await routeGroupCapabilityProvider.getExactGraphPublication(
              request,
              context,
            );

          return !controller.signal.aborted &&
            isExactLiveGroupCoordinatorOwner(currentGroupCoordinator) &&
            isCurrentGroupCoordinatorRouteAuthority(
              currentGroupCoordinator,
              evaluatedAtEpochMs,
            )
            ? publication
            : null;
        },
      };
      const result = await refreshGroupBookingExactGraphAuthority({
        owner: {
          draftId: currentGroupCoordinator.draftId,
          revision: currentGroupCoordinator.revision,
          fingerprint,
        },
        provider: liveOwnerProvider,
        catalog: locationAppointmentCatalog,
        requestId: createGroupBookingCoordinatorDraftId(),
        requestGeneration: generation,
        signal: controller.signal,
        evaluatedAtEpochMs,
        now: () => evaluatedAtEpochMs,
        isOperationOwnerCurrent: () =>
          isLiveGroupCoordinatorSessionOwner(currentGroupCoordinator),
      });
      const ownerIsCurrent =
        !controller.signal.aborted &&
        groupExactGraphAbortRef.current === controller &&
        groupRouteOperationKeyRef.current === routeOperationKey &&
        groupExactGraphGenerationRef.current === generation;

      if (!ownerIsCurrent) return;
      groupExactGraphAbortRef.current = null;

      if (result.status === "published") {
        groupExactGraphRetryRef.current = {
          ownerKey: attemptedOwnerKey,
          failedAttempts: 0,
        };
        setGroupCoordinatorRefreshVersion((current) => current + 1);
        return;
      }

      const priorRetry = groupExactGraphRetryRef.current;
      const failedAttempts =
        priorRetry.ownerKey === attemptedOwnerKey
          ? priorRetry.failedAttempts + 1
          : 1;
      groupExactGraphRetryRef.current = {
        ownerKey: attemptedOwnerKey,
        failedAttempts,
      };

      if (failedAttempts >= 3) return;
      groupExactGraphAttemptedOwnerRef.current = null;
      groupExactGraphRetryTimerRef.current = window.setTimeout(
        () => {
          groupExactGraphRetryTimerRef.current = null;
          if (
            groupExactGraphGenerationRef.current === generation &&
            groupRouteOperationKeyRef.current === routeOperationKey
          ) {
            setGroupExactGraphClockVersion((current) => current + 1);
          }
        },
        150 * 2 ** (failedAttempts - 1),
      );
    })();

    return () => {
      controller.abort();
      if (groupExactGraphAttemptedOwnerRef.current === attemptedOwnerKey) {
        groupExactGraphAttemptedOwnerRef.current = null;
      }
    };
  }, [
    activeGroupCatalogTransaction,
    currentGroupCoordinator,
    routeGroupCapabilityProvider,
    groupExactGraphClockVersion,
    isGroupMutationPending,
    isCurrentGroupCoordinatorRouteAuthority,
    locationAppointmentCatalog,
    groupLocationCatalogVersion,
  ]);

  const cancelGroupCatalogTransaction = useCallback(() => {
    setGroupCatalogTransaction((current) =>
      current
        ? reduceGroupBookingParticipantCatalogTransaction(current, {
            type: "cancel",
          })
        : null,
    );
    setGroupCatalogSelections([]);
    setGroupCatalogQuantities([]);
    window.requestAnimationFrame(() => setGroupCatalogTransaction(null));
  }, []);

  const handleAddGroupParticipant = useCallback(() => {
    if (
      !currentGroupCoordinator ||
      isGroupMutationPending ||
      !isExactLiveGroupCoordinatorOwner(currentGroupCoordinator) ||
      !isCurrentGroupCoordinatorRouteAuthority(currentGroupCoordinator) ||
      currentGroupCoordinator.participants.length - 1 >=
        currentGroupCoordinator.capability.effectiveMaximumGuests
    ) {
      return;
    }

    const transaction = createGroupBookingParticipantCatalogTransaction({
      operation: "add-guest",
      draft: currentGroupCoordinator,
      guestParticipantId: getNextGroupGuestParticipantId(
        currentGroupCoordinator.participants,
      ),
    });

    if (!transaction) {
      toast.error(t("serviceAddOnsModal.submissionError"));
      return;
    }

    setGroupCatalogSelections([]);
    setGroupCatalogQuantities([]);
    setGroupCatalogTransaction(transaction);
  }, [
    currentGroupCoordinator,
    isCurrentGroupCoordinatorRouteAuthority,
    isGroupMutationPending,
    t,
  ]);

  const handleEditGroupParticipant = useCallback(
    async (participantId: string) => {
      if (
        !currentGroupCoordinator ||
        !locationAppointmentCatalog ||
        isGroupMutationPending ||
        !isExactLiveGroupCoordinatorOwner(currentGroupCoordinator)
      ) {
        return;
      }
      const evaluatedAtEpochMs = readSpecialistServicesSafeEpochMs(
        servicesClockRef.current,
      );
      if (evaluatedAtEpochMs === null) {
        toast.error(t("serviceAddOnsModal.submissionError"));
        return;
      }
      const participant = currentGroupCoordinator.participants.find(
        (candidate) => candidate.participantId === participantId,
      );
      const editsSpecialistOrganizer = Boolean(
        !isLocationEntryPoint && participant?.role === "organizer",
      );
      const editsGatewayOrganizer = Boolean(
        editsSpecialistOrganizer &&
        groupCoordinatorOriginScope?.kind === "specialist-location-gateway",
      );
      if (
        !participant ||
        (editsSpecialistOrganizer
          ? !isRecoverableSpecialistOrganizerCoordinator(
              currentGroupCoordinator,
              evaluatedAtEpochMs,
            ) ||
            (editsGatewayOrganizer
              ? !specialistLocationGatewayOrganizerGroupCatalogLineProvider
              : !specialistOrganizerGroupCatalogLineProvider)
          : !isCurrentGroupCoordinatorRouteAuthority(
              currentGroupCoordinator,
              evaluatedAtEpochMs,
            ))
      ) {
        return;
      }
      const transaction = createGroupBookingParticipantCatalogTransaction({
        operation: "edit-participant",
        draft: currentGroupCoordinator,
        participantId,
      });

      if (!transaction) {
        toast.error(t("serviceAddOnsModal.submissionError"));
        return;
      }
      const operation = beginGroupProviderOperation();
      if (!operation) return;
      const specialistOrigin =
        currentGroupCoordinator.origin.kind === "specialist"
          ? currentGroupCoordinator.origin
          : null;
      const result =
        editsGatewayOrganizer &&
        groupCoordinatorOriginScope?.kind === "specialist-location-gateway" &&
        specialistOrigin &&
        specialistLocationGatewayOrganizerGroupCatalogLineProvider &&
        specialistOrganizerGroupCatalogFingerprint &&
        specialistGroupCurrentCatalogFingerprint
          ? await resolveEditableSpecialistLocationGatewayOrganizerGroupBookingParticipantCatalog(
              {
                runtime: groupCoordinatorOriginScope.runtime,
                provider:
                  specialistLocationGatewayOrganizerGroupCatalogLineProvider,
                request: {
                  schemaVersion:
                    SPECIALIST_ORGANIZER_GROUP_BOOKING_CATALOG_REQUEST_SCHEMA_VERSION,
                  kind: "specialist-organizer-group-booking-catalog-request",
                  requestId: createGroupBookingCoordinatorDraftId(),
                  requestGeneration: operation.generation,
                  participantId,
                  participantRole: "organizer",
                  sourcePersonId: specialistOrigin.personId,
                  sourceLocationId: specialistOrigin.sourceLocationId,
                  sourceUserLocationId: specialistOrigin.sourceUserLocationId,
                  selectedLocationId: specialistOrigin.selectedLocationId,
                  selectedUserLocationId:
                    specialistOrigin.selectedUserLocationId,
                  organizerCatalogVersion:
                    specialistOrganizerGroupCatalog.workplace.catalogVersion,
                  organizerCatalogFingerprint:
                    specialistOrganizerGroupCatalogFingerprint,
                  locationId: currentGroupCoordinator.locationId,
                  catalogVersion: currentGroupCoordinator.catalogVersion,
                  catalogOwnerUserLocationId:
                    locationAppointmentCatalog.catalogAssignment.userLocationId,
                  locationCatalogFingerprint:
                    specialistGroupCurrentCatalogFingerprint,
                  evaluatedAtEpochMs,
                },
                serviceLines: transaction.baseServiceLines,
                organizerCatalog: specialistOrganizerGroupCatalog,
                locationCatalog: locationAppointmentCatalog,
                signal: operation.controller.signal,
                now: () => evaluatedAtEpochMs,
              },
            )
          : editsSpecialistOrganizer &&
              specialistOrigin &&
              specialistOrganizerGroupCatalogLineProvider &&
              specialistOrganizerGroupCatalogFingerprint &&
              specialistGroupCurrentCatalogFingerprint
            ? await resolveEditableSpecialistOrganizerGroupBookingParticipantCatalog(
                {
                  provider: specialistOrganizerGroupCatalogLineProvider,
                  request: {
                    schemaVersion:
                      SPECIALIST_ORGANIZER_GROUP_BOOKING_CATALOG_REQUEST_SCHEMA_VERSION,
                    kind: "specialist-organizer-group-booking-catalog-request",
                    requestId: createGroupBookingCoordinatorDraftId(),
                    requestGeneration: operation.generation,
                    participantId,
                    participantRole: "organizer",
                    sourcePersonId: specialistOrigin.personId,
                    sourceLocationId: specialistOrigin.sourceLocationId,
                    sourceUserLocationId: specialistOrigin.sourceUserLocationId,
                    selectedLocationId: specialistOrigin.selectedLocationId,
                    selectedUserLocationId:
                      specialistOrigin.selectedUserLocationId,
                    organizerCatalogVersion:
                      specialistOrganizerGroupCatalog.workplace.catalogVersion,
                    organizerCatalogFingerprint:
                      specialistOrganizerGroupCatalogFingerprint,
                    locationId: currentGroupCoordinator.locationId,
                    catalogVersion: currentGroupCoordinator.catalogVersion,
                    catalogOwnerUserLocationId:
                      locationAppointmentCatalog.catalogAssignment
                        .userLocationId,
                    locationCatalogFingerprint:
                      specialistGroupCurrentCatalogFingerprint,
                    evaluatedAtEpochMs,
                  },
                  serviceLines: transaction.baseServiceLines,
                  organizerCatalog: specialistOrganizerGroupCatalog,
                  locationCatalog: locationAppointmentCatalog,
                  signal: operation.controller.signal,
                },
              )
            : await resolveEditableLocationGroupBookingParticipantCatalog({
                provider: groupCatalogLineProvider,
                request: {
                  schemaVersion:
                    LOCATION_GROUP_BOOKING_CATALOG_LINE_REQUEST_SCHEMA_VERSION,
                  kind: "location-group-booking-catalog-line-request",
                  requestId: createGroupBookingCoordinatorDraftId(),
                  requestGeneration: operation.generation,
                  participantId,
                  locationId: currentGroupCoordinator.locationId,
                  catalogVersion: currentGroupCoordinator.catalogVersion,
                  catalogOwnerUserLocationId:
                    locationAppointmentCatalog.catalogAssignment.userLocationId,
                  evaluatedAtEpochMs,
                },
                serviceLines: transaction.baseServiceLines,
                catalog: locationAppointmentCatalog,
                signal: operation.controller.signal,
              });
      const ownerIsCurrent =
        !operation.controller.signal.aborted &&
        groupRouteOperationKeyRef.current === operation.routeOperationKey &&
        groupProviderGenerationRef.current === operation.generation &&
        isExactLiveGroupCoordinatorOwner(currentGroupCoordinator) &&
        (editsSpecialistOrganizer
          ? isRecoverableSpecialistOrganizerCoordinator(
              currentGroupCoordinator,
              evaluatedAtEpochMs,
            )
          : isCurrentGroupCoordinatorRouteAuthority(
              currentGroupCoordinator,
              evaluatedAtEpochMs,
            ));
      const projected =
        ownerIsCurrent &&
        result.status === "ready" &&
        areGroupCatalogServiceLinesCurrent(
          result.serviceLines,
          evaluatedAtEpochMs,
        )
          ? editsSpecialistOrganizer
            ? projectOwnedLocationGroupBookingCatalogSelections({
                participantId,
                serviceLines: result.serviceLines,
              })
            : projectLocationGroupBookingCatalogSelections({
                participantId,
                locationId: currentGroupCoordinator.locationId,
                catalogAssignment: locationAppointmentCatalog.catalogAssignment,
                workplace: locationAppointmentCatalog.workplace,
                services: locationAppointmentCatalog.services,
                offerings: locationAppointmentCatalog.offerings,
                serviceLines: result.serviceLines,
                linePublications:
                  result.linePublications as readonly LocationGroupBookingCatalogLinePublicationV1[],
                evaluatedAtEpochMs,
              })
          : null;

      releaseGroupProviderOperation(operation);

      if (!ownerIsCurrent || !projected?.ok) {
        if (ownerIsCurrent) {
          toast.error(t("serviceAddOnsModal.submissionError"));
        }
        return;
      }

      setGroupCatalogSelections(cloneServiceSelections(projected.selections));
      setGroupCatalogQuantities([...projected.quantities]);
      groupRosterReturnFocusOwnerRef.current = {
        coordinatorDraftId: currentGroupCoordinator.draftId,
        participantId,
      };
      setGroupCatalogTransaction(transaction);
    },
    [
      beginGroupProviderOperation,
      currentGroupCoordinator,
      groupCatalogLineProvider,
      groupCoordinatorOriginScope,
      isLocationEntryPoint,
      isCurrentGroupCoordinatorRouteAuthority,
      isRecoverableSpecialistOrganizerCoordinator,
      isGroupMutationPending,
      locationAppointmentCatalog,
      releaseGroupProviderOperation,
      specialistGroupCurrentCatalogFingerprint,
      specialistLocationGatewayOrganizerGroupCatalogLineProvider,
      specialistOrganizerGroupCatalog,
      specialistOrganizerGroupCatalogFingerprint,
      specialistOrganizerGroupCatalogLineProvider,
      t,
    ],
  );

  const handleRemoveGroupParticipant = useCallback(
    (participantId: string) => {
      if (
        isGroupMutationPending ||
        (currentGroupCoordinator &&
          !isExactLiveGroupCoordinatorOwner(currentGroupCoordinator)) ||
        (currentGroupCoordinator &&
          !isCurrentGroupCoordinatorRouteAuthority(currentGroupCoordinator)) ||
        !currentGroupCoordinator?.participants.some(
          (participant) =>
            participant.participantId === participantId &&
            participant.role === "guest",
        )
      ) {
        return;
      }

      setPendingGroupRemoval({
        coordinatorDraftId: currentGroupCoordinator.draftId,
        coordinatorRevision: currentGroupCoordinator.revision,
        participantId,
      });
    },
    [
      currentGroupCoordinator,
      isCurrentGroupCoordinatorRouteAuthority,
      isGroupMutationPending,
    ],
  );

  const confirmGroupParticipantRemoval = useCallback(() => {
    const removal = pendingGroupRemoval;
    const evaluatedAtEpochMs = readSpecialistServicesSafeEpochMs(
      servicesClockRef.current,
    );

    if (
      evaluatedAtEpochMs === null ||
      !removal ||
      !currentGroupCoordinator ||
      removal.coordinatorDraftId !== currentGroupCoordinator.draftId ||
      removal.coordinatorRevision !== currentGroupCoordinator.revision ||
      !isExactLiveGroupCoordinatorOwner(currentGroupCoordinator) ||
      !isCurrentGroupCoordinatorRouteAuthority(
        currentGroupCoordinator,
        evaluatedAtEpochMs,
      ) ||
      groupMutationInFlightRef.current
    ) {
      return;
    }

    groupMutationInFlightRef.current = true;
    setIsGroupMutationPending(true);
    const routeOperationKey = groupRouteOperationKeyRef.current;
    const expected = readGroupBookingCoordinatorAuthorityPersisted(
      currentGroupCoordinator.draftId,
    );
    const next =
      expected &&
      haveExactSerializableStructure(expected, currentGroupCoordinator)
        ? removeGroupBookingCoordinatorGuest({
            draft: expected,
            participantId: removal.participantId,
            updatedAtEpochMs: evaluatedAtEpochMs,
          })
        : null;
    const removalOwnerIsCurrent = () => {
      const pureOwnerIsCurrent = () =>
        groupMutationInFlightRef.current &&
        groupRouteOperationKeyRef.current === routeOperationKey;
      if (!pureOwnerIsCurrent() || !expected || !next) return false;
      try {
        const customerAccountId = getStoredCustomerAccountId();
        const anonymousSessionOwnerId =
          readGroupBookingAnonymousSessionOwnerId();
        if (!pureOwnerIsCurrent()) return false;
        const storedOwnerMatches = expected.customerAccountId
          ? customerAccountId === expected.customerAccountId
          : customerAccountId === null &&
            anonymousSessionOwnerId === expected.anonymousSessionOwnerId;
        const live = readGroupBookingCoordinatorAuthorityPersisted(
          expected.draftId,
        );
        return Boolean(
          pureOwnerIsCurrent() &&
          storedOwnerMatches &&
          live &&
          (haveExactSerializableStructure(live, expected) ||
            haveExactSerializableStructure(live, next)) &&
          isCurrentGroupCoordinatorRouteAuthority(live) &&
          pureOwnerIsCurrent(),
        );
      } catch {
        return false;
      }
    };
    const persisted =
      expected && next
        ? replaceGroupBookingCoordinatorDraftOwnerAwarePersisted({
            expected,
            next,
            isCurrentOwner: removalOwnerIsCurrent,
          })
        : ({ status: "invalid" } as const);
    const committed = persisted.status === "replaced";

    groupMutationInFlightRef.current = false;
    setIsGroupMutationPending(false);
    setPendingGroupRemoval(null);
    setGroupCoordinatorRefreshVersion((current) => current + 1);

    if (!committed && persisted.status !== "owner-drift") {
      toast.error(t("serviceAddOnsModal.submissionError"));
    }
  }, [
    currentGroupCoordinator,
    isCurrentGroupCoordinatorRouteAuthority,
    pendingGroupRemoval,
    t,
  ]);

  const commitGroupCatalogTransaction = useCallback(
    async (selections: readonly SpecialistServiceSelection[]) => {
      const transaction = activeGroupCatalogTransaction;

      if (
        !transaction ||
        !currentGroupCoordinator ||
        !locationAppointmentCatalog ||
        selections.length === 0 ||
        groupCatalogQuantities.length !== selections.length
      ) {
        return;
      }
      const evaluatedAtEpochMs = readSpecialistServicesSafeEpochMs(
        servicesClockRef.current,
      );
      if (evaluatedAtEpochMs === null) {
        toast.error(t("serviceAddOnsModal.submissionError"));
        return;
      }
      const commitsSpecialistOrganizer = Boolean(
        !isLocationEntryPoint &&
        transaction.source.participantRole === "organizer",
      );
      if (
        !isExactLiveGroupCoordinatorOwner(currentGroupCoordinator) ||
        (commitsSpecialistOrganizer
          ? !isRecoverableSpecialistOrganizerCoordinator(
              currentGroupCoordinator,
              evaluatedAtEpochMs,
            )
          : !isCurrentGroupCoordinatorRouteAuthority(
              currentGroupCoordinator,
              evaluatedAtEpochMs,
            ))
      ) {
        setGroupCatalogTransaction(null);
        setGroupCatalogSelections([]);
        setGroupCatalogQuantities([]);
        setGroupCoordinatorRefreshVersion((current) => current + 1);
        return;
      }
      const operation = beginGroupProviderOperation();
      if (!operation) return;
      const specialistOrigin =
        currentGroupCoordinator.origin.kind === "specialist"
          ? currentGroupCoordinator.origin
          : null;
      const commitsGatewayOrganizer = Boolean(
        commitsSpecialistOrganizer &&
        groupCoordinatorOriginScope?.kind === "specialist-location-gateway",
      );
      if (
        commitsGatewayOrganizer &&
        groupCoordinatorOriginScope?.kind === "specialist-location-gateway"
      ) {
        const ownerFingerprint = createGroupBookingCoordinatorFingerprint(
          currentGroupCoordinator,
        );
        const gatewayCommitOwnerIsCurrent = (
          expectedCoordinator: GroupBookingCoordinatorDraftV2,
        ) => {
          const pureOwnerIsCurrent = () =>
            !operation.controller.signal.aborted &&
            groupRouteOperationKeyRef.current === operation.routeOperationKey &&
            groupProviderGenerationRef.current === operation.generation &&
            groupCatalogTransactionOwnerRef.current === transaction.ownerKey;
          if (!pureOwnerIsCurrent()) return false;
          const live = readGroupBookingCoordinatorAuthorityPersisted(
            expectedCoordinator.draftId,
          );
          const current = Boolean(
            live &&
            haveExactSerializableStructure(live, expectedCoordinator) &&
            isRecoverableSpecialistOrganizerCoordinator(
              live,
              evaluatedAtEpochMs,
            ),
          );
          return pureOwnerIsCurrent() && current && pureOwnerIsCurrent();
        };
        const refreshed =
          ownerFingerprint &&
          specialistLocationGatewayOrganizerGroupCatalogLineProvider
            ? await refreshSpecialistLocationGatewayOrganizerGroupBookingCatalogPersisted(
                {
                  runtime: groupCoordinatorOriginScope.runtime,
                  owner: {
                    draftId: currentGroupCoordinator.draftId,
                    revision: currentGroupCoordinator.revision,
                    fingerprint: ownerFingerprint,
                  },
                  provider:
                    specialistLocationGatewayOrganizerGroupCatalogLineProvider,
                  organizerCatalog: specialistOrganizerGroupCatalog,
                  locationCatalog: locationAppointmentCatalog,
                  requestId: createGroupBookingCoordinatorDraftId(),
                  requestGeneration: operation.generation,
                  signal: operation.controller.signal,
                  evaluatedAtEpochMs,
                  selections: cloneServiceSelections(selections),
                  quantities: [...groupCatalogQuantities],
                  now: () => evaluatedAtEpochMs,
                  isOperationOwnerCurrent: gatewayCommitOwnerIsCurrent,
                },
              )
            : null;
        releaseGroupProviderOperation(operation);
        if (
          refreshed?.status === "aborted" ||
          refreshed?.status === "conflict"
        ) {
          return;
        }
        setGroupCatalogTransaction(null);
        setGroupCatalogSelections([]);
        setGroupCatalogQuantities([]);
        setGroupCoordinatorRefreshVersion((current) => current + 1);
        if (refreshed?.status !== "refreshed") {
          toast.error(t("serviceAddOnsModal.submissionError"));
        }
        return;
      }
      const result =
        commitsSpecialistOrganizer &&
        specialistOrigin &&
        specialistOrganizerGroupCatalogLineProvider &&
        specialistOrganizerGroupCatalogFingerprint &&
        specialistGroupCurrentCatalogFingerprint
          ? await resolveSpecialistOrganizerGroupBookingParticipantCatalog({
              provider: specialistOrganizerGroupCatalogLineProvider,
              request: {
                schemaVersion:
                  SPECIALIST_ORGANIZER_GROUP_BOOKING_CATALOG_REQUEST_SCHEMA_VERSION,
                kind: "specialist-organizer-group-booking-catalog-request",
                requestId: createGroupBookingCoordinatorDraftId(),
                requestGeneration: operation.generation,
                participantId: transaction.source.participantId,
                participantRole: "organizer",
                sourcePersonId: specialistOrigin.personId,
                sourceLocationId: specialistOrigin.sourceLocationId,
                sourceUserLocationId: specialistOrigin.sourceUserLocationId,
                selectedLocationId: specialistOrigin.selectedLocationId,
                selectedUserLocationId: specialistOrigin.selectedUserLocationId,
                organizerCatalogVersion:
                  specialistOrganizerGroupCatalog.workplace.catalogVersion,
                organizerCatalogFingerprint:
                  specialistOrganizerGroupCatalogFingerprint,
                locationId: transaction.source.locationId,
                catalogVersion: transaction.source.catalogVersion,
                catalogOwnerUserLocationId:
                  locationAppointmentCatalog.catalogAssignment.userLocationId,
                locationCatalogFingerprint:
                  specialistGroupCurrentCatalogFingerprint,
                selections: cloneServiceSelections(selections),
                quantities: [...groupCatalogQuantities],
                evaluatedAtEpochMs,
              },
              organizerCatalog: specialistOrganizerGroupCatalog,
              locationCatalog: locationAppointmentCatalog,
              signal: operation.controller.signal,
            })
          : await resolveLocationGroupBookingParticipantCatalog({
              provider: groupCatalogLineProvider,
              request: {
                schemaVersion:
                  LOCATION_GROUP_BOOKING_CATALOG_LINE_REQUEST_SCHEMA_VERSION,
                kind: "location-group-booking-catalog-line-request",
                requestId: createGroupBookingCoordinatorDraftId(),
                requestGeneration: operation.generation,
                participantId: transaction.source.participantId,
                locationId: transaction.source.locationId,
                catalogVersion: transaction.source.catalogVersion,
                catalogOwnerUserLocationId:
                  locationAppointmentCatalog.catalogAssignment.userLocationId,
                selections: cloneServiceSelections(selections),
                quantities: [...groupCatalogQuantities],
                evaluatedAtEpochMs,
              },
              catalog: locationAppointmentCatalog,
              signal: operation.controller.signal,
            });
      let catalogCommitNextDraft: GroupBookingCoordinatorDraftV2 | null = null;
      const catalogCommitOwnerIsCurrent = () => {
        const pureOwnerIsCurrent = () =>
          !operation.controller.signal.aborted &&
          groupRouteOperationKeyRef.current === operation.routeOperationKey &&
          groupProviderGenerationRef.current === operation.generation &&
          groupCatalogTransactionOwnerRef.current === transaction.ownerKey;
        if (!pureOwnerIsCurrent()) return false;
        try {
          const customerAccountId = getStoredCustomerAccountId();
          const anonymousSessionOwnerId =
            readGroupBookingAnonymousSessionOwnerId();
          if (!pureOwnerIsCurrent()) return false;
          const storedOwnerMatches = currentGroupCoordinator.customerAccountId
            ? customerAccountId === currentGroupCoordinator.customerAccountId
            : customerAccountId === null &&
              anonymousSessionOwnerId ===
                currentGroupCoordinator.anonymousSessionOwnerId;
          const live = readGroupBookingCoordinatorAuthorityPersisted(
            currentGroupCoordinator.draftId,
          );
          const liveBytesAreOwned = Boolean(
            live &&
            (haveExactSerializableStructure(live, currentGroupCoordinator) ||
              (catalogCommitNextDraft &&
                haveExactSerializableStructure(live, catalogCommitNextDraft))),
          );
          return Boolean(
            pureOwnerIsCurrent() &&
            storedOwnerMatches &&
            live &&
            liveBytesAreOwned &&
            (commitsSpecialistOrganizer
              ? isRecoverableSpecialistOrganizerCoordinator(
                  live,
                  evaluatedAtEpochMs,
                )
              : isCurrentGroupCoordinatorRouteAuthority(
                  live,
                  evaluatedAtEpochMs,
                )) &&
            pureOwnerIsCurrent(),
          );
        } catch {
          return false;
        }
      };
      const ownerIsCurrent = catalogCommitOwnerIsCurrent();
      let committed = false;
      let ownerDriftedDuringCommit = false;

      if (
        ownerIsCurrent &&
        result.status === "ready" &&
        areGroupCatalogServiceLinesCurrent(
          result.serviceLines,
          evaluatedAtEpochMs,
        )
      ) {
        const staged = reduceGroupBookingParticipantCatalogTransaction(
          transaction,
          { type: "stage-service-lines", serviceLines: result.serviceLines },
        );
        const requested = reduceGroupBookingParticipantCatalogTransaction(
          staged,
          { type: "request-commit" },
        );
        const intent =
          createGroupBookingParticipantCatalogCommitIntent(requested);
        const expected = readGroupBookingCoordinatorAuthorityPersisted(
          currentGroupCoordinator.draftId,
        );
        const applied =
          intent &&
          intent.kind !== "create-organizer-coordinator" &&
          expected &&
          haveExactSerializableStructure(expected, currentGroupCoordinator)
            ? applyGroupBookingParticipantCatalogCommitIntent({
                intent,
                draft: expected,
                updatedAtEpochMs: evaluatedAtEpochMs,
              })
            : null;
        const candidateCatalogIsCurrent = Boolean(
          applied?.ok &&
          (!commitsSpecialistOrganizer ||
            (specialistOrganizerGroupCatalogLineProvider &&
              validateCurrentSpecialistOrganizerGroupBookingCoordinatorCatalog({
                coordinator: applied.nextDraft,
                provider: specialistOrganizerGroupCatalogLineProvider,
                organizerCatalog: specialistOrganizerGroupCatalog,
                locationCatalog: locationAppointmentCatalog,
                evaluatedAtEpochMs,
              }).status === "current")),
        );
        catalogCommitNextDraft = applied?.ok ? applied.nextDraft : null;
        const persisted =
          applied?.ok && candidateCatalogIsCurrent
            ? replaceGroupBookingCoordinatorDraftOwnerAwarePersisted({
                expected: applied.expectedDraft,
                next: applied.nextDraft,
                isCurrentOwner: catalogCommitOwnerIsCurrent,
              })
            : ({ status: "invalid" } as const);
        committed = persisted.status === "replaced";
        ownerDriftedDuringCommit = persisted.status === "owner-drift";
      }

      releaseGroupProviderOperation(operation);

      if (!ownerIsCurrent || ownerDriftedDuringCommit) return;
      setGroupCatalogTransaction(null);
      setGroupCatalogSelections([]);
      setGroupCatalogQuantities([]);
      setGroupCoordinatorRefreshVersion((current) => current + 1);

      if (!committed) {
        toast.error(t("serviceAddOnsModal.submissionError"));
      }
    },
    [
      activeGroupCatalogTransaction,
      beginGroupProviderOperation,
      currentGroupCoordinator,
      groupCatalogLineProvider,
      groupCatalogQuantities,
      groupCoordinatorOriginScope,
      isLocationEntryPoint,
      isCurrentGroupCoordinatorRouteAuthority,
      isRecoverableSpecialistOrganizerCoordinator,
      locationAppointmentCatalog,
      releaseGroupProviderOperation,
      specialistGroupCurrentCatalogFingerprint,
      specialistLocationGatewayOrganizerGroupCatalogLineProvider,
      specialistOrganizerGroupCatalog,
      specialistOrganizerGroupCatalogFingerprint,
      specialistOrganizerGroupCatalogLineProvider,
      t,
    ],
  );

  const continueGroupRoster = useCallback(async () => {
    const coordinator = currentGroupCoordinator;
    const evaluatedAtEpochMs = readSpecialistServicesSafeEpochMs(
      servicesClockRef.current,
    );
    if (evaluatedAtEpochMs === null) {
      setIsContinuing(false);
      toast.error(t("serviceAddOnsModal.submissionError"));
      return;
    }

    if (
      coordinator &&
      groupCoordinatorRouteState.status === "accepted-handoff"
    ) {
      if (
        !groupCoordinatorOriginScope ||
        !routeGroupCatalogOwnerUserLocationId ||
        !groupLocationCatalogVersion ||
        !coordinator.customerAccountId ||
        getStoredCustomerAccountId() !== coordinator.customerAccountId
      ) {
        return;
      }
      const operation = beginGroupProviderOperation();
      if (!operation) return;
      const acceptedReconciliationInput = {
        originScope: groupCoordinatorOriginScope,
        coordinatorDraftId: coordinator.draftId,
        customerAccountId: coordinator.customerAccountId,
        locationId: routeLocationId,
        catalogVersion: groupLocationCatalogVersion,
        catalogOwnerUserLocationId: routeGroupCatalogOwnerUserLocationId,
        returnHref: groupCoordinatorReturnHref,
        evaluatedAtEpochMs,
      };
      const acceptedReconciliation =
        reconcileAcceptedGroupAppointmentForOriginScope(
          acceptedReconciliationInput,
        );
      if (acceptedReconciliation.status !== "ready") {
        releaseGroupProviderOperation(operation);
        setGroupCoordinatorRefreshVersion((current) => current + 1);
        return;
      }
      const ownerIsCurrent = Boolean(
        !operation.controller.signal.aborted &&
        groupRouteOperationKeyRef.current === operation.routeOperationKey &&
        groupProviderGenerationRef.current === operation.generation &&
        getStoredCustomerAccountId() === coordinator.customerAccountId &&
        acceptedReconciliation.appointment.draftId ===
          acceptedGroupAppointmentDraftId &&
        (groupCoordinatorOriginScope.kind === "location"
          ? isLocationGroupSelectSlotCompositionAvailable()
          : specialistGroupOriginScopeOwnsAcceptedHandoff({
              originScope: groupCoordinatorOriginScope,
              coordinator: acceptedReconciliation.coordinator,
              appointment: acceptedReconciliation.appointment,
              evaluatedAtEpochMs,
            })),
      );
      if (!ownerIsCurrent) {
        releaseGroupProviderOperation(operation);
        setGroupCoordinatorRefreshVersion((current) => current + 1);
        return;
      }
      const acceptedRetryHref = buildGroupAppointmentBookingDraftHandoffHref(
        "/book/select-slot",
        acceptedReconciliation.appointment.draftId,
      );
      if (!acceptedRetryHref) {
        releaseGroupProviderOperation(operation);
        setGroupCoordinatorRefreshVersion((current) => current + 1);
        return;
      }

      setIsContinuing(true);
      try {
        await Promise.resolve(router.push(acceptedRetryHref));
      } catch {
        const failureOwnsRoute =
          !operation.controller.signal.aborted &&
          groupRouteOperationKeyRef.current === operation.routeOperationKey &&
          groupProviderGenerationRef.current === operation.generation &&
          getStoredCustomerAccountId() === coordinator.customerAccountId;
        releaseGroupProviderOperation(operation);
        if (failureOwnsRoute) {
          setIsContinuing(false);
          toast.error(t("serviceAddOnsModal.submissionError"));
        }
      }
      return;
    }

    if (
      !coordinator ||
      !groupCoordinatorOriginScope ||
      !locationAppointmentCatalog ||
      !groupLocationCatalogVersion ||
      !activeGroupBookingPolicy ||
      (groupCoordinatorOriginScope.kind === "location"
        ? !isCurrentGroupCoordinatorRouteAuthority(
            coordinator,
            evaluatedAtEpochMs,
          )
        : !isRecoverableSpecialistOrganizerCoordinator(
            coordinator,
            evaluatedAtEpochMs,
          )) ||
      (groupCoordinatorOriginScope.kind === "location"
        ? !isLocationGroupSelectSlotCompositionAvailable()
        : !specialistGroupOriginScopeCanConsumeDownstream(
            groupCoordinatorOriginScope,
            evaluatedAtEpochMs,
          )) ||
      (groupCoordinatorOriginScope.kind === "location"
        ? !isLocationGroupRosterContinueReady({
            allowProviderRefresh: Boolean(frontendGroupServicesHost && isLocationGroupServicesEntryAvailable(evaluatedAtEpochMs)),
            coordinator,
            customerAccountId: groupCustomerOwnerState.customerAccountId,
            anonymousSessionOwnerId:
              groupCustomerOwnerState.anonymousSessionOwnerId,
            evaluatedAtEpochMs,
          })
        : coordinator.authorityState !== "building" ||
          coordinator.expiresAtEpochMs <= evaluatedAtEpochMs ||
          !isCurrentGroupBookingCoordinatorCapability(
            coordinator.capability,
            evaluatedAtEpochMs,
          ) ||
          coordinator.participants.length - 1 <
            coordinator.capability.minimumGuests ||
          coordinator.participants.some(
            (participant) => participant.serviceLines.length === 0,
          ) ||
          (coordinator.customerAccountId === null
            ? groupCustomerOwnerState.customerAccountId !== null ||
              groupCustomerOwnerState.anonymousSessionOwnerId !==
                coordinator.anonymousSessionOwnerId
            : groupCustomerOwnerState.customerAccountId !==
              coordinator.customerAccountId))
    ) {
      return;
    }

    const operation = beginGroupProviderOperation();
    if (!operation) return;
    const currentCustomerAccountId = getStoredCustomerAccountId();
    const currentAnonymousSessionOwnerId =
      readGroupBookingAnonymousSessionOwnerId();
    const routeOwnerIsCurrent =
      groupRouteOperationKeyRef.current === operation.routeOperationKey &&
      groupProviderGenerationRef.current === operation.generation &&
      currentCustomerAccountId === groupCustomerOwnerState.customerAccountId &&
      (coordinator.customerAccountId === null
        ? currentAnonymousSessionOwnerId ===
          groupCustomerOwnerState.anonymousSessionOwnerId
        : currentCustomerAccountId === coordinator.customerAccountId);

    if (!routeOwnerIsCurrent) {
      releaseGroupProviderOperation(operation);
      return;
    }

    if (coordinator.customerAccountId === null) {
      // Authentication resumes the exact roster only. Acceptance deliberately
      // requires a second explicit Continue after customer binding and current
      // exact evidence; a generic/cross-tab login is never a one-shot intent.
      const resumeTarget =
        groupCoordinatorOriginScope.kind === "specialist-location-gateway"
          ? null
          : createGroupBookingCoordinatorAuthResumeTarget(coordinator);
      const connectHref = buildConnectHref(currentServicesHref);
      const gatewayCanResume = Boolean(
        groupCoordinatorOriginScope.kind === "specialist-location-gateway" &&
        specialistLocationGatewayGroupSessionOwner &&
        resolvedSpecialistLocationGatewayGroupNavigationLineage &&
        specialistLocationGatewayGroupRuntimeContext.status === "ready" &&
        specialistLocationGatewayGroupStepOneRuntimeOwnsCoordinator({
          runtime: groupCoordinatorOriginScope.runtime,
          coordinator,
          requireBuildingAuthority: true,
          requireCurrentRuntime: true,
          evaluatedAtEpochMs,
        }),
      );
      const canResume =
        currentCustomerAccountId === null &&
        currentAnonymousSessionOwnerId ===
          coordinator.anonymousSessionOwnerId &&
        (gatewayCanResume ||
          (resumeTarget !== null &&
            isExpectedGroupAuthResumeTarget({
              target: resumeTarget,
              coordinator,
              originScope: groupCoordinatorOriginScope,
            }))) &&
        connectHref !== "/connect";

      if (!canResume) {
        releaseGroupProviderOperation(operation);
        toast.error(t("serviceAddOnsModal.submissionError"));
        return;
      }

      setIsContinuing(true);
      try {
        router.push(connectHref);
      } catch {
        setIsContinuing(false);
        releaseGroupProviderOperation(operation);
        toast.error(t("serviceAddOnsModal.submissionError"));
      }
      return;
    }

    let acceptanceCoordinator = coordinator;
    if (groupCoordinatorOriginScope.kind === "specialist-current-assignment") {
      const refreshFingerprint = createGroupBookingCoordinatorFingerprint(
        acceptanceCoordinator,
      );
      if (!refreshFingerprint || !specialistOrganizerGroupCatalogLineProvider) {
        releaseGroupProviderOperation(operation);
        return;
      }
      const refresh =
        await refreshSpecialistOrganizerGroupBookingCatalogPersisted({
          owner: {
            draftId: acceptanceCoordinator.draftId,
            revision: acceptanceCoordinator.revision,
            fingerprint: refreshFingerprint,
          },
          provider: specialistOrganizerGroupCatalogLineProvider,
          organizerCatalog: specialistOrganizerGroupCatalog,
          locationCatalog: locationAppointmentCatalog,
          requestId: createGroupBookingCoordinatorDraftId(),
          requestGeneration: operation.generation,
          signal: operation.controller.signal,
          evaluatedAtEpochMs,
          now: () => evaluatedAtEpochMs,
          isOperationOwnerCurrent: () =>
            !operation.controller.signal.aborted &&
            groupRouteOperationKeyRef.current === operation.routeOperationKey &&
            groupProviderGenerationRef.current === operation.generation &&
            getStoredCustomerAccountId() === coordinator.customerAccountId,
        });
      if (refresh.status !== "refreshed") {
        releaseGroupProviderOperation(operation);
        setGroupCoordinatorRefreshVersion((current) => current + 1);
        if (refresh.status !== "aborted") {
          toast.error(t("serviceAddOnsModal.submissionError"));
        }
        return;
      }
      acceptanceCoordinator = refresh.coordinator;
      const liveRefreshedCoordinator =
        readGroupBookingCoordinatorAuthorityPersisted(
          acceptanceCoordinator.draftId,
        );
      if (
        operation.controller.signal.aborted ||
        groupRouteOperationKeyRef.current !== operation.routeOperationKey ||
        groupProviderGenerationRef.current !== operation.generation ||
        !liveRefreshedCoordinator ||
        !haveExactSerializableStructure(
          liveRefreshedCoordinator,
          acceptanceCoordinator,
        ) ||
        !isCurrentGroupCoordinatorRouteAuthority(
          acceptanceCoordinator,
          evaluatedAtEpochMs,
        )
      ) {
        releaseGroupProviderOperation(operation);
        setGroupCoordinatorRefreshVersion((current) => current + 1);
        return;
      }
      if (refresh.serviceCoreStatus === "changed-service-core") {
        releaseGroupProviderOperation(operation);
        setGroupCoordinatorRefreshVersion((current) => current + 1);
        return;
      }
    } else if (
      groupCoordinatorOriginScope.kind === "specialist-location-gateway"
    ) {
      const refreshFingerprint = createGroupBookingCoordinatorFingerprint(
        acceptanceCoordinator,
      );
      if (
        !refreshFingerprint ||
        !specialistLocationGatewayOrganizerGroupCatalogLineProvider
      ) {
        releaseGroupProviderOperation(operation);
        return;
      }
      const refresh =
        await refreshSpecialistLocationGatewayOrganizerGroupBookingCatalogPersisted(
          {
            runtime: groupCoordinatorOriginScope.runtime,
            owner: {
              draftId: acceptanceCoordinator.draftId,
              revision: acceptanceCoordinator.revision,
              fingerprint: refreshFingerprint,
            },
            provider:
              specialistLocationGatewayOrganizerGroupCatalogLineProvider,
            organizerCatalog: specialistOrganizerGroupCatalog,
            locationCatalog: locationAppointmentCatalog,
            requestId: createGroupBookingCoordinatorDraftId(),
            requestGeneration: operation.generation,
            signal: operation.controller.signal,
            evaluatedAtEpochMs,
            now: () => evaluatedAtEpochMs,
            isOperationOwnerCurrent: () =>
              !operation.controller.signal.aborted &&
              groupRouteOperationKeyRef.current ===
                operation.routeOperationKey &&
              groupProviderGenerationRef.current === operation.generation &&
              getStoredCustomerAccountId() === coordinator.customerAccountId,
          },
        );
      if (refresh.status !== "refreshed") {
        releaseGroupProviderOperation(operation);
        setGroupCoordinatorRefreshVersion((current) => current + 1);
        if (refresh.status !== "aborted") {
          toast.error(t("serviceAddOnsModal.submissionError"));
        }
        return;
      }
      acceptanceCoordinator = refresh.coordinator;
      const liveRefreshedCoordinator =
        readGroupBookingCoordinatorAuthorityPersisted(
          acceptanceCoordinator.draftId,
        );
      if (
        operation.controller.signal.aborted ||
        groupRouteOperationKeyRef.current !== operation.routeOperationKey ||
        groupProviderGenerationRef.current !== operation.generation ||
        !liveRefreshedCoordinator ||
        !haveExactSerializableStructure(
          liveRefreshedCoordinator,
          acceptanceCoordinator,
        ) ||
        !isCurrentGroupCoordinatorRouteAuthority(
          acceptanceCoordinator,
          evaluatedAtEpochMs,
        )
      ) {
        releaseGroupProviderOperation(operation);
        setGroupCoordinatorRefreshVersion((current) => current + 1);
        return;
      }
      if (refresh.serviceCoreStatus === "changed-service-core") {
        releaseGroupProviderOperation(operation);
        setGroupCoordinatorRefreshVersion((current) => current + 1);
        return;
      }
    }

    const fingerprint = createGroupBookingCoordinatorFingerprint(
      acceptanceCoordinator,
    );

    if (
      !fingerprint ||
      currentCustomerAccountId !== acceptanceCoordinator.customerAccountId
    ) {
      releaseGroupProviderOperation(operation);
      return;
    }

    let result: Awaited<
      ReturnType<typeof acceptCurrentGroupBookingCoordinator>
    >;

    try {
      result = await acceptCurrentGroupBookingCoordinator({
        owner: {
          draftId: acceptanceCoordinator.draftId,
          revision: acceptanceCoordinator.revision,
          fingerprint,
        },
        provider: routeGroupCapabilityProvider,
        catalog: locationAppointmentCatalog,
        preliminaryRequestId: createGroupBookingCoordinatorDraftId(),
        exactGraphRequestId: createGroupBookingCoordinatorDraftId(),
        requestGeneration: operation.generation,
        signal: operation.controller.signal,
        downstreamMaximumGuests:
          activeGroupBookingPolicy.maximumParticipants - 1,
        downstreamMaximumParticipants:
          activeGroupBookingPolicy.maximumParticipants,
        appointmentDraftId: createBookingDraftId("appointment"),
        evaluatedAtEpochMs,
        now: () => evaluatedAtEpochMs,
        isOperationOwnerCurrent: () =>
          !operation.controller.signal.aborted &&
          groupRouteOperationKeyRef.current === operation.routeOperationKey &&
          groupProviderGenerationRef.current === operation.generation &&
          isLiveGroupCoordinatorSessionOwner(acceptanceCoordinator) &&
          (groupCoordinatorOriginScope.kind === "location" ||
            isCurrentGroupCoordinatorRouteAuthority(
              acceptanceCoordinator,
              evaluatedAtEpochMs,
            )),
      });
    } catch {
      const failureOwnsRoute =
        !operation.controller.signal.aborted &&
        groupRouteOperationKeyRef.current === operation.routeOperationKey &&
        groupProviderGenerationRef.current === operation.generation;

      releaseGroupProviderOperation(operation);
      if (failureOwnsRoute) {
        setGroupCoordinatorRefreshVersion((current) => current + 1);
        toast.error(t("serviceAddOnsModal.submissionError"));
      }
      return;
    }

    const ownerIsCurrent =
      !operation.controller.signal.aborted &&
      groupRouteOperationKeyRef.current === operation.routeOperationKey &&
      groupProviderGenerationRef.current === operation.generation &&
      getStoredCustomerAccountId() === coordinator.customerAccountId;

    if (!ownerIsCurrent) {
      releaseGroupProviderOperation(operation);
      return;
    }

    if (result.status !== "accepted") {
      releaseGroupProviderOperation(operation);
      setGroupCoordinatorRefreshVersion((current) => current + 1);
      if (result.status !== "aborted") {
        toast.error(t("serviceAddOnsModal.submissionError"));
      }
      return;
    }

    const acceptedCustomerAccountId = acceptanceCoordinator.customerAccountId;
    const acceptedReconciliationInput =
      acceptedCustomerAccountId &&
      routeGroupCatalogOwnerUserLocationId &&
      groupLocationCatalogVersion
        ? {
            originScope: groupCoordinatorOriginScope,
            coordinatorDraftId: acceptanceCoordinator.draftId,
            customerAccountId: acceptedCustomerAccountId,
            locationId: routeLocationId,
            catalogVersion: groupLocationCatalogVersion,
            catalogOwnerUserLocationId: routeGroupCatalogOwnerUserLocationId,
            returnHref: groupCoordinatorReturnHref,
            evaluatedAtEpochMs,
          }
        : null;
    const acceptedReconciliation = acceptedReconciliationInput
      ? reconcileAcceptedGroupAppointmentForOriginScope(
          acceptedReconciliationInput,
        )
      : null;
    const postAcceptOwnerIsCurrent = Boolean(
      !operation.controller.signal.aborted &&
      groupRouteOperationKeyRef.current === operation.routeOperationKey &&
      groupProviderGenerationRef.current === operation.generation &&
      acceptedCustomerAccountId !== null &&
      getStoredCustomerAccountId() === acceptedCustomerAccountId &&
      acceptedReconciliation?.status === "ready" &&
      acceptedReconciliation.appointment.draftId ===
        result.handoff.appointmentDraftId,
    );
    const destinationConsumerIsReady = Boolean(
      postAcceptOwnerIsCurrent &&
      acceptedReconciliation?.status === "ready" &&
      (groupCoordinatorOriginScope.kind === "location"
        ? isLocationGroupSelectSlotCompositionAvailable() &&
          result.handoff.consumerStatus ===
            GROUP_APPOINTMENT_V2_SELECT_SLOT_CONSUMER_STATUS
        : specialistGroupOriginScopeOwnsAcceptedHandoff({
            originScope: groupCoordinatorOriginScope,
            coordinator: acceptedReconciliation.coordinator,
            appointment: acceptedReconciliation.appointment,
            evaluatedAtEpochMs,
          })),
    );
    const destinationHref = destinationConsumerIsReady
      ? buildGroupAppointmentBookingDraftHandoffHref(
          "/book/select-slot",
          result.handoff.appointmentDraftId,
        )
      : null;

    if (!destinationHref) {
      releaseGroupProviderOperation(operation);
      setGroupCoordinatorRefreshVersion((current) => current + 1);
      toast.error(t("serviceAddOnsModal.submissionError"));
      return;
    }

    setIsContinuing(true);
    try {
      await Promise.resolve(router.push(destinationHref));
    } catch {
      setIsContinuing(false);
      releaseGroupProviderOperation(operation);
      setGroupCoordinatorRefreshVersion((current) => current + 1);
      toast.error(t("serviceAddOnsModal.submissionError"));
    }
  }, [
    acceptedGroupAppointmentDraftId,
    activeGroupBookingPolicy,
    beginGroupProviderOperation,
    groupCoordinatorReturnHref,
    currentGroupCoordinator,
    frontendGroupServicesHost,
    isLocationGroupServicesEntryAvailable,
    currentServicesHref,
    groupCoordinatorOriginScope,
    groupCustomerOwnerState,
    groupCoordinatorRouteState.status,
    groupLocationCatalogVersion,
    isCurrentGroupCoordinatorRouteAuthority,
    isLocationGroupSelectSlotCompositionAvailable,
    isRecoverableSpecialistOrganizerCoordinator,
    locationAppointmentCatalog,
    releaseGroupProviderOperation,
    routeGroupCatalogOwnerUserLocationId,
    routeGroupCapabilityProvider,
    routeLocationId,
    router,
    specialistOrganizerGroupCatalog,
    specialistOrganizerGroupCatalogLineProvider,
    specialistLocationGatewayOrganizerGroupCatalogLineProvider,
    resolvedSpecialistLocationGatewayGroupNavigationLineage,
    specialistLocationGatewayGroupRuntimeContext.status,
    specialistLocationGatewayGroupSessionOwner,
    t,
  ]);

  const handleSelectedPackagesChange = useCallback(
    async (packageIds: string[]) => {
      if (haveExactSerializableStructure(packageIds, requestedPackageIds)) {
        return true;
      }

      const nextHref = buildSelectionHref({
        packageIds,
        timeResumeDraftId: null,
      });

      if (!nextHref) {
        toast.error(t("serviceAddOnsModal.submissionError"));
        return false;
      }

      if (!(await discardSchedulingResumeHandoffsBeforeEdit())) {
        return false;
      }

      return replaceAfterServicesEdit(nextHref);
    },
    [
      buildSelectionHref,
      discardSchedulingResumeHandoffsBeforeEdit,
      replaceAfterServicesEdit,
      requestedPackageIds,
      t,
    ],
  );

  const handleCatalogViewChange = useCallback(
    async (nextCatalogView: SpecialistCatalogView) => {
      if (
        specialistResultsAppointmentIntentPointer !== null &&
        nextCatalogView !== "services"
      ) {
        return false;
      }
      if (
        specialistLocationGatewayServicesCartIsApplicable &&
        nextCatalogView !== "services"
      ) {
        return false;
      }
      if (nextCatalogView === catalogView) {
        return true;
      }

      if (specialistLocationGatewaySession) {
        if (
          gatewayCatalogTransitionRef.current ||
          specialistLocationGatewaySession.signal.aborted ||
          !isSpecialistLocationGatewaySessionCurrent(
            specialistLocationGatewaySession,
          )
        ) {
          return false;
        }
        if (!(await discardSchedulingResumeHandoffsBeforeEdit())) {
          return false;
        }
        const claimedAtEpochMs = readSpecialistLocationGatewaySessionEpoch(
          specialistLocationGatewaySession,
        );
        if (claimedAtEpochMs === null) {
          toast.error(t("serviceAddOnsModal.submissionError"));
          return false;
        }
        gatewayCatalogTransitionRef.current = true;
        const result = await transitionSpecialistLocationGatewayCatalog({
          currentIntent: specialistLocationGatewaySession.intent,
          resolution: specialistLocationGatewaySession.resolution,
          targetCatalog: nextCatalogView,
          attemptId: createBookingDraftId("commerce"),
          claimedAtEpochMs,
          provider: specialistLocationGatewaySession.provider,
          signal: specialistLocationGatewaySession.signal,
          now: specialistLocationGatewaySession.now,
          isCurrentOwner: () =>
            isSpecialistLocationGatewaySessionCurrent(
              specialistLocationGatewaySession,
            ),
        });
        gatewayCatalogTransitionRef.current = false;
        if (
          result.status !== "transitioned" ||
          !isSpecialistLocationGatewaySessionCurrent(
            specialistLocationGatewaySession,
          )
        ) {
          toast.error(t("serviceAddOnsModal.submissionError"));
          return false;
        }
        const nextHref =
          nextCatalogView === "packages"
            ? buildSpecialistLocationGatewayResolvedPackageServicesHref({
                resolution: result.resolution,
                resultsAppointmentIntentHandoffId:
                  specialistResultsAppointmentIntentPointer,
              })
            : buildSpecialistLocationGatewayResolvedAppointmentServicesHref({
                resolution: result.resolution,
                appointmentMode: "individual",
                resultsAppointmentIntentHandoffId:
                  specialistResultsAppointmentIntentPointer,
              });
        if (!nextHref || !replaceAfterServicesEdit(nextHref)) {
          toast.error(t("serviceAddOnsModal.submissionError"));
          return false;
        }
        return true;
      }

      const nextHref = buildSelectionHref({
        nextCatalogView,
        timeResumeDraftId: null,
      });

      if (!nextHref) {
        toast.error(t("serviceAddOnsModal.submissionError"));
        return false;
      }

      if (!(await discardSchedulingResumeHandoffsBeforeEdit())) {
        return false;
      }

      return replaceAfterServicesEdit(nextHref);
    },
    [
      buildSelectionHref,
      catalogView,
      discardSchedulingResumeHandoffsBeforeEdit,
      replaceAfterServicesEdit,
      specialistLocationGatewaySession,
      specialistLocationGatewayServicesCartIsApplicable,
      specialistResultsAppointmentIntentPointer,
      t,
    ],
  );

  const handleAppointmentModeChange = useCallback(
    async (nextAppointmentMode: AppointmentDraftMode) => {
      const normalizedMode =
        nextAppointmentMode === "group" && groupBookingAvailable
          ? "group"
          : "individual";

      if (
        specialistResultsAppointmentIntentPointer !== null &&
        normalizedMode !== "individual"
      ) {
        return false;
      }

      if (normalizedMode === appointmentMode) {
        return true;
      }

      const nextHref = buildSelectionHref({
        nextAppointmentMode: normalizedMode,
        nextCatalogView: normalizedMode === "group" ? "services" : undefined,
        timeResumeDraftId: null,
      });

      if (!nextHref) {
        toast.error(t("serviceAddOnsModal.submissionError"));
        return false;
      }

      if (!(await discardSchedulingResumeHandoffsBeforeEdit())) {
        return false;
      }

      if (!replaceAfterServicesEdit(nextHref)) {
        return false;
      }

      setGroupCatalogSelections([]);
      setGroupCatalogTransaction(null);
      return true;
    },
    [
      appointmentMode,
      buildSelectionHref,
      discardSchedulingResumeHandoffsBeforeEdit,
      groupBookingAvailable,
      replaceAfterServicesEdit,
      specialistResultsAppointmentIntentPointer,
      t,
    ],
  );

  const beginDirectSpecialistLocationGatewayEntry = useCallback(
    (
      input:
        | { kind: "package"; packageId: string }
        | { kind: "service"; serviceId: string },
    ) => {
      if (
        isLocationEntryPoint ||
        specialistLocationGatewaySession ||
        continueGuardRef.current ||
        specialistLocationGatewayActivation() !== true
      ) {
        return false;
      }

      const source = getFrontendFixtureSpecialistLocationGatewaySource({
        sourceLocationId: assignment.locationId,
        sourceUserLocationId: assignment.userLocationId,
      });
      const sourceIntent =
        input.kind === "package"
          ? getFrontendFixtureSpecialistLocationGatewayPackageIntent({
              sourceLocationId: assignment.locationId,
              sourceUserLocationId: assignment.userLocationId,
              sourcePackageId: input.packageId,
            })
          : getFrontendFixtureSpecialistLocationGatewayServiceIntent({
              sourceUserLocationId: assignment.userLocationId,
              sourceServiceId: input.serviceId,
            });
      const sourceService =
        input.kind === "service"
          ? (services.find(({ serviceId }) => serviceId === input.serviceId) ??
            null)
          : null;
      const sourcePackage =
        input.kind === "package"
          ? (packages.find(({ packageId }) => packageId === input.packageId) ??
            null)
          : null;
      const sourceOffering =
        input.kind === "service"
          ? offerings.filter(({ serviceId }) => serviceId === input.serviceId)
          : [];
      const sourceAction =
        input.kind === "package"
          ? ("package-reserve" as const)
          : sourceService?.kind === "bundle"
            ? ("service-bundle-reserve" as const)
            : ("service-reserve" as const);
      const intentMatchesVisibleSource =
        source &&
        source.personId === person?.personId &&
        source.sourceLocationId === assignment.locationId &&
        source.sourceUserLocationId === assignment.userLocationId &&
        source.sourceCatalogVersion === workplace.catalogVersion &&
        source.canonicalReturnHref === canonicalProfilePath &&
        (input.kind === "package"
          ? Boolean(
              sourcePackage &&
              sourceIntent?.kind === "package-purchase" &&
              sourceIntent.sourcePackageId === sourcePackage.packageId,
            )
          : Boolean(
              sourceService &&
              sourceOffering.length === 1 &&
              (sourceIntent?.kind === "service" ||
                sourceIntent?.kind === "service-bundle") &&
              sourceIntent.sourceOfferingId === sourceOffering[0]!.offeringId &&
              (sourceService.kind === "bundle") ===
                (sourceIntent.kind === "service-bundle"),
            ));

      if (!source || !sourceIntent || !intentMatchesVisibleSource) {
        toast.error(t("serviceAddOnsModal.submissionError"));
        return true;
      }

      const customerAccountId = getStoredCustomerAccountId();
      if (customerAccountId === undefined) {
        toast.error(t("serviceAddOnsModal.submissionError"));
        return true;
      }
      const evaluatedAtEpochMs = readSpecialistServicesSafeEpochMs(
        servicesClockRef.current,
      );
      if (evaluatedAtEpochMs === null) {
        toast.error(t("serviceAddOnsModal.submissionError"));
        return true;
      }
      const generation = directGatewayGenerationRef.current + 1;
      directGatewayGenerationRef.current = generation;
      continueGuardRef.current = true;
      setIsContinuing(true);
      const result = beginSpecialistLocationGatewayEntry({
        gatewayProviderId:
          FRONTEND_FIXTURE_SPECIALIST_LOCATION_GATEWAY_PROVIDER_ID,
        gatewayProviderRevision:
          FRONTEND_FIXTURE_SPECIALIST_LOCATION_GATEWAY_PROVIDER_REVISION,
        customerAccountId,
        locale,
        generation,
        sourceAction,
        source,
        intent: sourceIntent,
        now: () => evaluatedAtEpochMs,
        isCurrentOwner: () => {
          const pureOwnerIsCurrent = () =>
            directGatewayGenerationRef.current === generation;
          if (!pureOwnerIsCurrent()) return false;
          const storedCustomerAccountId = getStoredCustomerAccountId();
          return (
            pureOwnerIsCurrent() &&
            storedCustomerAccountId === customerAccountId
          );
        },
      });
      if (result.status !== "ready") {
        continueGuardRef.current = false;
        setIsContinuing(false);
        toast.error(t("serviceAddOnsModal.submissionError"));
        return true;
      }

      try {
        router.push(result.href);
      } catch {
        removeSpecialistLocationGatewayIntentStrict(result.intent);
        continueGuardRef.current = false;
        setIsContinuing(false);
        toast.error(t("serviceAddOnsModal.submissionError"));
      }
      return true;
    },
    [
      assignment.locationId,
      assignment.userLocationId,
      canonicalProfilePath,
      isLocationEntryPoint,
      locale,
      offerings,
      packages,
      person?.personId,
      router,
      services,
      specialistLocationGatewaySession,
      specialistLocationGatewayActivation,
      t,
      workplace.catalogVersion,
    ],
  );

  const continueWithPackages = useCallback(
    (packageIds: readonly string[]) => {
      if (postCommitNavigationRecovery) {
        void retryPostCommitNavigation();
        return;
      }
      const packageId = packageIds.length === 1 ? packageIds[0] : null;
      const selectionKey = packageId ?? "";

      if (
        !packageId ||
        continueGuardRef.current ||
        latestPackageDraftRef.current?.selectionKey === selectionKey
      ) {
        return;
      }

      if (
        !specialistLocationGatewaySession &&
        !isLocationEntryPoint &&
        beginDirectSpecialistLocationGatewayEntry({
          kind: "package",
          packageId,
        })
      ) {
        return;
      }

      if (specialistLocationGatewaySession) {
        const authority =
          specialistLocationGatewaySession.resolution.selection.authority;
        const mappedPackage =
          authority.kind === "package" ? authority.mappedPackage : null;
        if (
          authority.kind !== "package" ||
          (specialistLocationGatewaySession.resolution.intent.kind ===
            "package-purchase" &&
            mappedPackage?.destinationPackageId !== packageId) ||
          !isSpecialistLocationGatewaySessionCurrent(
            specialistLocationGatewaySession,
          )
        ) {
          toast.error(t("serviceAddOnsModal.submissionError"));
          return;
        }

        continueGuardRef.current = true;
        setIsContinuing(true);
        void (async () => {
          const packageItem = packages.find(
            (candidate) => candidate.packageId === packageId,
          );
          const nestedQueryState =
            serializeSpecialistServiceSelectionQueryState(requestedServices);
          const serviceIds = requestedServices.map(
            ({ serviceId }) => serviceId,
          );
          const serviceVariants = requestedServices.flatMap(
            ({ serviceId, variantId }) =>
              variantId ? [{ serviceId, variantId }] : [],
          );
          const draftId = createBookingDraftId("package-purchase");
          const draft =
            packageItem && isPackageCatalogDefinitionV1(packageItem) && nestedQueryState
              ? createPackagePurchaseDraft({
                  draftId,
                  entryPointKind: "specialist-packages",
                  packageItem,
                  returnSelection: {
                    serviceIds,
                    serviceVariants,
                    serviceAddOns: nestedQueryState.serviceAddOns || null,
                    serviceBundleAddOns:
                      nestedQueryState.serviceBundleAddOns || null,
                  },
                })
              : null;
          const transaction = draft
            ? createSpecialistLocationGatewayPackageDestinationTransaction(
                draft,
              )
            : null;
          const revalidated =
            await refreshSpecialistLocationGatewayServicesSession(
              specialistLocationGatewaySession,
            );
          const claimedAtEpochMs = readSpecialistLocationGatewaySessionEpoch(
            specialistLocationGatewaySession,
          );
          if (
            !draft ||
            !transaction ||
            !revalidated ||
            claimedAtEpochMs === null
          ) {
            continueGuardRef.current = false;
            setIsContinuing(false);
            toast.error(t("serviceAddOnsModal.submissionError"));
            return;
          }
          const committed =
            await commitSpecialistLocationGatewayNavigationLineage({
              resolution: specialistLocationGatewaySession.resolution,
              revalidatedResolution: revalidated.resolution,
              attemptId: createBookingDraftId("package-purchase"),
              destinationKind: "package",
              destinationDraftId: transaction.destinationDraftId,
              expectedDestinationFingerprint:
                transaction.expectedDestinationFingerprint,
              claimedAtEpochMs,
              now: specialistLocationGatewaySession.now,
              signal: specialistLocationGatewaySession.signal,
              isCurrentOwner: () =>
                isSpecialistLocationGatewaySessionCurrent(
                  specialistLocationGatewaySession,
                ),
              destinationDraft: transaction.transaction,
            });
          if (committed.status !== "committed") {
            continueGuardRef.current = false;
            setIsContinuing(false);
            toast.error(t("serviceAddOnsModal.submissionError"));
            return;
          }
          const reviewHref = buildPackagePurchaseDraftHandoffHref(
            "/purchase/package/review",
            committed.lineage.destinationDraftId,
          );
          if (!reviewHref) {
            continueGuardRef.current = false;
            setIsContinuing(false);
            toast.error(t("serviceAddOnsModal.submissionError"));
            return;
          }
          const navigationTarget = {
            lineage: committed.lineage,
            href: reviewHref,
            navigation: "push" as const,
          };
          setPostCommitNavigationRecovery(navigationTarget);
          latestPackageDraftRef.current = {
            draftId: committed.lineage.destinationDraftId,
            selectionKey,
          };
          try {
            await Promise.resolve(router.push(reviewHref));
            continueGuardRef.current = false;
            setIsContinuing(false);
          } catch {
            continueGuardRef.current = false;
            setIsContinuing(false);
            toast.error(t("serviceAddOnsModal.submissionError"));
          }
        })();
        return;
      }

      const catalogPackage = packages.find(candidate => candidate.packageId === packageId);
      const currentPackage = catalogPackage && isPackageCatalogDefinitionV1(catalogPackage) && isLocationEntryPoint
        ? createFrontendPackagePurchaseDefinitionV2(catalogPackage) : catalogPackage;
      if (currentPackage && isPackageCatalogDefinitionV2(currentPackage)) {
        if (!isLocationEntryPoint) return;
        const customerAccountId = getStoredCustomerAccountId();
        const nested = serializeSpecialistServiceSelectionQueryState(requestedServices);
        if (customerAccountId === undefined || !nested) {
          toast.error(t("serviceAddOnsModal.submissionError"));
          return;
        }
        const sourceHref = currentServicesHref;
        const isCurrentOwner = () => packageEntryMountedRef.current &&
          packageEntryRouteRef.current.routeKind === "location-services" &&
          packageEntryRouteRef.current.href === sourceHref &&
          `${window.location.pathname}${window.location.search}` === sourceHref &&
          getStoredCustomerAccountId() === customerAccountId;
        const source = { isCurrentOwner,
          getCurrentPackages: () => {
            const matches = packageEntryRouteRef.current.packages.filter(item => item.packageId === packageId);
            if (matches.length !== 1) return [];
            return getFrontendPackagePurchaseV2PublicationForEntry(matches[0]!)?.packages ?? [];
          } };
        const cached = packageEntryV2Ref.current;
        const entry = cached?.sourceHref === sourceHref && cached.customerAccountId === customerAccountId &&
          cached.entry.draft.packageId === packageId
          ? (packagePurchaseLocationEntryV2IsCurrent(cached.entry, source) ? cached.entry : null)
          : preparePackagePurchaseLocationEntryV2({ ...source,
              draftId: createBookingDraftId("package-purchase"), packageItem: currentPackage,
              returnSelection: { serviceIds: requestedServices.map(item => item.serviceId),
                serviceVariants: requestedServices.flatMap(item => item.variantId ? [{ serviceId: item.serviceId, variantId: item.variantId }] : []),
                serviceAddOns: nested.serviceAddOns || null, serviceBundleAddOns: nested.serviceBundleAddOns || null } });
        if (!entry) {
          toast.error(t("serviceAddOnsModal.submissionError"));
          return;
        }
        packageEntryV2Ref.current = { entry, sourceHref, customerAccountId };
        continueGuardRef.current = true;
        setIsContinuing(true);
        void (async () => {
          try {
            if (!packagePurchaseLocationEntryV2IsCurrent(entry, source) ||
              !stagePackagePurchaseNavigationCompanion(entry.draft, sourceHref, isCurrentOwner) ||
              !stagePackageGiftCompanion(entry.draft, sourceHref, isCurrentOwner)) throw new Error("Package entry changed");
            await Promise.resolve(router.push(entry.href));
          } catch {
            // Keep the exact persisted entry. A retry only repeats navigation.
            if (packageEntryMountedRef.current) {
              continueGuardRef.current = false;
              setIsContinuing(false);
              toast.error(t("serviceAddOnsModal.submissionError"));
            }
          }
        })();
        return;
      }

      continueGuardRef.current = true;
      const packageItem = packages.find(
        (candidate) => candidate.packageId === packageId,
      );
      const nestedQueryState =
        serializeSpecialistServiceSelectionQueryState(requestedServices);
      const serviceIds = requestedServices.map(({ serviceId }) => serviceId);
      const serviceVariants = requestedServices.flatMap(
        ({ serviceId, variantId }) =>
          variantId ? [{ serviceId, variantId }] : [],
      );
      const draftId = createBookingDraftId("package-purchase");
      const draft =
        packageItem && isPackageCatalogDefinitionV1(packageItem) && nestedQueryState
          ? createPackagePurchaseDraft({
              draftId,
              entryPointKind: isLocationEntryPoint
                ? "location-packages"
                : "specialist-packages",
              packageItem,
              returnSelection: {
                serviceIds,
                serviceVariants,
                serviceAddOns: nestedQueryState.serviceAddOns || null,
                serviceBundleAddOns:
                  nestedQueryState.serviceBundleAddOns || null,
              },
            })
          : null;
      const storedDraft = draft ? storePackagePurchaseDraft(draft) : null;
      const reviewHref = storedDraft
        ? buildPackagePurchaseDraftHandoffHref(
            "/purchase/package/review",
            storedDraft.draftId,
          )
        : null;

      if (!storedDraft || !reviewHref) {
        if (storedDraft) {
          ensurePackagePurchaseDraftRemoved(storedDraft.draftId);
        }
        continueGuardRef.current = false;
        toast.error(t("serviceAddOnsModal.submissionError"));
        return;
      }

      const sourceHref = currentServicesHref;
      const customerAccountId = getStoredCustomerAccountId();
      const isCurrentOwner = () => packageEntryMountedRef.current &&
        packageEntryRouteRef.current.routeKind === "location-services" &&
        packageEntryRouteRef.current.href === sourceHref &&
        `${window.location.pathname}${window.location.search}` === sourceHref &&
        getStoredCustomerAccountId() === customerAccountId;
      if (!stagePackageGiftCompanion(storedDraft, sourceHref, isCurrentOwner)) {
        if (ensurePackagePurchaseDraftRemoved(storedDraft.draftId)) {
          retirePackageGiftCompanion(storedDraft, isCurrentOwner);
        }
        continueGuardRef.current = false;
        toast.error(t("serviceAddOnsModal.submissionError"));
        return;
      }

      latestPackageDraftRef.current = {
        draftId: storedDraft.draftId,
        selectionKey,
      };
      setIsContinuing(true);

      try {
        router.push(reviewHref);
      } catch {
        if (ensurePackagePurchaseDraftRemoved(storedDraft.draftId)) {
          retirePackageGiftCompanion(storedDraft, isCurrentOwner);
        }
        latestPackageDraftRef.current = null;
        continueGuardRef.current = false;
        setIsContinuing(false);
        toast.error(t("serviceAddOnsModal.submissionError"));
      }
    },
    [
      isLocationEntryPoint,
      currentServicesHref,
      beginDirectSpecialistLocationGatewayEntry,
      packages,
      postCommitNavigationRecovery,
      requestedServices,
      retryPostCommitNavigation,
      router,
      specialistLocationGatewaySession,
      t,
    ],
  );

  const handleServiceSelectionInteraction = useCallback(
    (change: {
      action: "deselected" | "selected";
      selectedServiceCount: number;
      serviceId: string;
    }) => {
      if (isLocationEntryPoint || !analyticsContext) {
        return;
      }

      emitSpecialistAnalyticsEvent({
        ...analyticsContext,
        ...change,
        name: "specialist_service_selection_changed",
        offeringId: offeringByServiceId.get(change.serviceId)?.offeringId,
      });
    },
    [analyticsContext, isLocationEntryPoint, offeringByServiceId],
  );

  const continueToBooking = useCallback(
    async (selections: readonly SpecialistServiceSelection[]) => {
      if (packageSelectionPublicationRef.current) return;
      if (parseMembershipSessionBookingHandoff(new URLSearchParams(searchString)).status !== "absent") {
        if (continueGuardRef.current || !locationAppointmentCatalog || readCurrentMembershipSessionServicesEntry().status !== "ready") return;
        continueGuardRef.current = true;
        const continued = continueMembershipSessionServices({ pathname, searchParams: new URLSearchParams(searchString),
          locationCatalog: locationAppointmentCatalog, selections, nowEpochMs: readSpecialistServicesSafeEpochMs(servicesClockRef.current) ?? Number.NaN,
          navigate: (href) => router.push(href) });
        continueGuardRef.current = false;
        if (!continued) toast.error(tAll("activity.membership.bookingUnavailable"));
        return;
      }
      if (postCommitNavigationRecovery) {
        void retryPostCommitNavigation();
        return;
      }
      if (hasIncompleteDirectSpecialistServiceRequest) {
        toast.error(t("serviceAddOnsModal.submissionError"));
        return;
      }
      if (
        !specialistLocationGatewaySession &&
        !isLocationEntryPoint &&
        !timeResumeRequestKey &&
        !groupCoordinatorPointerIsPresent &&
        specialistLocationGatewayActivation() === true
      ) {
        if (selections.length !== 1) {
          toast.error(t("serviceAddOnsModal.submissionError"));
          return;
        }
        beginDirectSpecialistLocationGatewayEntry({
          kind: "service",
          serviceId: selections[0]!.serviceId,
        });
        return;
      }

      if (
        resultsServicesEntryIsRequired &&
        resultsServicesEntryHydration.status !== "ready"
      ) {
        toast.error(t("serviceAddOnsModal.submissionError"));
        return;
      }
      if (
        packageSessionServicesEntryIsRequired &&
        packageSessionServicesEntryHydration.status !== "ready"
      ) {
        toast.error(t("serviceAddOnsModal.submissionError"));
        return;
      }

      let servicesCartCommitLease: SpecialistLocationGatewayServicesCartCommitLease | null =
        null;
      if (specialistLocationGatewayServicesCartIsApplicable) {
        if (servicesCartContinueGuardRef.current) return;
        servicesCartContinueGuardRef.current = true;
        try {
          servicesCartCommitLease =
            await revalidateSpecialistLocationGatewayServicesCartForContinue(
              selections,
            );
        } catch {
          servicesCartCommitLease = null;
        } finally {
          servicesCartContinueGuardRef.current = false;
        }
        if (
          !servicesCartCommitLease ||
          !servicesCartCommitLease.isCurrentOwner()
        ) {
          toast.error(t("serviceAddOnsModal.submissionError"));
          return;
        }
      }

      if (appointmentMode === "group") {
        const resultsServicesEntry = readCurrentResultsServicesEntry();
        if (
          shouldBlockResultsServicesGroupContinuation(
            resultsServicesEntry.status,
          )
        ) {
          toast.error(t("serviceAddOnsModal.submissionError"));
          return;
        }
        await continueInitialGroupOrganizerCatalog(
          selections,
          servicesCartCommitLease,
        );
        return;
      }

      const organizerSelections = selections;
      const serviceIds = organizerSelections.map(({ serviceId }) => serviceId);
      const selectedServiceCount = serviceIds.length;

      if (
        serviceIds.length === 0 ||
        isTimeResumeBlocking ||
        servicesEditCleanupPendingRef.current ||
        isContinuing ||
        continueGuardRef.current
      ) {
        return;
      }

      const refreshedTimeResume = timeResumeRequestKey
        ? resolveCurrentTimeResume()
        : null;

      if (timeResumeRequestKey && refreshedTimeResume?.status !== "restored") {
        if (refreshedTimeResume) {
          publishTimeResumeForHandoff(
            timeResumeHandoffKey,
            refreshedTimeResume,
          );
        }
        toast.error(t("serviceAddOnsModal.submissionError"));
        return;
      }

      if (
        refreshedTimeResume?.status === "restored" &&
        (!haveExactSerializableStructure(
          organizerSelections,
          requestedServices,
        ) ||
          refreshedTimeResume.sourceDraft.mode !== appointmentMode)
      ) {
        toast.error(t("serviceAddOnsModal.submissionError"));
        return;
      }

      const nestedQueryState =
        serializeSpecialistServiceSelectionQueryState(organizerSelections);

      if (!nestedQueryState) {
        toast.error(t("serviceAddOnsModal.submissionError"));
        return;
      }

      const initialResultsServicesEntry =
        refreshedTimeResume?.status === "restored"
          ? ({ status: "none" } as const)
          : resultsServicesEntryIsRequired
            ? resultsServicesEntryHydration.status === "ready"
              ? {
                  status: "ready" as const,
                  entry: resultsServicesEntryHydration.entry,
                  nowEpochMs: resultsServicesEntryHydration.nowEpochMs,
                }
              : ({ status: "unavailable" } as const)
            : readCurrentResultsServicesEntry();
      if (initialResultsServicesEntry.status === "unavailable") {
        toast.error(t("serviceAddOnsModal.submissionError"));
        return;
      }
      const initialPackageSessionEntry = packageSessionServicesEntryIsRequired
        ? readCurrentPackageSessionServicesEntry()
        : ({ status: "none" } as const);
      const packageRequiredVariantId = initialPackageSessionEntry.status === "ready" && initialPackageSessionEntry.intent.schemaVersion === 2 && initialPackageSessionEntry.intent.variant.kind === "variant"
        ? initialPackageSessionEntry.intent.variant.variantId : null;
      if (
        initialPackageSessionEntry.status === "unavailable" ||
        (initialPackageSessionEntry.status === "ready" &&
          (initialResultsServicesEntry.status === "ready" ||
            !(initialPackageSessionEntry.intent.schemaVersion === 2
              ? organizerSelections.filter(selection => selection.serviceId === initialPackageSessionEntry.intent.serviceId && selection.variantId === packageRequiredVariantId).length === 1
              : hasExactUnconfiguredPackageSessionServiceSelection({
              serviceId: initialPackageSessionEntry.intent.serviceId,
              selections: organizerSelections,
            }))))
      ) {
        toast.error(t("serviceAddOnsModal.submissionError"));
        return;
      }

      let resumedPackageSessionPreference: AppointmentPackageSessionPreferenceV1 | AppointmentPackageSessionPreferenceV2 | null =
        null;
      if (refreshedTimeResume?.status === "restored") {
        const packageOwner = initialPackageSessionEntry.status === "ready"
          ? readPackageSessionBookingUiCustomerOwner(initialPackageSessionEntry.intent.providerId)
          : null;
        const expectedCustomerAccountId = packageOwner?.status === "ready"
          ? packageOwner.customerAccountId : getStoredCustomerAccountId();
        const resumeEpochMs = readSpecialistServicesSafeEpochMs(
          servicesClockRef.current,
        );
        if (resumeEpochMs === null) {
          toast.error(t("serviceAddOnsModal.submissionError"));
          return;
        }
        const packagePreferencePresence =
          inspectAppointmentPackageSessionPreferencePresenceStrict(
            refreshedTimeResume.sourceDraft,
            resumeEpochMs,
          );
        if (packagePreferencePresence === "unreadable") {
          toast.error(t("serviceAddOnsModal.submissionError"));
          return;
        }
        if (
          initialPackageSessionEntry.status === "ready" ||
          packagePreferencePresence === "present"
        ) {
          if (typeof expectedCustomerAccountId !== "string") {
            toast.error(t("serviceAddOnsModal.submissionError"));
            return;
          }
          const routeRead = readAppointmentPackageSessionForRouteStrict({
            sourceAppointmentDraft: refreshedTimeResume.sourceDraft,
            searchParams: new URLSearchParams(searchString),
            allowedRouteQueryKeys:
              LOCATION_PACKAGE_SESSION_SERVICES_ROUTE_QUERY_KEYS,
            expectedCustomerAccountId,
            nowEpochMs: resumeEpochMs,
          });
          if (
            routeRead.status !== "ready" ||
            initialPackageSessionEntry.status !== "ready" ||
            routeRead.preference.packageIntentOwnerKey !==
              initialPackageSessionEntry.intent.ownerKey
          ) {
            toast.error(t("serviceAddOnsModal.submissionError"));
            return;
          }
          resumedPackageSessionPreference = routeRead.preference;
        }
      }

      if (specialistLocationGatewaySession) {
        const authority =
          specialistLocationGatewaySession.resolution.selection.authority;
        if (
          authority.kind !== "appointment" ||
          authority.individualCapability !== true ||
          (specialistLocationGatewayServicesCartIsApplicable &&
            (!servicesCartCommitLease ||
              servicesCartCommitLease.handoffKey !==
                specialistLocationGatewayServicesCartHandoffKey ||
              !servicesCartCommitLease.isCurrentOwner())) ||
          !isSpecialistLocationGatewaySessionCurrent(
            specialistLocationGatewaySession,
          )
        ) {
          toast.error(t("serviceAddOnsModal.submissionError"));
          return;
        }
        continueGuardRef.current = true;
        setIsContinuing(true);
        void (async () => {
          const draftId = createSpecialistServicesAppointmentDraftId(
            initialResultsServicesEntry.status === "ready",
          );
          const draftResult = createSpecialistServicesAppointmentDraft({
            draftId,
            mode: "individual",
            assignment,
            workplace,
            services,
            offerings,
            selections: organizerSelections,
            groupGuests: [],
          });
          const transaction = draftResult.ok
            ? createSpecialistLocationGatewayAppointmentDestinationTransaction(
                draftResult.draft,
              )
            : null;
          const revalidated =
            await refreshSpecialistLocationGatewayServicesSession(
              specialistLocationGatewaySession,
            );
          const claimedAtEpochMs = readSpecialistLocationGatewaySessionEpoch(
            specialistLocationGatewaySession,
          );
          if (
            !draftResult.ok ||
            !transaction ||
            !revalidated ||
            claimedAtEpochMs === null
          ) {
            continueGuardRef.current = false;
            setIsContinuing(false);
            toast.error(t("serviceAddOnsModal.submissionError"));
            return;
          }
          let stagedResultsPreference: AppointmentResultsEntryPreferenceV1 | null =
            null;
          if (initialResultsServicesEntry.status === "ready") {
            const currentResultsServicesEntry =
              revalidateCurrentResultsServicesEntry(
                initialResultsServicesEntry.entry,
              );
            if (currentResultsServicesEntry.status !== "ready") {
              continueGuardRef.current = false;
              setIsContinuing(false);
              toast.error(t("serviceAddOnsModal.submissionError"));
              return;
            }
            const preferenceResult =
              bindResultsServicesEntryToAppointmentDraftStrict({
                entry: currentResultsServicesEntry.entry,
                sourceAppointmentDraft: draftResult.draft,
                expectedCustomerAccountId:
                  currentResultsServicesEntry.entry.intent.customerAccountId,
                expectedLocale: locale,
                createdAtEpochMs: currentResultsServicesEntry.nowEpochMs,
              });
            if (preferenceResult.status !== "created") {
              continueGuardRef.current = false;
              setIsContinuing(false);
              toast.error(t("serviceAddOnsModal.submissionError"));
              return;
            }
            stagedResultsPreference = preferenceResult.draft;
          }

          let committed: Awaited<
            ReturnType<typeof commitSpecialistLocationGatewayNavigationLineage>
          >;
          try {
            committed =
              await runSpecialistGatewayCommitWithAmbiguousFailureQuarantine(
                () =>
                  commitSpecialistLocationGatewayNavigationLineage({
                    resolution: specialistLocationGatewaySession.resolution,
                    revalidatedResolution: revalidated.resolution,
                    attemptId: createBookingDraftId("appointment"),
                    destinationKind: "individual",
                    destinationDraftId: transaction.destinationDraftId,
                    expectedDestinationFingerprint:
                      transaction.expectedDestinationFingerprint,
                    claimedAtEpochMs,
                    now: specialistLocationGatewaySession.now,
                    signal: specialistLocationGatewaySession.signal,
                    isCurrentOwner: () =>
                      isSpecialistLocationGatewaySessionCurrent(
                        specialistLocationGatewaySession,
                      ) &&
                      (!servicesCartCommitLease ||
                        servicesCartCommitLease.isCurrentOwner()),
                    destinationDraft: transaction.transaction,
                  }),
              );
          } catch {
            // The commit API may throw after mutating the SLG lineage. Because
            // that point cannot distinguish a pre-write failure from a partial
            // cross-domain commit, neither the staged Results companion nor
            // the gateway state may remain actionable.
            continueGuardRef.current = false;
            setIsContinuing(false);
            toast.error(t("serviceAddOnsModal.submissionError"));
            return;
          }
          if (committed.status !== "committed") {
            if (
              stagedResultsPreference &&
              shouldCompensateResultsPreferenceAfterGatewayCommit(
                committed.status,
              )
            ) {
              const removed = removeAppointmentResultsEntryPreferenceStrict(
                stagedResultsPreference,
              );
              if (removed.status !== "removed" && removed.status !== "absent") {
                quarantineCustomerOwnedSessionStorage();
              }
            }
            continueGuardRef.current = false;
            setIsContinuing(false);
            toast.error(t("serviceAddOnsModal.submissionError"));
            return;
          }
          const params = new URLSearchParams({
            location: assignment.locationId,
            [BOOKING_FLOW_ORIGIN_PARAM]: BOOKING_FLOW_ORIGIN_SPECIALIST,
            staff: assignment.userLocationId,
            booking_draft_id: committed.lineage.destinationDraftId,
            booking_draft_version: String(APPOINTMENT_DRAFT_SCHEMA_VERSION),
          });
          appendBookingFlowServiceParams(
            params,
            serviceIds,
            organizerSelections.flatMap(({ serviceId, variantId }) =>
              variantId ? [{ serviceId, variantId }] : [],
            ),
          );
          if (nestedQueryState.serviceAddOns) {
            params.set(
              SPECIALIST_SERVICE_ADD_ONS_PARAM,
              nestedQueryState.serviceAddOns,
            );
          }
          if (nestedQueryState.serviceBundleAddOns) {
            params.set(
              SPECIALIST_SERVICE_BUNDLE_ADD_ONS_PARAM,
              nestedQueryState.serviceBundleAddOns,
            );
          }
          const baseDestinationHref = `/book/select-slot?${params.toString()}`;
          const destinationHref = stagedResultsPreference
            ? appendAppointmentResultsEntryHandoffToHref({
                href: baseDestinationHref,
                handoffId: stagedResultsPreference.handoffId,
              })
            : baseDestinationHref;
          if (!destinationHref) {
            continueGuardRef.current = false;
            setIsContinuing(false);
            toast.error(t("serviceAddOnsModal.submissionError"));
            return;
          }
          const navigationTarget = {
            lineage: committed.lineage,
            href: destinationHref,
            navigation: "push" as const,
          };
          setPostCommitNavigationRecovery(navigationTarget);
          prepareSpecialistBookingHistoryNavigation({
            destinationHref,
            locationId: assignment.locationId,
            staffId: assignment.userLocationId,
            step: "select-slot",
          });
          try {
            await Promise.resolve(router.push(destinationHref));
            retireSpecialistLocationGatewayServicesCartAfterCanonicalNavigation(
              {
                lineage: committed.lineage,
                isNavigationOwnerCurrent: () => true,
              },
            );
            continueGuardRef.current = false;
            setIsContinuing(false);
          } catch {
            continueGuardRef.current = false;
            setIsContinuing(false);
            toast.error(t("serviceAddOnsModal.submissionError"));
          }
        })();
        return;
      }

      continueGuardRef.current = true;
      const buildIndividualDraft = (draftId: string) => isLocationEntryPoint
        ? locationAppointmentCatalog ? createLocationServicesAppointmentDraft({
            draftId, mode: "individual", locationId: routeLocationId, catalogAssignment: assignment,
            workplace, services, offerings, selections: organizerSelections, groupGuests: [],
            catalogSnapshot: locationAppointmentCatalog, initialProfessionalUserLocationId: requestedInitialProfessionalId,
            initialServiceProfessionals: requestedInitialServiceProfessionals,
          }) : { ok: false as const, reason: "invalid-catalog" as const }
        : createSpecialistServicesAppointmentDraft({ draftId, mode: "individual", assignment, workplace,
            services, offerings, selections: organizerSelections, groupGuests: [] });
      const packageReturn = initialPackageSessionEntry.status === "ready" && refreshedTimeResume?.status !== "restored"
        ? preparePackageSessionServicesReturn({ expectedIntent: initialPackageSessionEntry.intent,
            buildProposedDraft: id => { const result = buildIndividualDraft(id); return result.ok ? result.draft : null; },
            nowEpochMs: initialPackageSessionEntry.nowEpochMs,
            ownerIsCurrent: () => {
              const owner = readCurrentPackageSessionServicesEntry();
              return owner.status === "ready" && owner.intent.ownerKey === initialPackageSessionEntry.intent.ownerKey;
            } }) : null;
      if (packageReturn?.status === "blocked") {
        continueGuardRef.current = false;
        toast.error(t("serviceAddOnsModal.submissionError"));
        return;
      }
      if (packageReturn?.status === "reused") resumedPackageSessionPreference = packageReturn.preference;
      let draftId =
        refreshedTimeResume?.status === "restored"
          ? refreshedTimeResume.sourceDraft.draftId
          : packageReturn?.status === "reused" ? packageReturn.sourceDraft.draftId
          : createSpecialistServicesAppointmentDraftId(
              initialResultsServicesEntry.status === "ready",
              initialPackageSessionEntry.status === "ready",
            );
      let createdAppointmentDraft: AppointmentBookingDraftV1 | null = null;
      let createdOrdinaryAppointmentDraftId: string | null = null;
      let bindOrdinaryRewardReturn: (() => boolean) | null = null;
      let ordinaryRewardEntryWasBound = false;
      let createdResultsPreference: AppointmentResultsEntryPreferenceV1 | null =
        null;
      let createdPackageSessionPreference: AppointmentPackageSessionPreferenceV1 | AppointmentPackageSessionPreferenceV2 | null =
        null;
      if (refreshedTimeResume?.status !== "restored" && packageReturn?.status !== "reused") {
        const draftResult = buildIndividualDraft(draftId);

        if (!draftResult.ok) {
          continueGuardRef.current = false;
          toast.error(t("serviceAddOnsModal.submissionError"));
          return;
        }

        if (initialPackageSessionEntry.status === "ready") {
          let persistedAppointment:
            | ReturnType<typeof createAppointmentBookingDraftStrictPersisted>
            | { status: "unreadable" };
          try {
            persistedAppointment = createAppointmentBookingDraftStrictPersisted(
              draftResult.draft,
            );
          } catch {
            persistedAppointment = { status: "unreadable" };
          }
          if (persistedAppointment.status !== "created") {
            if (
              appointmentDraftStrictMutationIsAmbiguous(
                persistedAppointment.status,
              )
            ) {
              quarantineCustomerOwnedSessionStorage();
            }
            continueGuardRef.current = false;
            toast.error(t("serviceAddOnsModal.submissionError"));
            return;
          }
          createdAppointmentDraft = persistedAppointment.draft;
          draftId = persistedAppointment.draft.draftId;

          const currentPackageSessionEntry =
            readCurrentPackageSessionServicesEntry();
          const exactLine =
            currentPackageSessionEntry.status === "ready"
              ? getExactPackageSessionAppointmentLine({
                  draft: persistedAppointment.draft,
                  intent: currentPackageSessionEntry.intent,
                })
              : null;
          if (
            currentPackageSessionEntry.status !== "ready" ||
            currentPackageSessionEntry.intent.ownerKey !==
              initialPackageSessionEntry.intent.ownerKey ||
            !exactLine
          ) {
            removeAppointmentDraftExactAfterFailedServicesTransaction(
              persistedAppointment.draft,
            );
            createdAppointmentDraft = null;
            continueGuardRef.current = false;
            toast.error(t("serviceAddOnsModal.submissionError"));
            return;
          }

          const preferenceInput = {
              expectedIntent: currentPackageSessionEntry.intent,
              sourceAppointmentDraft: persistedAppointment.draft,
              expectedCustomerAccountId:
                currentPackageSessionEntry.intent.customerAccountId,
              participantId: persistedAppointment.draft.organizerParticipantId,
              appointmentServiceLineId: exactLine.lineId,
              createdAtEpochMs: currentPackageSessionEntry.nowEpochMs,
            };
          const preferenceResult = currentPackageSessionEntry.intent.schemaVersion === 2 ? (() => {
            const preference = createAppointmentPackageSessionPreferenceV2({ ...preferenceInput, packageIntent: currentPackageSessionEntry.intent });
            const stored = createAppointmentPackageSessionPreferenceV2StrictPersisted(preference, { evaluatedAtEpochMs: currentPackageSessionEntry.nowEpochMs,
              mutationIsCurrent: () => { const owner = readCurrentPackageSessionServicesEntry(); return owner.status === "ready" && owner.intent.ownerKey === currentPackageSessionEntry.intent.ownerKey; } });
            return stored.status === "created" ? { status: "bound" as const, preference: stored.draft } : stored;
          })() : bindPackageSessionBookingIntentToAppointmentStrict({ ...preferenceInput, expectedIntent: currentPackageSessionEntry.intent });
          if (preferenceResult.status !== "bound") {
            if (preferenceResult.status !== "recovery-required") {
              removeAppointmentDraftExactAfterFailedServicesTransaction(
                persistedAppointment.draft,
              );
            }
            createdAppointmentDraft = null;
            continueGuardRef.current = false;
            toast.error(t("serviceAddOnsModal.submissionError"));
            return;
          }
          createdPackageSessionPreference = preferenceResult.preference;
        } else if (initialResultsServicesEntry.status === "ready") {
          let persistedAppointment:
            | ReturnType<typeof createAppointmentBookingDraftStrictPersisted>
            | { status: "unreadable" };
          try {
            persistedAppointment = createAppointmentBookingDraftStrictPersisted(
              draftResult.draft,
            );
          } catch {
            persistedAppointment = { status: "unreadable" };
          }
          if (persistedAppointment.status !== "created") {
            if (
              appointmentDraftStrictMutationIsAmbiguous(
                persistedAppointment.status,
              )
            ) {
              quarantineCustomerOwnedSessionStorage();
            }
            continueGuardRef.current = false;
            toast.error(t("serviceAddOnsModal.submissionError"));
            return;
          }
          createdAppointmentDraft = persistedAppointment.draft;
          draftId = persistedAppointment.draft.draftId;

          const currentResultsServicesEntry =
            revalidateCurrentResultsServicesEntry(
              initialResultsServicesEntry.entry,
            );
          if (currentResultsServicesEntry.status !== "ready") {
            removeAppointmentDraftExactAfterFailedServicesTransaction(
              persistedAppointment.draft,
            );
            createdAppointmentDraft = null;
            continueGuardRef.current = false;
            toast.error(t("serviceAddOnsModal.submissionError"));
            return;
          }
          const preferenceResult =
            bindResultsServicesEntryToAppointmentDraftStrict({
              entry: currentResultsServicesEntry.entry,
              sourceAppointmentDraft: persistedAppointment.draft,
              expectedCustomerAccountId:
                currentResultsServicesEntry.entry.intent.customerAccountId,
              expectedLocale: locale,
              createdAtEpochMs: currentResultsServicesEntry.nowEpochMs,
            });
          if (preferenceResult.status !== "created") {
            removeAppointmentDraftExactAfterFailedServicesTransaction(
              persistedAppointment.draft,
            );
            createdAppointmentDraft = null;
            continueGuardRef.current = false;
            toast.error(t("serviceAddOnsModal.submissionError"));
            return;
          }
          createdResultsPreference = preferenceResult.draft;
        } else {
          const rewardEntry = resolveCustomerRewardBookingEntryForRoute();
          const rewardBrowserHref = window.location.href;
          if (rewardEntry.status === "invalid") {
            continueGuardRef.current = false;
            toast.error(tAll("customerWallet.rewards.bookingUnavailable"));
            return;
          }
          if (rewardEntry.status === "ready" && rewardEntry.entry.draftId &&
            !await releaseRewardAppointmentSelectionForNavigationV1({ draftId: rewardEntry.entry.draftId,
              customerAccountId: rewardEntry.entry.customerAccountId,
              isCurrentOwner: () => window.location.href === rewardBrowserHref && rewardAppointmentProfileOwnerIsCurrent(rewardEntry.entry.customerAccountId, rewardEntry.entry.profileOwnerKey) })) {
            continueGuardRef.current = false;
            toast.error(tAll("customerWallet.rewards.bookingUnavailable"));
            return;
          }
          const rewardSourceCanChange = rewardEntry.status === "ready" && rewardEntry.entry.draftId
            ? await prepareRewardAppointmentSourceChangeV1(rewardEntry.entry.draftId, rewardEntry.entry.customerAccountId) : () => true;
          if (!rewardSourceCanChange() || window.location.href !== rewardBrowserHref || rewardEntry.status === "ready" &&
            JSON.stringify(readCustomerRewardBookingEntryForRoute()) !== JSON.stringify(rewardEntry.entry)) {
            continueGuardRef.current = false;
            toast.error(tAll("customerWallet.rewards.bookingUnavailable"));
            return;
          }
          const bindRewardEntry = prepareCustomerRewardBookingEntryBinding(draftResult.draft, {
            sourceCanChange: rewardSourceCanChange,
          });
          bindOrdinaryRewardReturn = prepareCustomerRewardServicesBinding(draftResult.draft);
          const persisted = rewardEntry.status === "ready"
            ? createAppointmentBookingDraftStrictPersisted(draftResult.draft).status === "created"
            : Boolean(storeAppointmentBookingDraft(draftResult.draft));
          if (!persisted) {
            continueGuardRef.current = false;
            toast.error(t("serviceAddOnsModal.submissionError"));
            return;
          }
          if (!bindRewardEntry()) {
            continueGuardRef.current = false;
            toast.error(tAll("customerWallet.rewards.bookingUnavailable"));
            return;
          }
          ordinaryRewardEntryWasBound = rewardEntry.status === "ready";
          draftId = draftResult.draft.draftId;
          createdOrdinaryAppointmentDraftId = draftResult.draft.draftId;
        }
      }

      let packageNavigationAttempted = false;
      try {
        setIsContinuing(true);
        if (!isLocationEntryPoint && analyticsContext) {
          emitSpecialistAnalyticsEvent({
            ...analyticsContext,
            name: "specialist_continue_activated",
            selectedServiceCount,
          });
        }

        const resumedSource =
          refreshedTimeResume?.status === "restored"
            ? refreshedTimeResume.sourceDraft
            : null;
        const isSpecialistOrigin = resumedSource
          ? resumedSource.entryPoint.kind === "specialist-services"
          : !isLocationEntryPoint;
        const navigationLocationId =
          resumedSource?.locationId ?? routeLocationId;
        const entrySpecialistId = resumedSource
          ? resumedSource.entryPoint.kind === "specialist-services"
            ? resumedSource.entryPoint.userLocationId
            : null
          : !isLocationEntryPoint
            ? assignment.userLocationId
            : null;
        const params = new URLSearchParams({
          location: navigationLocationId,
          [BOOKING_FLOW_ORIGIN_PARAM]: isSpecialistOrigin
            ? BOOKING_FLOW_ORIGIN_SPECIALIST
            : BOOKING_FLOW_ORIGIN_LOCATION,
        });
        if (entrySpecialistId) {
          params.set("staff", entrySpecialistId);
        }
        params.set("booking_draft_id", draftId);
        params.set(
          "booking_draft_version",
          String(APPOINTMENT_DRAFT_SCHEMA_VERSION),
        );
        appendBookingFlowServiceParams(
          params,
          serviceIds,
          organizerSelections.flatMap(({ serviceId, variantId }) =>
            variantId ? [{ serviceId, variantId }] : [],
          ),
        );
        if (nestedQueryState.serviceAddOns) {
          params.set(
            SPECIALIST_SERVICE_ADD_ONS_PARAM,
            nestedQueryState.serviceAddOns,
          );
        }

        if (nestedQueryState.serviceBundleAddOns) {
          params.set(
            SPECIALIST_SERVICE_BUNDLE_ADD_ONS_PARAM,
            nestedQueryState.serviceBundleAddOns,
          );
        }
        const baseDestinationHref = `/book/select-slot?${params.toString()}`;
        const resultsDestinationHref = createdResultsPreference
          ? appendAppointmentResultsEntryHandoffToHref({
              href: baseDestinationHref,
              handoffId: createdResultsPreference.handoffId,
            })
          : baseDestinationHref;
        const activePackageSessionPreference =
          createdPackageSessionPreference ?? resumedPackageSessionPreference;
        const destinationHref = activePackageSessionPreference
          ? appendPackageSessionBookingHandoffToHref({
              href: resultsDestinationHref ?? "",
              handoffId: activePackageSessionPreference.handoffId,
            })
          : resultsDestinationHref;
        if (!destinationHref) {
          throw new Error("Invalid Appointment companion handoff");
        }
        if (packageReturn?.status === "reused" && !packageReturn.isCurrent()) {
          throw new Error("Changed Package Services return source");
        }

        if (isSpecialistOrigin && entrySpecialistId) {
          prepareSpecialistBookingHistoryNavigation({
            destinationHref,
            locationId: navigationLocationId,
            staffId: entrySpecialistId,
            step: "select-slot",
          });
        }
        packageNavigationAttempted = activePackageSessionPreference !== null;
        if (isLocationEntryPoint && !activePackageSessionPreference && !createdResultsPreference) {
          if (bindOrdinaryRewardReturn) bindOrdinaryRewardReturn();
          else bindCustomerRewardBookingReturn(draftId);
        }
        router.push(destinationHref);
      } catch (error) {
        if (ordinaryRewardEntryWasBound) {
          // A destination may already have mounted. Preserve the exact bound
          // source for navigation-only recovery instead of detaching its use.
          continueGuardRef.current = false;
          setIsContinuing(false);
          toast.error(tAll("customerWallet.rewards.bookingUnavailable"));
          return;
        }
        if (packageNavigationAttempted) {
          // Navigation may have mounted the destination before throwing. Keep
          // this exact pair for navigation-only retry; never erase its source.
          continueGuardRef.current = false;
          setIsContinuing(false);
          toast.error(t("serviceAddOnsModal.submissionError"));
          return;
        }
        let exactPackageSessionPreferenceWasRemoved = true;
        if (createdPackageSessionPreference) {
          const removed = createdPackageSessionPreference.schemaVersion === 2 ? removeAppointmentPackageSessionPreferenceV2StrictPersisted(createdPackageSessionPreference) : removeAppointmentPackageSessionPreferenceStrict({
            expected: createdPackageSessionPreference,
          });
          exactPackageSessionPreferenceWasRemoved =
            removed.status === "removed" || removed.status === "absent";
          if (!exactPackageSessionPreferenceWasRemoved) {
            quarantineCustomerOwnedSessionStorage();
          }
        }
        let exactResultsAppointmentWasRemoved = true;
        if (
          createdAppointmentDraft &&
          exactPackageSessionPreferenceWasRemoved
        ) {
          exactResultsAppointmentWasRemoved =
            removeAppointmentDraftExactAfterFailedServicesTransaction(
              createdAppointmentDraft,
            );
        }
        if (createdOrdinaryAppointmentDraftId) {
          removeAppointmentBookingDraft(createdOrdinaryAppointmentDraftId);
        }
        if (createdResultsPreference && exactResultsAppointmentWasRemoved) {
          removeAppointmentResultsEntryPreferenceStrict(
            createdResultsPreference,
          );
        }
        continueGuardRef.current = false;
        setIsContinuing(false);
        throw error;
      }
    },
    [
      analyticsContext,
      appointmentMode,
      assignment,
      beginDirectSpecialistLocationGatewayEntry,
      continueInitialGroupOrganizerCatalog,
      isLocationEntryPoint,
      isTimeResumeBlocking,
      isContinuing,
      locationAppointmentCatalog,
      offerings,
      packageSessionServicesEntryHydration,
      packageSessionServicesEntryIsRequired,
      postCommitNavigationRecovery,
      requestedInitialProfessionalId,
      requestedInitialServiceProfessionals,
      requestedServices,
      revalidateSpecialistLocationGatewayServicesCartForContinue,
      retireSpecialistLocationGatewayServicesCartAfterCanonicalNavigation,
      publishTimeResumeForHandoff,
      readCurrentPackageSessionServicesEntry,
      readCurrentMembershipSessionServicesEntry,
      readCurrentResultsServicesEntry,
      revalidateCurrentResultsServicesEntry,
      resultsServicesEntryHydration,
      resultsServicesEntryIsRequired,
      resolveCurrentTimeResume,
      routeLocationId,
      router,
      searchString,
      pathname,
      retryPostCommitNavigation,
      services,
      specialistLocationGatewayServicesCartHandoffKey,
      specialistLocationGatewayServicesCartIsApplicable,
      specialistLocationGatewaySession,
      specialistLocationGatewayActivation,
      groupCoordinatorPointerIsPresent,
      hasIncompleteDirectSpecialistServiceRequest,
      t,
      tAll,
      timeResumeHandoffKey,
      timeResumeRequestKey,
      locale,
      workplace,
    ],
  );

  const readyResultsServicesEntry =
    resultsServicesEntryHydration.status === "ready"
      ? resultsServicesEntryHydration.entry
      : null;
  const resultsServicesReturnHref =
    readyResultsServicesEntry?.intent.returnContext.canonicalResultsHref ??
    null;

  const handlePageExit = useCallback(
    async (action: SpecialistServicesPageExitAction) => {
      const isRewardCatalogExit = isLocationEntryPoint && catalogView === "packages";
      const walletReturn = isRewardCatalogExit ? captureCustomerRewardCatalogExitWalletReturn() : captureCustomerWalletBookingReturn();
      const rewardEntry = isLocationEntryPoint ? isRewardCatalogExit
        ? readCustomerRewardBookingEntryForCatalogExit() : readCustomerRewardBookingEntryForRoute() : null;
      if (rewardEntry) {
        const browserHref = window.location.href;
        const isCurrentOwner = () => window.location.href === browserHref &&
          rewardAppointmentProfileOwnerIsCurrent(rewardEntry.customerAccountId, rewardEntry.profileOwnerKey);
        if (rewardEntry.draftId && (!await releaseRewardAppointmentSelectionForNavigationV1({ draftId: rewardEntry.draftId,
          customerAccountId: rewardEntry.customerAccountId, isCurrentOwner }) || !isCurrentOwner() ||
          !removeAppointmentCompletionDrafts(rewardEntry.draftId))) {
          toast.error(tAll("customerWallet.rewards.bookingUnavailable"));
          return;
        }
        if (!isCurrentOwner() || !(rewardEntry.draftId ? retireSettledCustomerRewardBookingEntry(rewardEntry)
          : cancelCustomerRewardBookingEntry(rewardEntry.entryId))) {
          toast.error(tAll("customerWallet.rewards.bookingUnavailable"));
          return;
        }
        router.push(walletReturn.resolve(canonicalProfilePath));
        return;
      }
      if (walletReturn.isRewardOrigin && isLocationEntryPoint) {
        if (walletReturn.draftId && !removeAppointmentCompletionDrafts(walletReturn.draftId)) {
          toast.error(t("serviceAddOnsModal.submissionError"));
          return;
        }
        router.push(walletReturn.resolve(canonicalProfilePath));
        return;
      }
      if (membershipSessionServicesEntryIsRequired) {
        const currentMembership = readCurrentMembershipSessionServicesEntry();
        if (currentMembership.status !== "ready") {
          toast.error(tAll("activity.membership.bookingUnavailable"));
          return;
        }
        const nowEpochMs = Date.now();
        const browserHref = window.location.href;
        const browserUrl = new URL(browserHref);
        const browserMembership = readMembershipSessionServicesEntry({ pathname: browserUrl.pathname,
          searchParams: browserUrl.searchParams, locationCatalog: locationAppointmentCatalog, nowEpochMs });
        if (browserMembership.status !== "ready" ||
          browserMembership.entry.intent.draftId !== currentMembership.entry.intent.draftId ||
          browserMembership.entry.source.sourceOwnerKey !== currentMembership.entry.source.sourceOwnerKey) {
          toast.error(tAll("activity.membership.bookingUnavailable"));
          return;
        }
        const companion = inspectAppointmentMembershipSessionPreferenceForIntentStrict(currentMembership.entry.intent, nowEpochMs);
        if (companion.status === "unavailable") {
          toast.error(tAll("activity.membership.bookingUnavailable"));
          return;
        }
        if (companion.status === "present") {
          if (!removeAppointmentCompletionDrafts(companion.preference.draftId, {
            membershipSessionPreference: companion.preference, nowEpochMs,
          })) {
            toast.error(tAll("activity.membership.bookingUnavailable"));
            return;
          }
          router.push(walletReturn.resolve(currentMembership.entry.intent.canonicalActivityDetailReturnHref));
          return;
        }
        if (!closeMembershipSessionServicesIntentStrict({
          expectedIntent: currentMembership.entry.intent,
          nowEpochMs,
          ownerIsCurrent: () => membershipServicesMountedRef.current && membershipServicesRouteKeyRef.current === membershipServicesRouteKey && window.location.href === browserHref,
          navigate: (href) => router.push(walletReturn.resolve(href)),
        })) toast.error(tAll("activity.membership.bookingUnavailable"));
        return;
      }
      if (!isLocationEntryPoint && analyticsContext) {
        emitSpecialistAnalyticsEvent({
          ...analyticsContext,
          method: action,
          name: "specialist_services_page_exited",
          selectedServiceCount: effectiveSelectedServiceCount,
        });
      }

      if ((action === "close" || walletReturn.isWalletOrigin) && readyPackageSessionServicesEntry) {
        const browserHref = window.location.href;
        const currentPackageEntry = readCurrentPackageSessionServicesEntry();
        if (
          currentPackageEntry.status !== "ready" ||
          currentPackageEntry.intent.ownerKey !==
            readyPackageSessionServicesEntry.intent.ownerKey
        ) {
          toast.error(t("serviceAddOnsModal.submissionError"));
          return;
        }

        const returnHref =
          currentPackageEntry.intent.canonicalActivityDetailReturnHref;
        const closeState =
          resolveLocationPackageSessionServicesCloseStateStrict({
            expectedIntent: currentPackageEntry.intent,
            nowEpochMs: currentPackageEntry.nowEpochMs,
          });
        if (closeState.status === "blocked") {
          toast.error(t("serviceAddOnsModal.submissionError"));
          return;
        }
        if (closeState.status === "appointment-companion") {
          if (
            !removeAppointmentCompletionDrafts(closeState.preference.draftId, {
              packageSessionPreference: closeState.preference,
              nowEpochMs: currentPackageEntry.nowEpochMs,
            })
          ) {
            toast.error(t("serviceAddOnsModal.submissionError"));
            return;
          }
          router.push(walletReturn.resolve(returnHref));
          return;
        }

        const closed = closeLocationPackageSessionServicesIntentStrict({
          expectedIntent: currentPackageEntry.intent,
          nowEpochMs: currentPackageEntry.nowEpochMs,
          ownerIsCurrent: () =>
            (() => {
              const owner = readPackageSessionBookingUiCustomerOwner(currentPackageEntry.intent.providerId);
              return packageEntryMountedRef.current && packageEntryRouteRef.current.href === currentServicesHref &&
                window.location.href === browserHref && owner.status === "ready" && owner.customerAccountId === currentPackageEntry.intent.customerAccountId;
            })(),
        });
        if (closed.status !== "closed") {
          toast.error(t("serviceAddOnsModal.submissionError"));
          return;
        }
        router.push(walletReturn.resolve(closed.returnHref));
        return;
      }

      const giftCatalogIntent = isLocationEntryPoint
        ? readPackageGiftCatalogIntent(currentServicesHref) : null;
      if (giftCatalogIntent) {
        if (!packageEntryMountedRef.current || packageEntryRouteRef.current.href !== currentServicesHref ||
          `${window.location.pathname}${window.location.search}${window.location.hash}` !== currentServicesHref) return;
        router.push(action === "back"
          ? buildLocationGiftsPath(giftCatalogIntent.locationId)
          : canonicalProfilePath);
        return;
      }

      if (action === "back") {
        if (specialistLocationGatewaySession) {
          const backHref =
            buildSpecialistLocationGatewayResolvedServicesBackHref({
              resolution: specialistLocationGatewaySession.resolution,
              resultsAppointmentIntentHandoffId:
                readyResultsServicesEntry?.intent.draftId ??
                specialistResultsAppointmentIntentPointer,
            });
          if (backHref) router.replace(backHref);
          return;
        }
        const backDistance = getLocationServicesBackDistance(canonicalServicesPath);
        if (backDistance !== null) {
          if (backDistance === 1) router.back();
          else window.history.go(-backDistance);
          return;
        }

        router.replace(resultsServicesReturnHref ?? canonicalProfilePath);
        return;
      }

      if (specialistLocationGatewaySession) {
        const nowEpochMs = readSpecialistLocationGatewaySessionEpoch(
          specialistLocationGatewaySession,
        );
        if (nowEpochMs === null) return;
        const pureOperationIsCurrent = () =>
          !specialistLocationGatewaySession.signal.aborted;
        const retired =
          retireSpecialistLocationGatewayConfiguredServicesCartsForIntentStrict(
            {
              intentDraftId: specialistLocationGatewaySession.intent.draftId,
              nowEpochMs,
              isCurrentOwner: () =>
                isSpecialistLocationGatewaySessionCurrent(
                  specialistLocationGatewaySession,
                ),
            },
          );
        if (retired.status !== "retired" && retired.status !== "absent") {
          toast.error(t("serviceAddOnsModal.submissionError"));
          return;
        }
        const discarded =
          discardSpecialistLocationGatewayPreDraftForOwnerStrict({
            surface: "services",
            intent: specialistLocationGatewaySession.intent,
            nowEpochMs,
            signal: specialistLocationGatewaySession.signal,
            isCurrentOwner: () =>
              isSpecialistLocationGatewaySessionCurrent(
                specialistLocationGatewaySession,
              ),
          });
        if (discarded.status === "discarded" && pureOperationIsCurrent()) {
          router.push(
            resultsServicesReturnHref ??
              specialistLocationGatewaySession.resolution.source
                .canonicalReturnHref,
          );
        }
        return;
      }
      router.push(resultsServicesReturnHref ?? canonicalProfilePath);
    },
    [
      analyticsContext,
      canonicalProfilePath,
      canonicalServicesPath,
      catalogView,
      effectiveSelectedServiceCount,
      currentServicesHref,
      entryPointKind,
      isLocationEntryPoint,
      readCurrentPackageSessionServicesEntry,
      readCurrentMembershipSessionServicesEntry,
      membershipSessionServicesEntryIsRequired,
      tAll,
      membershipServicesRouteKey,
      locationAppointmentCatalog,
      readyResultsServicesEntry?.intent.draftId,
      readyPackageSessionServicesEntry,
      resultsServicesReturnHref,
      router,
      specialistLocationGatewaySession,
      specialistResultsAppointmentIntentPointer,
      t,
    ],
  );

  const handleServicesPageExit = useCallback(
    (action: SpecialistServicesPageExitAction) => {
      if (membershipSessionServicesEntryIsRequired) { handlePageExit(action); return; }
      if (activeGroupCatalogTransaction) {
        cancelGroupCatalogTransaction();
        if (action === "close" && currentGroupCoordinator) {
          setPendingGroupExit({
            action: "close",
            coordinatorDraftId: currentGroupCoordinator.draftId,
            coordinatorRevision: currentGroupCoordinator.revision,
          });
        }
        return;
      }

      handlePageExit(action);
    },
    [
      activeGroupCatalogTransaction,
      cancelGroupCatalogTransaction,
      currentGroupCoordinator,
      handlePageExit,
      membershipSessionServicesEntryIsRequired,
    ],
  );

  const retryGroupLifecycleNavigation = useCallback(
    async (action: SpecialistServicesPageExitAction) => {
      const recovery = groupLifecycleNavigationRecovery;
      if (!recovery || recovery.action !== action) return false;
      setIsGroupMutationPending(true);
      try {
        const navigation =
          recovery.navigation === "replace"
            ? router.replace(recovery.href, { scroll: false })
            : router.push(recovery.href);
        await Promise.resolve(navigation);
      } catch {
        toast.error(t("serviceAddOnsModal.submissionError"));
      } finally {
        setIsGroupMutationPending(false);
      }
      return true;
    },
    [groupLifecycleNavigationRecovery, router, t],
  );

  const requestGroupRosterExit = useCallback(
    (action: SpecialistServicesPageExitAction) => {
      if (groupLifecycleNavigationRecovery?.action === action) {
        void retryGroupLifecycleNavigation(action);
        return;
      }
      if (!isGroupMutationPending && currentGroupCoordinator &&
        (groupCoordinatorOriginScope?.kind !== "location" ||
          groupCoordinatorRouteState.status === "accepted-handoff" ||
          (isExactLiveGroupCoordinatorOwner(currentGroupCoordinator) &&
            isCurrentGroupCoordinatorRouteAuthority(currentGroupCoordinator)))) {
        setPendingGroupExit({
          action,
          coordinatorDraftId: currentGroupCoordinator.draftId,
          coordinatorRevision: currentGroupCoordinator.revision,
        });
      }
    },
    [
      currentGroupCoordinator,
      groupCoordinatorOriginScope,
      groupCoordinatorRouteState.status,
      isCurrentGroupCoordinatorRouteAuthority,
      groupLifecycleNavigationRecovery,
      isGroupMutationPending,
      retryGroupLifecycleNavigation,
    ],
  );

  const confirmGroupRosterExit = useCallback(async (requestedExit = pendingGroupExit) => {
    const exit = requestedExit;
    const coordinator = currentGroupCoordinator;

    if (
      !exit ||
      !coordinator ||
      exit.coordinatorDraftId !== coordinator.draftId ||
      exit.coordinatorRevision !== coordinator.revision ||
      isGroupMutationPending
    ) {
      return;
    }
    if (
      groupCoordinatorRouteState.status !== "accepted-handoff" &&
      (!isExactLiveGroupCoordinatorOwner(coordinator) ||
        (groupCoordinatorOriginScope?.kind === "location" &&
          !isCurrentGroupCoordinatorRouteAuthority(coordinator)))
    ) {
      setPendingGroupExit(null);
      toast.error(t("serviceAddOnsModal.submissionError"));
      return;
    }

    const committedGatewaySession =
      specialistLocationGatewayCommittedGroupSession;
    if (committedGatewaySession) {
      const registry =
        specialistLocationGatewayGroupDownstreamRegistry === undefined
          ? readDefaultSpecialistLocationGatewayGroupDownstreamRegistry()
          : specialistLocationGatewayGroupDownstreamRegistry;
      const runtime = specialistLocationGatewayGroupRuntimeIdentity;
      const expectedCustomerAccountId = coordinator.customerAccountId;
      const operationOwnerIsCurrent = () => {
        const pureOwnerIsCurrent = () => {
          if (committedGatewaySession.signal.aborted) return false;
          const current = committedGatewaySession.isCurrentOwner();
          return !committedGatewaySession.signal.aborted && current;
        };
        if (!pureOwnerIsCurrent()) return false;
        const currentCustomerAccountId =
          committedGatewaySession.readCustomerAccountId();
        return (
          pureOwnerIsCurrent() &&
          currentCustomerAccountId === expectedCustomerAccountId
        );
      };
      let evaluatedAtEpochMs: number | null = null;
      try {
        const candidate = committedGatewaySession.now();
        evaluatedAtEpochMs =
          Number.isSafeInteger(candidate) && candidate >= 0 ? candidate : null;
      } catch {
        evaluatedAtEpochMs = null;
      }
      const exactCoordinator = readGroupBookingCoordinatorAuthorityPersisted(
        coordinator.draftId,
      );
      let liveAuthorityIsCurrent = Boolean(
        registry &&
        runtime &&
        evaluatedAtEpochMs !== null &&
        operationOwnerIsCurrent() &&
        exactCoordinator &&
        haveExactSerializableStructure(exactCoordinator, coordinator),
      );
      if (
        liveAuthorityIsCurrent &&
        registry &&
        runtime &&
        evaluatedAtEpochMs !== null
      ) {
        if (groupCoordinatorRouteState.status === "accepted-handoff") {
          const currentAuthority =
            expectedCustomerAccountId === null
              ? null
              : resolveSpecialistLocationGatewayGroupDownstreamAuthority({
                  appointmentDraftId:
                    groupCoordinatorRouteState.appointment.draftId,
                  customerAccountId: expectedCustomerAccountId,
                  evaluatedAtEpochMs,
                  registry,
                });
          liveAuthorityIsCurrent = Boolean(
            currentAuthority &&
            haveExactSerializableStructure(
              currentAuthority.coordinator,
              coordinator,
            ) &&
            haveExactSerializableStructure(
              currentAuthority.appointment,
              groupCoordinatorRouteState.appointment,
            ) &&
            haveExactSerializableStructure(
              currentAuthority.navigationLineage,
              committedGatewaySession.navigationLineage,
            ) &&
            operationOwnerIsCurrent(),
          );
        } else {
          let organizerCatalog: SpecialistOrganizerGroupBookingCatalogSourceV1 | null =
            null;
          let locationCatalog: LocationAppointmentCatalog | null = null;
          try {
            organizerCatalog = registry.resolveOrganizerCatalog({
              selectedLocationId: runtime.selectedLocationId,
              selectedUserLocationId: runtime.selectedUserLocationId,
            });
            locationCatalog = registry.resolveLocationCatalog({
              selectedLocationId: runtime.selectedLocationId,
              catalogVersion: runtime.catalogVersion,
              catalogOwnerUserLocationId: runtime.catalogOwnerUserLocationId,
            });
          } catch {
            organizerCatalog = null;
            locationCatalog = null;
          }
          liveAuthorityIsCurrent = Boolean(
            organizerCatalog &&
            locationCatalog &&
            haveExactSerializableStructure(
              organizerCatalog,
              specialistOrganizerGroupCatalog,
            ) &&
            haveExactSerializableStructure(
              locationCatalog,
              locationAppointmentCatalog,
            ) &&
            specialistLocationGatewayGroupStepOneRuntimeOwnsCoordinator({
              runtime,
              coordinator,
              requireBuildingAuthority: true,
              requireCurrentRuntime: false,
              evaluatedAtEpochMs: runtime.generatedAtEpochMs,
            }) &&
            resolveSpecialistLocationGatewayGroupCurrentAuthoritySnapshot({
              registry,
              intentRecord: committedGatewaySession.intent,
              navigationLineage: committedGatewaySession.navigationLineage,
              coordinator,
              organizerCatalog,
              locationCatalog,
              evaluatedAtEpochMs,
            }) !== null &&
            operationOwnerIsCurrent(),
          );
        }
      }
      const destinationLifecycle =
        liveAuthorityIsCurrent &&
        expectedCustomerAccountId !== null &&
        groupCoordinatorRouteState.status === "accepted-handoff"
          ? createSpecialistLocationGatewayGroupAppointmentDestinationLifecycle(
              {
                appointmentDraft: groupCoordinatorRouteState.appointment,
                customerAccountId: expectedCustomerAccountId,
                evaluatedAtEpochMs: evaluatedAtEpochMs!,
                isCustomerOwnerCurrent: operationOwnerIsCurrent,
              },
            )
          : liveAuthorityIsCurrent
            ? createSpecialistLocationGatewayGroupBookingCoordinatorDestinationLifecycle(
                {
                  coordinatorDraft: coordinator,
                  customerAccountId: expectedCustomerAccountId,
                },
              )
            : null;
      if (!destinationLifecycle || !operationOwnerIsCurrent()) {
        setPendingGroupExit(null);
        toast.error(t("serviceAddOnsModal.submissionError"));
        return;
      }

      groupMutationInFlightRef.current = true;
      setIsGroupMutationPending(true);
      const pureLifecycleOperationIsCurrent = () =>
        !committedGatewaySession.signal.aborted &&
        groupMutationInFlightRef.current;
      const lifecycleAttemptId = `slg-group-services-${exit.action}-${coordinator.revision}`;
      const result =
        exit.action === "back"
          ? await returnSpecialistLocationGatewayDestinationToPreDraftStrict({
              lineage: committedGatewaySession.navigationLineage,
              intent: committedGatewaySession.intent,
              lifecycleAttemptId,
              provider: committedGatewaySession.provider,
              destinationLifecycle,
              signal: committedGatewaySession.signal,
              now: committedGatewaySession.now,
              isCurrentOwner: operationOwnerIsCurrent,
            })
          : await closeSpecialistLocationGatewayCommittedDestinationStrict({
              lineage: committedGatewaySession.navigationLineage,
              intent: committedGatewaySession.intent,
              lifecycleAttemptId,
              destinationLifecycle,
              signal: committedGatewaySession.signal,
              now: committedGatewaySession.now,
              isCurrentOwner: operationOwnerIsCurrent,
            });
      const operationRemainsCurrent = pureLifecycleOperationIsCurrent();
      groupMutationInFlightRef.current = false;
      if (!operationRemainsCurrent) return;
      setIsGroupMutationPending(false);
      const href =
        result.status === "returned"
          ? buildSpecialistLocationGatewayResolvedServicesBackHref({
              resolution: result.resolution,
            })
          : result.status === "closed"
            ? result.returnHref
            : null;
      if (!href) {
        setPendingGroupExit(null);
        toast.error(t("serviceAddOnsModal.submissionError"));
        return;
      }
      const recovery = {
        action: exit.action,
        href,
        navigation: (exit.action === "back" ? "replace" : "push") as
          "replace" | "push",
      };
      setGroupLifecycleNavigationRecovery(recovery);
      setPendingGroupExit(null);
      try {
        const navigation =
          recovery.navigation === "replace"
            ? router.replace(recovery.href, { scroll: false })
            : router.push(recovery.href);
        await Promise.resolve(navigation);
      } catch {
        toast.error(t("serviceAddOnsModal.submissionError"));
      }
      return;
    }

    const acceptedCleanupEpochMs =
      groupCoordinatorRouteState.status === "accepted-handoff"
        ? readSpecialistServicesSafeEpochMs(servicesClockRef.current)
        : null;
    if (
      groupCoordinatorRouteState.status === "accepted-handoff" &&
      acceptedCleanupEpochMs === null
    ) {
      toast.error(t("serviceAddOnsModal.submissionError"));
      return;
    }

    const removed =
      groupCoordinatorRouteState.status === "accepted-handoff"
        ? (() => {
            const customerAccountId = getStoredCustomerAccountId();
            if (
              !customerAccountId ||
              customerAccountId !== coordinator.customerAccountId
            ) {
              return false;
            }

            return (
              discardCurrentGroupAppointmentFlow({
                appointmentDraft: groupCoordinatorRouteState.appointment,
                customerAccountId,
                evaluatedAtEpochMs: acceptedCleanupEpochMs!,
              }).status === "discarded"
            );
          })()
        : groupCoordinatorOriginScope?.kind === "location"
          ? (() => {
              const epoch = readSpecialistServicesSafeEpochMs(servicesClockRef.current);
              const routeKey = groupRouteOperationKeyRef.current;
              if (epoch === null) return false;
              return closeLocationGroupRosterStrict({
                coordinator,
                evaluatedAtEpochMs: epoch,
                operationIsCurrent: () => groupRouteOperationKeyRef.current === routeKey,
                ownerIsCurrent: () => isLiveGroupCoordinatorSessionOwner(coordinator) &&
                  isCurrentGroupCoordinatorRouteAuthority(coordinator),
              });
            })()
          : ensureGroupBookingCoordinatorDraftRemoved(coordinator.draftId);

    if (!removed) {
      toast.error(t("serviceAddOnsModal.submissionError"));
      return;
    }

    setPendingGroupExit(null);
    handlePageExit(exit.action);
  }, [
    currentGroupCoordinator,
    groupCoordinatorOriginScope,
    isCurrentGroupCoordinatorRouteAuthority,
    groupCoordinatorRouteState,
    handlePageExit,
    isGroupMutationPending,
    locationAppointmentCatalog,
    pendingGroupExit,
    router,
    specialistLocationGatewayCommittedGroupSession,
    specialistLocationGatewayGroupDownstreamRegistry,
    specialistLocationGatewayGroupRuntimeIdentity,
    specialistOrganizerGroupCatalog,
    t,
  ]);

  const handleServicesRetry = useCallback(() => {
    if (
      resultsServicesEntryIsRequired &&
      resultsServicesEntryHydration.status === "unavailable"
    ) {
      setResultsServicesEntryRefreshVersion((version) => version + 1);
      return;
    }

    if (isTimeResumeCanonicalizationBlocked) {
      // Retry only the pointer-free history publication. The invalid Time
      // derivative stays cleaned and the resume resolver is not reactivated.
      setTimeResumeCanonicalizationRetry({
        handoffKey: timeResumeHandoffKey,
        failedAttempts: 0,
        status: "active",
      });
      return;
    }

    router.refresh();
  }, [
    isTimeResumeCanonicalizationBlocked,
    resultsServicesEntryHydration.status,
    resultsServicesEntryIsRequired,
    router,
    timeResumeHandoffKey,
  ]);

  const groupSelectionSummary = useMemo(() => {
    if (!currentGroupCoordinator) return null;

    return resolveGroupBookingSelectionSummary(
      currentGroupCoordinator.participants.map((participant) => ({
        participantId: participant.participantId,
        isOrganizer: participant.role === "organizer",
        serviceLines: participant.serviceLines.map((line) => ({
          lineId: line.lineId,
          durationMinutes: line.quote.durationMinutes * line.quantity,
          durationMaxMinutes:
            (line.quote.durationMaxMinutes ?? line.quote.durationMinutes) *
            line.quantity,
          isStartingPrice: line.quote.priceType === "from",
          priceAmountMinor: line.quote.amountMinor * line.quantity,
        })),
      })),
    );
  }, [currentGroupCoordinator]);
  const groupOrganizerDisplayName = getCustomerDisplayName();
  const groupParticipantLabels = useMemo(() => {
    const labels = new Map<string, string>();
    if (!currentGroupCoordinator) return labels;
    let guestIndex = 0;

    currentGroupCoordinator.participants.forEach((participant) => {
      if (participant.role === "organizer") {
        labels.set(
          participant.participantId,
          groupOrganizerDisplayName ?? t("servicesModal.groupOrganizer"),
        );
        return;
      }

      labels.set(
        participant.participantId,
        t("servicesModal.groupGuest", {
          number: getStableGroupGuestNumber(
            participant.participantId,
            guestIndex,
          ),
        }),
      );
      guestIndex += 1;
    });

    return labels;
  }, [currentGroupCoordinator, groupOrganizerDisplayName, t]);
  const groupRosterParticipants = useMemo<AppointmentGroupRosterParticipant[]>(
    () =>
      currentGroupCoordinator?.participants.map((participant) => {
        const label =
          groupParticipantLabels.get(participant.participantId) ??
          t("servicesModal.groupOrganizer");
        const fallback = label
          .split(/\s+/u)
          .filter(Boolean)
          .slice(0, 2)
          .map((part) => part[0]?.toLocaleUpperCase(locale) ?? "")
          .join("");
        const serviceSummary = resolveGroupRosterServiceSummary(
          participant.serviceLines,
          (count) => t("servicesModal.groupParticipantServices", { count }),
        );

        return {
          participantId: participant.participantId,
          isOrganizer: participant.role === "organizer",
          label,
          avatarFallback: fallback || null,
          serviceSummary,
        };
      }) ?? [],
    [currentGroupCoordinator, groupParticipantLabels, locale, t],
  );

  useEffect(() => {
    const focusOwner = groupRosterReturnFocusOwnerRef.current;

    if (
      !focusOwner ||
      !currentGroupCoordinator ||
      !currentGroupCoordinator.exactGraphPublication ||
      activeGroupCatalogTransaction
    ) {
      return;
    }
    if (focusOwner.coordinatorDraftId !== currentGroupCoordinator.draftId) {
      groupRosterReturnFocusOwnerRef.current = null;
      return;
    }
    const participantLabel = groupParticipantLabels.get(
      focusOwner.participantId,
    );

    if (!participantLabel) {
      groupRosterReturnFocusOwnerRef.current = null;
      return;
    }
    const actionLabel = t("servicesModal.groupParticipantActions", {
      participant: participantLabel,
    });
    const frame = window.requestAnimationFrame(() => {
      const action = Array.from(
        document.querySelectorAll<HTMLButtonElement>(
          '[data-appointment-group-roster] button[aria-haspopup]',
        ),
      ).find(
        (candidate) => candidate.getAttribute("aria-label") === actionLabel,
      );

      if (!action || action.disabled) return;
      groupRosterReturnFocusOwnerRef.current = null;
      action.focus();
    });

    return () => window.cancelAnimationFrame(frame);
  }, [
    activeGroupCatalogTransaction,
    currentGroupCoordinator,
    groupParticipantLabels,
    t,
  ]);
  const groupSummaryCurrency = useMemo(() => {
    if (!currentGroupCoordinator) return null;
    const currencies = new Set(
      currentGroupCoordinator.participants.flatMap((participant) =>
        participant.serviceLines.map((line) => line.quote.currency),
      ),
    );

    return currencies.size === 1 ? ([...currencies][0] ?? null) : null;
  }, [currentGroupCoordinator]);
  const groupSummaryItems = useMemo<SpecialistBookingSummaryItem[]>(
    () =>
      currentGroupCoordinator?.participants.flatMap((participant) => {
        const participantLabel =
          groupParticipantLabels.get(participant.participantId) ??
          t("servicesModal.groupOrganizer");

        return participant.serviceLines.map((line) => {
          const coverageLabel = workplace.locationId === "venue-glow-room" && line.quote.currency === "RON"
            ? getServiceSessionDemoBadgeLabel({ serviceId: line.selection.serviceId, locale }) ?? undefined
            : undefined;
          const amountMinor = line.quote.amountMinor * line.quantity;
          const amount = specialistPriceMinorUnitsToAmount(
            amountMinor,
            line.quote.currency,
          );
          const compareAtAmount = line.quote.compareAt
            ? specialistPriceMinorUnitsToAmount(line.quote.compareAt.amountMinor * line.quantity, line.quote.compareAt.currency)
            : null;
          const durationMinutes = line.quote.durationMinutes * line.quantity;
          const durationMaxMinutes =
            (line.quote.durationMaxMinutes ?? line.quote.durationMinutes) *
            line.quantity;
          const durationLabel =
            durationMaxMinutes > durationMinutes
              ? t("common.durationRange", {
                  min: durationMinutes,
                  max: durationMaxMinutes,
                })
              : t("common.duration", { count: durationMinutes });

          return {
            id: line.lineId,
            benefitLineIds: Array.from({ length: line.quantity }, (_, index) =>
              `catalog:${line.selection.serviceId}${index === 0 ? "" : `:unit:${index}`}`),
            isStartingPrice: line.quote.priceType === "from",
            coverageLabel,
            hidePrice: Boolean(coverageLabel) && amountMinor === 0,
            participant: { id: participant.participantId, label: participantLabel },
            name: line.snapshot.serviceName,
            metaLabel: durationLabel,
            originalPriceLabel: compareAtAmount !== null && line.quote.compareAt
              ? formatSpecialistCurrencyAmount(locale, compareAtAmount, line.quote.compareAt.currency)
              : null,
            supplementaryLabels: getAppointmentLineConfigurationSummary(
              line,
              new Map(line.taskExpansion.map(task => [task.serviceId, task.snapshot.serviceName])),
              {
                addOnSummary: values => tAll("booking.selectSlot.addOnSummary", values),
                bundleAddOnSummary: values => tAll("booking.selectSlot.bundleAddOnSummary", values),
                sequentialBundle: tAll("booking.selectSlot.sequentialBundle"),
                simultaneousBundle: tAll("booking.selectSlot.simultaneousBundle"),
              },
            ),
            priceLabel:
              line.quote.priceType === "free"
                ? t("common.free")
                : amount === null
                  ? t("common.priceUnavailable")
                  : formatSpecialistCurrencyAmount(
                      locale,
                      amount,
                      line.quote.currency,
                    ),
          };
        });
      }) ?? [],
    [currentGroupCoordinator, groupParticipantLabels, locale, workplace.locationId, t, tAll],
  );
  const groupRosterBenefits = useServiceBenefitsGroupBookingPreview(
    currentGroupCoordinator && (!activeGroupCatalogTransaction || activeGroupCatalogTransaction.operation === "add-guest")
      ? { coordinator: currentGroupCoordinator } : null,
    { locale, rewardLabel: tAll('customerWallet.rewards.sessionLabel'), free: t('common.free'),
      fromPrice: price => t('servicesModal.totalFrom', { total: price }),
      formatAmount: (amountMinor, currency) => {
        const amount = specialistPriceMinorUnitsToAmount(amountMinor, currency);
        return amount === null ? t('common.priceUnavailable') : formatSpecialistCurrencyAmount(locale, amount, currency);
      } },
  );
  const groupRosterSummaryItems = groupSummaryItems.map(item => {
    const presentation = groupRosterBenefits?.presentation.get(item.id);
    return presentation ? { ...item, priceLabel: presentation.priceLabel ?? item.priceLabel,
      originalPriceLabel: item.originalPriceLabel ?? presentation.originalPriceLabel,
      isStartingPrice: item.isStartingPrice || groupRosterBenefits?.startingLineIds.includes(item.id) } : item;
  });
  const groupTotalValueLabel = useMemo(() => {
    if (!groupSelectionSummary || !groupSummaryCurrency) {
      return t("common.priceUnavailable");
    }
    const totalAmountMinor = groupRosterBenefits?.totalAmountMinor ?? groupSelectionSummary.totalAmountMinor;
    const amount = specialistPriceMinorUnitsToAmount(totalAmountMinor, groupSummaryCurrency);

    if (amount === null) return t("common.priceUnavailable");
    if (totalAmountMinor === 0) return t("common.free");
    const formatted = formatSpecialistCurrencyAmount(
      locale,
      amount,
      groupSummaryCurrency,
    );

    return groupSelectionSummary.hasStartingPrice || Boolean(groupRosterBenefits?.startingLineIds.length)
      ? t("servicesModal.totalFrom", { total: formatted })
      : formatted;
  }, [groupRosterBenefits, groupSelectionSummary, groupSummaryCurrency, locale, t]);
  const groupDurationLabel = groupSelectionSummary
    ? groupSelectionSummary.durationMaxMinutes >
      groupSelectionSummary.durationMinutes
      ? t("common.durationRange", {
          min: groupSelectionSummary.durationMinutes,
          max: groupSelectionSummary.durationMaxMinutes,
        })
      : t("common.duration", {
          count: groupSelectionSummary.durationMinutes,
        })
    : t("common.duration", { count: 0 });
  const groupGuestCount = currentGroupCoordinator
    ? currentGroupCoordinator.participants.length - 1
    : 0;
  const groupRosterEvaluatedAtEpochMs = readSpecialistServicesSafeEpochMs(
    servicesClockRef.current,
  );
  const acceptedGroupConsumerIsReady = Boolean(
    currentGroupCoordinator &&
    groupCoordinatorRouteState.status === "accepted-handoff" &&
    (groupCoordinatorOriginScope?.kind === "location"
      ? isLocationGroupSelectSlotCompositionAvailable()
      : groupCoordinatorOriginScope &&
        specialistGroupOriginScopeOwnsAcceptedHandoff({
          originScope: groupCoordinatorOriginScope,
          coordinator: currentGroupCoordinator,
          appointment: groupCoordinatorRouteState.appointment,
          // Accepted `updatedAt` is immutable evidence produced by the
          // successful A/B/C acceptance. It is late enough to validate the
          // frozen capability, unlike runtime generation, while avoiding a
          // fresh mutable clock read on terminal retry/Close rendering.
          evaluatedAtEpochMs: currentGroupCoordinator.updatedAtEpochMs,
        })),
  );
  const specialistOrganizerRosterIsRecoverable = Boolean(
    currentGroupCoordinator &&
    groupCoordinatorRouteState.status === "ready" &&
    groupCoordinatorOriginScope !== null &&
    groupCoordinatorOriginScope.kind !== "location" &&
    groupRosterEvaluatedAtEpochMs !== null &&
    isExactLiveGroupCoordinatorOwner(currentGroupCoordinator) &&
    isRecoverableSpecialistOrganizerCoordinator(
      currentGroupCoordinator,
      groupRosterEvaluatedAtEpochMs,
    ),
  );
  const groupRosterAuthorityIsCurrent = Boolean(
    currentGroupCoordinator &&
    (groupCoordinatorRouteState.status === "accepted-handoff"
      ? acceptedGroupConsumerIsReady
      : groupRosterEvaluatedAtEpochMs !== null &&
        isExactLiveGroupCoordinatorOwner(currentGroupCoordinator) &&
        isCurrentGroupCoordinatorRouteAuthority(
          currentGroupCoordinator,
          groupRosterEvaluatedAtEpochMs,
        ) &&
        isLocationGroupRosterAuthorityCurrent({
          coordinator: currentGroupCoordinator,
          evaluatedAtEpochMs: groupRosterEvaluatedAtEpochMs,
        })),
  );
  const locationGroupRosterCanBeRepaired = Boolean(
    currentGroupCoordinator &&
    groupCoordinatorRouteState.status === "ready" &&
    groupCoordinatorOriginScope?.kind === "location" &&
    groupRosterEvaluatedAtEpochMs !== null &&
    isExactLiveGroupCoordinatorOwner(currentGroupCoordinator) &&
    isCurrentGroupCoordinatorRouteAuthority(currentGroupCoordinator, groupRosterEvaluatedAtEpochMs),
  );
  const specialistOrganizerRosterHasStaleAuthority = Boolean(
    specialistOrganizerRosterIsRecoverable && !groupRosterAuthorityIsCurrent,
  );
  const initialGroupRosterFocusParticipantId = useMemo(() => {
    if (!currentGroupCoordinator || groupCoordinatorRouteState.status !== "ready" || !groupRosterAuthorityIsCurrent) return null;
    const requestedServiceId = getSingleFocusIdentifier(new URLSearchParams(searchString), "focus_service_id");
    const organizer = currentGroupCoordinator.participants.find(participant => participant.role === "organizer");
    const firstServiceId = organizer && [...organizer.serviceLines]
      .sort((left, right) => left.selectionOrder - right.selectionOrder)[0]?.selection.serviceId;
    return organizer && requestedServiceId && requestedServiceId === firstServiceId ? organizer.participantId : null;
  }, [currentGroupCoordinator, groupCoordinatorRouteState.status, groupRosterAuthorityIsCurrent, searchString]);
  const groupMinimumIsSatisfied = Boolean(
    currentGroupCoordinator &&
    groupGuestCount >= currentGroupCoordinator.capability.minimumGuests,
  );
  const groupContinueIsActionable = Boolean(
    currentGroupCoordinator &&
    (groupCoordinatorRouteState.status === "accepted-handoff"
      ? acceptedGroupConsumerIsReady && acceptedGroupSelectSlotHref !== null
      : groupCoordinatorOriginScope &&
        (groupCoordinatorOriginScope.kind === "location"
          ? groupRosterEvaluatedAtEpochMs !== null &&
            isCurrentGroupCoordinatorRouteAuthority(
              currentGroupCoordinator,
              groupRosterEvaluatedAtEpochMs,
            ) &&
            isLocationGroupSelectSlotCompositionAvailable() &&
            isLocationGroupRosterContinueReady({
              allowProviderRefresh: Boolean(frontendGroupServicesHost && isLocationGroupServicesEntryAvailable(groupRosterEvaluatedAtEpochMs)),
              coordinator: currentGroupCoordinator,
              customerAccountId: groupCustomerOwnerState.customerAccountId,
              anonymousSessionOwnerId:
                groupCustomerOwnerState.anonymousSessionOwnerId,
              evaluatedAtEpochMs: groupRosterEvaluatedAtEpochMs,
            })
          : groupRosterEvaluatedAtEpochMs !== null &&
            specialistOrganizerRosterIsRecoverable &&
            specialistGroupOriginScopeCanConsumeDownstream(
              groupCoordinatorOriginScope,
              groupRosterEvaluatedAtEpochMs,
            ) &&
            currentGroupCoordinator.expiresAtEpochMs >
              groupRosterEvaluatedAtEpochMs &&
            isCurrentGroupBookingCoordinatorCapability(
              currentGroupCoordinator.capability,
              groupRosterEvaluatedAtEpochMs,
            ) &&
            currentGroupCoordinator.participants.length - 1 >=
              currentGroupCoordinator.capability.minimumGuests &&
            (currentGroupCoordinator.customerAccountId === null
              ? groupCustomerOwnerState.customerAccountId === null &&
                groupCustomerOwnerState.anonymousSessionOwnerId ===
                  currentGroupCoordinator.anonymousSessionOwnerId
              : groupCustomerOwnerState.customerAccountId ===
                currentGroupCoordinator.customerAccountId))),
  );
  const pendingGroupRemovalIsCurrent = Boolean(
    pendingGroupRemoval &&
    currentGroupCoordinator &&
    pendingGroupRemoval.coordinatorDraftId ===
      currentGroupCoordinator.draftId &&
    pendingGroupRemoval.coordinatorRevision ===
      currentGroupCoordinator.revision,
  );
  const pendingGroupRemovalLabel = pendingGroupRemovalIsCurrent
    ? (groupParticipantLabels.get(pendingGroupRemoval!.participantId) ??
      t("servicesModal.groupGuest", { number: GROUP_FIRST_GUEST_NUMBER }))
    : null;
  const pendingGroupExitIsCurrent = Boolean(
    pendingGroupExit &&
    currentGroupCoordinator &&
    pendingGroupExit.coordinatorDraftId === currentGroupCoordinator.draftId &&
    pendingGroupExit.coordinatorRevision === currentGroupCoordinator.revision,
  );
  const initialGroupCatalogOwnerKey = (() => {
    if (
      appointmentMode !== "group" ||
      groupCoordinatorPointerIsPresent ||
      !groupCustomerOwnerState.hydrated ||
      !groupCustomerOwnerState.anonymousSessionOwnerId ||
      !routeGroupCatalogOwnerUserLocationId
    ) {
      return null;
    }
    const locationOwnerParts = [
      "location-group-organizer-catalog",
      routeLocationId,
      workplace.catalogVersion,
      routeGroupCatalogOwnerUserLocationId,
      groupCustomerOwnerState.anonymousSessionOwnerId,
      groupCustomerOwnerState.customerAccountId ?? "anonymous",
    ];

    if (isLocationEntryPoint) {
      // Keep the established Location child owner byte-semantic.
      return locationOwnerParts.join(":");
    }
    if (specialistLocationGatewayPreDraftGroupDescriptor) {
      return createCanonicalGroupBookingTupleKey([
        ...locationOwnerParts,
        "specialist-location-gateway-pre-draft",
        specialistLocationGatewayPreDraftGroupDescriptor.servicesOwnerKey,
      ]);
    }
    const runtime =
      specialistGroupStepOneRuntimeResolution.status === "ready"
        ? specialistGroupStepOneRuntimeResolution.runtime
        : null;
    if (!runtime) return null;

    return [
      ...locationOwnerParts,
      "specialist-current-assignment",
      person?.personId,
      assignment.personId,
      assignment.locationId,
      assignment.userLocationId,
      runtime.sourcePersonId,
      runtime.sourceLocationId,
      runtime.sourceUserLocationId,
      runtime.selectedLocationId,
      runtime.selectedUserLocationId,
      runtime.providerId,
      runtime.providerRevision,
      runtime.catalogFingerprint,
      specialistOrganizerGroupCatalogFingerprint ??
        "invalid-organizer-catalog-fingerprint",
      specialistOrganizerGroupCatalogProviderId ??
        "invalid-organizer-catalog-provider",
      runtime.returnHref,
      runtime.generatedAtEpochMs,
      runtime.expiresAtEpochMs,
    ].join(":");
  })();
  const servicesCatalogOwnerKey =
    activeGroupCatalogTransaction?.ownerKey ?? initialGroupCatalogOwnerKey;
  const servicesModalInstanceKey = activeGroupCatalogTransaction
    ? activeGroupCatalogTransaction.ownerKey
    : groupCoordinatorPointerIsPresent
      ? `group-handoff:${groupCoordinatorHandoffKey}`
      : (initialGroupCatalogOwnerKey ?? `services-page-catalog${locationResourceOnlyExecutionsV1 ? `:${JSON.stringify(locationAppointmentCatalog?.eligibilityPublication)}` : ""}`);
  const initialGroupOwnerState =
    appointmentMode === "group" && !groupCoordinatorPointerIsPresent
      ? !groupCustomerOwnerState.hydrated
        ? "loading"
        : groupCustomerOwnerState.anonymousSessionOwnerId &&
            initialGroupCatalogOwnerKey
          ? "ready"
          : "error"
      : "ready";

  const readGroupResourceCatalogEpoch = useCallback(() => readSpecialistServicesSafeEpochMs(servicesClockRef.current), []);
  const isGroupResourceCatalogOwnerCurrent = useCallback(() => {
    if (!isLocationEntryPoint || !groupCustomerOwnerState.hydrated || !groupCustomerOwnerState.anonymousSessionOwnerId || !isLocationGroupServicesEntryAvailable() ||
      readGroupBookingAnonymousSessionOwnerId() !== groupCustomerOwnerState.anonymousSessionOwnerId || getStoredCustomerAccountId() !== groupCustomerOwnerState.customerAccountId) return false;
    try { const actual = new URL(window.location.href); return actual.pathname === pathname && actual.searchParams.toString() === searchString; } catch { return false; }
  }, [groupCustomerOwnerState, isLocationEntryPoint, isLocationGroupServicesEntryAvailable, pathname, searchString]);
  const groupResourceCatalogPermission = useGroupBookingResourceCatalogPermissionV1({
    enabled: appointmentMode === "group" && isLocationEntryPoint && locationGroupServicesEntryAvailable,
    catalog: locationAppointmentCatalog, provider: routeGroupCapabilityProvider,
    ownerKey: servicesCatalogOwnerKey ?? groupCoordinatorHandoffKey, now: readGroupResourceCatalogEpoch, isCurrentOwner: isGroupResourceCatalogOwnerCurrent,
  });
  const groupResourceCatalogExecutionsV1 = groupResourceCatalogPermission.executions;
  const resourceOfferingIdsForSelections = (selections: readonly SpecialistServiceSelection[]) => selections.flatMap(selection => {
    const offering = offeringByServiceId.get(selection.serviceId);
    return offering && locationResourceOnlyExecutionsV1?.some(execution => execution.offeringId === offering.offeringId) ? [offering.offeringId] : [];
  });
  const selectedGroupResourceOfferingIds = appointmentMode === "group" && isLocationEntryPoint ? resourceOfferingIdsForSelections(activeSelectedServices) : [];
  const groupResourceSelectionUnavailable = selectedGroupResourceOfferingIds.some(id => !groupResourceCatalogExecutionsV1?.some(execution => execution.offeringId === id));

  if (isLocationEntryPoint && groupCoordinatorPointerIsPresent &&
    groupCoordinatorRouteState.status === "invalid" && !membershipSessionServicesEntryIsRequired) {
    // An opaque pointer is neither a live selection nor an in-flight action.
    // Restart is explicit navigation only: do not clear old storage, restore
    // stale line authority, or infer whether a missing source was accepted.
    const restart = () => {
      router.replace(`${canonicalServicesPath}?appointment_mode=group`);
    };
    const close = () => { router.replace(canonicalProfilePath); };
    return <BookingFlowPageShell
      title={t("servicesModal.groupRecovery.title")}
      backLabel={t("servicesModal.groupRecovery.restart")}
      closeLabel={tAll("booking.selectSlot.close")}
      onBack={restart}
      onClose={close}
      contentClassName={bookingCompletionStyles.main}
    >
      <section className={bookingCompletionStyles.state} role="status" aria-live="polite">
        <p>{t("servicesModal.groupRecovery.description")}</p>
        <div className={bookingCompletionStyles.stateActions}>
          <VoleraButton onClick={restart}>{t("servicesModal.groupRecovery.restart")}</VoleraButton>
        </div>
      </section>
    </BookingFlowPageShell>;
  }

  if (currentGroupCoordinator && !activeGroupCatalogTransaction && !membershipSessionServicesEntryIsRequired) {
    const maximumReached =
      groupGuestCount >=
      currentGroupCoordinator.capability.effectiveMaximumGuests;
    const rosterIsBusy =
      isGroupMutationPending ||
      (!groupRosterAuthorityIsCurrent &&
        !locationGroupRosterCanBeRepaired &&
        !specialistOrganizerRosterHasStaleAuthority);
    const footer = groupSelectionSummary ? (
      <BookingFlowSelectionFooter
        actionBusy={isGroupMutationPending}
        actionDisabled={!groupContinueIsActionable}
        actionLabel={t("servicesModal.continue")}
        durationLabel={groupDurationLabel}
        itemCountLabel={t("servicesModal.footerItems", {
          count: groupSelectionSummary.itemCount,
        })}
        onAction={() => {
          void continueGroupRoster();
        }}
        totalValueLabel={groupTotalValueLabel}
      />
    ) : null;
    const workplaceRating = workplacePresentation?.rating;
    const normalizedWorkplaceRating = typeof workplaceRating === "number" &&
      Number.isFinite(workplaceRating) && workplaceRating >= 0 && workplaceRating <= 5
      ? workplaceRating : null;
    const formattedWorkplaceRating = normalizedWorkplaceRating === null ? null :
      new Intl.NumberFormat(locale, { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(normalizedWorkplaceRating);
    const summary = groupSelectionSummary ? (
      <SpecialistBookingSummary
        actionBusy={isGroupMutationPending}
        actionDisabled={!groupContinueIsActionable}
        actionLabel={t("servicesModal.continue")}
        emptySelectionLabel={t("servicesModal.noneSelected")}
        items={groupRosterSummaryItems}
        liveSelectionLabel={t("servicesModal.selectedCount", {
          count: groupSelectionSummary.itemCount,
        })}
        onAction={() => {
          void continueGroupRoster();
        }}
        onWorkplaceNavigate={(event) => {
          event.preventDefault();
          requestGroupRosterExit("close");
        }}
        rating={workplacePresentation && normalizedWorkplaceRating !== null && formattedWorkplaceRating !== null ? {
          accessibleLabel: t("common.ratingWithReviews", { rating: formattedWorkplaceRating, count: workplacePresentation.reviewCount }),
          reviewCountLabel: t("profile.reviewCountShort", { count: workplacePresentation.reviewCount }),
          value: normalizedWorkplaceRating,
          valueLabel: formattedWorkplaceRating,
        } : null}
        selectionListLabel={t("servicesModal.selectedCount", {
          count: groupSelectionSummary.itemCount,
        })}
        showForwardArrow
        totalLabel={t("servicesModal.totalLabel")}
        totalValueLabel={groupTotalValueLabel}
        workplaceAddress={workplace.address}
        workplaceImageAlt={workplacePresentation?.imageAlt ?? ""}
        workplaceImageSrc={workplacePresentation?.imageSrc ?? null}
        workplaceHref={canonicalProfilePath}
        workplaceName={workplace.name}
        workplacePreviewImageSources={workplacePresentation?.images?.map(
          (image) => image.src,
        )}
      />
    ) : null;

    return (
      <>
        <AppointmentGroupRosterStep
          actionsLabel={(participantLabel) =>
            t("servicesModal.groupParticipantActions", {
              participant: participantLabel,
            })
          }
          addDisabled={
            maximumReached || specialistOrganizerRosterHasStaleAuthority
          }
          canEditParticipant={(participant) =>
            !specialistOrganizerRosterHasStaleAuthority ||
            participant.isOrganizer
          }
          canRemoveParticipant={() =>
            !specialistOrganizerRosterHasStaleAuthority
          }
          hideAddGuest={maximumReached}
          addGuestLabel={t("servicesModal.groupAddGuest")}
          backLabel={tAll("location.servicesPage.backToProfile")}
          closeActionsLabel={t("servicesModal.groupParticipantActionsClose")}
          closeLabel={tAll("location.servicesPage.close")}
          contentStageKey={`${currentGroupCoordinator.draftId}:${currentGroupCoordinator.revision}`}
          disabled={rosterIsBusy}
          editServicesLabel={t("servicesModal.groupEditServices")}
          footer={footer}
          initialFocusParticipantId={initialGroupRosterFocusParticipantId}
          maximumReachedDescription={[
            maximumReached
              ? t("servicesModal.groupMaximumReached", {
                  count: currentGroupCoordinator.capability.effectiveMaximumGuests + 1,
                })
              : !groupMinimumIsSatisfied
                ? t("servicesModal.groupMinimumGuestsRequired", {
                    count: currentGroupCoordinator.capability.minimumGuests,
                  })
                : null,
            !(process.env.NODE_ENV === "development" && frontendGroupServicesHost) &&
              locationGroupRosterCanBeRepaired && !groupRosterAuthorityIsCurrent
              ? t("servicesModal.groupCombinationUnconfirmed")
              : null,
          ].filter(Boolean).join(" ") || null}
          onAddGuest={handleAddGroupParticipant}
          onBack={() => {
            if (groupLifecycleNavigationRecovery?.action === "back") {
              void retryGroupLifecycleNavigation("back");
              return;
            }
            if (currentGroupCoordinator) void confirmGroupRosterExit({
              action: "back", coordinatorDraftId: currentGroupCoordinator.draftId,
              coordinatorRevision: currentGroupCoordinator.revision,
            });
          }}
          onClose={() => requestGroupRosterExit("close")}
          onEditServices={(participantId) => {
            void handleEditGroupParticipant(participantId);
          }}
          onInitialFocusHandled={handleInitialFocusHandled}
          onRemoveGuest={handleRemoveGroupParticipant}
          participants={groupRosterParticipants}
          removeGuestLabel={t("servicesModal.groupRemoveConfirm")}
          rosterDisabled={
            rosterIsBusy ||
            groupCoordinatorRouteState.status === "accepted-handoff"
          }
          subtitle={t("servicesModal.groupParticipantsDescription", {
            count: currentGroupCoordinator.capability.effectiveMaximumGuests + 1,
          })}
          summary={summary}
          title={t("servicesModal.groupParticipantsTitle")}
        />

        <ConfirmationModal
          compactActions="inline"
          isOpen={pendingGroupRemovalLabel !== null}
          presentation="booking-exit"
          title={t("servicesModal.groupRemoveTitle", {
            participant: pendingGroupRemovalLabel ?? "",
          })}
          message={t("servicesModal.groupRemoveDescription")}
          secondaryLabel={t("servicesModal.groupRemoveCancel")}
          primaryLabel={t("servicesModal.groupRemoveConfirm")}
          isLoading={isGroupMutationPending}
          onClose={() => setPendingGroupRemoval(null)}
          onSecondaryAction={() => setPendingGroupRemoval(null)}
          onPrimaryAction={confirmGroupParticipantRemoval}
        />

        <CheckoutConfirmationModal
          isOpen={pendingGroupExitIsCurrent}
          title={t("servicesModal.leaveConfirmation.title")}
          message={t("servicesModal.leaveConfirmation.message")}
          secondaryLabel={t("servicesModal.leaveConfirmation.cancel")}
          primaryLabel={t("servicesModal.leaveConfirmation.confirm")}
          isLoading={isGroupMutationPending}
          onClose={() => setPendingGroupExit(null)}
          onSecondaryAction={() => setPendingGroupExit(null)}
          onPrimaryAction={() => void confirmGroupRosterExit()}
        />
      </>
    );
  }

  const specialistGuestCatalogTransaction = Boolean(
    !isLocationEntryPoint &&
    activeGroupCatalogTransaction?.source.participantRole === "guest",
  );
  const specialistGuestLocationCatalog =
    specialistLocationGatewayGroupRuntimeContext.status === "ready"
      ? locationAppointmentCatalog
      : specialistGroupStepOneRuntimeResolution.status === "ready"
        ? specialistGroupStepOneRuntimeResolution.catalog
        : null;
  const specialistGuestCatalogProjection =
    specialistGuestCatalogTransaction &&
    specialistGuestLocationCatalog &&
    activeSpecialistGroupGuestServiceCategories
      ? {
          assignment: specialistGuestLocationCatalog.catalogAssignment,
          workplace: specialistGuestLocationCatalog.workplace,
          services: specialistGuestLocationCatalog.services,
          offerings: specialistGuestLocationCatalog.offerings,
          serviceCategoryPublication:
            activeSpecialistGroupGuestServiceCategories,
        }
      : null;
  const presentedEntryPointKind: SpecialistServicesEntryPointKind =
    specialistGuestCatalogTransaction ? "location-services" : entryPointKind;
  const presentedAssignment = specialistGuestCatalogTransaction
    ? (specialistGuestCatalogProjection?.assignment ?? assignment)
    : assignment;
  const presentedWorkplace = specialistGuestCatalogTransaction
    ? (specialistGuestCatalogProjection?.workplace ?? workplace)
    : workplace;
  const presentedServices = specialistGuestCatalogTransaction
    ? (specialistGuestCatalogProjection?.services ?? [])
    : services;
  const presentedOfferings = specialistGuestCatalogTransaction
    ? (specialistGuestCatalogProjection?.offerings ?? [])
    : offerings;
  const presentedServiceCategoryPublication = specialistGuestCatalogTransaction
    ? (specialistGuestCatalogProjection?.serviceCategoryPublication ?? [])
    : serviceCategoryPublication;

  const membershipDisplay = readyMembershipSessionServicesEntry && locationAppointmentCatalog
    ? createMembershipSessionServicesDisplay({ entry: readyMembershipSessionServicesEntry, locationCatalog: locationAppointmentCatalog,
        activeSelections: activeSelectedServices, nowEpochMs: readSpecialistServicesSafeEpochMs(servicesClockRef.current) ?? Number.NaN }, {
        sessionLabel: tAll("activity.membership.sessionLabel"),
        now: () => readSpecialistServicesSafeEpochMs(servicesClockRef.current) ?? Number.NaN,
        isCurrentOwner: () => {
          if (!membershipServicesMountedRef.current || membershipServicesRouteKeyRef.current !== membershipServicesRouteKey) return false;
          const actual = new URL(window.location.href);
          return actual.pathname === pathname && actual.searchParams.toString() === searchString;
        },
      })
    : null;
  const membershipSessionPresentation = membershipDisplay?.presentation ?? null;
  const packageSessionPresentation = readyPackageSessionServicesEntry?.intent.schemaVersion === 2 && locationAppointmentCatalog
    ? createPackageSessionServicesDisplayV2({ intent: readyPackageSessionServicesEntry.intent, locationCatalog: locationAppointmentCatalog }, {
        sessionLabel: tAll("activity.package.booking.sessionLabel"),
        now: () => readSpecialistServicesSafeEpochMs(servicesClockRef.current) ?? Number.NaN,
        isCurrentOwner: () => {
          if (!packageEntryMountedRef.current || packageEntryRouteRef.current.href !== currentServicesHref) return false;
          const actual = new URL(window.location.href);
          return actual.pathname === pathname && actual.searchParams.toString() === searchString;
        },
      }) : null;
  const rewardEntry = groupCustomerOwnerState.hydrated && isLocationEntryPoint && appointmentMode === "individual" &&
    !membershipSessionServicesEntryIsRequired && !packageSessionServicesEntryIsRequired && !resultsServicesEntryIsRequired && !specialistLocationGatewaySession
    ? readCustomerRewardBookingEntryForRoute() : null;
  const readRewardDisplaySource = () => {
    if (!rewardEntry) return null;
    const selected = rewardEntry.draftId ? readRewardAppointmentSelectionV1(rewardEntry.draftId, rewardEntry.customerAccountId) : null;
    if (selected?.status === "invalid") return null;
    if (selected?.status === "ready" && isRewardAppointmentSelectionCurrentV1(selected.selection)) {
      return selected.selection.allocation.selected
        ? selected.selection.allocation.context.publication.rewards.find(row => row.rewardId === rewardEntry.rewardId && row.rewardRevision === rewardEntry.rewardRevision) ?? null
        : null;
    }
    // Fresh Wallet companions carry navigation only; retained V1 allocations
    // above continue to render under their historical contract.
    return null;
  };
  const rewardDisplaySource = readRewardDisplaySource();
  const rewardSessionPresentation = rewardEntry && rewardDisplaySource && locationAppointmentCatalog
    ? createCustomerRewardServicesDisplay({ entry: rewardEntry, reward: rewardDisplaySource, locationCatalog: locationAppointmentCatalog,
      selections: activeSelectedServices, applied: rewardEntry.selected }, {
      sessionLabel: tAll("customerWallet.rewards.sessionLabel"), now: Date.now, readCurrentReward: readRewardDisplaySource,
      isCurrentOwner: () => rewardAppointmentProfileOwnerIsCurrent(rewardEntry.customerAccountId, rewardEntry.profileOwnerKey),
    }) : null;
  const membershipContinuationUnavailableLabel = (() => {
    if (!membershipSessionServicesEntryIsRequired) return null;
    if (!readyMembershipSessionServicesEntry || !locationAppointmentCatalog) {
      return tAll("activity.membership.bookingUnavailable");
    }
    if (activeSelectedServices.length === 0) return null;
    const preview = membershipDisplay?.cart;
    if (!preview) return tAll("activity.membership.bookingSelectionUnavailable");
    return null;
  })();

  const aggregateParticipantId = activeGroupCatalogTransaction?.source.participantId ?? 'organizer';
  const aggregateOtherLines = appointmentMode === 'group' && currentGroupCoordinator
    ? buildGroupServiceCatalogOtherLines({ coordinator: currentGroupCoordinator, participantId: aggregateParticipantId,
        services: presentedServices, offerings: presentedOfferings, workplace: presentedWorkplace }) : [];
  const aggregateSelectionQuantities = activeGroupCatalogTransaction
    ? activeSelectedServices.map((selection, index) => ({ serviceId: selection.serviceId, quantity: groupCatalogQuantities[index] ?? 1 }))
    : undefined;
  const aggregateOtherStaff = currentGroupCoordinator?.participants.flatMap(participant => {
    if (participant.participantId === aggregateParticipantId) return [];
    const staffIds = new Set(participant.serviceLines.map(line => line.specialistOrganizerCatalogAuthorization?.selectedUserLocationId ?? null));
    const staffUserLocationId = staffIds.size === 1 ? [...staffIds][0] : null;
    return staffUserLocationId ? [{ participantId: participant.participantId, staffUserLocationId }] : [];
  });
  const aggregateSourceDraftId = timeResumeHydration.status === 'restored' ? timeResumeHydration.sourceDraft.draftId : rewardEntry?.draftId;
  const aggregateCustomer = getCustomerAccessForFrontendUi();
  const aggregateStored = aggregateSourceDraftId && aggregateCustomer
    ? readServiceBenefitsAppointmentSelection(aggregateSourceDraftId, aggregateCustomer.account.uuid) : null;
  const aggregateProfile = frontendSessionCustomerProfileProvider.readCurrentProfile();
  const aggregateGroupChoice = currentGroupCoordinator && aggregateProfile.status === 'ready'
    ? readServiceBenefitsGroupCatalogChoice({ coordinator: currentGroupCoordinator,
        customerAccountId: aggregateProfile.snapshot.customerAccountId, profileOwnerKey: aggregateProfile.snapshot.ownerKey }) : null;
  const serviceBenefitsContext: ServiceCatalogBenefitsContextV1 = {
    unavailable: aggregateStored?.status === 'invalid' || aggregateGroupChoice?.status === 'unavailable' || aggregateOtherLines === null ||
      Boolean(activeGroupCatalogTransaction && groupCatalogQuantities.length !== activeSelectedServices.length),
    ...(aggregateSourceDraftId ? { sourceId: aggregateSourceDraftId } : {}), participantId: aggregateParticipantId,
    staffUserLocationId: presentedEntryPointKind === 'location-services' ? null : presentedAssignment.userLocationId,
    ...(aggregateSelectionQuantities ? { selectionQuantities: aggregateSelectionQuantities } : {}),
    ...(aggregateOtherStaff?.length ? { otherStaffUserLocationIds: aggregateOtherStaff } : {}),
    ...(aggregateOtherLines ? { otherLines: aggregateOtherLines } : {}),
    ...(aggregateGroupChoice?.status === 'ready' ? { selection: aggregateGroupChoice.selection } : {}),
    ...(aggregateStored?.status === 'ready' && !aggregateStored.selection.binding ? { selection: aggregateStored.selection.evidence.plan.selection } : {}),
    ...(readyMembershipSessionServicesEntry && locationAppointmentCatalog ? {
      resolveCoverage: (lines, selections) => {
        const preview = resolveFrontendMembershipSessionCartPreviewV2({ entry: readyMembershipSessionServicesEntry,
          locationCatalog: locationAppointmentCatalog, nowEpochMs: Date.now(), selections });
        if (!preview) return null;
        const allocations = preview.lines.flatMap(line => {
          const source = lines.find(row => row.serviceId === line.lineId && row.participantId === aggregateParticipantId);
          return source && line.coverage ? [{ participantId: source.participantId, lineId: source.lineId,
            baseAmountMinor: line.coverage.discountAmountMinor, extrasAmountMinor: 0 }] : [];
        });
        return allocations.length ? [{ coverageId: readyMembershipSessionServicesEntry.intent.draftId, coverageRevision: '1',
          title: tAll('activity.membership.sessionLabel'), kind: 'membership', allocations }] : [];
      },
    } : packageSessionPresentation && readyPackageSessionServicesEntry ? {
      resolveCoverage: lines => {
        const allocations = [];
        for (const line of lines) {
          const selected = { serviceId: line.serviceId, variantId: line.variantId,
            addOns: line.addOns.map(extra => ({ groupId: extra.groupId, addOnId: extra.addOnId, quantity: extra.quantity })) };
          const covered = packageSessionPresentation.resolveSelection(selected);
          if (!covered?.covered) continue;
          const payable = specialistPriceAmountToMinorUnits(covered.price.amount, covered.price.currency);
          if (payable === null || line.totalAmountMinor - payable !== line.baseAmountMinor) return null;
          allocations.push({ participantId: line.participantId, lineId: line.lineId, baseAmountMinor: line.baseAmountMinor, extrasAmountMinor: 0 });
        }
        return allocations.length ? [{ coverageId: readyPackageSessionServicesEntry.intent.draftId, coverageRevision: '1',
          title: tAll('activity.package.booking.sessionLabel'), kind: 'package', allocations }] : [];
      },
    } : {}),
  };

  return (
    <SpecialistServicesModal
      giftPackageCatalog={isGiftPackageCatalog}
      serviceBenefitsContext={serviceBenefitsContext}
      key={`${servicesModalInstanceKey}:${membershipSessionServicesEntryIsRequired ? readyMembershipSessionServicesEntry?.source.sourceOwnerKey ?? "membership-unavailable" : "ordinary"}`}
      appointmentMode={appointmentMode}
      groupParticipantIsGuest={activeGroupCatalogTransaction?.source.participantRole === "guest"}
      groupSummaryContext={appointmentMode === "group" && activeGroupCatalogTransaction && currentGroupCoordinator ? {
        participantId: activeGroupCatalogTransaction.source.participantId,
        participantOrder: [...new Set([...currentGroupCoordinator.participants.map(participant => participant.participantId), activeGroupCatalogTransaction.source.participantId])],
        pendingGuestSummary: activeGroupCatalogTransaction.operation === "add-guest"
          ? { totalValueLabel: groupTotalValueLabel } : undefined,
        others: currentGroupCoordinator.participants.filter(participant => participant.participantId !== activeGroupCatalogTransaction.source.participantId).map(participant => {
          const duration = groupSelectionSummary?.participantDurations.find(value => value.participantId === participant.participantId);
          return {
            participantId: participant.participantId,
            items: groupRosterSummaryItems.filter(item => item.participant?.id === participant.participantId),
            totalAmountMinor: participant.serviceLines.reduce((sum, line) => sum + line.quote.amountMinor * line.quantity, 0),
            currency: groupSummaryCurrency ?? "",
            durationMinutes: duration?.durationMinutes ?? 0,
            durationMaxMinutes: duration?.durationMaxMinutes ?? 0,
            hasStartingPrice: participant.serviceLines.some(line => line.quote.priceType === "from"),
          };
        }),
      } : null}
      groupParticipantName={appointmentMode === "group"
        ? activeGroupCatalogTransaction?.source.participantRole === "guest"
          ? groupParticipantLabels.get(activeGroupCatalogTransaction.source.participantId) ?? t("servicesModal.groupGuest", {
              number: getStableGroupGuestNumber(activeGroupCatalogTransaction.source.participantId,
                Math.max((currentGroupCoordinator?.participants.length ?? 1) - 1, 0)),
            })
          : groupOrganizerDisplayName ?? t("servicesModal.groupOrganizer")
        : null}
      revealSelectedServiceOnMount={activeGroupCatalogTransaction?.operation === "edit-participant"}
      catalogSelectionOwnerKey={membershipSessionPresentation || packageSessionPresentation || rewardSessionPresentation
        ? `${servicesCatalogOwnerKey}:${(membershipSessionPresentation ?? packageSessionPresentation ?? rewardSessionPresentation)!.ownerKey}` : servicesCatalogOwnerKey}
      confirmSelectionOnDismiss={!activeGroupCatalogTransaction}
      dataState={
        groupResourceSelectionUnavailable
          ? "error"
          : membershipSessionServicesEntryIsRequired && !readyMembershipSessionServicesEntry
          ? "error"
          : specialistGuestCatalogTransaction && !specialistGuestCatalogProjection
          ? "error"
          : resultsServicesEntryIsRequired &&
              resultsServicesEntryHydration.status !== "ready"
            ? resultsServicesEntryHydration.status === "hydrating"
              ? "loading"
              : "error"
            : packageSessionServicesEntryIsRequired &&
                packageSessionServicesEntryHydration.status !== "ready"
              ? packageSessionServicesEntryHydration.status === "hydrating"
                ? "loading"
                : "error"
              : specialistLocationGatewayServicesCartIsApplicable &&
                  (currentSpecialistLocationGatewayServicesCartState.status ===
                    "hydrating" ||
                    currentSpecialistLocationGatewayServicesCartState.status ===
                      "unavailable")
                ? currentSpecialistLocationGatewayServicesCartState.status ===
                  "hydrating"
                  ? "loading"
                  : "error"
                : groupCoordinatorPointerIsPresent &&
                    groupCoordinatorRouteState.status !== "ready"
                  ? groupCoordinatorRouteState.status === "hydrating"
                    ? "loading"
                    : "error"
                  : initialGroupOwnerState !== "ready"
                    ? initialGroupOwnerState
                    : isTimeResumeCanonicalizationBlocked
                      ? "error"
                      : "ready"
      }
      entryPointKind={presentedEntryPointKind}
      presentation="page"
      isOpen
      person={person}
      locationResourceOnlyExecutionsV1={appointmentMode === "individual" ? locationResourceOnlyExecutionsV1 : groupResourceCatalogExecutionsV1}
      assignment={presentedAssignment}
      workplace={presentedWorkplace}
      workplacePresentation={workplacePresentation}
      services={presentedServices}
      offerings={presentedOfferings}
      serviceReviewSummaries={
        specialistGuestCatalogTransaction ? [] : serviceReviewSummaries
      }
      servicePortfolioImages={
        specialistGuestCatalogTransaction ? [] : servicePortfolioImages
      }
      packages={
        specialistGuestCatalogTransaction ||
        specialistLocationGatewayServicesCartIsApplicable ||
        membershipSessionServicesEntryIsRequired ||
        packageSessionServicesEntryIsRequired
          ? []
          : packages
      }
      freshLocationPackagePurchase={isLocationEntryPoint && !specialistLocationGatewaySession}
      canPurchasePackage={item => isPackageCatalogDefinitionV1(item) ||
        (isLocationEntryPoint && !specialistLocationGatewaySession && isPackageCatalogDefinitionV2(item))}
      serviceCategoryPublication={presentedServiceCategoryPublication}
      packageCategoryPublication={
        specialistGuestCatalogTransaction ||
        specialistLocationGatewayServicesCartIsApplicable ||
        membershipSessionServicesEntryIsRequired ||
        packageSessionServicesEntryIsRequired
          ? []
          : packageCategoryPublication
      }
      catalogView={
        specialistLocationGatewayServicesCartIsApplicable ||
        membershipSessionServicesEntryIsRequired ||
        packageSessionServicesEntryIsRequired
          ? "services"
          : catalogView
      }
      serviceBenefitPresentation={
        readyPackageSessionServicesEntry?.intent.schemaVersion === 1
          ? {
              providerId: readyPackageSessionServicesEntry.intent.providerId,
              serviceId: readyPackageSessionServicesEntry.intent.serviceId,
              offeringId: readyPackageSessionServicesEntry.intent.offeringId,
              priceLabel: tAll("activity.package.booking.sessionLabel"),
            }
          : null
      }
      membershipSessionPresentation={membershipSessionPresentation}
      serviceSessionPresentation={packageSessionPresentation ?? rewardSessionPresentation}
      continuationUnavailableLabel={groupResourceSelectionUnavailable ? t("servicesModal.unavailable") : readyPackageSessionServicesEntry?.intent.schemaVersion === 2 && !packageSessionPresentation
        ? tAll("activity.package.owned.notBookable") : membershipContinuationUnavailableLabel}
      selectedServices={activeSelectedServices}
      selectedPackageIds={
        specialistLocationGatewayServicesCartIsApplicable
          ? []
          : requestedPackageIds
      }
      groupBookingAvailable={
        groupBookingAvailable && !packageSessionServicesEntryIsRequired && !membershipSessionServicesEntryIsRequired
      }
      initialConfigurationTarget={initialConfigurationTarget}
      initialFocusTarget={initialFocusTarget}
      initialServiceProfessionalNames={initialServiceProfessionalNames}
      initialProfessionalName={
        specialistGuestCatalogTransaction
          ? null
          : currentServicesProfessionalName
      }
      isContinuing={
        isContinuing ||
        (packageSessionServicesEntryIsRequired && packageSelectionPublication !== null) ||
        isGroupMutationPending ||
        (groupCoordinatorPointerIsPresent &&
          (groupCoordinatorRouteState.status === "hydrating" ||
            groupCoordinatorRouteState.status === "auth-binding-required")) ||
        (resultsServicesEntryIsRequired &&
          resultsServicesEntryHydration.status !== "ready") ||
        (packageSessionServicesEntryIsRequired &&
          packageSessionServicesEntryHydration.status !== "ready") ||
        isTimeResumeBlocking ||
        (specialistLocationGatewayServicesCartIsApplicable &&
          currentSpecialistLocationGatewayServicesCartState.status ===
            "hydrating")
      }
      returnFocusRef={returnFocusRef}
      onSelectedServicesChange={handleSelectedServicesChange}
      onSelectedPackageIdsChange={
        specialistLocationGatewayServicesCartIsApplicable
          ? undefined
          : handleSelectedPackagesChange
      }
      onCatalogViewChange={isGiftPackageCatalog ? undefined : handleCatalogViewChange}
      onAppointmentModeChange={
        groupCoordinatorPointerIsPresent ||
        membershipSessionServicesEntryIsRequired ||
        packageSessionServicesEntryIsRequired
          ? undefined
          : handleAppointmentModeChange
      }
      giftCardsHref={
        specialistGuestCatalogTransaction ||
        specialistLocationGatewayServicesCartIsApplicable
          ? undefined
          : giftCardsHref
      }
      membershipsHref={
        specialistGuestCatalogTransaction ||
        specialistLocationGatewayServicesCartIsApplicable
          ? undefined
          : membershipsHref
      }
      productsHref={
        specialistGuestCatalogTransaction ||
        specialistLocationGatewayServicesCartIsApplicable
          ? undefined
          : productsHref
      }
      onInitialConfigurationHandled={handleInitialConfigurationHandled}
      onInitialFocusHandled={handleInitialFocusHandled}
      onServiceSelectionChange={
        specialistGuestCatalogTransaction
          ? undefined
          : handleServiceSelectionInteraction
      }
      onContinue={(selections) => {
        if (appointmentMode === "group" && isLocationEntryPoint) {
          const proposedResourceIds = resourceOfferingIdsForSelections(selections);
          const currentExecutions = groupResourceCatalogPermission.readCurrentExecutions();
          if (selectedGroupResourceOfferingIds.some(id => !proposedResourceIds.includes(id)) ||
            [...selectedGroupResourceOfferingIds, ...proposedResourceIds].some(id => !currentExecutions?.some(execution => execution.offeringId === id))) {
            toast.error(t("serviceAddOnsModal.submissionError"));
            return;
          }
        }
        if (activeGroupCatalogTransaction) {
          void commitGroupCatalogTransaction(selections);
          return;
        }

        void continueToBooking(selections);
      }}
      onContinuePackages={
        specialistLocationGatewayServicesCartIsApplicable
          ? undefined
          : continueWithPackages
      }
      onClose={ignoreModalCloseInPagePresentation}
      onPageExit={handleServicesPageExit}
      onRetry={groupResourceSelectionUnavailable ? groupResourceCatalogPermission.refresh : handleServicesRetry}
    />
  );
}
